import type {
  BusinessAuditTimelineReadModel,
  CoachSessionStopPreview,
  CoachSessionStopResult,
  ConnectivityReadModel,
  DeviceIncidentReadModel,
  FrontdeskOperatingFactReadModel,
  FrontdeskPricingOptionsReadModel,
  ManualReviewReadItem,
  ManualReviewSummaryReadModel,
  MemberReadModel,
  MemberRecord,
  MemberSpendPreviewResult,
  OfficialPaymentOperatorResultV1,
  OfficialPaymentRail,
  OrderDetail,
  OrderHistoryReadModel,
  OrderItemRecord,
  OrderStrongReportFilter,
  OrderStrongReportReadModel,
  PaymentMethod,
  PriceSnapshot,
  ProductCatalogPreviewResult,
  ProductSaleCatalogItem,
  ReceiptReprintResult,
  SaleReturnPhysicalDisposition,
  ServiceDutyReadModel,
  ServiceRequestReadModel,
  ServiceRoutingConfigurationReadModel,
  SessionStartAdjustmentPreview,
  TableSnapshot,
  VersionInfo,
  WorkforceMutationPending,
  WorkforcePrincipalContext,
} from "@billiard/contracts";
import {
  buildBusinessAuditTimelineCsv,
  businessAuditTimelineFileName,
  COACH_SESSION_OPERATIONS_VIEW,
  canOperateCoachSessionForRoles,
  canonicalCoachOnboardingInvitationIdempotencyKey,
  isOrderNumber,
  NETWORK_SIMULATION_OPERATOR_ROLES,
  orderAnomalyTerm,
  orderOpeningMethodTerm,
  orderStatusTerm,
  paymentMethodTerm,
  paymentStatusTerm,
  syncStateTerm,
  tableStateTerm,
} from "@billiard/contracts";
import { orderReportFilename } from "@billiard/domain";
import {
  type Query,
  type QueryClient,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRightLeft,
  BadgeAlert,
  Banknote,
  BarChart3,
  BellRing,
  Check,
  ChevronRight,
  CircleDollarSign,
  CircleHelp,
  Clock3,
  Database,
  Download,
  FileCheck2,
  FileUp,
  Gift,
  HardDrive,
  LayoutGrid,
  LockKeyhole,
  LogIn,
  LogOut,
  Megaphone,
  PackagePlus,
  Power,
  ReceiptText,
  RefreshCw,
  ScanBarcode,
  Search,
  ServerCog,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  UserRoundCheck,
  UserRoundCog,
  UsersRound,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import {
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Navigate,
  NavLink,
  type NavLinkRenderProps,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AnnouncementReminder } from "./AnnouncementReminder";
import {
  ApprovalCenterDesk,
  OrderCashRefundApprovalForm,
  PaidOrderItemReturnApprovalPanel,
  TableCleaningCompletionFeedback,
  type TableCleaningWorkItem,
  TableCleaningWorkItemAction,
  tableCleaningLightOffLabel,
  useTableCleaningWorkItemCompletion,
} from "./ApprovalCenterDesk";
import {
  ApiRequestError,
  acknowledgeServiceRequest,
  addGiftOrderItem,
  addOrderItem,
  adjustSessionStart,
  assertOperatorIdentityUnchanged,
  cancelPendingOperatorRequests,
  cancelWorkforceInvitation,
  checkoutSession,
  completeServiceRequest,
  completeTableCleaning,
  createCanonicalCoachOnboarding,
  createWorkforceInvitation,
  createWorkforcePasswordResetInvitation,
  currentOperatorIdentityEpoch,
  disableWorkforceAccount,
  endSessionEarly,
  findProductByBarcode,
  getBusinessAuditTimeline,
  getCoachConfiguration,
  getCoachSessionStopPreview,
  getCoachSessionStopResultByRequest,
  getConnectivity,
  getDeviceAdapterStatus,
  getDiagnosticBundle,
  getFrontdeskPricingOptions,
  getManualReviewSummary,
  getMemberSpendPreview,
  getOperatorSession,
  getOrder,
  getOrderStrongReport,
  getOrdersCsv,
  getOwnerReconciliationSummary,
  getPendingWorkforceMutation,
  getProductCatalogTemplate,
  getPublicVersion,
  getReconciliationSummary,
  getServiceDuty,
  getSessionStartAdjustmentPreview,
  getSystemStatus,
  getWorkforceInvitationAdmin,
  getWorkforcePrincipalContext,
  importProductCatalogWithProductSaveGuard,
  listCashShifts,
  listFrontdeskOperatingFacts,
  listInventoryProducts,
  listManualReview,
  listMembers,
  listProducts,
  listServiceRequestHistory,
  listServiceRequests,
  listServiceWorkItems,
  listTables,
  loginOperator,
  logoutOperator,
  type OperatorIdentity,
  type OperatorSessionContext,
  type OwnerBusinessSummary,
  openSession,
  pauseSession,
  previewProductCatalog,
  type ReconciliationSummary,
  recoverOfficialPayment,
  reissueWorkforceInvitation,
  releaseTable,
  reprintReceipt,
  requestSecondaryAuthorization,
  resolveDeviceIncidentManually,
  resumeSession,
  returnSaleableOrderItem,
  type ServiceRequestHistoryPageBoundary,
  setOperatorRequestIdentity,
  simulateNetwork,
  startOfficialPayment,
  subscribeOperatorAuthenticationInvalidation,
  transferServiceRequest,
  transferSession,
  upsertCanonicalWorkforceEmployment,
  voidSession,
} from "./api";
import { AutofillSafeInput, loginCredentialAttributes } from "./autofillGuard";
import { BusinessAuditTimelinePanel } from "./BusinessAuditTimelinePanel";
import { BusinessMetricDictionaryPanel } from "./BusinessMetricDictionaryPanel";
import { businessErrorMessage } from "./businessErrorMessage";
import { type CheckoutKind, CheckoutKindSelector, CheckoutSplitPanel } from "./CheckoutSplitPanel";
import { coachCustomerRequestAssignmentQuery } from "./CoachServiceAssignmentPanel";
import { CoachSessionOperationsPanel } from "./CoachSessionOperationsPanel";
import { CombinedCheckoutPanel } from "./CombinedCheckoutPanel";
import { CombinedCheckoutRecoveryProbe } from "./CombinedCheckoutRecoveryProbe";
import {
  CommissionPayableBusinessMetricDetailPanel,
  commissionPayableBusinessMetricDetailRouteFromSearch,
} from "./CommissionPayableBusinessMetricDetailPanel";
import {
  ConfirmedReceiptBusinessMetricDetailPanel,
  confirmedReceiptBusinessMetricDetailRouteFromSearch,
} from "./ConfirmedReceiptBusinessMetricDetailPanel";
import { CustomerReservationDesk } from "./CustomerReservationDesk";
import {
  beginCanonicalCoachOnboardingDispatch,
  type CanonicalCoachOnboardingRecovery,
  canonicalCoachOnboardingIdentityKey,
  clearCanonicalCoachOnboardingRecovery,
  finishCanonicalCoachOnboardingDispatch,
  readCanonicalCoachOnboardingRecovery,
} from "./canonicalCoachOnboardingRecovery";
import { readCheckoutMemberAuthority } from "./checkoutMemberAuthority";
import {
  applyCheckoutResultToRecovery,
  beginCheckoutDispatch,
  type CheckoutRecovery,
  type CheckoutRecoveryView,
  checkoutIdentityKey,
  checkoutOutcomeIsUnknown,
  claimCheckoutRecovery,
  clearCheckoutRecovery,
  finishCheckoutDispatch,
  markCheckoutOutcomeUnknown,
  readCheckoutRecovery,
} from "./checkoutRecovery";
import {
  type CombinedCheckoutRecoveryView,
  combinedCheckoutRecoveryBlocksTable,
  combinedCheckoutRecoveryContext,
  readCombinedCheckoutRecovery,
} from "./combinedCheckoutRecovery";
import {
  type ConnectivityTrustInput,
  connectivityTrustState,
  requireTrustedStoreAuthority,
  trustedConnectivity,
} from "./connectivity-trust";
import {
  type CustomerReservationRecoveryView,
  customerReservationRecoveryBlocksTable,
  customerReservationRecoveryContext,
  readCustomerReservationRecovery,
} from "./customerReservationRecovery";
import {
  type DeviceIncidentTrustState,
  deviceIncidentTrustState,
  startDeviceIncidentTrustClock,
} from "./device-status-trust";
import {
  beginDeviceIncidentResolutionDispatch,
  claimDeviceIncidentResolutionRecovery,
  clearDeviceIncidentResolutionRecovery,
  type DeviceIncidentResolutionRecovery,
  type DeviceIncidentResolutionRecoveryView,
  deviceIncidentResolutionIdentityKey,
  finishDeviceIncidentResolutionDispatch,
  markDeviceIncidentResolutionOutcomeUnknown,
  readDeviceIncidentResolutionRecovery,
} from "./deviceIncidentResolutionRecovery";
import { legacyRouteDestination, legacyRouteNavigationState } from "./legacyRouteNavigation";
import { MarketingUnsubscribePage } from "./MarketingUnsubscribePage";
import { MemberPrepaidOpenPanel } from "./MemberPrepaidOpenPanel";
import { NativeRuntimeNotice } from "./NativeRuntimeNotice";
import { isNativeRuntime, listenForNativeResume, nativeAppInfo } from "./nativeRuntime";
import type { NativeSessionMode } from "./nativeSession";
import { OrderReceivableBusinessMetricDetailPanel } from "./OrderReceivableBusinessMetricDetailPanel";
import { OwnerOperatingDashboardPanel } from "./OwnerOperatingDashboardPanel";
import { officialPaymentPresentation } from "./officialPaymentPresentation";
import {
  beginOfficialPaymentDispatch,
  claimOfficialPaymentRecovery,
  clearOfficialPaymentAfterDefiniteNoEffect,
  finishOfficialPaymentDispatch,
  markOfficialPaymentOutcomeUnknown,
  type OfficialPaymentRecovery,
  type OfficialPaymentRecoveryView,
  officialPaymentIdentityKey,
  officialPaymentOutcomeIsUnknown,
  officialPaymentRecoveryCanRetry,
  readOfficialPaymentRecovery,
  recordOfficialPaymentResult,
} from "./officialPaymentRecovery";
import { useOperatorMutation as useMutation } from "./operatorMutation";
import {
  isOperatorPasswordValid,
  OPERATOR_PASSWORD_MAX_LENGTH,
  OPERATOR_PASSWORD_MIN_LENGTH,
} from "./operatorPassword";
import { type OrderDisplayReference, orderDisplayReference } from "./orderDisplayNumber";
import {
  beginOrderItemAddDispatch,
  claimOrderItemAddRecovery,
  clearOrderItemAddRecovery,
  finishOrderItemAddDispatch,
  markOrderItemAddOutcomeUnknown,
  type OrderItemAddRecovery,
  type OrderItemAddRecoveryView,
  orderItemAddIdentityKey,
  orderItemAddRecoveryOutcomeIsUnknown,
  readOrderItemAddRecoveries,
} from "./orderItemAddRecovery";
import {
  beginOrderItemSaleReturnDispatch,
  claimOrderItemSaleReturnRecovery,
  clearOrderItemSaleReturnRecovery,
  finishOrderItemSaleReturnDispatch,
  markOrderItemSaleReturnOutcomeUnknown,
  type OrderItemSaleReturnRecovery,
  type OrderItemSaleReturnRecoveryView,
  orderItemSaleReturnIdentityKey,
  orderItemSaleReturnOutcomeIsUnknown,
  readOrderItemSaleReturnRecovery,
} from "./orderItemSaleReturnRecovery";
import { orderReceivableBusinessMetricDetailRouteFromSearch } from "./orderReceivableBusinessMetricDetail";
import {
  ProductCostBusinessMetricDetailPanel,
  productCostBusinessMetricDetailRouteFromSearch,
} from "./ProductCostBusinessMetricDetailPanel";
import {
  beginProductCatalogImportDispatch,
  claimProductCatalogImportRecovery,
  clearProductCatalogImportRecovery,
  finishProductCatalogImportDispatch,
  markProductCatalogImportOutcomeUnknown,
  type ProductCatalogImportRecovery,
  type ProductCatalogImportRecoveryView,
  productCatalogImportIdentityKey,
  productCatalogImportOutcomeIsUnknown,
  readProductCatalogImportRecovery,
} from "./productCatalogImportRecovery";
import { productSaveIdentityKey, readProductSaveRecovery } from "./productSaveRecovery";
import {
  RefundBusinessMetricDetailPanel,
  refundBusinessMetricDetailRouteFromSearch,
} from "./RefundBusinessMetricDetailPanel";
import { RoleHome } from "./RoleHome";
import {
  beginReceiptReprintDispatch,
  claimReceiptReprintRecovery,
  clearReceiptReprintRecovery,
  finishReceiptReprintDispatch,
  markReceiptReprintOutcomeUnknown,
  type ReceiptReprintRecovery,
  type ReceiptReprintRecoveryView,
  readReceiptReprintRecovery,
  receiptReprintIdentityKey,
} from "./receiptReprintRecovery";
import {
  isPersonalCashier,
  KioskProductOrderWorkItemPanel,
  ServiceDutyStaffPanel,
  serviceDutyQueryKey,
  serviceWorkItemsQueryKey,
} from "./ServiceDutyDesk";
import { ServiceRequestTransferComposer } from "./ServiceRequestTransferComposer";
import {
  StorePackageOrderPanel,
  storePackageOperationRecoveryBlocksOrderWrites,
} from "./StorePackageOrderPanel";
import { SupportAccessPanel } from "./SupportAccessPanel";
import {
  beginServiceRequestTransferDispatch,
  claimServiceRequestTransferRecovery,
  clearServiceRequestTransferRecovery,
  finishServiceRequestTransferDispatch,
  markServiceRequestTransferOutcomeUnknown,
  readServiceRequestTransferRecovery,
  type ServiceRequestTransferFrozenCommand,
  type ServiceRequestTransferRecovery,
  type ServiceRequestTransferRecoveryView,
  serviceRequestTransferIdentityKey,
  serviceRequestTransferOutcomeIsUnknown,
} from "./serviceRequestTransferRecovery";
import {
  beginServiceRequestTransitionDispatch,
  claimServiceRequestTransitionRecovery,
  clearServiceRequestTransitionRecovery,
  finishServiceRequestTransitionDispatch,
  markServiceRequestTransitionOutcomeUnknown,
  readServiceRequestTransitionRecovery,
  type ServiceRequestTransitionAction,
  type ServiceRequestTransitionRecovery,
  type ServiceRequestTransitionRecoveryView,
  serviceRequestTransitionIdentityKey,
  serviceRequestTransitionOutcomeIsUnknown,
} from "./serviceRequestTransitionRecovery";
import {
  type ServiceRequestWorkItemReadModel,
  serviceRequestDeadlineLabel,
  serviceRequestEscalationPresentation,
  serviceRequestStaffRows,
} from "./serviceRequestWorkItemPresentation";
import {
  beginSessionBillingDispatch,
  claimSessionBillingRecovery,
  clearSessionBillingRecovery,
  finishSessionBillingDispatch,
  markSessionBillingOutcomeUnknown,
  readSessionBillingRecovery,
  type SessionBillingAction,
  type SessionBillingRecovery,
  type SessionBillingRecoveryView,
  sessionBillingIdentityKey,
  sessionBillingOutcomeIsUnknown,
} from "./sessionBillingRecovery";
import {
  beginSessionTransferDispatch,
  claimSessionTransferRecovery,
  clearSessionTransferRecovery,
  finishSessionTransferDispatch,
  markSessionTransferOutcomeUnknown,
  readSessionTransferRecovery,
  type SessionTransferRecovery,
  type SessionTransferRecoveryView,
  sessionTransferIdentityKey,
  sessionTransferOutcomeIsUnknown,
} from "./sessionTransferRecovery";
import {
  beginTableCloseoutDispatch,
  claimTableCloseoutRecovery,
  clearTableCloseoutRecovery,
  finishTableCloseoutDispatch,
  markTableCloseoutOutcomeUnknown,
  readTableCloseoutRecovery,
  type TableCloseoutAction as TableCloseoutActionKind,
  type TableCloseoutRecovery,
  type TableCloseoutRecoveryView,
  tableCloseoutIdentityKey,
  tableCloseoutOutcomeIsUnknown,
} from "./tableCloseoutRecovery";
import {
  beginTableSessionOpenDispatch,
  claimTableSessionOpenRecovery,
  clearTableSessionOpenRecovery,
  finishTableSessionOpenDispatch,
  markTableSessionOpenOutcomeUnknown,
  readTableSessionOpenRecovery,
  type TableSessionOpenRecovery,
  type TableSessionOpenRecoveryView,
  tableSessionOpenIdentityKey,
  tableSessionOpenOutcomeIsUnknown,
} from "./tableSessionOpenRecovery";
import { WorkforceInvitationActivationPage } from "./WorkforceInvitationActivationPage";

export function receiptReprintNotice(result: ReceiptReprintResult): {
  message: string;
  needsAttention: boolean;
} {
  const deviceLabel = result.evidence === "MOCK" ? "测试设备（模拟，非真实硬件）" : "门店打印设备";
  if (result.status === "NEEDS_ATTENTION") {
    return {
      message: `小票补打没有确认完成，请检查${deviceLabel}和纸质小票；设备异常提醒会继续保留。`,
      needsAttention: true,
    };
  }
  return {
    message: `小票补打请求已登记并交给${deviceLabel}；如果失败或结果未知，设备异常提醒会继续保留。`,
    needsAttention: false,
  };
}

export function dispatchReceiptReprint<T>(
  connectivity: ConnectivityTrustInput,
  orderId: string,
  dispatch: (targetOrderId: string) => T,
): T {
  requireTrustedStoreAuthority(connectivity, "重新打印小票");
  return dispatch(orderId);
}

const CashShiftDesk = lazy(() =>
  import("./CashShiftDesk").then((module) => ({ default: module.CashShiftDesk })),
);
const CoachDesk = lazy(() =>
  import("./CoachDesk").then((module) => ({ default: module.CoachDesk })),
);
const ProductsDesk = lazy(() =>
  import("./ProductsWorkspace").then((module) => ({ default: module.ProductsWorkspace })),
);
const CustomerStorageDesk = lazy(() =>
  import("./CustomerStorageDesk").then((module) => ({ default: module.CustomerStorageDesk })),
);
const CustomerRentalDesk = lazy(() =>
  import("./CustomerRentalDesk").then((module) => ({ default: module.CustomerRentalDesk })),
);
const MembersDesk = lazy(() =>
  import("./CommercialDesk").then((module) => ({ default: module.MembersDesk })),
);
const MobileBarcodeScanner = lazy(() =>
  import("./CommercialDesk").then((module) => ({ default: module.MobileBarcodeScanner })),
);
const GroupBuyDesk = lazy(() =>
  import("./GroupBuyDesk").then((module) => ({ default: module.GroupBuyDesk })),
);
const AnnouncementDesk = lazy(() =>
  import("./AnnouncementDesk").then((module) => ({ default: module.AnnouncementDesk })),
);
const OperatingConfigurationDesk = lazy(() =>
  import("./OperatingConfigurationDesk").then((module) => ({
    default: module.OperatingConfigurationDesk,
  })),
);
const WorkforceManagementDesk = lazy(() =>
  import("./WorkforceManagementDesk").then((module) => ({
    default: module.WorkforceManagementDesk,
  })),
);
const MyWorkforceProfileDesk = lazy(() =>
  import("./MyWorkforceProfileDesk").then((module) => ({
    default: module.MyWorkforceProfileDesk,
  })),
);
const WorkforceAttendanceDesk = lazy(() =>
  import("./WorkforceAttendanceDesk").then((module) => ({
    default: module.WorkforceAttendanceDesk,
  })),
);
const WorkforcePayrollDesk = lazy(() =>
  import("./WorkforcePayrollDesk").then((module) => ({
    default: module.WorkforcePayrollDesk,
  })),
);

type OperatorRole = OperatorIdentity["role"];

const exactPublicOperatorQueryKeys = new Set(["operator-session", "public-version"]);

function isPrivateOperatorQuery(query: Query, preserveWorkforceContext: boolean): boolean {
  const root = query.queryKey[0];
  if (
    preserveWorkforceContext &&
    (root === "workforce-principal-context" ||
      root === "workforce-pending-operation" ||
      root === "workforce-mutation-recovery")
  ) {
    return false;
  }
  return !(
    query.queryKey.length === 1 &&
    typeof root === "string" &&
    exactPublicOperatorQueryKeys.has(root)
  );
}

export async function clearPrivateOperatorQueries(
  queryClient: QueryClient,
  preserveWorkforceContext = false,
): Promise<void> {
  cancelPendingOperatorRequests();
  const predicate = (query: Query) => isPrivateOperatorQuery(query, preserveWorkforceContext);
  await queryClient.cancelQueries({ predicate });
  queryClient.removeQueries({ predicate });
}

export function operatorSessionChangeRequiresRefresh(
  currentScope: string | null,
  incomingScope: string | null,
): boolean {
  return currentScope !== incomingScope;
}

export function personalServiceDutySession(
  authenticationEvidence: "SIMULATED" | "STORE_ACCEPTED",
  sessionContext: Pick<OperatorSessionContext, "mode"> | undefined,
): boolean {
  return authenticationEvidence === "SIMULATED" || sessionContext?.mode === "PERSONAL";
}

export function memberFundsSessionMode(
  authenticationEvidence: "SIMULATED" | "STORE_ACCEPTED",
  sessionContext: Pick<OperatorSessionContext, "mode"> | undefined,
): NativeSessionMode | undefined {
  return authenticationEvidence === "SIMULATED" ? "PERSONAL" : sessionContext?.mode;
}

export function announcementReminderEnabled(
  sessionContext: Pick<OperatorSessionContext, "mode"> | undefined,
  pathname: string,
  defaultRoute: string,
): boolean {
  return (
    sessionContext?.mode !== "SHARED_SHIFT" &&
    pathname === defaultRoute &&
    pathname !== "/announcements"
  );
}

export function operatorPrivateQueryScope(
  operator: OperatorIdentity,
  workforceContext?: WorkforcePrincipalContext,
): string {
  return JSON.stringify([
    operator.tenantId,
    operator.storeId,
    operator.id,
    operator.role,
    operator.coachProfileId,
    workforceContext?.account.identityVersion ?? null,
    workforceContext?.projection.revision ?? null,
    workforceContext?.projection.contentSha256 ?? null,
    workforceContext?.currentStoreContext ?? null,
  ]);
}

const allOperatorRoles: readonly OperatorRole[] = [
  "CASHIER",
  "MANAGER",
  "OWNER",
  "WAITER",
  "COACH",
];
const networkSimulationOperatorRoles = new Set<OperatorRole>(NETWORK_SIMULATION_OPERATOR_ROLES);

const roleLabels: Record<OperatorRole, string> = {
  CASHIER: "前台收银员",
  MANAGER: "店长",
  OWNER: "老板",
  WAITER: "服务员",
  COACH: "助教",
};

export const routePermissions: Record<string, readonly OperatorRole[]> = {
  "/home": allOperatorRoles,
  "/frontdesk": ["CASHIER", "MANAGER", "OWNER"],
  "/announcements": ["CASHIER", "MANAGER", "OWNER", "WAITER", "COACH"],
  "/shifts": allOperatorRoles,
  "/coach": ["CASHIER", "MANAGER", "OWNER", "COACH"],
  "/tasks": ["CASHIER", "MANAGER", "OWNER", "WAITER"],
  "/workdesk": ["CASHIER", "MANAGER", "OWNER", "WAITER"],
  "/sessions": ["CASHIER", "MANAGER", "OWNER"],
  "/coaches/workbench": ["CASHIER", "MANAGER", "OWNER", "COACH"],
  "/orders": ["CASHIER", "MANAGER", "OWNER"],
  "/review": ["CASHIER", "MANAGER", "OWNER"],
  "/products": ["MANAGER", "OWNER"],
  "/storage": ["CASHIER", "MANAGER", "OWNER"],
  "/rentals": ["CASHIER", "MANAGER", "OWNER"],
  "/group-buy": ["CASHIER", "MANAGER", "OWNER"],
  "/members": ["CASHIER", "MANAGER", "OWNER"],
  "/staff": ["CASHIER", "MANAGER", "OWNER", "WAITER"],
  "/attendance": ["MANAGER", "CASHIER", "WAITER", "COACH"],
  "/payroll": allOperatorRoles,
  "/workforce": ["MANAGER", "OWNER"],
  "/me": allOperatorRoles,
  "/owner": ["MANAGER", "OWNER"],
  "/configuration": ["MANAGER", "OWNER"],
  "/system": ["MANAGER", "OWNER"],
  "/help": allOperatorRoles,
};

export function canAccessRoute(role: OperatorRole, path: string): boolean {
  return routePermissions[path]?.includes(role) ?? false;
}

export function canAccessRouteForRoles(roles: readonly OperatorRole[], path: string): boolean {
  return roles.some((role) => canAccessRoute(role, path));
}

export function effectiveRolesFromWorkforceContext(
  context: WorkforcePrincipalContext,
): OperatorRole[] {
  const current = context.currentStoreContext;
  if (!current || context.resolutionStatus !== "RESOLVED") return [];
  const roles = new Set<OperatorRole>(current.roles);
  if (current.ownershipId) roles.add("OWNER");
  return [...roles].sort();
}

export function canOperateStandardSessionForRoles(roles: readonly OperatorRole[]): boolean {
  return roles.includes("MANAGER") || roles.includes("CASHIER");
}

export function canAddOrderItemsForRoles(roles: readonly OperatorRole[]): boolean {
  return roles.includes("CASHIER");
}

export function canCollectPaymentForRoles(roles: readonly OperatorRole[]): boolean {
  return roles.includes("MANAGER") || roles.includes("CASHIER");
}

export function canViewCombinedCheckoutForRoles(roles: readonly OperatorRole[]): boolean {
  return roles.includes("OWNER") || canCollectPaymentForRoles(roles);
}

export function reservationModuleEnabled(status: ConnectivityReadModel | undefined): boolean {
  return status?.selectedConditionalModules?.includes("RESERVATION") ?? false;
}

export function customerSelfServiceReservationModuleEnabled(
  status: ConnectivityReadModel | undefined,
): boolean {
  return (
    reservationModuleEnabled(status) &&
    (status?.selectedConditionalModules?.includes("CUSTOMER_SELF_SERVICE_RESERVATION") ?? false)
  );
}

export function checkoutKindAfterSplitPlanDiscovery(
  current: CheckoutKind,
  focusSplit: boolean,
): CheckoutKind {
  return focusSplit ? "SPLIT" : current;
}

export function canUseNetworkSimulation(
  roles: readonly OperatorRole[],
  authenticationEvidence: "SIMULATED" | "STORE_ACCEPTED",
  simulationAllowed: boolean,
): boolean {
  return (
    authenticationEvidence === "SIMULATED" &&
    simulationAllowed &&
    roles.some((role) => networkSimulationOperatorRoles.has(role))
  );
}

export type SessionDialogTab = "ORDER" | "COACH" | "BILLING" | "TRANSFER" | "CHECKOUT";

export function visibleSessionDialogTab(
  tab: SessionDialogTab,
  visibility: {
    productOrderSelected: boolean;
    canOperateStandardSession: boolean;
    canCollectPayment: boolean;
    canOperateCoachSession?: boolean;
  },
): SessionDialogTab {
  if (tab === "BILLING") return tab;
  if (tab === "ORDER" && visibility.productOrderSelected) return tab;
  if (tab === "COACH" && visibility.canOperateCoachSession) return tab;
  if (tab === "TRANSFER" && visibility.canOperateStandardSession) return tab;
  if (tab === "CHECKOUT" && visibility.canCollectPayment) return tab;
  return visibility.productOrderSelected ? "ORDER" : "BILLING";
}

export function frontdeskCoachSessionDeepLink(
  search: string,
  tables: readonly TableSnapshot[],
): TableSnapshot | null {
  const parameters = new URLSearchParams(search);
  const sessionIds = parameters.getAll("sessionId");
  const tabs = parameters.getAll("tab");
  if (sessionIds.length !== 1 || tabs.length !== 1 || tabs[0] !== COACH_SESSION_OPERATIONS_VIEW) {
    return null;
  }
  const sessionId = sessionIds[0];
  if (
    sessionId === undefined ||
    sessionId.length === 0 ||
    sessionId.length > 64 ||
    sessionId.trim() !== sessionId
  ) {
    return null;
  }
  return tables.find((table) => table.state === "IN_USE" && table.sessionId === sessionId) ?? null;
}

export function simulatedSessionAllowedForDeployment(deploymentEnvironment: string): boolean {
  const normalized = deploymentEnvironment.trim().toLowerCase();
  return new Set(["local-demo", "development", "test"]).has(normalized);
}

export type OperatorAccessGateReason =
  | "SESSION_UNAVAILABLE"
  | "WORKFORCE_UNAVAILABLE"
  | "SIMULATED_NOT_ALLOWED";

export function operatorAccessGate(input: {
  deploymentEnvironment: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED" | null;
  sessionHasData: boolean;
  sessionError: unknown;
  workforceHasData: boolean;
  workforceError: unknown;
}): OperatorAccessGateReason | null {
  if (input.sessionError || !input.sessionHasData) return "SESSION_UNAVAILABLE";
  if (
    input.evidence === "SIMULATED" &&
    !simulatedSessionAllowedForDeployment(input.deploymentEnvironment)
  ) {
    return "SIMULATED_NOT_ALLOWED";
  }
  if (input.evidence === "STORE_ACCEPTED" && (input.workforceError || !input.workforceHasData)) {
    return "WORKFORCE_UNAVAILABLE";
  }
  return null;
}

export function defaultRouteForRole(role: OperatorRole): string {
  return routePermissions["/home"]?.includes(role) ? "/home" : "/help";
}

export function defaultRouteForRoles(roles: readonly OperatorRole[]): string {
  return roles.some((role) => allOperatorRoles.includes(role)) ? "/home" : "/help";
}

export function sessionLandingRoute(
  role: OperatorRole,
  session: OperatorSessionContext | undefined,
): string {
  if (session?.mode !== "SHARED_SHIFT") return defaultRouteForRole(role);
  return session.shiftStatus === "OPEN" ? "/frontdesk" : "/shifts";
}

export function sessionCanOpenRoute(
  session: OperatorSessionContext | undefined,
  path: string,
): boolean {
  if (session?.mode !== "SHARED_SHIFT" || session.shiftStatus === "OPEN") return true;
  return path === "/shifts" || path === "/help";
}

export function operatorSessionEvidenceLabel(evidence: "SIMULATED" | "STORE_ACCEPTED"): string {
  return evidence === "SIMULATED" ? "演示账号" : "账号已启用";
}

export function operatorDataEvidenceLabel(evidence: "SIMULATED" | "STORE_ACCEPTED"): string {
  return evidence === "SIMULATED" ? "演示数据" : "门店数据";
}

export function isMemberPaymentAmountValid({
  selectedMember,
  orderTotalFen,
  memberOnly,
}: {
  selectedMember: Pick<MemberRecord, "id" | "availableBalanceFen"> | undefined;
  orderTotalFen: number | undefined;
  memberOnly: boolean;
}): boolean {
  if (!memberOnly) return true;
  return Boolean(selectedMember && orderTotalFen !== undefined && orderTotalFen > 0);
}

export interface MemberCheckoutPlan {
  walletAmountFen: number;
  externalAmountFen: number;
  externalLabel: string;
  externalMethod: "CASH" | "STATIC_WECHAT" | "STATIC_ALIPAY" | null;
}

export interface CheckoutFeedback {
  code: string | null;
  message: string;
}

export type OfficialPaymentFeedback =
  | { kind: "RESULT"; result: OfficialPaymentOperatorResultV1 }
  | { kind: "ERROR"; code: string | null; message: string };

export function OfficialPaymentFeedbackNotice({
  feedback,
}: {
  feedback: OfficialPaymentFeedback | null;
}): React.JSX.Element | null {
  if (!feedback) return null;
  if (feedback.kind === "ERROR") {
    return (
      <section className="error-band checkout-failure-notice" role="alert">
        <AlertTriangle size={18} aria-hidden="true" />
        <div>
          <strong>正式支付结果没有安全确认</strong>
          <p>{feedback.message}</p>
        </div>
      </section>
    );
  }
  const presentation = officialPaymentPresentation(feedback.result);
  const confirmedSettlement =
    presentation.canRecognizeAsPaid && feedback.result.gatewaySettlement === "SETTLED";
  return (
    <section
      className={`payment-notice ${confirmedSettlement ? "" : "is-pending"}`}
      aria-live="polite"
    >
      {confirmedSettlement ? (
        <Check size={18} aria-hidden="true" />
      ) : (
        <BadgeAlert size={18} aria-hidden="true" />
      )}
      <span>
        <strong>{presentation.title}</strong>
        <small>{presentation.description}</small>
        <small>{presentation.nextStep}</small>
        <small>
          {feedback.result.providerEvidence === "PROVIDER_ACCEPTED"
            ? "结果来源：已接入渠道"
            : "结果来源：测试渠道"}
          {` · 门店结账：${feedback.result.gatewaySettlement === "SETTLED" ? "已完成" : "未完成"}`}
        </small>
      </span>
    </section>
  );
}

export function memberCheckoutPlan({
  orderTotalFen,
  memberOnly,
}: {
  orderTotalFen: number | undefined;
  memberOnly: boolean;
}): MemberCheckoutPlan | null {
  if (orderTotalFen === undefined || orderTotalFen <= 0 || !memberOnly) {
    return null;
  }
  return {
    walletAmountFen: orderTotalFen,
    externalAmountFen: 0,
    externalLabel: "外部应收",
    externalMethod: null,
  };
}

export function orderCheckoutOutstandingFen(
  order: (Pick<OrderDetail, "totalFen"> & Partial<Pick<OrderDetail, "paidFen">>) | undefined,
): number | undefined {
  if (!order) return undefined;
  return Math.max(0, order.totalFen - (order.paidFen ?? 0));
}

export type MemberCombinedCheckoutMethod = "CASH" | "STATIC_WECHAT" | "STATIC_ALIPAY";

export function memberCombinedCheckoutMethodLabel(method: MemberCombinedCheckoutMethod): string {
  return method === "CASH" ? "现金" : method === "STATIC_WECHAT" ? "微信收款码" : "支付宝收款码";
}

export function checkoutMemberAmountFen(value: string): number | null {
  const normalized = value.trim();
  if (!/^(?:0|[1-9][0-9]{0,7})(?:\.[0-9]{1,2})?$/u.test(normalized)) return null;
  const [yuan = "0", fen = ""] = normalized.split(".");
  const amountFen = Number(yuan) * 100 + Number(fen.padEnd(2, "0"));
  return Number.isSafeInteger(amountFen) ? amountFen : null;
}

export function memberCombinedCheckoutPlan({
  orderTotalFen,
  memberAmountYuan,
  externalMethod,
}: {
  orderTotalFen: number | undefined;
  memberAmountYuan: string;
  externalMethod: MemberCombinedCheckoutMethod;
}): MemberCheckoutPlan | null {
  const walletAmountFen = checkoutMemberAmountFen(memberAmountYuan);
  if (
    orderTotalFen === undefined ||
    orderTotalFen <= 1 ||
    walletAmountFen === null ||
    walletAmountFen <= 0 ||
    walletAmountFen >= orderTotalFen
  ) {
    return null;
  }
  return {
    walletAmountFen,
    externalAmountFen: orderTotalFen - walletAmountFen,
    externalLabel: `${memberCombinedCheckoutMethodLabel(externalMethod)}应收`,
    externalMethod,
  };
}

export function CheckoutPaymentPlan({
  plan,
  deferMemberBalance = false,
}: {
  plan: MemberCheckoutPlan;
  deferMemberBalance?: boolean;
}): React.JSX.Element {
  return (
    <section className="checkout-payment-plan" aria-label="本次收款计划" aria-live="polite">
      <div>
        <span>本次从会员余额扣除</span>
        <strong>{formatFee(plan.walletAmountFen)}</strong>
      </div>
      <div>
        <span>{plan.externalAmountFen > 0 ? plan.externalLabel : "还需现金或扫码"}</span>
        <strong>{formatFee(plan.externalAmountFen)}</strong>
      </div>
      <p>
        {plan.externalMethod === null
          ? deferMemberBalance
            ? "当前离线，台单会先结账并进入待清洁；会员余额不会显示为已扣，将在联网后按本单金额扣除。"
            : "会员系统确认足额扣款后才会结账；余额不足会停止结账，本单不会改为现金或扫码收取。"
          : plan.externalMethod === "CASH"
            ? "系统先按本计划扣会员余额，剩余现金由收银员当面确认。两路都成功才会结账；结果不明确时只能恢复这笔原请求。"
            : `系统先按本计划扣会员余额，剩余${memberCombinedCheckoutMethodLabel(plan.externalMethod)}由收银员确认，结账后保持“待渠道核对”。结果不明确时只能恢复这笔原请求。`}
      </p>
    </section>
  );
}

function memberSpendBusinessTypeLabel(
  businessType: MemberSpendPreviewResult["entries"][number]["businessType"],
): string {
  const labels = {
    STORED_VALUE: "储值余额",
    PROMOTIONAL: "赠送余额",
    LOYALTY: "积分权益",
    TIME_CREDIT: "时长权益",
    COUNT_CREDIT: "次数权益",
    CUSTOM: "其他权益",
  } as const;
  return labels[businessType];
}

function formatMemberWalletAmount(
  amount: number,
  entry: MemberSpendPreviewResult["entries"][number],
): string {
  if (entry.unit === "CNY_FEN") return formatFee(amount);
  const divisor = 10 ** entry.precision;
  const value = (amount / divisor).toFixed(entry.precision);
  const unitLabel =
    entry.unit === "POINT"
      ? "积分"
      : entry.unit === "MINUTE"
        ? "分钟"
        : entry.unit === "COUNT"
          ? "次"
          : (entry.customUnitLabel ?? "单位");
  return `${value} ${unitLabel}`;
}

function MemberSpendPreviewPanel({
  preview,
  loading,
  error,
  inputReady,
  memberFundsAvailable,
  remainingOnsiteFen,
  onRetry,
}: {
  preview: MemberSpendPreviewResult | null;
  loading: boolean;
  error: string | null;
  inputReady: boolean;
  memberFundsAvailable: boolean;
  remainingOnsiteFen: number | null;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <section className="member-spend-preview" aria-label="会员钱包扣款预览" aria-live="polite">
      <header>
        <div>
          <strong>会员钱包扣款预览</strong>
          <small>以下分配由会员系统按当前台单规则给出，页面不会自行选择钱包。</small>
        </div>
      </header>
      <p className="member-spend-refund-policy">
        退款只按原钱包退回，不能退现金；不可退钱包金额不退。
      </p>
      {!memberFundsAvailable ? (
        <div className="disabled-reason">
          当前无法连接会员钱包服务，不能取得本次权威扣款预览，也不能确认会员余额收款。
        </div>
      ) : !inputReady ? (
        <div className="member-spend-preview-state">
          请先选定会员并填写有效的会员扣款金额，系统随后核对每个钱包。
        </div>
      ) : loading ? (
        <div className="member-spend-preview-state">正在核对当前会员、台单和扣款金额…</div>
      ) : error ? (
        <div className="member-spend-preview-error" role="alert">
          <span>会员钱包扣款预览失败：{error}。旧预览已隐藏，本次不能确认收款。</span>
          <button className="button secondary" type="button" onClick={onRetry}>
            <RefreshCw size={15} aria-hidden="true" />
            重新获取扣款预览
          </button>
        </div>
      ) : preview ? (
        <>
          <div className="member-spend-wallet-list">
            {preview.entries.map((entry) => (
              <article key={entry.walletDefinitionId}>
                <header>
                  <strong>{entry.walletName}</strong>
                  <span>{memberSpendBusinessTypeLabel(entry.businessType)}</span>
                </header>
                <dl>
                  <div>
                    <dt>扣前额度</dt>
                    <dd>{formatMemberWalletAmount(entry.balanceBefore, entry)}</dd>
                  </div>
                  <div>
                    <dt>本次扣减</dt>
                    <dd>{formatMemberWalletAmount(Math.abs(entry.delta), entry)}</dd>
                  </div>
                  <div>
                    <dt>扣后额度</dt>
                    <dd>{formatMemberWalletAmount(entry.balanceAfter, entry)}</dd>
                  </div>
                  <div>
                    <dt>抵扣金额</dt>
                    <dd>{formatFee(entry.coveredAmountFen)}</dd>
                  </div>
                </dl>
                <p className={entry.refundableAtCheckout ? "is-refundable" : "is-nonrefundable"}>
                  {entry.refundableAtCheckout
                    ? "退款时可以退回这个原钱包"
                    : "这个钱包抵扣的金额不可退回"}
                </p>
              </article>
            ))}
          </div>
          <div className="member-spend-preview-total">
            <span>会员钱包本次抵扣</span>
            <strong>{formatFee(preview.walletCoveredAmountFen)}</strong>
            <span>剩余现场收款</span>
            <strong>{formatFee(remainingOnsiteFen ?? 0)}</strong>
          </div>
        </>
      ) : null}
    </section>
  );
}

function rolesFor(path: string): readonly OperatorRole[] {
  return routePermissions[path] ?? [];
}

const navigationSections = [
  {
    id: "operations",
    label: "日常营业",
    items: [
      { to: "/home", label: "首页", icon: LayoutGrid, roles: rolesFor("/home") },
      { to: "/frontdesk", label: "前台", icon: LayoutGrid, roles: rolesFor("/frontdesk") },
      {
        to: "/announcements",
        label: "公告",
        icon: Megaphone,
        roles: rolesFor("/announcements"),
      },
      { to: "/shifts", label: "开店与交接班", icon: Clock3, roles: rolesFor("/shifts") },
      { to: "/coach", label: "助教", icon: UsersRound, roles: rolesFor("/coach") },
      { to: "/orders", label: "订单", icon: ReceiptText, roles: rolesFor("/orders") },
      { to: "/review", label: "工作中心", icon: BadgeAlert, roles: rolesFor("/review") },
    ],
  },
  {
    id: "customers",
    label: "会员与商品",
    items: [
      { to: "/products", label: "商品库存", icon: PackagePlus, roles: rolesFor("/products") },
      { to: "/storage", label: "顾客寄存", icon: PackagePlus, roles: rolesFor("/storage") },
      { to: "/rentals", label: "顾客租赁", icon: PackagePlus, roles: rolesFor("/rentals") },
      { to: "/group-buy", label: "团购", icon: FileCheck2, roles: rolesFor("/group-buy") },
      { to: "/members", label: "会员", icon: CircleDollarSign, roles: rolesFor("/members") },
    ],
  },
  {
    id: "management",
    label: "人员与权限",
    items: [
      {
        to: "/staff",
        label: "员工工作中心",
        icon: UserRoundCheck,
        roles: rolesFor("/staff"),
      },
      { to: "/attendance", label: "出勤排班", icon: Clock3, roles: rolesFor("/attendance") },
      { to: "/payroll", label: "工资确认", icon: CircleDollarSign, roles: rolesFor("/payroll") },
      { to: "/workforce", label: "人员管理", icon: UsersRound, roles: rolesFor("/workforce") },
      { to: "/me", label: "我的资料", icon: UserRoundCog, roles: rolesFor("/me") },
    ],
  },
  {
    id: "venue",
    label: "门店经营",
    items: [
      { to: "/owner", label: "经营看板", icon: BarChart3, roles: rolesFor("/owner") },
      {
        to: "/configuration",
        label: "门店设置",
        icon: SlidersHorizontal,
        roles: rolesFor("/configuration"),
      },
    ],
  },
  {
    id: "support",
    label: "技术支持",
    items: [
      { to: "/system", label: "设备与连接", icon: Settings, roles: rolesFor("/system") },
      { to: "/help", label: "说明", icon: CircleHelp, roles: rolesFor("/help") },
    ],
  },
];

export function navigationSectionsForRole(role: OperatorRole) {
  return navigationSectionsForRoles([role]);
}

export function navigationSectionsForRoles(roles: readonly OperatorRole[]) {
  return navigationSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item.roles.some((role) => roles.includes(role))),
    }))
    .filter((section) => section.items.length > 0);
}

export function navigationItemAllowedByIdentityEvidence(
  path: string,
  authenticationEvidence: "SIMULATED" | "STORE_ACCEPTED",
): boolean {
  return authenticationEvidence === "STORE_ACCEPTED" || (path !== "/workforce" && path !== "/me");
}

const stageLabels: Record<ConnectivityReadModel["syncStage"], string> = {
  IDLE: "准备同步",
  UPLOADING_OFFLINE_ORDERS: "上传离线订单",
  RECONCILING_PAYMENTS: "核对收款",
  PULLING_CONFIGURATION: "拉取配置",
  CHECKING_CONFLICTS: "检查冲突",
};

function formatFee(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

type FrontdeskPricingSelection = {
  pricingPlanId: string;
  expectedPricingPlanVersion: number;
  pricingPlanName: string;
};

function pricingPlanChargeSummary(plan: PriceSnapshot): string {
  if ((plan.billingMode ?? "HOURLY") === "PACKAGE") {
    return `包时 ${plan.packageMinutes} 分钟，固定 ${formatFee(plan.packagePriceFen ?? 0)}；超过后每小时 ${formatFee(plan.hourlyRateFen)}`;
  }
  return `按时计费，每小时 ${formatFee(plan.hourlyRateFen)}`;
}

function pricingPlanMinimumSummary(plan: PriceSnapshot): string {
  if ((plan.billingMode ?? "HOURLY") === "PACKAGE") {
    const minimumAmount =
      (plan.minimumAmountFen ?? 0) > 0
        ? `最低消费 ${formatFee(plan.minimumAmountFen ?? 0)}`
        : "不设最低消费金额";
    return `包时固定时长不另设最低计费时长；${minimumAmount}`;
  }
  const minimumMinutes =
    plan.minimumMinutes > 0 ? `最低计费 ${plan.minimumMinutes} 分钟` : "不设最低计费时长";
  const minimumAmount =
    (plan.minimumAmountFen ?? 0) > 0
      ? `最低消费 ${formatFee(plan.minimumAmountFen ?? 0)}`
      : "不设最低消费金额";
  return `${minimumMinutes}；${minimumAmount}`;
}

function pricingPlanRoundingSummary(plan: PriceSnapshot): string {
  const mode =
    (plan.roundingMode ?? "UP") === "UP"
      ? "向上取整"
      : plan.roundingMode === "DOWN"
        ? "向下取整"
        : "就近取整";
  const scope =
    (plan.roundingScope ?? "SEGMENT") === "SESSION" ? "整次台局统一计算" : "每个价格时段分别计算";
  return `每 ${plan.roundingMinutes} 分钟为一步，${mode}；${scope}`;
}

function pricingPlanPauseSummary(plan: PriceSnapshot): string {
  if ((plan.billingMode ?? "HOURLY") === "PACKAGE") return "包时台单暂不支持暂停计费";
  return (plan.pauseBillingMode ?? "FREE") === "FREE" ? "暂停期间不计费" : "暂停期间继续计费";
}

const pricingWeekdayNames = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
const pricingWeekdayDisplayOrder = [1, 2, 3, 4, 5, 6, 0] as const;
const legacyPriceSnapshotTimeZone = "Asia/Shanghai";

function pricingMinuteLabel(minute: number): string {
  if (minute === 1_440) return "24:00";
  return `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
}

function pricingWeekdaysLabel(daysOfWeek: readonly number[] | undefined): string {
  if (!daysOfWeek || daysOfWeek.length === 7) return "每天";
  return pricingWeekdayDisplayOrder
    .filter((day) => daysOfWeek.includes(day))
    .map((day) => pricingWeekdayNames[day])
    .join("、");
}

function PricingPlanTimeBandSummary({ plan }: { plan: PriceSnapshot }): React.JSX.Element {
  const timeBands = plan.timeBands ?? [];
  const cutover = plan.businessDayCutover ?? "00:00";
  const packagePlan = (plan.billingMode ?? "HOURLY") === "PACKAGE";
  const businessDayRule =
    cutover === "00:00"
      ? "营业日每天 00:00 切换"
      : `营业日于 ${cutover} 切换，${cutover} 前仍归前一营业日`;
  return (
    <span className="frontdesk-pricing-time-bands">
      <span className="frontdesk-pricing-time-band-rule">
        {packagePlan ? "包时结束后的特殊时段" : "特殊时段"}按门店当地自然日判断；
        {businessDayRule}；{packagePlan ? "包时结束后" : ""}未列时段按基础价，每小时{" "}
        {formatFee(plan.hourlyRateFen)}。
      </span>
      {timeBands.length === 0 ? (
        <span className="frontdesk-pricing-time-band-row">当前没有特殊时段。</span>
      ) : (
        timeBands.map((band) => (
          <span className="frontdesk-pricing-time-band-row" key={band.id}>
            <strong>{band.name}</strong>：{pricingWeekdaysLabel(band.daysOfWeek)}，
            {pricingMinuteLabel(band.startMinute)}-{pricingMinuteLabel(band.endMinute)}，每小时{" "}
            {formatFee(band.hourlyRateFen)}
          </span>
        ))
      )}
    </span>
  );
}

function storeLocalDateTimeFormatter(timeZone: string): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat("zh-CN", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  } catch {
    return new Intl.DateTimeFormat("zh-CN", {
      timeZone: legacyPriceSnapshotTimeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  }
}

function formatStoreLocalDateTime(value: string, formatter: Intl.DateTimeFormat): string {
  const parts = Object.fromEntries(
    formatter
      .formatToParts(new Date(value))
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
}

function frontdeskPricingOptionsMatchContext(
  options: FrontdeskPricingOptionsReadModel,
  operator: Pick<OperatorIdentity, "tenantId" | "storeId">,
  table: TableSnapshot,
): boolean {
  return (
    options.tenantId === operator.tenantId &&
    options.storeId === operator.storeId &&
    options.tableId === table.tableId &&
    options.tableNumber === table.tableNumber &&
    options.tableRevision === table.revision
  );
}

export function coachSessionStopSuccessMessage(
  tableNumber: string,
  result: CoachSessionStopResult,
): string {
  if (result.coachServiceClosure === "LEGACY_UNRECORDED") {
    return `${tableNumber} 号台的历史提前停表结果已确认；旧版本没有记录整桌助教是否随停表处理，请在助教工作台逐项核对，系统没有补写费用或提成。`;
  }
  const serviceFeeFen = result.completedCoachServices.reduce(
    (total, service) => total + service.serviceFeeFen,
    0,
  );
  return `${tableNumber} 号台已提前停表，完成 ${result.completedCoachServices.length} 名助教下钟处理，服务费合计 ${formatFee(serviceFeeFen)}；台费已冻结，可继续核对商品后结账。`;
}

export function OrderBreakdown({
  order,
}: {
  order:
    | Pick<
        OrderDetail,
        | "tableFeeFen"
        | "productTotalFen"
        | "coachServiceTotalFen"
        | "rentalTotalFen"
        | "rentalItems"
        | "groupBuyFulfillments"
        | "refundedFen"
      >
    | Pick<
        OrderHistoryReadModel,
        | "tableFeeFen"
        | "productTotalFen"
        | "coachServiceTotalFen"
        | "rentalTotalFen"
        | "rentalItems"
        | "groupBuyFulfillments"
        | "refundedFen"
      >;
}): React.JSX.Element {
  return (
    <div className="order-breakdown">
      <span>台费 {formatFee(order.tableFeeFen)}</span>
      <span>商品 {formatFee(order.productTotalFen)}</span>
      <span>助教服务 {formatFee(order.coachServiceTotalFen)}</span>
      <span>租赁 {formatFee(order.rentalTotalFen ?? 0)}</span>
      {(order.rentalItems ?? []).map((item) => (
        <span key={item.id}>
          租赁明细 {item.productName} × {item.quantity} · {formatFee(item.unitRentalPriceFen)}/
          {item.unit} · {formatFee(item.receivableFen)}
        </span>
      ))}
      {(order.groupBuyFulfillments ?? []).map((fulfillment) => (
        <span key={fulfillment.id}>
          {("provider" in fulfillment
            ? fulfillment.provider
            : fulfillment.channelSnapshot.provider) === "MEITUAN"
            ? "美团"
            : "抖音"}
          券覆盖 {formatFee(fulfillment.voucherCoveredFen)} · 到店补差{" "}
          {formatFee(fulfillment.customerSupplementFen)}
        </span>
      ))}
      <span>已退 {formatFee(order.refundedFen)}</span>
    </div>
  );
}

function formatTime(value: string | null): string {
  if (!value) return "尚无成功记录";
  return new Date(value).toLocaleString("zh-CN", { hour12: false });
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  const remaining = seconds % 60;
  return [hours, minutes, remaining].map((value) => String(value).padStart(2, "0")).join(":");
}

export function canResolveOrderConflict(status: ConnectivityReadModel | undefined): boolean {
  return Boolean(
    status?.offlineAuthorityAvailable &&
      (status.mode === "ONLINE" ||
        (status.mode === "RECOVERING" && status.syncStage === "CHECKING_CONFLICTS")),
  );
}

export function connectivityRecoveryPresentation(input: {
  failedRecords: number;
  syncStage: ConnectivityReadModel["syncStage"];
  showReviewDetails: boolean;
}): { blocked: boolean; title: string; detail: string } {
  if (input.failedRecords > 0) {
    if (!input.showReviewDetails) {
      return {
        blocked: true,
        title: "网络已恢复，同步等待授权人员核对",
        detail: "部分记录暂时不能继续同步。你仍可使用页面中明确可用的普通业务。",
      };
    }
    return {
      blocked: true,
      title: "业务同步已暂停，等待人工核对",
      detail: `云端网络可达，但有 ${input.failedRecords} 条经营记录连续同步失败；本地原始记录已保留，请由老板或店长到“工作中心—异常核对”查看安全只读清单并保留现场凭证。当前仅只读，不在此页提交处置。`,
    };
  }

  if (input.syncStage === "PULLING_CONFIGURATION") {
    return {
      blocked: false,
      title: "门店已连通，正在核对经营配置",
      detail:
        "已送达的经营记录会保留原结果。配置核对完成前，请继续使用页面明确允许的业务，不要重新提交原单。",
    };
  }

  return {
    blocked: false,
    title: "网络已恢复，正在同步和核对",
    detail: `当前阶段：${stageLabels[input.syncStage]}`,
  };
}

function useDuration(startedAt: string | null): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [startedAt]);
  if (!startedAt) return "00:00:00";
  return formatDuration(Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1_000)));
}

function CspModal({
  open,
  onOpenChange,
  labelledBy,
  describedBy,
  className,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  labelledBy: string;
  describedBy: string;
  className: string;
  children: ReactNode;
}): React.JSX.Element {
  const modalRef = useRef<HTMLDialogElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal || !open) return;

    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!modal.open) {
      if (typeof modal.showModal === "function") modal.showModal();
      else modal.setAttribute("open", "");
    }
    document.body.classList.add("dialog-scroll-locked");

    return () => {
      if (modal.open) modal.close();
      if (!document.querySelector("dialog.csp-dialog[open]")) {
        document.body.classList.remove("dialog-scroll-locked");
      }
      returnFocusRef.current?.focus({ preventScroll: true });
      returnFocusRef.current = null;
    };
  }, [open]);

  return (
    <dialog
      ref={modalRef}
      className={`csp-dialog dialog-content ${className}`}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      onCancel={(event) => {
        event.preventDefault();
        onOpenChange(false);
      }}
      onKeyDown={(event) => {
        // Chromium normally emits `cancel` for Escape on a modal dialog, but
        // keyboard automation and some embedded browser shells only deliver
        // the key event to the focused control. Keep the close behavior
        // deterministic without changing the CSP or focus boundary.
        if (event.key === "Escape") {
          event.preventDefault();
          onOpenChange(false);
        }
      }}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onOpenChange(false);
      }}
    >
      {children}
    </dialog>
  );
}

export function LoginScreen({
  onAuthenticated,
}: {
  onAuthenticated: () => Promise<void>;
}): React.JSX.Element {
  const titleId = useId();
  const loginHintId = useId();
  const passwordInputId = useId();
  const passwordHintId = useId();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const native = isNativeRuntime();
  const [sessionMode, setSessionMode] = useState<NativeSessionMode>("PERSONAL");
  const loginInFlightRef = useRef(false);
  const failureRef = useRef<HTMLDivElement>(null);
  const authentication = useMutation({
    mutationFn: () => loginOperator(login.trim(), password, native ? sessionMode : "PERSONAL"),
    onSuccess: async () => {
      setPassword("");
      setPasswordVisible(false);
      await onAuthenticated();
    },
  });
  useEffect(() => {
    if (authentication.error) failureRef.current?.focus({ preventScroll: true });
  }, [authentication.error]);

  const resetStaleFailure = (): void => {
    if (authentication.error && !authentication.isPending) authentication.reset();
  };

  return (
    <main className="auth-screen">
      <section className="auth-panel" aria-labelledby={titleId}>
        <div className="auth-brand">
          <span className="brand-number">14</span>
          <div>
            <strong>北岸桌球</strong>
            <span>门店经营系统</span>
          </div>
        </div>
        <div className="auth-heading">
          <LockKeyhole size={22} aria-hidden="true" />
          <div>
            <h1 id={titleId}>员工登录</h1>
            <p>使用门店分配的员工账号</p>
          </div>
        </div>
        <form
          name="operator-login"
          autoComplete="on"
          aria-busy={authentication.isPending}
          onSubmit={(event) => {
            event.preventDefault();
            if (loginInFlightRef.current || authentication.isPending) return;
            loginInFlightRef.current = true;
            authentication.mutate(undefined, {
              onSettled: () => {
                loginInFlightRef.current = false;
              },
            });
          }}
        >
          <label>
            登录名
            <input
              {...loginCredentialAttributes("username")}
              value={login}
              onChange={(event) => {
                setLogin(event.target.value);
                resetStaleFailure();
              }}
              aria-describedby={loginHintId}
              disabled={authentication.isPending}
              minLength={3}
              maxLength={64}
              required
            />
            <small className="form-hint" id={loginHintId}>
              登录名至少 3 位；首尾多余空格会自动忽略。
            </small>
          </label>
          {native ? (
            <NativeSessionModePicker
              value={sessionMode}
              disabled={authentication.isPending}
              onChange={(mode) => {
                setSessionMode(mode);
                resetStaleFailure();
              }}
            />
          ) : null}
          <div className="auth-form-field">
            <label htmlFor={passwordInputId}>密码</label>
            <div className="auth-password-control">
              <input
                id={passwordInputId}
                type={passwordVisible ? "text" : "password"}
                {...loginCredentialAttributes("password")}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  resetStaleFailure();
                }}
                aria-describedby={passwordHintId}
                disabled={authentication.isPending}
                minLength={OPERATOR_PASSWORD_MIN_LENGTH}
                maxLength={OPERATOR_PASSWORD_MAX_LENGTH}
                required
              />
              <button
                className="button secondary auth-password-toggle"
                type="button"
                aria-label={passwordVisible ? "隐藏登录密码" : "显示登录密码"}
                aria-pressed={passwordVisible}
                disabled={authentication.isPending}
                onClick={() => setPasswordVisible((visible) => !visible)}
              >
                {passwordVisible ? "隐藏" : "显示"}
              </button>
            </div>
            <small className="form-hint" id={passwordHintId}>
              {`密码默认隐藏；可用“显示”核对输入。密码至少 ${OPERATOR_PASSWORD_MIN_LENGTH} 位，登录失败时可直接修改后重试。`}
            </small>
          </div>
          {authentication.error instanceof Error ? (
            <div className="error-band" role="alert" ref={failureRef} tabIndex={-1}>
              登录失败：{authentication.error.message}
            </div>
          ) : null}
          <button
            className="button primary auth-submit"
            type="submit"
            disabled={
              authentication.isPending ||
              login.trim().length < 3 ||
              !isOperatorPasswordValid(password)
            }
          >
            <LogIn size={18} aria-hidden="true" />
            {authentication.isPending ? "正在登录" : "登录"}
          </button>
        </form>
      </section>
    </main>
  );
}

export function NativeSessionModePicker({
  value,
  onChange,
  disabled = false,
}: {
  value: NativeSessionMode;
  onChange: (mode: NativeSessionMode) => void;
  disabled?: boolean;
}): React.JSX.Element {
  return (
    <fieldset className="native-session-mode" disabled={disabled}>
      <legend>这台设备怎么使用？</legend>
      <label>
        <input
          type="radio"
          name="native-session-mode"
          value="PERSONAL"
          checked={value === "PERSONAL"}
          onChange={() => onChange("PERSONAL")}
        />
        <span>
          <strong>我的私人手机</strong>
          <small>安全保存可撤销会话，下次打开 App 可继续使用。</small>
        </span>
      </label>
      <label>
        <input
          type="radio"
          name="native-session-mode"
          value="SHARED_SHIFT"
          checked={value === "SHARED_SHIFT"}
          onChange={() => onChange("SHARED_SHIFT")}
        />
        <span>
          <strong>公共收银设备</strong>
          <small>必须开始班次；交班后自动退出，关闭 App 后也要重新登录。</small>
        </span>
      </label>
    </fieldset>
  );
}

export interface WebBuildProvenance {
  productVersion: string;
  gitCommit: string;
  buildTime: string;
  deploymentEnvironment: string;
}

export const webBuildProvenance: WebBuildProvenance = {
  productVersion: import.meta.env.VITE_APP_VERSION ?? "0.8.63",
  gitCommit: import.meta.env.VITE_GIT_COMMIT ?? "working-tree",
  buildTime: import.meta.env.VITE_BUILD_TIME ?? "unknown",
  deploymentEnvironment: import.meta.env.VITE_DEPLOYMENT_ENVIRONMENT ?? "local-demo",
};

function shortCommit(commit: string): string {
  return commit.length > 12 ? commit.slice(0, 12) : commit;
}

export function buildProvenanceMatches(
  web: WebBuildProvenance,
  gateway: Pick<VersionInfo, "productVersion" | "gitCommit">,
): boolean {
  return web.productVersion === gateway.productVersion && web.gitCommit === gateway.gitCommit;
}

export function BuildProvenanceStamp({
  gateway,
  loading,
  unavailable,
  web = webBuildProvenance,
}: {
  gateway: VersionInfo | undefined;
  loading: boolean;
  unavailable: boolean;
  web?: WebBuildProvenance;
}): React.JSX.Element {
  const matches = gateway ? buildProvenanceMatches(web, gateway) : false;
  return (
    <div
      className="auth-version"
      aria-live="polite"
      data-version-match={gateway ? matches : "unknown"}
    >
      <span>
        网页 v{web.productVersion} · {shortCommit(web.gitCommit)} · {web.deploymentEnvironment}
      </span>
      <span>构建时间 {web.buildTime}</span>
      {loading ? <span>正在核对服务版本…</span> : null}
      {unavailable ? <span className="auth-version-warning">服务版本暂时无法核对</span> : null}
      {gateway ? (
        <span className={matches ? undefined : "auth-version-warning"}>
          网关 v{gateway.productVersion} · {shortCommit(gateway.gitCommit)} · schema{" "}
          {gateway.schemaVersion} · {gateway.deploymentEnvironment}
          {matches ? " · 版本一致" : " · 页面与服务版本不一致"}
        </span>
      ) : null}
    </div>
  );
}

const pageLabels: Record<string, string> = {
  "/home": "我的首页",
  "/frontdesk": "前台营业台",
  "/announcements": "门店公告",
  "/shifts": "交接班",
  "/coach": "助教工作台",
  "/tasks": "服务待办总览",
  "/orders": "订单中心",
  "/review": "工作中心",
  "/products": "商品与库存管理",
  "/storage": "顾客寄存",
  "/rentals": "顾客租赁",
  "/group-buy": "团购履约与核对",
  "/members": "会员与储值管理",
  "/staff": "服务员工作台",
  "/attendance": "出勤排班",
  "/payroll": "工资确认",
  "/workforce": "人员与本店任职",
  "/me": "我的资料",
  "/owner": "老板经营看板",
  "/configuration": "门店经营设置",
  "/system": "门店状态与工具",
  "/help": "使用说明",
};

export function trustedLegacyRouteMigration(
  state: unknown,
  currentDestination: string,
  effectiveRoles: readonly OperatorRole[],
): { legacyPath: string; message: string } | null {
  if (!state || typeof state !== "object" || Array.isArray(state)) return null;
  const stateRecord = state as Record<string, unknown>;
  if (Object.keys(stateRecord).length !== 1) return null;
  const candidate = stateRecord.legacyRouteMigration;
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) return null;
  const candidateRecord = candidate as Record<string, unknown>;
  if (
    Object.keys(candidateRecord).length !== 2 ||
    typeof candidateRecord.legacyPath !== "string" ||
    typeof candidateRecord.message !== "string"
  ) {
    return null;
  }
  const trustedState = legacyRouteNavigationState(candidateRecord.legacyPath);
  if (!trustedState) return null;
  const trustedMigration = trustedState.legacyRouteMigration;
  if (
    candidateRecord.message !== trustedMigration.message ||
    legacyRouteDestination(trustedMigration.legacyPath, effectiveRoles) !== currentDestination
  ) {
    return null;
  }
  return trustedMigration;
}

export function LegacyRouteMigrationNotice({
  state,
  currentDestination,
  currentEntryLabel,
  effectiveRoles,
}: {
  state: unknown;
  currentDestination: string;
  currentEntryLabel: string;
  effectiveRoles: readonly OperatorRole[];
}): React.JSX.Element | null {
  const migration = trustedLegacyRouteMigration(state, currentDestination, effectiveRoles);
  if (!migration) return null;
  return (
    <output className="shared-shift-session-banner" aria-live="polite">
      <ArrowRightLeft size={18} aria-hidden="true" />
      <div>
        <strong>{migration.message}</strong>
        <span>当前新入口：{currentEntryLabel}</span>
      </div>
    </output>
  );
}

export function accessDeniedPageLabel(path: string): string {
  return pageLabels[path] ?? "这个页面";
}

function AccessDenied({
  effectiveRoles,
  path,
}: {
  effectiveRoles: readonly OperatorRole[];
  path: string;
}): React.JSX.Element {
  const fallback = defaultRouteForRoles(effectiveRoles);
  const effectiveRoleLabels = effectiveRoles.map((role) => roleLabels[role]).join("、");
  return (
    <section className="access-denied" role="alert">
      <LockKeyhole size={28} aria-hidden="true" />
      <div>
        <span>权限已拦截</span>
        <h1>当前账号不能进入{accessDeniedPageLabel(path)}</h1>
        <p>
          系统当前核验到你的本店身份为“{effectiveRoleLabels || "暂无有效任职"}
          ”。系统没有加载该页面的数据；如需访问，请由老板或店长核对你的本店任职。
        </p>
      </div>
      <NavLink className="button primary" to={fallback}>
        返回我的工作台
      </NavLink>
      <NavLink className="button secondary" to="/help">
        查看权限说明
      </NavLink>
    </section>
  );
}

export function AuthorizedRoute({
  effectiveRoles,
  path,
  children,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
  path: string;
  children: React.JSX.Element;
}): React.JSX.Element {
  return canAccessRouteForRoles(effectiveRoles, path) ? (
    children
  ) : (
    <AccessDenied effectiveRoles={effectiveRoles} path={path} />
  );
}

export function LegacyRouteRedirect({
  legacyPath,
  effectiveRoles,
  fallback,
}: {
  legacyPath: string;
  effectiveRoles: readonly OperatorRole[];
  fallback: string;
}): React.JSX.Element {
  const destination = legacyRouteDestination(legacyPath, effectiveRoles);
  const state = legacyRouteNavigationState(legacyPath);
  return destination && state ? (
    <Navigate replace to={destination} state={state} />
  ) : (
    <Navigate replace to={fallback} />
  );
}

function DeferredRoute({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <Suspense
      fallback={
        <section className="loading-state" aria-live="polite" aria-busy="true">
          <RefreshCw className="spin" size={20} aria-hidden="true" />
          正在打开工作页面…
        </section>
      }
    >
      {children}
    </Suspense>
  );
}

const helpRows = [
  { path: "/frontdesk", purpose: "开台、计时、转台、商品加退单、结账和清台" },
  { path: "/announcements", purpose: "查看发给当前岗位的公告，并按要求确认或回复" },
  {
    path: "/shifts",
    purpose: "查看开店准备；有收银权限时可开始当班、清点现金并办理交接",
  },
  { path: "/coach", purpose: "助教排钟、服务；助教账号登录后只显示本人工作台" },
  { path: "/orders", purpose: "查询订单、查看历史退款并重新打印小票" },
  {
    path: "/review",
    purpose: "统一查看现在要处理、我的申请、现场待办、异常核对和全部记录",
  },
  { path: "/products", purpose: "逐条维护商品资料，查看库存、低库存提醒和历史流水" },
  { path: "/storage", purpose: "从会员已结清订单办理商品寄存、领取、到期提醒和异常处理" },
  { path: "/rentals", purpose: "从活动台单办理物品借出、归还、逾期提醒和异常处理" },
  { path: "/members", purpose: "会员建档、充值福利、待核对充值和资金流水" },
  { path: "/staff", purpose: "服务员接单、处理并完成桌边待办" },
  {
    path: "/attendance",
    purpose: "查看本人排班和实际打卡；店长可安排全店班次并处理补卡申请",
  },
  {
    path: "/payroll",
    purpose: "员工查看并确认本人工资；店长复核，老板生成、锁定并登记实际支付",
  },
  { path: "/workforce", purpose: "查看本店人员，新增在职人员并更正基础资料" },
  { path: "/owner", purpose: "营业日应收、实收、退款、离线单和差异核对" },
  { path: "/configuration", purpose: "门店资料、价格规则、球台和灯控/打印设备配置" },
  {
    path: "/system",
    purpose: "查看门店连接和本地数据状态；按需使用订单导出、脱敏诊断和商品批量导入",
  },
] as const;

function helpRowPurpose(
  row: (typeof helpRows)[number],
  effectiveRoles: readonly OperatorRole[],
): string {
  if (row.path !== "/workforce") return row.purpose;
  return effectiveRoles.includes("OWNER")
    ? "查看本店人员；可新增和维护人员、调整普通员工角色、办理离职或重新入职，并紧急停用符合条件的登录账号。邀请启用、换号重绑、老板或助教身份变更需走独立安全流程"
    : "查看本店人员；可新增、维护、办理离职或重新入职本店收银员和服务员。姓名、手机号、角色和登录账号由老板或独立安全流程管理";
}

export function HelpDesk({
  operator,
  effectiveRoles,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
}): React.JSX.Element {
  const accessibleRows = helpRows.filter((row) => canAccessRouteForRoles(effectiveRoles, row.path));
  const defaultRoute = defaultRouteForRoles(effectiveRoles);
  const effectiveRoleLabels = effectiveRoles.map((role) => roleLabels[role]).join("、");
  return (
    <>
      <PageHeader
        eyebrow={`${operator.displayName} · ${effectiveRoleLabels || "暂无有效任职"}`}
        title="使用说明与入口"
        action={<span className="demo-pill">当前账号权限</span>}
      />
      <section className="help-intro">
        <div>
          <CircleHelp size={24} aria-hidden="true" />
          <div>
            <strong>你当前应该使用：{pageLabels[defaultRoute]}</strong>
            <span>登录后只显示当前角色可用入口；手工输入无权网址也会在加载数据前被拦截。</span>
          </div>
        </div>
        <NavLink className="button primary" to={defaultRoute}>
          进入我的工作台
        </NavLink>
      </section>

      <section className="help-section">
        <div className="section-heading">
          <div>
            <h2>当前账号可用入口</h2>
            <p>这里只列出“{effectiveRoleLabels || "暂无有效任职"}”可以实际进入的页面。</p>
          </div>
        </div>
        <div className="help-route-grid">
          {accessibleRows.map((row) => (
            <NavLink to={row.path} key={row.path}>
              <strong>{pageLabels[row.path]}</strong>
              <span className="help-route-location">在本应用内打开</span>
              <span>{helpRowPurpose(row, effectiveRoles)}</span>
            </NavLink>
          ))}
        </div>
      </section>

      <section className="help-section">
        <div className="section-heading">
          <div>
            <h2>角色怎么分</h2>
            <p>
              一个员工一个账号，不共享老板账号。店长可在本店新增、维护、办理离职和重新入职收银员或服务员；老板可维护更完整的人员资料、调整普通员工角色，并在紧急情况下停用符合条件的登录账号。邀请启用、换号重绑、老板或助教身份变更仍走独立安全流程。
            </p>
          </div>
        </div>
        <div className="permission-table-wrap">
          <table className="permission-table">
            <thead>
              <tr>
                <th scope="col">角色</th>
                <th scope="col">主要工作台</th>
                <th scope="col">不能做</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">老板 / 店长</th>
                <td>经营看板、系统、前台与核对；店长可进入服务员工作台，老板通过待办总览监督</td>
                <td>老板账号不能代替服务员接单；不能把模拟数据当成真实支付或现场验收</td>
              </tr>
              <tr>
                <th scope="row">前台收银员</th>
                <td>前台、助教运营、待办、订单、待核对</td>
                <td>看不到老板看板、门店状态与工具和服务员专属接单台</td>
              </tr>
              <tr>
                <th scope="row">服务员</th>
                <td>服务员工作台、待办、助教现场只读信息</td>
                <td>不能收款、退款、改价格，也看不到老板经营数据</td>
              </tr>
              <tr>
                <th scope="row">助教</th>
                <td>同一个 /coach，但只显示本人班次、队列、任务、服务和提成基础</td>
                <td>不能看其他助教敏感资料，不能收款、改提成或人工调队</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="help-section kiosk-help">
        <div>
          <span>独立门店设备</span>
          <h2>桌边运行屏不是老板端，也不是服务员端</h2>
          <p>
            桌边屏由每张球桌的 Kiosk 应用连接该桌的 <code>table-edge-agent</code>
            ，显示桌号、计时、预估台费，并让顾客呼叫服务或请求结账。正式门店使用局域网设备地址，不直接暴露在公网。
          </p>
        </div>
        <div className="kiosk-address-card">
          <strong>当前公网</strong>
          <span>未公开桌边 Kiosk，这是安全边界，不是 /frontdesk 的别名。</span>
          <strong>本地单桌演示</strong>
          <code>pnpm.cmd run dev:slice</code>
          <code>http://127.0.0.1:5174</code>
          <strong>真实门店</strong>
          <span>由每桌设备固定打开门店局域网页面；真实智能屏还需要到店连接和验收。</span>
        </div>
      </section>
      <NativeAppHelpCard />
    </>
  );
}

function NativeAppHelpCard(): React.JSX.Element | null {
  const native = isNativeRuntime();
  const [info, setInfo] = useState<{ version: string; build: string } | null>(null);
  useEffect(() => {
    if (!native) return;
    void nativeAppInfo().then((result) => {
      if (result) setInfo({ version: result.version, build: result.build });
    });
  }, [native]);
  if (!native) return null;
  return (
    <section className="help-section native-app-help">
      <div className="section-heading">
        <div>
          <h2>手机 App 权限与隐私</h2>
          <p>
            当前 App v{info?.version ?? "读取中"}（build {info?.build ?? "-"}）· Android / iOS
            共用同一套角色权限。
          </p>
        </div>
      </div>
      <div className="help-route-grid">
        <article>
          <strong>登录安全</strong>
          <span>
            App 不保存明文密码。私人手机只把可撤销会话安全存入 Keystore /
            Keychain；公共收银设备不保存会话，关闭 App 或交班后都要重新登录。
          </span>
        </article>
        <article>
          <strong>相机权限</strong>
          <span>
            只有你主动点击商品扫码时才调用相机。拒绝权限后仍可手工输入条码，不影响其他经营功能。
          </span>
        </article>
        <article>
          <strong>断网与更新</strong>
          <span>
            断网时顶部会持续提示，并以门店主机页面列出的受限离线能力为准；有新版本时会提示取得经过验证的更新包。
          </span>
        </article>
      </div>
    </section>
  );
}

function Shell({
  operator,
  effectiveRoles,
  authenticationEvidence,
  authorizationIdentityScope,
  sessionContext,
  workforceContext,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
  authenticationEvidence: "SIMULATED" | "STORE_ACCEPTED";
  authorizationIdentityScope: string | null;
  sessionContext: OperatorSessionContext | undefined;
  workforceContext: WorkforcePrincipalContext | null;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigationRef = useRef<HTMLElement>(null);
  const logout = useMutation({
    mutationFn: logoutOperator,
    onSuccess: async () => {
      await clearPrivateOperatorQueries(queryClient);
      await queryClient.resetQueries({ queryKey: ["operator-session"] });
    },
  });
  const sharedShiftSession = sessionContext?.mode === "SHARED_SHIFT";
  const canPreloadInventory = effectiveRoles.some((role) => role === "OWNER" || role === "MANAGER");
  const defaultRoute =
    sessionContext?.mode === "SHARED_SHIFT"
      ? sessionContext.shiftStatus === "OPEN"
        ? "/frontdesk"
        : "/shifts"
      : defaultRouteForRoles(effectiveRoles);
  const visibleNavigationSections = navigationSectionsForRoles(effectiveRoles)
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          sessionCanOpenRoute(sessionContext, item.to) &&
          navigationItemAllowedByIdentityEvidence(item.to, authenticationEvidence),
      ),
    }))
    .filter((section) => section.items.length > 0);
  useEffect(() => {
    if (!canPreloadInventory) return;
    const preload = window.setTimeout(() => {
      void queryClient.prefetchQuery({
        queryKey: ["inventory-products"],
        queryFn: listInventoryProducts,
        staleTime: 30_000,
      });
    }, 0);
    return () => window.clearTimeout(preload);
  }, [canPreloadInventory, queryClient]);
  useEffect(() => {
    if (!window.matchMedia("(max-width: 760px)").matches) return;
    const activeItem = navigationRef.current?.querySelector<HTMLElement>(
      `.nav-item[href="${location.pathname}"]`,
    );
    if (typeof activeItem?.scrollIntoView === "function") {
      activeItem.scrollIntoView({ behavior: "auto", block: "nearest", inline: "center" });
    }
  }, [location.pathname]);
  if (sharedShiftSession && !sessionCanOpenRoute(sessionContext, location.pathname)) {
    return <Navigate to="/shifts" replace />;
  }
  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand-mark" role="img" aria-label="北岸桌球">
          <span className="brand-number">14</span>
          <span className="brand-name">北岸桌球</span>
        </div>
        <nav aria-label="主导航" ref={navigationRef}>
          {visibleNavigationSections.map((section) => (
            <section
              className="nav-section"
              aria-labelledby={`nav-section-${section.id}`}
              key={section.id}
            >
              <span className="nav-section-label" id={`nav-section-${section.id}`}>
                {section.label}
              </span>
              <div className="nav-section-items">
                {section.items.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    className={({ isActive }: NavLinkRenderProps) =>
                      `nav-item ${isActive ? "is-active" : ""}`
                    }
                    to={to}
                    key={to}
                  >
                    <Icon size={19} aria-hidden="true" />
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            </section>
          ))}
        </nav>
        <button
          className="sidebar-user"
          type="button"
          title={authenticationEvidence === "SIMULATED" ? "显式演示模式" : "退出当前员工账号"}
          onClick={() => logout.mutate()}
          disabled={authenticationEvidence === "SIMULATED" || logout.isPending}
        >
          <span className="user-avatar">{operator.displayName.slice(0, 1)}</span>
          <span>
            <strong>{operator.displayName}</strong>
            <small>
              {effectiveRoles.map((role) => roleLabels[role]).join("、") || "暂无有效任职"} ·{" "}
              {operatorSessionEvidenceLabel(authenticationEvidence)}
            </small>
          </span>
          <LogOut size={17} aria-hidden="true" />
        </button>
        {logout.error instanceof Error ? (
          <div className="error-band sidebar-logout-error" role="alert">
            退出未完成：{logout.error.message}
          </div>
        ) : null}
      </aside>
      <main className="workspace">
        {sharedShiftSession ? (
          <output className="shared-shift-session-banner" aria-live="polite">
            <ShieldCheck size={18} aria-hidden="true" />
            <div>
              <strong>当前是公共收银设备</strong>
              <span>
                {sessionContext.shiftStatus === "OPEN"
                  ? "只在当前班次内有效；提交交班后会自动退出。"
                  : sessionContext.shiftStatus === "HANDOVER_PENDING"
                    ? "当前只允许核对并确认接班；确认后会自动退出。"
                    : "请先进入交接班开始班次；开始前不能办理经营业务。"}
              </span>
            </div>
          </output>
        ) : null}
        <ConnectivityBar
          effectiveRoles={effectiveRoles}
          authenticationEvidence={authenticationEvidence}
        />
        <LegacyRouteMigrationNotice
          state={location.state}
          currentDestination={`${location.pathname}${location.search}`}
          currentEntryLabel={pageLabels[location.pathname] ?? "当前页面"}
          effectiveRoles={effectiveRoles}
        />
        <AnnouncementReminder
          operator={operator}
          enabled={announcementReminderEnabled(sessionContext, location.pathname, defaultRoute)}
        />
        <Routes>
          <Route
            path="/home"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/home">
                <RoleHome
                  displayName={operator.displayName}
                  effectiveRoles={effectiveRoles}
                  authenticationEvidence={authenticationEvidence}
                  sharedShift={sharedShiftSession}
                  operator={operator}
                  personalSession={personalServiceDutySession(
                    authenticationEvidence,
                    sessionContext,
                  )}
                />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/frontdesk"
            element={
              <AuthorizedRoute
                operator={operator}
                effectiveRoles={effectiveRoles}
                path="/frontdesk"
              >
                <FrontDesk operator={operator} effectiveRoles={effectiveRoles} />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/shifts"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/shifts">
                <DeferredRoute>
                  <CashShiftDesk operator={operator} effectiveRoles={effectiveRoles} />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/announcements"
            element={
              <AuthorizedRoute
                operator={operator}
                effectiveRoles={effectiveRoles}
                path="/announcements"
              >
                <DeferredRoute>
                  <AnnouncementDesk operator={operator} effectiveRoles={effectiveRoles} />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/coach"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/coach">
                <DeferredRoute>
                  <CoachCustomerRequestPage operator={operator} effectiveRoles={effectiveRoles} />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/tasks">
                <LegacyRouteRedirect
                  legacyPath="/tasks"
                  effectiveRoles={effectiveRoles}
                  fallback={defaultRoute}
                />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/workdesk"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/workdesk">
                <LegacyRouteRedirect
                  legacyPath="/workdesk"
                  effectiveRoles={effectiveRoles}
                  fallback={defaultRoute}
                />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/sessions"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/sessions">
                <LegacyRouteRedirect
                  legacyPath="/sessions"
                  effectiveRoles={effectiveRoles}
                  fallback={defaultRoute}
                />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/coaches/workbench"
            element={
              <AuthorizedRoute
                operator={operator}
                effectiveRoles={effectiveRoles}
                path="/coaches/workbench"
              >
                <LegacyRouteRedirect
                  legacyPath="/coaches/workbench"
                  effectiveRoles={effectiveRoles}
                  fallback={defaultRoute}
                />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/orders">
                <OrdersDesk
                  operator={operator}
                  effectiveRoles={effectiveRoles}
                  authenticationEvidence={authenticationEvidence}
                  {...(sessionContext ? { sessionContext } : {})}
                />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/review"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/review">
                <ApprovalCenterDesk
                  operator={operator}
                  effectiveRoles={effectiveRoles}
                  personalSession={personalServiceDutySession(
                    authenticationEvidence,
                    sessionContext,
                  )}
                  simulated={authenticationEvidence === "SIMULATED"}
                />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/review/:approvalId"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/review">
                <ApprovalCenterDesk
                  operator={operator}
                  effectiveRoles={effectiveRoles}
                  personalSession={personalServiceDutySession(
                    authenticationEvidence,
                    sessionContext,
                  )}
                  simulated={authenticationEvidence === "SIMULATED"}
                />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/products">
                <DeferredRoute>
                  <ProductsDesk operator={operator} effectiveRoles={effectiveRoles} />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/storage"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/storage">
                <DeferredRoute>
                  <CustomerStorageDesk operator={operator} effectiveRoles={effectiveRoles} />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/rentals"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/rentals">
                <DeferredRoute>
                  <CustomerRentalDesk operator={operator} effectiveRoles={effectiveRoles} />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/members"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/members">
                <DeferredRoute>
                  <MembersDesk
                    operator={operator}
                    effectiveRoles={effectiveRoles}
                    sessionMode={memberFundsSessionMode(authenticationEvidence, sessionContext)}
                  />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/group-buy"
            element={
              <AuthorizedRoute
                operator={operator}
                effectiveRoles={effectiveRoles}
                path="/group-buy"
              >
                <DeferredRoute>
                  <GroupBuyDesk
                    operator={operator}
                    effectiveRoles={effectiveRoles}
                    authenticationEvidence={authenticationEvidence}
                  />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/staff"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/staff">
                <StaffDesk
                  operator={operator}
                  effectiveRoles={effectiveRoles}
                  personalSession={personalServiceDutySession(
                    authenticationEvidence,
                    sessionContext,
                  )}
                  simulated={authenticationEvidence === "SIMULATED"}
                />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/workforce"
            element={
              <AuthorizedRoute
                operator={operator}
                effectiveRoles={effectiveRoles}
                path="/workforce"
              >
                <DeferredRoute>
                  <WorkforceManagementDesk
                    operator={operator}
                    effectiveRoles={effectiveRoles}
                    authenticationEvidence={authenticationEvidence}
                    authorizationIdentityScope={authorizationIdentityScope}
                    currentPersonId={workforceContext?.person?.id ?? null}
                  />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/attendance"
            element={
              <AuthorizedRoute
                operator={operator}
                effectiveRoles={effectiveRoles}
                path="/attendance"
              >
                <DeferredRoute>
                  <WorkforceAttendanceDesk operator={operator} effectiveRoles={effectiveRoles} />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/payroll"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/payroll">
                <DeferredRoute>
                  <WorkforcePayrollDesk
                    operator={operator}
                    effectiveRoles={effectiveRoles}
                    currentEmployeeId={workforceContext?.currentStoreContext?.employmentId ?? null}
                  />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/me"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/me">
                <DeferredRoute>
                  <MyWorkforceProfileDesk
                    authenticationEvidence={authenticationEvidence}
                    context={workforceContext}
                    personalSession={sessionContext?.mode === "PERSONAL"}
                  />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/owner"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/owner">
                <OwnerDesk
                  operator={operator}
                  effectiveRoles={effectiveRoles}
                  authenticationEvidence={authenticationEvidence}
                />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/configuration"
            element={
              <AuthorizedRoute
                operator={operator}
                effectiveRoles={effectiveRoles}
                path="/configuration"
              >
                <DeferredRoute>
                  <OperatingConfigurationDesk
                    operator={operator}
                    effectiveRoles={effectiveRoles}
                    authenticationEvidence={authenticationEvidence}
                  />
                </DeferredRoute>
              </AuthorizedRoute>
            }
          />
          <Route
            path="/system"
            element={
              <AuthorizedRoute operator={operator} effectiveRoles={effectiveRoles} path="/system">
                <SystemDesk operator={operator} />
              </AuthorizedRoute>
            }
          />
          <Route
            path="/help"
            element={<HelpDesk operator={operator} effectiveRoles={effectiveRoles} />}
          />
          <Route path="*" element={<Navigate to={defaultRoute} replace />} />
        </Routes>
      </main>
    </div>
  );
}

export function ConnectivityBar({
  effectiveRoles,
  authenticationEvidence,
}: {
  effectiveRoles: readonly OperatorRole[];
  authenticationEvidence: "SIMULATED" | "STORE_ACCEPTED";
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [simulationNotice, setSimulationNotice] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["connectivity"],
    queryFn: getConnectivity,
    refetchInterval: 1_000,
  });
  const simulation = useMutation({
    mutationFn: simulateNetwork,
    onMutate: () => setSimulationNotice(null),
    onError: (error) => {
      setSimulationNotice(
        error instanceof ApiRequestError &&
          [
            "NETWORK_SIMULATION_OUTCOME_UNKNOWN",
            "GATEWAY_UNREACHABLE",
            "GATEWAY_RESPONSE_INVALID",
          ].includes(error.code)
          ? "断网演练切换结果待确认：本页正在重新读取当前连接状态，请不要重复点击"
          : error instanceof Error
            ? `断网演练操作未完成：${error.message}`
            : "断网演练操作未完成：请查看当前连接状态后再决定是否重试",
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["connectivity"] });
      await queryClient.invalidateQueries({ queryKey: ["tables"] });
      setSimulationNotice(null);
      simulation.reset();
    },
  });
  const connectivityState = connectivityTrustState(query);
  if (connectivityState.kind === "LOADING") {
    return (
      <section className="connectivity-bar is-connecting" aria-live="polite">
        <RefreshCw className="spin" size={17} />
        <strong>正在连接门店网关</strong>
      </section>
    );
  }
  if (connectivityState.kind !== "TRUSTED") {
    return (
      <section className="connectivity-bar is-unreachable" role="alert">
        <WifiOff size={18} />
        <strong>当前连接状态无法确认</strong>
        <span>系统没有继续使用上次的在线结果；请检查门店主机连接或稍后刷新</span>
      </section>
    );
  }
  const status = connectivityState.data;
  const simulationVisible = canUseNetworkSimulation(
    effectiveRoles,
    authenticationEvidence,
    status.simulationAllowed,
  );
  const businessAttentionVisible = effectiveRoles.some((role) =>
    ["CASHIER", "MANAGER", "OWNER"].includes(role),
  );
  if (status.mode === "ONLINE") {
    return (
      <section className="online-status" aria-label="连接状态">
        <div className="connectivity-bar is-online">
          <Wifi size={17} />
          <span>门店系统在线</span>
          <small>上次同步 {formatTime(status.lastSuccessfulSyncAt)}</small>
          {simulationVisible ? (
            <button
              className="simulation-button"
              type="button"
              onClick={() => simulation.mutate(true)}
              disabled={simulation.isPending}
            >
              <WifiOff size={15} />
              开始断网演练
            </button>
          ) : null}
          {simulationVisible ? (
            <span className="evidence-label">断网演练 · 仅用于演示门店断网营业流程</span>
          ) : null}
        </div>
        {businessAttentionVisible && status.pendingReconciliationCount > 0 ? (
          <output className="business-attention">
            <AlertTriangle size={18} aria-hidden="true" />
            <div>
              <strong>系统在线，{status.pendingReconciliationCount} 条记录待人工处理</strong>
              <span>只影响对应记录，开台、计费和其他正常业务可以继续。</span>
            </div>
            <NavLink to="/review?view=MANUAL">查看待处理记录</NavLink>
          </output>
        ) : null}
        {simulationNotice ? (
          <div className="error-band" role="alert">
            {simulationNotice}
          </div>
        ) : null}
        {businessAttentionVisible && status.lastRecoverySummary ? (
          <div className="recovery-summary">
            最近恢复：已同步 {status.lastRecoverySummary.synced} 笔 · 仍待核对{" "}
            {status.lastRecoverySummary.pendingReconciliation} 笔 · 冲突{" "}
            {status.lastRecoverySummary.conflicts} 笔 · 已恢复{" "}
            {status.lastRecoverySummary.restoredFeatures.join("、")}
          </div>
        ) : null}
      </section>
    );
  }
  if (status.mode === "RECOVERING") {
    const presentation = connectivityRecoveryPresentation({
      failedRecords: status.syncProgress.failed,
      syncStage: status.syncStage,
      showReviewDetails: businessAttentionVisible,
    });
    const percentage =
      status.syncProgress.total === 0
        ? 100
        : Math.round((status.syncProgress.processed / status.syncProgress.total) * 100);
    return (
      <section
        className={`connectivity-panel ${presentation.blocked ? "is-blocked" : "is-recovering"}`}
        aria-live={presentation.blocked ? "assertive" : "polite"}
        role={presentation.blocked ? "alert" : undefined}
      >
        <div className="connectivity-title">
          {presentation.blocked ? (
            <AlertTriangle size={20} />
          ) : (
            <RefreshCw className="spin" size={20} />
          )}
          <div>
            <strong>{presentation.title}</strong>
            <span>{presentation.detail}</span>
          </div>
          {simulationVisible ? (
            <span className="evidence-label">断网演练 · 仅用于演示门店断网营业流程</span>
          ) : null}
        </div>
        <progress
          className="sync-progress"
          max={100}
          value={percentage}
          aria-label="离线记录同步进度"
          aria-valuetext={`${status.syncProgress.processed}/${status.syncProgress.total}，剩余 ${status.syncProgress.remaining}，失败 ${status.syncProgress.failed}`}
        />
        <div className="recovery-metrics">
          <span>
            进度 {status.syncProgress.processed}/{status.syncProgress.total}
          </span>
          <span>剩余 {status.syncProgress.remaining}</span>
          {businessAttentionVisible ? (
            <>
              <span>失败 {status.syncProgress.failed}</span>
              <span>待人工处理 {status.pendingReconciliationCount}</span>
            </>
          ) : null}
        </div>
      </section>
    );
  }
  const authorityExpired = status.mode === "AUTHORITY_EXPIRED";
  return (
    <section
      className={`connectivity-panel ${authorityExpired ? "is-blocked" : "is-offline"}`}
      role="alert"
    >
      <div className="connectivity-title">
        <WifiOff size={21} />
        <div>
          <strong>{authorityExpired ? "离线写入权威已失效" : "当前处于离线营业模式"}</strong>
          <span>
            断网时长 {formatDuration(status.offlineDurationSeconds)} · 最后成功同步{" "}
            {formatTime(status.lastSuccessfulSyncAt)}
          </span>
        </div>
        <div className="offline-counts">
          <strong>{status.pendingRecords}</strong>
          <span>待同步记录</span>
        </div>
        {simulationVisible ? (
          <button
            className="simulation-button"
            type="button"
            onClick={() => simulation.mutate(false)}
            disabled={simulation.isPending}
          >
            <Wifi size={15} />
            尝试结束断网演练
          </button>
        ) : null}
        {simulationVisible ? (
          <span className="evidence-label">
            断网演练 · 仅用于演示门店断网营业流程 · 真实网络故障不会被此按钮修复
          </span>
        ) : null}
      </div>
      {simulationNotice ? (
        <div className="error-band" role="alert">
          {simulationNotice}
        </div>
      ) : null}
      {authorityExpired ? (
        <p className="authority-warning">
          为防止双主，开台、转台、加退单、收款和关台已暂停；仍可查看本地可靠保存的台局与订单。
        </p>
      ) : (
        <div className="availability-grid">
          <div>
            <strong>当前可用</strong>
            <ul>
              {status.availableFeatures.map((feature) => (
                <li key={feature.code}>
                  <Check size={14} /> {feature.label}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <strong>当前不可用</strong>
            <ul>
              {status.unavailableFeatures.map((feature) => (
                <li key={feature.code} title={feature.reason}>
                  <X size={14} /> {feature.label}：{feature.reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}

function PageHeader({
  eyebrow,
  title,
  action,
}: {
  eyebrow: string;
  title: string;
  action?: React.ReactNode;
}): React.JSX.Element {
  return (
    <header className="topbar">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
      </div>
      <div className="topbar-status">{action}</div>
    </header>
  );
}

export function claimPendingSku(pending: Set<string>, skuId: string): boolean {
  if (pending.has(skuId)) return false;
  pending.add(skuId);
  return true;
}

export function productSaleAvailabilityLabel(product: ProductSaleCatalogItem): string {
  if (!product.inventoryManaged) return "不跟踪库存";
  if (product.stockOnHand === null) return "库存待确认";
  if (!product.canSell) return product.unavailableReason ?? "当前不可销售";
  if (product.stockOnHand <= 0) return `现存 ${product.stockOnHand} ${product.unit}，仍可销售`;
  return `现存 ${product.stockOnHand} ${product.unit}`;
}

export function productSaleAddConsequenceLabel(product: ProductSaleCatalogItem): string {
  if (!product.inventoryManaged) return "点击后直接加入订单；此商品不跟踪库存";
  if (product.stockOnHand === null) return "库存无法确认，暂不可加单";
  if (!product.canSell || product.stockOnHand <= 0) {
    return `已售罄：现存 ${product.stockOnHand} ${product.unit}，不能加入订单`;
  }
  if (product.stockOnHand === 1) {
    return `低库存：现存 1 ${product.unit}；点击后将售罄并立即扣库存`;
  }
  return `现存 ${product.stockOnHand} ${product.unit}；点击后预计剩 ${product.stockOnHand - 1} ${product.unit}并立即扣库存`;
}

export function productAddDefiniteFailureMessage(error: unknown, productName: string): string {
  const reason = businessErrorMessage(error, "系统没有返回明确原因");
  const code = error instanceof ApiRequestError ? error.code : "";
  if (code === "GIFT_ORDER_ITEM_PRODUCT_CHANGED") {
    return `“${productName}”的名称或原价已经变化，这次没有加入订单，也没有扣库存。请刷新商品目录，重新核对当前商品和原价，再请店长或老板重新授权。`;
  }
  if (
    /INVENTORY.*INSUFFICIENT|INSUFFICIENT.*INVENTORY|OUT_OF_STOCK|PRODUCT_UNAVAILABLE/u.test(
      code,
    ) ||
    /库存不足|缺货|售罄/u.test(reason)
  ) {
    return `“${productName}”库存不足，未加入订单，也没有扣库存。请刷新商品目录后换选其他商品，或请店长补货。`;
  }
  if (
    /^TRACKED_SALE_INVENTORY_VALUATION_(?:MISSING|SOURCE_INVALID|DRIFT)$/u.test(code) ||
    /INVENTORY.*(?:VALUE|VALUATION|COST).*UNKNOWN|(?:VALUE|VALUATION|COST).*UNKNOWN/u.test(code) ||
    /库存价值.*(?:无法|不能|待确认)|价值无法确认/u.test(reason)
  ) {
    return `“${productName}”当前库存价值无法安全确认，未加入订单，也没有扣库存。请先刷新目录；如果仍有提示，请店长到“商品库存”核对后再试。`;
  }
  return `“${productName}”未加入订单：${reason}。系统已确认这次请求没有扣库存；请按提示修正后重试。`;
}

export function requireTrustedProductForAdd(
  products: readonly ProductSaleCatalogItem[] | undefined,
  selectedSkuId: string,
  catalogUntrusted: boolean,
): ProductSaleCatalogItem {
  if (catalogUntrusted) {
    throw new Error("商品目录当前无法确认，系统没有提交加单；请刷新目录后重试");
  }
  const product = products?.find((candidate) => candidate.skuId === selectedSkuId);
  if (!product || !product.canSell) {
    throw new Error(product?.unavailableReason ?? "商品目录已经变化，请刷新后重试");
  }
  return product;
}

export function preferProductAddOrder(
  current: OrderDetail | undefined,
  candidate: OrderDetail,
): OrderDetail {
  if (!current) return candidate;
  const currentQuantity = current.items.reduce((total, item) => total + item.quantity, 0);
  const candidateQuantity = candidate.items.reduce((total, item) => total + item.quantity, 0);
  return candidateQuantity >= currentQuantity ? candidate : current;
}

export function productAddIntroducedItem(
  current: OrderDetail | undefined,
  candidate: OrderDetail,
  skuId: string,
): boolean {
  const currentItemIds = new Set(current?.items.map((item) => item.id) ?? []);
  return candidate.items.some((item) => item.skuId === skuId && !currentItemIds.has(item.id));
}

export function canReadDeviceIncidentsForRoles(roles: readonly OperatorRole[]): boolean {
  return roles.some((role) => role === "OWNER" || role === "MANAGER" || role === "CASHIER");
}

export function DeviceIncidentPanel({
  state,
  resolution,
}: {
  state: DeviceIncidentTrustState | null;
  resolution?: {
    view: DeviceIncidentResolutionRecoveryView;
    busy: boolean;
    feedback: string | null;
    onResolve: (incident: DeviceIncidentReadModel) => void;
    onRetry: (recovery: DeviceIncidentResolutionRecovery) => void;
  };
}): React.JSX.Element | null {
  if (!state) return null;
  if (resolution?.view.status === "OWNED") {
    const { recovery } = resolution.view;
    return (
      <section className="device-attention-band" role="alert">
        <AlertTriangle size={19} aria-hidden="true" />
        <div>
          <strong>上次关闭设备提醒的结果待确认</strong>
          <span>原提醒编号已保留。请查询或重试原确认；系统不会重复改写台单、订单或付款。</span>
          {resolution.feedback ? <span aria-live="polite">{resolution.feedback}</span> : null}
          <button
            className="button secondary compact"
            type="button"
            disabled={resolution.busy}
            onClick={() => resolution.onRetry(recovery)}
          >
            {resolution.busy ? "正在确认原结果…" : "查询或重试原确认"}
          </button>
        </div>
      </section>
    );
  }
  if (
    resolution?.view.status === "OTHER_OPERATOR" ||
    resolution?.view.status === "RECOVERY_STORAGE_UNAVAILABLE"
  ) {
    return (
      <section className="device-attention-band" role="alert">
        <AlertTriangle size={19} aria-hidden="true" />
        <div>
          <strong>设备提醒暂时不能安全关闭</strong>
          <span>
            {resolution.view.status === "OTHER_OPERATOR"
              ? "原账号还有一条确认结果待核对，请切回原账号继续；当前账号不会重复提交。"
              : "当前标签页无法可靠保存原确认，请不要提交；刷新页面或更换正常标签页后再试。"}
          </span>
        </div>
      </section>
    );
  }
  if (state.kind === "LOADING") {
    return (
      <output className="device-attention-band" aria-live="polite">
        <RefreshCw size={19} aria-hidden="true" />
        <div>
          <strong>正在确认设备异常</strong>
          <span>请稍候，系统正在读取门店主机保存的设备记录。</span>
        </div>
      </output>
    );
  }
  if (state.kind === "ERROR" || state.kind === "STALE") {
    return (
      <section className="device-attention-band" role="alert">
        <AlertTriangle size={19} aria-hidden="true" />
        <div>
          <strong>暂时无法确认设备异常</strong>
          <span>为防止把旧记录当成当前情况，旧提醒已隐藏，请刷新后重试。</span>
          {resolution?.feedback ? <span aria-live="polite">{resolution.feedback}</span> : null}
        </div>
      </section>
    );
  }
  if (state.kind === "EMPTY") {
    return (
      <output className="device-attention-band">
        <Check size={19} aria-hidden="true" />
        <div>
          <strong>设备异常状态已确认</strong>
          <span>当前没有需要人工处理的设备操作。</span>
          {resolution?.feedback ? <span aria-live="polite">{resolution.feedback}</span> : null}
        </div>
      </output>
    );
  }
  return (
    <section className="device-attention-band" role="alert">
      <AlertTriangle size={19} aria-hidden="true" />
      <div>
        <strong>设备操作需要人工处理 · 共 {state.incidents.length} 条需要处理</strong>
        {state.incidents.map((incident) => (
          <span key={incident.incidentId}>
            <strong>{incident.subject}</strong>：{incident.reason}（
            {incident.evidence === "MOCK" ? "测试设备" : "门店设备"}）
            <br />
            {incident.businessResult}
            <br />
            {incident.nextStep}
            {incident.manualResolutionAllowed && resolution ? (
              <>
                <br />
                <button
                  className="button secondary compact"
                  type="button"
                  disabled={resolution.busy}
                  onClick={() => resolution.onResolve(incident)}
                >
                  {resolution.busy ? "正在确认…" : "已人工处理，关闭提醒"}
                </button>
              </>
            ) : null}
          </span>
        ))}
        {resolution?.feedback ? <span aria-live="polite">{resolution.feedback}</span> : null}
      </div>
    </section>
  );
}

export type FrontDeskTableQueryView = "LOADING" | "ERROR" | "EMPTY" | "READY";

export function frontDeskTableQueryView(
  data: readonly unknown[] | undefined,
  state: { isPending: boolean; isError: boolean },
): FrontDeskTableQueryView {
  if (state.isError) return "ERROR";
  if (state.isPending || data === undefined) return "LOADING";
  return data.length === 0 ? "EMPTY" : "READY";
}

export function frontDeskTableCountLabel(view: FrontDeskTableQueryView, count: number): string {
  if (view === "ERROR") return "无法确认";
  if (view === "LOADING") return "读取中";
  return String(count).padStart(2, "0");
}

export function frontdeskOperatingFactMatchesTable(
  fact: FrontdeskOperatingFactReadModel,
  table: TableSnapshot | undefined,
): table is TableSnapshot {
  if (
    !table ||
    table.tenantId !== fact.tenantId ||
    table.storeId !== fact.storeId ||
    table.tableId !== fact.tableId ||
    table.tableNumber !== fact.tableNumber ||
    table.area !== fact.area ||
    table.state !== fact.table.state ||
    table.revision !== fact.table.revision
  ) {
    return false;
  }
  if (fact.table.state === "IDLE" || fact.table.state === "DISABLED") {
    return fact.session === null && fact.order === null && table.sessionId === null;
  }
  if (fact.table.state === "AWAITING_CLEANING") {
    return fact.session !== null && fact.order !== null;
  }
  return (
    fact.session !== null &&
    fact.order !== null &&
    table.sessionId === fact.session.sessionId &&
    table.sessionStartedAt === fact.session.startedAt &&
    table.billingState === fact.session.billingState &&
    table.billingPausedAt === fact.session.billingPausedAt
  );
}

function frontdeskCacheHasNewerTableRevision(
  queryClient: QueryClient,
  operator: Pick<OperatorIdentity, "tenantId" | "storeId">,
  tableId: string,
  returnedRevision: number,
): boolean {
  const cachedTable = queryClient
    .getQueryData<TableSnapshot[]>(["tables"])
    ?.find((candidate) => candidate.tableId === tableId);
  const cachedFact = queryClient
    .getQueryData<FrontdeskOperatingFactReadModel[]>([
      "frontdesk-operating-facts",
      operator.tenantId,
      operator.storeId,
    ])
    ?.find((candidate) => candidate.tableId === tableId);
  return (
    (cachedTable?.revision ?? -1) > returnedRevision ||
    (cachedFact?.table.revision ?? -1) > returnedRevision
  );
}

function frontdeskOperatingFactCollectionLabel(
  fact: FrontdeskOperatingFactReadModel | undefined,
): string | null {
  switch (fact?.order?.collectionState) {
    case "PENDING_RECONCILIATION":
      return paymentStatusTerm("PENDING_RECONCILIATION").label;
    case "PENDING_MEMBER_DEBIT":
      return paymentStatusTerm("PENDING_MEMBER_DEBIT").label;
    case "MEMBER_RECEIVABLE":
      return paymentStatusTerm("MEMBER_RECEIVABLE").label;
    default:
      return null;
  }
}

export function FrontDesk({
  operator,
  effectiveRoles,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [selected, setSelected] = useState<TableSnapshot | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [openFeedback, setOpenFeedback] = useState<string | null>(null);
  const [deviceTrustNow, setDeviceTrustNow] = useState(() => Date.now());
  const openedTaskTarget = useRef<string | null>(null);
  const openedRecoveryRequest = useRef<string | null>(null);
  const openedOrderItemRecoverySession = useRef<string | null>(null);
  const openedCloseoutRecoveryRequest = useRef<string | null>(null);
  const openedSessionBillingRecoveryRequest = useRef<string | null>(null);
  const preservedSelectionDuringBillingRefresh = useRef<string | null>(null);
  const openedSessionTransferRecoveryRequest = useRef<string | null>(null);
  const openIdentityKey = tableSessionOpenIdentityKey(operator);
  const itemAddIdentityKey = orderItemAddIdentityKey(operator);
  const checkoutRecoveryIdentityKey = checkoutIdentityKey(operator);
  const officialPaymentRecoveryIdentityKey = officialPaymentIdentityKey(operator);
  const closeoutRecoveryIdentityKey = tableCloseoutIdentityKey(operator);
  const sessionBillingRecoveryIdentityKey = sessionBillingIdentityKey(operator);
  const sessionTransferRecoveryIdentityKey = sessionTransferIdentityKey(operator);
  const deviceIncidentResolutionOwnerKey = deviceIncidentResolutionIdentityKey(operator);
  const currentOpenIdentityKey = useRef(openIdentityKey);
  currentOpenIdentityKey.current = openIdentityKey;
  const currentCheckoutIdentityKey = useRef(checkoutRecoveryIdentityKey);
  currentCheckoutIdentityKey.current = checkoutRecoveryIdentityKey;
  const currentOfficialPaymentIdentityKey = useRef(officialPaymentRecoveryIdentityKey);
  currentOfficialPaymentIdentityKey.current = officialPaymentRecoveryIdentityKey;
  const currentCloseoutIdentityKey = useRef(closeoutRecoveryIdentityKey);
  currentCloseoutIdentityKey.current = closeoutRecoveryIdentityKey;
  const currentSessionBillingIdentityKey = useRef(sessionBillingRecoveryIdentityKey);
  currentSessionBillingIdentityKey.current = sessionBillingRecoveryIdentityKey;
  const currentSessionTransferIdentityKey = useRef(sessionTransferRecoveryIdentityKey);
  currentSessionTransferIdentityKey.current = sessionTransferRecoveryIdentityKey;
  const currentDeviceIncidentResolutionOwnerKey = useRef(deviceIncidentResolutionOwnerKey);
  currentDeviceIncidentResolutionOwnerKey.current = deviceIncidentResolutionOwnerKey;
  const [openRecovery, setOpenRecovery] = useState<TableSessionOpenRecoveryView>(() =>
    readTableSessionOpenRecovery(openIdentityKey),
  );
  const [itemAddRecovery, setItemAddRecovery] = useState<OrderItemAddRecoveryView>(() =>
    readOrderItemAddRecoveries(itemAddIdentityKey),
  );
  const [checkoutRecovery, setCheckoutRecovery] = useState<CheckoutRecoveryView>(() =>
    readCheckoutRecovery(checkoutRecoveryIdentityKey),
  );
  const [checkoutFeedback, setCheckoutFeedback] = useState<CheckoutFeedback | null>(null);
  const [officialPaymentRecovery, setOfficialPaymentRecovery] =
    useState<OfficialPaymentRecoveryView>(() =>
      readOfficialPaymentRecovery(officialPaymentRecoveryIdentityKey),
    );
  const [officialPaymentFeedback, setOfficialPaymentFeedback] =
    useState<OfficialPaymentFeedback | null>(null);
  const [closeoutRecovery, setCloseoutRecovery] = useState<TableCloseoutRecoveryView>(() =>
    readTableCloseoutRecovery(closeoutRecoveryIdentityKey),
  );
  const [closeoutFeedback, setCloseoutFeedback] = useState<string | null>(null);
  const [sessionBillingRecovery, setSessionBillingRecovery] = useState<SessionBillingRecoveryView>(
    () => readSessionBillingRecovery(sessionBillingRecoveryIdentityKey),
  );
  const [sessionBillingFeedback, setSessionBillingFeedback] = useState<string | null>(null);
  const [sessionBillingFeedbackAction, setSessionBillingFeedbackAction] =
    useState<SessionBillingAction | null>(null);
  const [sessionTransferRecovery, setSessionTransferRecovery] =
    useState<SessionTransferRecoveryView>(() =>
      readSessionTransferRecovery(sessionTransferRecoveryIdentityKey),
    );
  const [deviceIncidentResolutionRecovery, setDeviceIncidentResolutionRecovery] =
    useState<DeviceIncidentResolutionRecoveryView>(() =>
      readDeviceIncidentResolutionRecovery(deviceIncidentResolutionOwnerKey),
    );
  const [deviceIncidentResolutionFeedback, setDeviceIncidentResolutionFeedback] = useState<
    string | null
  >(null);
  useEffect(() => {
    setDeviceIncidentResolutionRecovery(
      readDeviceIncidentResolutionRecovery(deviceIncidentResolutionOwnerKey),
    );
    setDeviceIncidentResolutionFeedback(null);
  }, [deviceIncidentResolutionOwnerKey]);
  const [sessionTransferFeedback, setSessionTransferFeedback] = useState<string | null>(null);
  const combinedCheckoutContext = useMemo(
    () =>
      combinedCheckoutRecoveryContext({
        id: operator.id,
        storeId: operator.storeId,
        tenantId: operator.tenantId,
      }),
    [operator.id, operator.storeId, operator.tenantId],
  );
  const [combinedCheckoutRecovery, setCombinedCheckoutRecovery] =
    useState<CombinedCheckoutRecoveryView>(() =>
      readCombinedCheckoutRecovery(combinedCheckoutContext),
    );
  const customerReservationContext = useMemo(
    () =>
      customerReservationRecoveryContext({
        id: operator.id,
        storeId: operator.storeId,
        tenantId: operator.tenantId,
      }),
    [operator.id, operator.storeId, operator.tenantId],
  );
  const [customerReservationRecovery, setCustomerReservationRecovery] =
    useState<CustomerReservationRecoveryView>(() =>
      readCustomerReservationRecovery(customerReservationContext),
    );
  const openTableCustomerRequest = openTableCustomerRequestQuery(location.search);
  const linkedOpenRecoveryEventId =
    openRecovery.status === "OWNED" ? openRecovery.recovery.command.customerRequestEventId : null;
  const canPotentiallyOpenCustomerRequest = effectiveRoles.some(
    (role) => role === "MANAGER" || role === "CASHIER",
  );
  const linkedOpenRequestReadEnabled =
    openTableCustomerRequest.status === "VALID" && canPotentiallyOpenCustomerRequest;
  const linkedOpenRequestsQuery = useQuery({
    queryKey: [
      ...SERVICE_REQUEST_ACTIVE_QUERY_KEY,
      "open-table-customer-request",
      operator.tenantId,
      operator.storeId,
      operator.id,
    ],
    queryFn: () => listServiceRequests(),
    enabled: linkedOpenRequestReadEnabled,
    retry: false,
  });
  const linkedOpenWorkItemsQuery = useQuery({
    queryKey: serviceWorkItemsQueryKey(operator),
    queryFn: listServiceWorkItems,
    enabled: linkedOpenRequestReadEnabled,
    retry: false,
  });
  const linkedOpenNeedsDuty =
    effectiveRoles.includes("CASHIER") &&
    !effectiveRoles.includes("MANAGER") &&
    (openTableCustomerRequest.status === "VALID" || linkedOpenRecoveryEventId !== null);
  const linkedOpenDutyQuery = useQuery({
    queryKey: serviceDutyQueryKey(operator),
    queryFn: getServiceDuty,
    enabled: linkedOpenNeedsDuty,
    retry: false,
    refetchInterval: 1_000,
  });
  const canReadOperatingFacts = canReadDeviceIncidentsForRoles(effectiveRoles);
  const canReadDeviceIncidents = canReadOperatingFacts;
  const canOperateCoachSession = canOperateCoachSessionForRoles(effectiveRoles);
  const operatingFactsQuery = useQuery({
    queryKey: ["frontdesk-operating-facts", operator.tenantId, operator.storeId],
    queryFn: () =>
      listFrontdeskOperatingFacts({ tenantId: operator.tenantId, storeId: operator.storeId }),
    // The authoritative board read can take longer than one second on a
    // resource-capped store host. Polling faster cancels every in-flight read
    // and leaves the cashier with no actionable table after opening a session.
    refetchInterval: 5_000,
    enabled: canReadOperatingFacts,
  });
  const tablesQuery = useQuery({
    queryKey: ["tables"],
    queryFn: () => listTables({ tenantId: operator.tenantId, storeId: operator.storeId }),
    refetchInterval: 5_000,
    enabled: canReadOperatingFacts,
  });
  const connectivity = useQuery({
    queryKey: ["connectivity"],
    queryFn: getConnectivity,
    refetchInterval: 1_000,
    enabled: canReadOperatingFacts,
  });
  const devices = useQuery({
    queryKey: ["device-status"],
    queryFn: getDeviceAdapterStatus,
    refetchInterval: 2_000,
    enabled: canReadDeviceIncidents,
  });
  const deviceTrust = canReadDeviceIncidents
    ? deviceIncidentTrustState(devices, deviceTrustNow)
    : null;
  const deviceIncidentResolutionMutation = useMutation({
    mutationFn: async (recovery: DeviceIncidentResolutionRecovery) => {
      const identityKey = recovery.ownerIdentityKey;
      if (currentDeviceIncidentResolutionOwnerKey.current !== identityKey) {
        throw new ApiRequestError(
          "OPERATOR_IDENTITY_CHANGED",
          "账号已切换；原账号的设备处理结果不会显示在当前账号",
        );
      }
      if (!beginDeviceIncidentResolutionDispatch(recovery, identityKey)) {
        throw new ApiRequestError(
          "RECOVERY_STORAGE_UNAVAILABLE",
          "无法确认原设备处理记录仍被可靠保存，系统没有提交",
        );
      }
      try {
        const result = await resolveDeviceIncidentManually(recovery.command.incidentId, {
          expectedUpdatedAt: recovery.command.expectedUpdatedAt,
          note: recovery.command.note,
          idempotencyKey: recovery.command.idempotencyKey,
        });
        return { result, recovery, identityKey };
      } catch (error) {
        const definiteRejection =
          error instanceof ApiRequestError &&
          error.statusCode !== undefined &&
          error.statusCode >= 400 &&
          error.statusCode < 500;
        if (definiteRejection) {
          clearDeviceIncidentResolutionRecovery(recovery, identityKey);
        } else {
          markDeviceIncidentResolutionOutcomeUnknown(recovery, identityKey);
        }
        throw error;
      } finally {
        finishDeviceIncidentResolutionDispatch(recovery, identityKey);
      }
    },
    onMutate: () => setDeviceIncidentResolutionFeedback(null),
    onSuccess: async ({ result, recovery, identityKey }) => {
      if (currentDeviceIncidentResolutionOwnerKey.current !== identityKey) return;
      const cleared = clearDeviceIncidentResolutionRecovery(recovery, identityKey);
      setDeviceIncidentResolutionRecovery(readDeviceIncidentResolutionRecovery(identityKey));
      setDeviceIncidentResolutionFeedback(
        cleared
          ? `${result.subject} 的设备提醒已关闭；台单、订单和付款结果均未改动。`
          : `${result.subject} 已确认人工处理，但本地恢复记录未能清除；请刷新页面后核对，暂勿重复操作。`,
      );
      queryClient.setQueryData<Awaited<ReturnType<typeof getDeviceAdapterStatus>>>(
        ["device-status"],
        (current) =>
          current
            ? {
                attention: current.attention.filter(
                  (incident) => incident.incidentId !== result.incidentId,
                ),
              }
            : current,
      );
      await queryClient.invalidateQueries({ queryKey: ["device-status"] });
      setDeviceTrustNow(Date.now());
    },
    onError: async (error) => {
      const identityKey = currentDeviceIncidentResolutionOwnerKey.current;
      setDeviceIncidentResolutionRecovery(readDeviceIncidentResolutionRecovery(identityKey));
      setDeviceIncidentResolutionFeedback(
        businessErrorMessage(error, "系统没有返回明确结果，请查询或重试原确认"),
      );
      if (error instanceof ApiRequestError && error.statusCode && error.statusCode < 500) {
        await queryClient.invalidateQueries({ queryKey: ["device-status"] });
      }
    },
  });
  const submitDeviceIncidentResolution = useCallback(
    (incident: DeviceIncidentReadModel) => {
      const identityKey = currentDeviceIncidentResolutionOwnerKey.current;
      const claimed = claimDeviceIncidentResolutionRecovery(identityKey, {
        incidentId: incident.incidentId,
        expectedUpdatedAt: incident.updatedAt,
        note: "现场已人工处理并核对",
        idempotencyKey: crypto.randomUUID(),
      });
      setDeviceIncidentResolutionRecovery(claimed);
      if (claimed.status === "OWNED") {
        deviceIncidentResolutionMutation.mutate(claimed.recovery);
      } else {
        setDeviceIncidentResolutionFeedback(
          claimed.status === "OTHER_OPERATOR"
            ? "原账号还有一条设备处理结果待核对，当前账号没有提交。"
            : "当前标签页无法可靠保存原确认，系统没有提交。",
        );
      }
    },
    [deviceIncidentResolutionMutation],
  );
  const trustedConnectivityValue = trustedConnectivity(connectivity);
  const reservationSelected = reservationModuleEnabled(trustedConnectivityValue);
  const customerSelfServiceReservationSelected =
    customerSelfServiceReservationModuleEnabled(trustedConnectivityValue);
  const canReadReservations = effectiveRoles.some((role) =>
    ["OWNER", "MANAGER", "CASHIER"].includes(role),
  );
  const tables = tablesQuery.data ?? [];
  const operatingFactsView = frontDeskTableQueryView(operatingFactsQuery.data, operatingFactsQuery);
  const visibleFacts =
    operatingFactsView === "READY" || operatingFactsView === "EMPTY"
      ? (operatingFactsQuery.data ?? [])
      : [];
  const visibleTables = visibleFacts.flatMap((fact) => {
    const table = tables.find((candidate) => candidate.tableId === fact.tableId);
    return frontdeskOperatingFactMatchesTable(fact, table) ? [table] : [];
  });
  const coachSessionDeepLinkTarget = canOperateCoachSession
    ? frontdeskCoachSessionDeepLink(location.search, visibleTables)
    : null;
  const coachSessionDeepLinkKey = coachSessionDeepLinkTarget
    ? `${operator.tenantId}:${operator.storeId}:${coachSessionDeepLinkTarget.sessionId}`
    : null;
  const openedCoachSessionDeepLink = useRef<string | null>(null);
  const writableTables = visibleFacts.flatMap((fact) => {
    const table = tables.find((candidate) => candidate.tableId === fact.tableId);
    return fact.blockers.length === 0 && frontdeskOperatingFactMatchesTable(fact, table)
      ? [table]
      : [];
  });
  const linkedOpenMatchingRequests =
    openTableCustomerRequest.status === "VALID"
      ? (linkedOpenRequestsQuery.data ?? []).filter(
          (request) =>
            request.eventId === openTableCustomerRequest.customerRequestEventId &&
            request.tableNodeId === openTableCustomerRequest.tableNodeId &&
            request.type === "OPEN_TABLE" &&
            request.status === "ACKNOWLEDGED" &&
            request.sessionId === null &&
            (request.assignedToCurrentActor || effectiveRoles.includes("MANAGER")),
        )
      : [];
  const linkedOpenRequest =
    linkedOpenMatchingRequests.length === 1 ? (linkedOpenMatchingRequests[0] ?? null) : null;
  const linkedOpenWorkItem =
    linkedOpenRequest && linkedOpenWorkItemsQuery.data
      ? (serviceRequestStaffRows(
          [linkedOpenRequest],
          linkedOpenWorkItemsQuery.data.items,
          !linkedOpenWorkItemsQuery.isError,
          linkedOpenRequest.eventId,
        )[0]?.workItem ?? null)
      : null;
  const linkedOpenTableMatches =
    openTableCustomerRequest.status === "VALID"
      ? writableTables.filter(
          (table) =>
            table.tableNodeId === openTableCustomerRequest.tableNodeId &&
            table.tenantId === operator.tenantId &&
            table.storeId === operator.storeId &&
            table.state === "IDLE" &&
            table.sessionId === null &&
            table.sessionStartedAt === null &&
            table.billingState === "ENDED",
        )
      : [];
  const linkedOpenTable =
    linkedOpenTableMatches.length === 1 ? (linkedOpenTableMatches[0] ?? null) : null;
  const linkedOpenRoleAllowed = canOpenTableCustomerRequestForRoles(
    effectiveRoles,
    linkedOpenDutyQuery.data,
  );
  const linkedOpenBindingReady = Boolean(
    openTableCustomerRequest.status === "VALID" &&
      linkedOpenRequest &&
      linkedOpenWorkItem?.workItemId === `service:${linkedOpenRequest.eventId}` &&
      (linkedOpenWorkItem.assignedToCurrentActor || effectiveRoles.includes("MANAGER")) &&
      linkedOpenTable &&
      linkedOpenRoleAllowed,
  );
  const linkedOpenBindingMessage = (() => {
    if (openTableCustomerRequest.status === "NONE") return null;
    if (openTableCustomerRequest.status === "INVALID") {
      return "顾客开台申请信息不完整或已被改动，系统没有打开开台操作。请返回员工工作中心重新进入。";
    }
    if (!canPotentiallyOpenCustomerRequest) {
      return "当前岗位不能办理顾客开台申请。请返回员工工作中心，由服务员转交给店长或当前服务值班收银员。";
    }
    if (linkedOpenRequestsQuery.isPending || linkedOpenWorkItemsQuery.isPending) {
      return "正在核对原顾客开台申请和员工接单状态，确认完成前不能开台。";
    }
    if (linkedOpenRequestsQuery.isError) {
      return "原顾客开台申请暂时无法确认，系统没有打开开台操作。请返回员工工作中心刷新后重试。";
    }
    if (!linkedOpenRequest) {
      return "原顾客申请已结束、尚未接单、已由其他员工接单或对应球桌已经变化，不能从这个链接开台。";
    }
    if (
      linkedOpenWorkItemsQuery.isError ||
      !linkedOpenWorkItem ||
      linkedOpenWorkItem.workItemId !== `service:${linkedOpenRequest.eventId}` ||
      (!linkedOpenWorkItem.assignedToCurrentActor && !effectiveRoles.includes("MANAGER"))
    ) {
      return "分派状态暂时无法确认，系统没有打开开台操作。请返回员工工作中心重新核对。";
    }
    if (linkedOpenNeedsDuty && linkedOpenDutyQuery.isPending) {
      return "正在核对当前服务值班收银员，确认完成前不能开台。";
    }
    if (linkedOpenNeedsDuty && linkedOpenDutyQuery.isError) {
      return "当前服务值班状态暂时无法确认，系统没有打开开台操作。请返回员工工作中心重试或转交店长。";
    }
    if (!linkedOpenRoleAllowed) {
      return "当前收银员没有承担服务值班，不能办理这笔申请。请返回员工工作中心转交店长或当前服务值班收银员。";
    }
    if (!linkedOpenTable) {
      return "申请对应的球桌已不是可开台的空闲状态，不能改选其他球桌。请返回员工工作中心核对最新状态。";
    }
    return null;
  })();
  const activeCount = visibleFacts.filter((fact) => fact.table.state === "IN_USE").length;
  const cleaningCount = visibleFacts.filter(
    (fact) => fact.table.state === "AWAITING_CLEANING",
  ).length;
  const selectedFact = selected
    ? (visibleFacts.find((fact) => fact.tableId === selected.tableId) ?? null)
    : null;
  const selectedFactMatches =
    selectedFact !== null &&
    frontdeskOperatingFactMatchesTable(selectedFact, selected ?? undefined);
  const trustedStoreAuthority =
    operatingFactsView === "READY" &&
    trustedConnectivity(connectivity)?.offlineAuthorityAvailable === true;
  const operatingFactAllowsWrite = (tableId: string): boolean => {
    const fact = visibleFacts.find((candidate) => candidate.tableId === tableId);
    const table = tables.find((candidate) => candidate.tableId === tableId);
    return (
      fact !== undefined &&
      fact.blockers.length === 0 &&
      frontdeskOperatingFactMatchesTable(fact, table)
    );
  };

  useEffect(() => {
    setCombinedCheckoutRecovery(readCombinedCheckoutRecovery(combinedCheckoutContext));
  }, [combinedCheckoutContext]);

  useEffect(() => {
    setCustomerReservationRecovery(readCustomerReservationRecovery(customerReservationContext));
  }, [customerReservationContext]);

  useEffect(() => {
    if (
      operatingFactsView === "ERROR" ||
      operatingFactsView === "EMPTY" ||
      (selected !== null && (!selectedFact || !selectedFactMatches))
    ) {
      if (preservedSelectionDuringBillingRefresh.current !== selected?.tableId) {
        setSelected(null);
      }
    }
  }, [operatingFactsView, selected, selectedFact, selectedFactMatches]);

  useEffect(() => {
    if (!coachSessionDeepLinkTarget || !coachSessionDeepLinkKey) {
      openedCoachSessionDeepLink.current = null;
      return;
    }
    if (openedCoachSessionDeepLink.current === coachSessionDeepLinkKey) return;
    openedCoachSessionDeepLink.current = coachSessionDeepLinkKey;
    setSelected(coachSessionDeepLinkTarget);
  }, [coachSessionDeepLinkKey, coachSessionDeepLinkTarget]);

  const checkoutPayment = useMutation({
    mutationFn: async ({
      recovery,
      identityKey,
    }: {
      recovery: CheckoutRecovery;
      identityKey: string;
    }) => {
      requireTrustedStoreAuthority(connectivity, "收款结账");
      try {
        const { command } = recovery;
        const result = await checkoutSession(
          command.sessionId,
          command.method,
          command.idempotencyKey,
          {
            tenantId: operator.tenantId,
            storeId: operator.storeId,
            baselineOrder: command.baselineOrder,
            memberId: command.memberId,
          },
          command.memberId
            ? {
                memberId: command.memberId,
                ...(command.memberBalanceFen === undefined
                  ? {}
                  : { memberBalanceFen: command.memberBalanceFen }),
                ...(command.memberSpendPlanHash === undefined
                  ? {}
                  : { memberSpendPlanHash: command.memberSpendPlanHash }),
              }
            : undefined,
        );
        if (currentCheckoutIdentityKey.current !== identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的收款结果不会显示在当前账号，请切回原账号继续核对",
          );
        }
        const recoveryResult = applyCheckoutResultToRecovery(
          recovery,
          identityKey,
          result.memberDebit,
        );
        if (recoveryResult === null) {
          throw new ApiRequestError(
            "CHECKOUT_RECOVERY_UNAVAILABLE",
            "收款结果已经返回，但无法与原请求和会员待扣状态安全对应；页面继续保留原结账恢复入口",
          );
        }
        return { recovery, result, identityKey, recoveryResult };
      } catch (error) {
        if (checkoutOutcomeIsUnknown(error)) {
          markCheckoutOutcomeUnknown(recovery, identityKey);
        } else {
          clearCheckoutRecovery(recovery, identityKey);
        }
        throw error;
      } finally {
        finishCheckoutDispatch(recovery, identityKey);
      }
    },
    onMutate: () => setCheckoutFeedback(null),
    onSuccess: async ({ recovery, result, identityKey, recoveryResult }) => {
      if (currentCheckoutIdentityKey.current !== identityKey) return;
      setCheckoutRecovery(readCheckoutRecovery(identityKey));
      queryClient.setQueryData<OrderDetail>(
        ["session-order", recovery.command.sessionId],
        result.order,
      );
      queryClient.setQueryData<TableSnapshot[]>(["tables"], (current) =>
        replaceTableSnapshot(current, result.table),
      );
      const baselineOutstandingFen =
        orderCheckoutOutstandingFen(recovery.command.baselineOrder) ?? 0;
      const message =
        baselineOutstandingFen === 0
          ? recovery.command.baselineOrder.paidFen > 0
            ? "开台时的会员预付已覆盖当前账单，本次没有重复收款，台单已结账；下一步请清洁球台。"
            : "本单无需补收，已结账；下一步请清洁球台。"
          : recoveryResult === "MEMBER_RECEIVABLE"
            ? "台单已结账并进入待清洁，余额待联网扣除。会员余额尚未扣除并已形成会员应收，请先为会员充值，再从工作中心按应收业务号重新扣款。"
            : recoveryResult === "PENDING_MEMBER_DEBIT"
              ? "台单已结账并进入待清洁，会员余额尚未扣除。系统会在联网后用原请求继续扣款，请勿再次收款。"
              : recovery.command.memberBalanceFen !== undefined
                ? recovery.command.method === "CASH"
                  ? `会员余额已扣 ${formatFee(recovery.command.memberBalanceFen)}，现金已收 ${formatFee(baselineOutstandingFen - recovery.command.memberBalanceFen)}；${recovery.command.baselineOrder.paidFen > 0 ? "开台预付部分未重复收取，" : ""}两路均按原计划完成，台单已结账。`
                  : `会员余额已扣 ${formatFee(recovery.command.memberBalanceFen)}，${recovery.command.method === "STATIC_WECHAT" ? "微信收款码" : "支付宝收款码"}已按现场确认记录 ${formatFee(baselineOutstandingFen - recovery.command.memberBalanceFen)}；${recovery.command.baselineOrder.paidFen > 0 ? "开台预付部分未重复收取，" : ""}台单已结账，该扫码收款继续保留“待渠道核对”。`
                : recovery.command.method === "MEMBER_BALANCE"
                  ? "会员余额已准确扣减一次，台单已结账；下一步请清洁球台。"
                  : recovery.command.method === "CASH"
                    ? recovery.command.baselineOrder.paidFen > 0
                      ? `现金已按本次待收 ${formatFee(baselineOutstandingFen)} 本地入账，开台预付部分未重复收取；台单已结账，下一步请清洁球台。`
                      : "现金已按确认金额本地入账，台单已结账；下一步请清洁球台。"
                    : "台单已结账；下一步请清洁球台。静态码付款继续保留“待渠道核对”。";
      setNotice(message);
      setSelected(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
        queryClient.invalidateQueries({ queryKey: ["tables"] }),
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        queryClient.invalidateQueries({ queryKey: ["manual-review"] }),
        queryClient.invalidateQueries({ queryKey: ["connectivity"] }),
        queryClient.invalidateQueries({ queryKey: ["device-status"] }),
      ]);
    },
    onError: async (error, input) => {
      if (currentCheckoutIdentityKey.current !== input.identityKey) return;
      const view = readCheckoutRecovery(input.identityKey);
      setCheckoutRecovery(view);
      const reason = businessErrorMessage(error, "系统没有返回明确结果");
      if (error instanceof ApiRequestError && error.code === "CHECKOUT_TOTAL_CHANGED") {
        setCheckoutFeedback({
          code: error.code,
          message: `${reason}。页面正在刷新最新账单，请重新核对金额后再确认收款。`,
        });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
          queryClient.invalidateQueries({
            queryKey: ["session-order", input.recovery.command.sessionId],
          }),
          queryClient.invalidateQueries({ queryKey: ["tables"] }),
        ]);
        return;
      }
      if (
        error instanceof ApiRequestError &&
        (error.code === "MEMBER_SPEND_PLAN_CHANGED" ||
          error.code === "MEMBER_SPEND_PREVIEW_CHANGED")
      ) {
        setCheckoutFeedback({
          code: error.code,
          message: `${reason}。页面正在重新获取钱包扣款明细，请让顾客核对新方案后再确认收款。`,
        });
        await queryClient.invalidateQueries({ queryKey: ["checkout-member-spend-preview"] });
        return;
      }
      setCheckoutFeedback({
        code: error instanceof ApiRequestError ? error.code : null,
        message: checkoutOutcomeIsUnknown(error)
          ? view.status === "OWNED"
            ? `${input.recovery.command.tableNumber} 号台的收款结果尚未确认：${reason}。系统已保留${input.recovery.command.memberBalanceFen === undefined ? "原金额、原方式" : "原会员扣款、原剩余收款方式和两路金额"}及原请求，安全重试不会重复收款。`
            : `收款结果尚未确认：${reason}。本机暂时无法读取保护记录，请勿再次收款或继续修改台单。`
          : `这次收款没有执行：${reason}。系统已确认没有写入付款或关闭台单。`,
      });
    },
  });

  const dispatchCheckoutRecovery = useCallback(
    (recovery: CheckoutRecovery): void => {
      try {
        requireTrustedStoreAuthority(connectivity, "收款结账");
      } catch (error) {
        setCheckoutFeedback({
          code: error instanceof ApiRequestError ? error.code : null,
          message: businessErrorMessage(error, "当前不能安全收款"),
        });
        return;
      }
      if (!beginCheckoutDispatch(recovery, checkoutRecoveryIdentityKey)) {
        const view = readCheckoutRecovery(checkoutRecoveryIdentityKey);
        setCheckoutRecovery(view);
        setCheckoutFeedback({
          code: null,
          message:
            view.status === "RECOVERY_STORAGE_UNAVAILABLE"
              ? "无法安全读取收款保护记录，系统没有发出请求。"
              : "原收款请求正在确认中，请勿重复点击。",
        });
        return;
      }
      checkoutPayment.mutate({ recovery, identityKey: checkoutRecoveryIdentityKey });
    },
    [checkoutPayment, checkoutRecoveryIdentityKey, connectivity],
  );

  const requestCheckout = useCallback(
    (
      table: TableSnapshot,
      baselineOrder: OrderDetail,
      method: PaymentMethod,
      member?: {
        memberId: string;
        memberBalanceFen?: number;
        memberSpendPlanHash?: string;
      },
    ): void => {
      try {
        requireTrustedStoreAuthority(connectivity, "收款结账");
      } catch (error) {
        setCheckoutFeedback({
          code: error instanceof ApiRequestError ? error.code : null,
          message: businessErrorMessage(error, "当前不能安全收款"),
        });
        return;
      }
      const claimed = claimCheckoutRecovery(checkoutRecoveryIdentityKey, {
        kind: "CHECKOUT_SESSION",
        sessionId: baselineOrder.sessionId,
        tableId: table.tableId,
        tableNumber: table.tableNumber,
        baselineOrder,
        method,
        memberId: member?.memberId ?? null,
        ...(member?.memberBalanceFen === undefined
          ? {}
          : { memberBalanceFen: member.memberBalanceFen }),
        ...(member?.memberSpendPlanHash === undefined
          ? {}
          : { memberSpendPlanHash: member.memberSpendPlanHash }),
        idempotencyKey: crypto.randomUUID(),
      });
      setCheckoutRecovery(claimed);
      if (claimed.status !== "OWNED") {
        setCheckoutFeedback({
          code: null,
          message:
            claimed.status === "OTHER_OPERATOR"
              ? "另一位员工有一笔收款结果尚未确认，系统没有发出新的收款请求。"
              : "本机无法安全保存收款保护记录，系统没有发出收款请求。",
        });
        return;
      }
      if (claimed.recovery.command.sessionId !== baselineOrder.sessionId) {
        setCheckoutFeedback({
          code: null,
          message: `${claimed.recovery.command.tableNumber} 号台仍有一笔收款结果待确认，请先处理原请求。`,
        });
        return;
      }
      dispatchCheckoutRecovery(claimed.recovery);
    },
    [checkoutRecoveryIdentityKey, connectivity, dispatchCheckoutRecovery],
  );

  const officialPayment = useMutation({
    mutationFn: async ({
      recovery,
      identityKey,
    }: {
      recovery: OfficialPaymentRecovery;
      identityKey: string;
    }) => {
      requireTrustedStoreAuthority(connectivity, "微信或支付宝正式支付");
      try {
        const { command } = recovery;
        const request = {
          rail: command.rail,
          expectedTotalFen: command.expectedTotalFen,
          idempotencyKey: command.idempotencyKey,
        };
        const result =
          recovery.state === "CLAIMED"
            ? await startOfficialPayment(
                command.sessionId,
                request,
                command.expectedPaymentFen ?? command.expectedTotalFen,
              )
            : await recoverOfficialPayment(
                command.sessionId,
                request,
                command.expectedPaymentFen ?? command.expectedTotalFen,
              );
        if (currentOfficialPaymentIdentityKey.current !== identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的正式支付结果不会显示在当前账号，请切回原账号继续核对",
          );
        }
        if (!recordOfficialPaymentResult(recovery, identityKey, result)) {
          throw new ApiRequestError(
            "OFFICIAL_PAYMENT_RECOVERY_UNAVAILABLE",
            "渠道结果已经返回，但本机未能安全保存或清除保护记录；页面不会把这笔钱显示为到账",
          );
        }
        return { recovery, result, identityKey };
      } catch (error) {
        if (officialPaymentOutcomeIsUnknown(error, recovery)) {
          markOfficialPaymentOutcomeUnknown(recovery, identityKey);
        } else {
          clearOfficialPaymentAfterDefiniteNoEffect(recovery, identityKey);
        }
        throw error;
      } finally {
        finishOfficialPaymentDispatch(recovery, identityKey);
      }
    },
    onMutate: () => setOfficialPaymentFeedback(null),
    onSuccess: async ({ recovery, result, identityKey }) => {
      if (currentOfficialPaymentIdentityKey.current !== identityKey) return;
      setOfficialPaymentRecovery(readOfficialPaymentRecovery(identityKey));
      setOfficialPaymentFeedback({ kind: "RESULT", result });
      const presentation = officialPaymentPresentation(result);
      if (presentation.canRecognizeAsPaid && result.gatewaySettlement === "SETTLED") {
        setNotice(
          `${recovery.command.rail === "WECHAT_PAY" ? "微信支付" : "支付宝"}已由渠道确认到账，台单已结账；下一步请清洁球台。`,
        );
        setSelected(null);
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
        queryClient.invalidateQueries({ queryKey: ["session-order", recovery.command.sessionId] }),
        queryClient.invalidateQueries({ queryKey: ["tables"] }),
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        queryClient.invalidateQueries({ queryKey: ["manual-review"] }),
        queryClient.invalidateQueries({ queryKey: ["connectivity"] }),
      ]);
    },
    onError: (error, input) => {
      if (currentOfficialPaymentIdentityKey.current !== input.identityKey) return;
      const view = readOfficialPaymentRecovery(input.identityKey);
      setOfficialPaymentRecovery(view);
      if (view.status === "OWNED" && view.recovery.result) {
        setOfficialPaymentFeedback({ kind: "RESULT", result: view.recovery.result });
        return;
      }
      const reason = businessErrorMessage(error, "系统没有返回明确结果");
      setOfficialPaymentFeedback({
        kind: "ERROR",
        code: error instanceof ApiRequestError ? error.code : null,
        message:
          view.status === "OWNED"
            ? `${input.recovery.command.tableNumber} 号台的正式支付结果尚未确认：${reason}。系统已锁定原渠道、原金额和原请求号；只能使用原请求继续确认，不能改收或让顾客再次付款。`
            : `这次正式支付没有执行：${reason}。系统已确认可以重新选择付款方式。`,
      });
    },
  });

  const dispatchOfficialPaymentRecovery = useCallback(
    (recovery: OfficialPaymentRecovery): void => {
      try {
        requireTrustedStoreAuthority(connectivity, "微信或支付宝正式支付");
      } catch (error) {
        setOfficialPaymentFeedback({
          kind: "ERROR",
          code: error instanceof ApiRequestError ? error.code : null,
          message: businessErrorMessage(error, "当前不能安全发起正式支付"),
        });
        return;
      }
      if (!beginOfficialPaymentDispatch(recovery, officialPaymentRecoveryIdentityKey)) {
        const view = readOfficialPaymentRecovery(officialPaymentRecoveryIdentityKey);
        setOfficialPaymentRecovery(view);
        setOfficialPaymentFeedback({
          kind: "ERROR",
          code: null,
          message:
            view.status === "RECOVERY_STORAGE_UNAVAILABLE"
              ? "无法安全读取正式支付保护记录，系统没有发出请求。"
              : "原正式支付请求正在确认中，或服务端已要求人工核对；系统没有创建第二笔付款。",
        });
        return;
      }
      officialPayment.mutate({
        recovery,
        identityKey: officialPaymentRecoveryIdentityKey,
      });
    },
    [connectivity, officialPayment, officialPaymentRecoveryIdentityKey],
  );

  const requestOfficialPayment = useCallback(
    (table: TableSnapshot, order: OrderDetail, rail: OfficialPaymentRail): void => {
      try {
        requireTrustedStoreAuthority(connectivity, "微信或支付宝正式支付");
      } catch (error) {
        setOfficialPaymentFeedback({
          kind: "ERROR",
          code: error instanceof ApiRequestError ? error.code : null,
          message: businessErrorMessage(error, "当前不能安全发起正式支付"),
        });
        return;
      }
      const claimed = claimOfficialPaymentRecovery(officialPaymentRecoveryIdentityKey, {
        kind: "OFFICIAL_PAYMENT",
        sessionId: order.sessionId,
        tableId: table.tableId,
        tableNumber: table.tableNumber,
        orderId: order.id,
        rail,
        expectedTotalFen: order.totalFen,
        expectedPaymentFen: orderCheckoutOutstandingFen(order) ?? order.totalFen,
        idempotencyKey: crypto.randomUUID(),
      });
      setOfficialPaymentRecovery(claimed);
      if (claimed.status !== "OWNED") {
        setOfficialPaymentFeedback({
          kind: "ERROR",
          code: null,
          message:
            claimed.status === "OTHER_OPERATOR"
              ? "另一位员工有一笔正式支付结果尚未确认，系统没有发出新的付款请求。"
              : "本机无法安全保存正式支付保护记录，系统没有发出付款请求。",
        });
        return;
      }
      if (claimed.recovery.command.sessionId !== order.sessionId) {
        setOfficialPaymentFeedback({
          kind: "ERROR",
          code: null,
          message: `${claimed.recovery.command.tableNumber} 号台仍有一笔正式支付待确认，请先处理原请求。`,
        });
        return;
      }
      dispatchOfficialPaymentRecovery(claimed.recovery);
    },
    [connectivity, dispatchOfficialPaymentRecovery, officialPaymentRecoveryIdentityKey],
  );

  const sessionBillingMutation = useMutation({
    mutationFn: async ({
      recovery,
      identityKey,
    }: {
      recovery: SessionBillingRecovery;
      identityKey: string;
    }) => {
      requireTrustedStoreAuthority(
        connectivity,
        "暂停、恢复、修改开台时间、提前停止计费或误开台作废",
      );
      try {
        const { command } = recovery;
        let result: OrderDetail;
        let releasedTable: TableSnapshot | null = null;
        let coachStopResult: CoachSessionStopResult | null = null;
        if (command.action === "VOID") {
          const voided = await voidSession(
            command.sessionId,
            command.reason,
            command.idempotencyKey,
            { baselineOrder: command.baselineOrder, baselineTable: command.baselineTable },
          );
          result = voided.order;
          releasedTable = voided.table;
        } else {
          const expected = { baselineOrder: command.baselineOrder };
          if (command.action === "PAUSE") {
            result = await pauseSession(
              command.sessionId,
              command.reason,
              command.idempotencyKey,
              expected,
            );
          } else if (command.action === "RESUME") {
            result = await resumeSession(
              command.sessionId,
              command.reason,
              command.idempotencyKey,
              expected,
            );
          } else if (command.action === "ADJUST_START") {
            result = await adjustSessionStart(
              {
                preview: command.preview,
                reason: command.reason,
                idempotencyKey: command.idempotencyKey,
              },
              expected,
            );
          } else {
            if (command.action !== "END_EARLY") {
              throw new ApiRequestError(
                "REQUEST_VALIDATION_FAILED",
                "本机保存的计费操作类型无法识别，系统没有发出请求。",
                400,
              );
            }
            const coachStopPreview = command.coachStopPreview;
            const mustQueryOriginalResult =
              recovery.state === "OUTCOME_UNKNOWN" || coachStopPreview === null;
            if (mustQueryOriginalResult) {
              coachStopResult = await getCoachSessionStopResultByRequest(
                command.sessionId,
                command.idempotencyKey,
                { ...expected, baselineTable: command.baselineTable },
                coachStopPreview,
              );
            }
            if (!coachStopResult) {
              if (command.baselineTable === null) {
                throw new ApiRequestError(
                  "SESSION_COACH_STOP_BASELINE_REQUIRED",
                  "原停表请求已明确查不到执行结果，但旧保护记录缺少请求前球台版本，系统不会重发旧请求；请刷新台单并重新核对全部助教后再发起。",
                  409,
                );
              }
              if (coachStopPreview === null) {
                throw new ApiRequestError(
                  "SESSION_COACH_STOP_PREVIEW_REQUIRED",
                  "原停表请求使用旧版预览且已明确查不到执行结果，系统不会重发旧请求；请刷新并重新核对全部助教后再发起。",
                  409,
                );
              }
              coachStopResult = await endSessionEarly(
                command.sessionId,
                command.reason,
                command.idempotencyKey,
                { ...expected, baselineTable: command.baselineTable },
                coachStopPreview,
              );
            }
            result = coachStopResult.order;
          }
        }
        if (currentSessionBillingIdentityKey.current !== identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的计费操作结果不会显示在当前账号，请切回原账号继续核对",
          );
        }
        if (!clearSessionBillingRecovery(recovery, identityKey)) {
          throw new ApiRequestError(
            "SESSION_BILLING_RECOVERY_UNAVAILABLE",
            "计费操作结果已经返回，但本机未能安全清除保护记录；页面继续保留待确认状态",
          );
        }
        return { recovery, result, releasedTable, coachStopResult, identityKey };
      } catch (error) {
        if (sessionBillingOutcomeIsUnknown(error)) {
          markSessionBillingOutcomeUnknown(recovery, identityKey);
        } else {
          clearSessionBillingRecovery(recovery, identityKey);
        }
        throw error;
      } finally {
        finishSessionBillingDispatch(recovery, identityKey);
      }
    },
    onMutate: (input) => {
      setSessionBillingFeedback(null);
      setSessionBillingFeedbackAction(input.recovery.command.action);
    },
    onSuccess: async ({ recovery, result, releasedTable, coachStopResult, identityKey }) => {
      if (currentSessionBillingIdentityKey.current !== identityKey) return;
      setSessionBillingRecovery(readSessionBillingRecovery(identityKey));
      setSessionBillingFeedbackAction(null);
      openedSessionBillingRecoveryRequest.current = null;
      queryClient.setQueryData<OrderDetail>(["session-order", recovery.command.sessionId], result);
      if (coachStopResult && !coachStopResult.table) {
        queryClient.setQueryData<CoachSessionStopResult>(
          ["coach-session-stop-result", recovery.command.sessionId],
          coachStopResult,
        );
      }
      if (coachStopResult?.table) {
        const stoppedTable = coachStopResult.table;
        preservedSelectionDuringBillingRefresh.current = stoppedTable.tableId;
        queryClient.setQueryData<TableSnapshot[]>(["tables"], (current) =>
          replaceTableSnapshot(current, stoppedTable),
        );
        setSelected(stoppedTable);
      }
      if (releasedTable) {
        queryClient.setQueryData<TableSnapshot[]>(["tables"], (current) =>
          replaceTableSnapshot(current, releasedTable),
        );
        setSelected(null);
      }
      setNotice(
        recovery.command.action === "PAUSE"
          ? `${recovery.command.tableNumber} 号台计费已暂停，暂停原因和计费边界已经保存。`
          : recovery.command.action === "RESUME"
            ? `${recovery.command.tableNumber} 号台计费已恢复，暂停区间和恢复原因已经保存。`
            : recovery.command.action === "END_EARLY"
              ? coachStopResult
                ? coachSessionStopSuccessMessage(recovery.command.tableNumber, coachStopResult)
                : `${recovery.command.tableNumber} 号台计费已提前停止，台费金额已经冻结，可继续核对商品后结账。`
              : recovery.command.action === "ADJUST_START"
                ? `${recovery.command.tableNumber} 号台开台时间已修改，台费已按预览更新；本次操作已记录到老板和店长可查看的经营操作记录。`
                : `${recovery.command.tableNumber} 号台误开记录已确认作废，账单归零且球台已经恢复空闲。`,
      );
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
          queryClient.invalidateQueries({
            queryKey: ["session-order", recovery.command.sessionId],
          }),
          queryClient.invalidateQueries({ queryKey: ["tables"] }),
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.invalidateQueries({ queryKey: ["connectivity"] }),
          queryClient.invalidateQueries({ queryKey: ["device-status"] }),
          queryClient.invalidateQueries({ queryKey: ["coach-session-stop-preview"] }),
          queryClient.invalidateQueries({ queryKey: ["coach-runtime"] }),
          queryClient.invalidateQueries({ queryKey: ["coach-service-options"] }),
        ]);
      } finally {
        if (
          coachStopResult?.table &&
          preservedSelectionDuringBillingRefresh.current === coachStopResult.table.tableId
        ) {
          const refreshedTable = queryClient
            .getQueryData<TableSnapshot[]>(["tables"])
            ?.find(
              (candidate) =>
                candidate.tableId === coachStopResult.table?.tableId &&
                candidate.sessionId === coachStopResult.table.sessionId &&
                candidate.revision === coachStopResult.table.revision &&
                candidate.state === "IN_USE" &&
                candidate.billingState === "ENDED",
            );
          const refreshedFact = queryClient
            .getQueryData<FrontdeskOperatingFactReadModel[]>([
              "frontdesk-operating-facts",
              operator.tenantId,
              operator.storeId,
            ])
            ?.find((candidate) => candidate.tableId === coachStopResult.table?.tableId);
          if (
            refreshedTable &&
            refreshedFact &&
            frontdeskOperatingFactMatchesTable(refreshedFact, refreshedTable)
          ) {
            queryClient.setQueryData<CoachSessionStopResult>(
              ["coach-session-stop-result", recovery.command.sessionId],
              coachStopResult,
            );
            setSelected(refreshedTable);
          } else {
            setSelected(null);
          }
          preservedSelectionDuringBillingRefresh.current = null;
        }
      }
    },
    onError: (error, input) => {
      if (currentSessionBillingIdentityKey.current !== input.identityKey) return;
      const view = readSessionBillingRecovery(input.identityKey);
      setSessionBillingRecovery(view);
      const reason = businessErrorMessage(error, "系统没有返回明确结果");
      const actionLabel =
        input.recovery.command.action === "PAUSE"
          ? "暂停计费"
          : input.recovery.command.action === "RESUME"
            ? "恢复计费"
            : input.recovery.command.action === "END_EARLY"
              ? "提前停止计费"
              : input.recovery.command.action === "ADJUST_START"
                ? "修改开台时间"
                : "误开台作废";
      const coachStopPreviewInvalid =
        input.recovery.command.action === "END_EARLY" &&
        error instanceof ApiRequestError &&
        ["SESSION_COACH_STOP_PREVIEW_REQUIRED", "SESSION_COACH_SERVICE_SET_CHANGED"].includes(
          error.code,
        );
      setSessionBillingFeedback(
        coachStopPreviewInvalid
          ? error.code === "SESSION_COACH_SERVICE_SET_CHANGED"
            ? "本桌助教清单或状态已经变化，原停表预览和原确认已清除。系统没有按旧清单停表；请重新读取全部助教，逐项核对后再确认。"
            : "原停表请求缺少当前版本的助教预览，且已明确查不到执行结果。系统不会重发旧请求；请重新读取全部助教，逐项核对后再发起。"
          : sessionBillingOutcomeIsUnknown(error)
            ? view.status === "OWNED"
              ? `${input.recovery.command.tableNumber} 号台的“${actionLabel}”结果尚未确认：${reason}。系统保留了原台单、原原因和原请求，安全重试不会重复产生业务效果。`
              : `“${actionLabel}”结果尚未确认：${reason}。本机暂时无法读取保护记录，请勿继续修改该台单。`
            : input.recovery.command.action === "VOID"
              ? `这次“${actionLabel}”没有执行：${reason}。台单和球台没有因本次请求改变。`
              : input.recovery.command.action === "ADJUST_START"
                ? `这次“${actionLabel}”没有执行：${reason}。原开台时间、营业日和台费没有因本次请求改变；请按提示处理商品、助教、团购或收款记录后再预览。`
                : `这次“${actionLabel}”没有执行：${reason}。台费状态和金额没有因本次请求改变。`,
      );
      if (coachStopPreviewInvalid) {
        void Promise.all([
          queryClient.resetQueries({
            queryKey: ["coach-session-stop-preview", input.recovery.command.sessionId],
          }),
          queryClient.invalidateQueries({ queryKey: ["coach-runtime"] }),
          queryClient.invalidateQueries({
            queryKey: ["session-order", input.recovery.command.sessionId],
          }),
        ]);
      }
    },
  });

  const dispatchSessionBillingRecovery = useCallback(
    (recovery: SessionBillingRecovery): void => {
      try {
        requireTrustedStoreAuthority(
          connectivity,
          "暂停、恢复、修改开台时间、提前停止计费或误开台作废",
        );
      } catch (error) {
        setSessionBillingFeedback(businessErrorMessage(error, "当前不能安全修改计费"));
        return;
      }
      if (!beginSessionBillingDispatch(recovery, sessionBillingRecoveryIdentityKey)) {
        const view = readSessionBillingRecovery(sessionBillingRecoveryIdentityKey);
        setSessionBillingRecovery(view);
        setSessionBillingFeedback(
          view.status === "RECOVERY_STORAGE_UNAVAILABLE"
            ? "无法安全读取计费保护记录，系统没有发出请求。"
            : "原计费请求正在确认中，请勿重复点击。",
        );
        setSessionBillingFeedbackAction(recovery.command.action);
        return;
      }
      sessionBillingMutation.mutate({
        recovery,
        identityKey: sessionBillingRecoveryIdentityKey,
      });
    },
    [connectivity, sessionBillingMutation, sessionBillingRecoveryIdentityKey],
  );

  const requestSessionBillingChange = useCallback(
    (
      table: TableSnapshot,
      baselineOrder: OrderDetail,
      action: SessionBillingAction,
      reason: string,
      coachStopPreview: CoachSessionStopPreview | null = null,
      startAdjustmentPreview: SessionStartAdjustmentPreview | null = null,
    ): void => {
      try {
        requireTrustedStoreAuthority(
          connectivity,
          "暂停、恢复、修改开台时间、提前停止计费或误开台作废",
        );
        if (
          action === "VOID"
            ? !effectiveRoles.includes("CASHIER")
            : action === "ADJUST_START"
              ? !effectiveRoles.some((role) => role === "MANAGER" || role === "CASHIER")
              : !canOperateStandardSessionForRoles(effectiveRoles)
        ) {
          throw new Error(
            action === "VOID"
              ? "误开台只能由收银员确认作废"
              : action === "ADJUST_START"
                ? "修改开台时间只能由店长或收银员办理"
                : "当前岗位只能查看台局，不能暂停、恢复或提前停止计费",
          );
        }
        const expectedBillingState =
          action === "VOID" || action === "ADJUST_START"
            ? baselineOrder.billingState
            : action === "RESUME"
              ? "PAUSED"
              : "RUNNING";
        const noVoidBusinessFacts =
          baselineOrder.productTotalFen === 0 &&
          baselineOrder.coachServiceTotalFen === 0 &&
          baselineOrder.paidFen === 0 &&
          baselineOrder.refundedFen === 0 &&
          baselineOrder.items.length === 0 &&
          baselineOrder.coachServiceItems.length === 0 &&
          (baselineOrder.groupBuyFulfillments ?? []).length === 0 &&
          baselineOrder.payments.length === 0 &&
          baselineOrder.refunds.length === 0;
        if (
          table.tenantId !== operator.tenantId ||
          table.storeId !== operator.storeId ||
          table.state !== "IN_USE" ||
          !table.sessionId ||
          table.sessionId !== baselineOrder.sessionId ||
          table.tableId !== baselineOrder.tableId ||
          table.tableNumber !== baselineOrder.tableNumber ||
          table.sessionStartedAt !== baselineOrder.startedAt ||
          table.billingState !== baselineOrder.billingState ||
          table.billingPausedAt !== baselineOrder.billingPausedAt ||
          table.pricingPlanName !== baselineOrder.priceSnapshot.name ||
          table.estimatedFeeFen !== baselineOrder.tableFeeFen ||
          table.currentTotalFen !== baselineOrder.totalFen ||
          baselineOrder.status !== "OPEN" ||
          baselineOrder.endedAt !== null ||
          baselineOrder.billingState !== expectedBillingState ||
          (baselineOrder.billingState === "PAUSED"
            ? baselineOrder.billingPausedAt === null
            : baselineOrder.billingPausedAt !== null) ||
          (action === "VOID" && !noVoidBusinessFacts) ||
          (action === "ADJUST_START" &&
            (!noVoidBusinessFacts ||
              !startAdjustmentPreview ||
              startAdjustmentPreview.tenantId !== operator.tenantId ||
              startAdjustmentPreview.storeId !== operator.storeId ||
              startAdjustmentPreview.sessionId !== baselineOrder.sessionId ||
              startAdjustmentPreview.orderId !== baselineOrder.id ||
              startAdjustmentPreview.tableId !== table.tableId ||
              startAdjustmentPreview.tableNumber !== table.tableNumber ||
              startAdjustmentPreview.previousStartedAt !== baselineOrder.startedAt ||
              startAdjustmentPreview.previousBusinessDate !== baselineOrder.businessDate ||
              startAdjustmentPreview.proposedBusinessDate !== baselineOrder.businessDate ||
              startAdjustmentPreview.cashShiftBusinessDate !== baselineOrder.businessDate ||
              startAdjustmentPreview.notificationAudience[0] !== "OWNER" ||
              startAdjustmentPreview.notificationAudience[1] !== "MANAGER" ||
              startAdjustmentPreview.notificationChannel !== "BUSINESS_OPERATION_LOG" ||
              startAdjustmentPreview.notificationOutcome !== "RECORDED_IN_SAME_TRANSACTION" ||
              startAdjustmentPreview.previousTableFeeFen < baselineOrder.tableFeeFen ||
              startAdjustmentPreview.billingState !== baselineOrder.billingState)) ||
          (action === "END_EARLY" &&
            (!coachStopPreview ||
              coachStopPreview.sessionId !== baselineOrder.sessionId ||
              coachStopPreview.tableId !== table.tableId ||
              coachStopPreview.tableNumber !== table.tableNumber))
        ) {
          throw new Error(
            action === "VOID"
              ? "当前台单不是没有经营记录的误开台，系统没有发出作废请求；请刷新台单，或按原记录办理退单、下钟、退款或冲正"
              : action === "END_EARLY"
                ? "停表前的球桌或助教服务预览已经变化，系统没有发出请求；请刷新并重新核对全部助教后再确认"
                : action === "ADJUST_START"
                  ? "调时预览与当前台单不一致，或本单已有商品、助教、团购、收款记录；系统没有发出请求，请按原记录处理后重新预览"
                  : "当前台单的计费状态无法安全确认，系统没有发出请求；请刷新台单后重试",
          );
        }
      } catch (error) {
        setSessionBillingFeedbackAction(action);
        setSessionBillingFeedback(businessErrorMessage(error, "当前不能安全修改计费"));
        return;
      }
      const command =
        action === "VOID"
          ? {
              action,
              sessionId: baselineOrder.sessionId,
              tableId: table.tableId,
              tableNumber: table.tableNumber,
              baselineOrder,
              baselineTable: table,
              reason: reason.trim(),
              idempotencyKey: crypto.randomUUID(),
            }
          : action === "END_EARLY"
            ? {
                action,
                sessionId: baselineOrder.sessionId,
                tableId: table.tableId,
                tableNumber: table.tableNumber,
                baselineOrder,
                reason: reason.trim(),
                idempotencyKey: crypto.randomUUID(),
                coachStopPreview,
                baselineTable: table,
              }
            : action === "ADJUST_START"
              ? {
                  action,
                  sessionId: baselineOrder.sessionId,
                  tableId: table.tableId,
                  tableNumber: table.tableNumber,
                  baselineOrder,
                  reason: reason.trim(),
                  idempotencyKey: crypto.randomUUID(),
                  preview: startAdjustmentPreview as SessionStartAdjustmentPreview,
                }
              : {
                  action,
                  sessionId: baselineOrder.sessionId,
                  tableId: table.tableId,
                  tableNumber: table.tableNumber,
                  baselineOrder,
                  reason: reason.trim(),
                  idempotencyKey: crypto.randomUUID(),
                };
      const claimed = claimSessionBillingRecovery(sessionBillingRecoveryIdentityKey, command);
      setSessionBillingRecovery(claimed);
      if (claimed.status !== "OWNED") {
        setSessionBillingFeedbackAction(action);
        setSessionBillingFeedback(
          claimed.status === "OTHER_OPERATOR"
            ? "另一位员工有一笔计费或作废结果尚未确认，系统没有发出新的请求。"
            : "本机无法安全保存计费或作废保护记录，系统没有发出请求。",
        );
        return;
      }
      if (claimed.recovery.command.sessionId !== baselineOrder.sessionId) {
        setSessionBillingFeedback(
          `${claimed.recovery.command.tableNumber} 号台仍有一笔计费操作结果待确认，请先处理原请求。`,
        );
        return;
      }
      dispatchSessionBillingRecovery(claimed.recovery);
    },
    [
      connectivity,
      dispatchSessionBillingRecovery,
      effectiveRoles,
      operator,
      sessionBillingRecoveryIdentityKey,
    ],
  );

  const sessionTransferMutation = useMutation({
    mutationFn: async ({
      recovery,
      identityKey,
    }: {
      recovery: SessionTransferRecovery;
      identityKey: string;
    }) => {
      requireTrustedStoreAuthority(connectivity, "转台");
      try {
        const { command } = recovery;
        const result = await transferSession(
          command.sessionId,
          command.targetTable.tableId,
          command.idempotencyKey,
          command.reason,
          {
            sourceTable: command.sourceTable,
            targetTable: command.targetTable,
            baselineOrder: command.baselineOrder,
          },
        );
        if (currentSessionTransferIdentityKey.current !== identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的转台结果不会显示在当前账号，请切回原账号继续核对",
          );
        }
        if (!clearSessionTransferRecovery(recovery, identityKey)) {
          throw new ApiRequestError(
            "SESSION_TRANSFER_RECOVERY_UNAVAILABLE",
            "转台结果已经返回，但本机未能安全清除保护记录；页面继续保留待确认状态",
          );
        }
        return { recovery, result, identityKey };
      } catch (error) {
        if (sessionTransferOutcomeIsUnknown(error)) {
          markSessionTransferOutcomeUnknown(recovery, identityKey);
        } else {
          clearSessionTransferRecovery(recovery, identityKey);
        }
        throw error;
      } finally {
        finishSessionTransferDispatch(recovery, identityKey);
      }
    },
    onMutate: () => setSessionTransferFeedback(null),
    onSuccess: async ({ recovery, result, identityKey }) => {
      if (currentSessionTransferIdentityKey.current !== identityKey) return;
      setSessionTransferRecovery(readSessionTransferRecovery(identityKey));
      openedSessionTransferRecoveryRequest.current = null;
      queryClient.setQueryData<OrderDetail>(
        ["session-order", recovery.command.sessionId],
        result.order,
      );
      queryClient.setQueryData<TableSnapshot[]>(["tables"], (existing) =>
        existing?.map((table) => {
          if (table.tableId === result.sourceTable.tableId) return result.sourceTable;
          if (table.tableId === result.targetTable.tableId) return result.targetTable;
          return table;
        }),
      );
      setSelected(result.targetTable);
      setNotice(
        `已从 ${recovery.command.sourceTable.tableNumber} 号台转到 ${recovery.command.targetTable.tableNumber} 号台；原台单、商品和助教服务保持不变，请核对目标球台台费。`,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
        queryClient.invalidateQueries({
          queryKey: ["session-order", recovery.command.sessionId],
        }),
        queryClient.invalidateQueries({ queryKey: ["tables"] }),
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        queryClient.invalidateQueries({ queryKey: ["connectivity"] }),
        queryClient.invalidateQueries({ queryKey: ["device-status"] }),
      ]);
    },
    onError: (error, input) => {
      if (currentSessionTransferIdentityKey.current !== input.identityKey) return;
      const view = readSessionTransferRecovery(input.identityKey);
      setSessionTransferRecovery(view);
      const reason = businessErrorMessage(error, "系统没有返回明确结果");
      setSessionTransferFeedback(
        sessionTransferOutcomeIsUnknown(error)
          ? view.status === "OWNED"
            ? `${input.recovery.command.sourceTable.tableNumber} 号台转到 ${input.recovery.command.targetTable.tableNumber} 号台的结果尚未确认：${reason}。系统保留了原球台、原台单、原原因和原请求，安全重试不会重复转台。`
            : `转台结果尚未确认：${reason}。本机暂时无法读取保护记录，请勿继续修改该台单。`
          : `这次转台没有执行：${reason}。球台、台单和金额没有因本次请求改变。`,
      );
    },
  });

  const dispatchSessionTransferRecovery = useCallback(
    (recovery: SessionTransferRecovery): void => {
      try {
        requireTrustedStoreAuthority(connectivity, "转台");
      } catch (error) {
        setSessionTransferFeedback(businessErrorMessage(error, "当前不能安全转台"));
        return;
      }
      if (!beginSessionTransferDispatch(recovery, sessionTransferRecoveryIdentityKey)) {
        const view = readSessionTransferRecovery(sessionTransferRecoveryIdentityKey);
        setSessionTransferRecovery(view);
        setSessionTransferFeedback(
          view.status === "RECOVERY_STORAGE_UNAVAILABLE"
            ? "无法安全读取转台保护记录，系统没有发出请求。"
            : "原转台请求正在确认中，请勿重复点击。",
        );
        return;
      }
      sessionTransferMutation.mutate({
        recovery,
        identityKey: sessionTransferRecoveryIdentityKey,
      });
    },
    [connectivity, sessionTransferMutation, sessionTransferRecoveryIdentityKey],
  );

  const requestSessionTransfer = useCallback(
    (
      sourceTable: TableSnapshot,
      targetTable: TableSnapshot,
      baselineOrder: OrderDetail,
      reason: string,
    ): void => {
      try {
        requireTrustedStoreAuthority(connectivity, "转台");
        if (!canOperateStandardSessionForRoles(effectiveRoles)) {
          throw new Error("当前岗位只能查看台局，不能转台");
        }
        if (
          sourceTable.tenantId !== operator.tenantId ||
          sourceTable.storeId !== operator.storeId ||
          sourceTable.state !== "IN_USE" ||
          !sourceTable.sessionId ||
          sourceTable.sessionId !== baselineOrder.sessionId ||
          sourceTable.tableId !== baselineOrder.tableId ||
          sourceTable.tableNumber !== baselineOrder.tableNumber ||
          sourceTable.sessionStartedAt !== baselineOrder.startedAt ||
          sourceTable.billingState !== baselineOrder.billingState ||
          sourceTable.billingPausedAt !== baselineOrder.billingPausedAt ||
          sourceTable.pricingPlanName !== baselineOrder.priceSnapshot.name ||
          sourceTable.billingEvidence !== baselineOrder.priceSnapshot.evidence ||
          sourceTable.estimatedFeeFen !== baselineOrder.tableFeeFen ||
          sourceTable.currentTotalFen !== baselineOrder.totalFen ||
          baselineOrder.status !== "OPEN"
        ) {
          throw new Error("当前台单与原球台状态无法安全确认，系统没有发出请求；请刷新后重试");
        }
        if (
          targetTable.tenantId !== operator.tenantId ||
          targetTable.storeId !== operator.storeId ||
          targetTable.tableId === sourceTable.tableId ||
          targetTable.state !== "IDLE" ||
          targetTable.sessionId !== null ||
          targetTable.sessionStartedAt !== null ||
          targetTable.billingState !== "ENDED" ||
          targetTable.billingPausedAt !== null ||
          targetTable.pricingPlanName !== null ||
          targetTable.estimatedFeeFen !== 0 ||
          targetTable.currentTotalFen !== 0 ||
          targetTable.sessionElapsedSeconds !== 0
        ) {
          throw new Error("目标球台已不是空闲状态，系统没有发出请求；请重新选择空闲球台");
        }
        const normalizedReason = reason.trim();
        if (normalizedReason.length < 2 || [...normalizedReason].length > 200) {
          throw new Error("请填写 2 至 200 个字的转台原因");
        }
      } catch (error) {
        setSessionTransferFeedback(businessErrorMessage(error, "当前不能安全转台"));
        return;
      }
      const claimed = claimSessionTransferRecovery(sessionTransferRecoveryIdentityKey, {
        kind: "TRANSFER_SESSION",
        sessionId: baselineOrder.sessionId,
        sourceTable,
        targetTable,
        baselineOrder,
        reason: reason.trim(),
        idempotencyKey: crypto.randomUUID(),
      });
      setSessionTransferRecovery(claimed);
      if (claimed.status !== "OWNED") {
        setSessionTransferFeedback(
          claimed.status === "OTHER_OPERATOR"
            ? "另一位员工有一笔转台结果尚未确认，系统没有发出新的转台请求。"
            : "本机无法安全保存转台保护记录，系统没有发出请求。",
        );
        return;
      }
      if (claimed.recovery.command.sessionId !== baselineOrder.sessionId) {
        setSessionTransferFeedback(
          `${claimed.recovery.command.sourceTable.tableNumber} 号台仍有一笔转台结果待确认，请先处理原请求。`,
        );
        return;
      }
      dispatchSessionTransferRecovery(claimed.recovery);
    },
    [
      connectivity,
      dispatchSessionTransferRecovery,
      effectiveRoles,
      operator,
      sessionTransferRecoveryIdentityKey,
    ],
  );

  const tableCloseoutMutation = useMutation({
    mutationFn: async ({
      recovery,
      identityKey,
    }: {
      recovery: TableCloseoutRecovery;
      identityKey: string;
    }) => {
      try {
        requireTrustedStoreAuthority(connectivity, "球台收尾");
        const { command } = recovery;
        const requestBody = {
          idempotencyKey: command.idempotencyKey,
          expectedTableRevision: command.expectedTableRevision,
          expectedSessionId: command.expectedSessionId,
          expectedAggregateVersion: command.expectedAggregateVersion,
        };
        const receipt =
          command.action === "CONFIRM_CLEANING"
            ? await completeTableCleaning(command.tableId, requestBody)
            : await releaseTable(command.tableId, requestBody);
        if (currentCloseoutIdentityKey.current !== identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的球台收尾结果不会显示在当前账号，请切回原账号继续核对",
          );
        }
        if (!clearTableCloseoutRecovery(recovery, identityKey)) {
          throw new ApiRequestError(
            "TABLE_CLOSEOUT_RECOVERY_UNAVAILABLE",
            "球台收尾结果已经返回，但本机未能安全清除保护记录；页面继续保留待确认状态",
          );
        }
        return { recovery, receipt, identityKey };
      } catch (error) {
        if (tableCloseoutOutcomeIsUnknown(error)) {
          markTableCloseoutOutcomeUnknown(recovery, identityKey);
        } else {
          clearTableCloseoutRecovery(recovery, identityKey);
        }
        throw error;
      } finally {
        finishTableCloseoutDispatch(recovery, identityKey);
      }
    },
    onMutate: () => setCloseoutFeedback(null),
    onSuccess: async ({ recovery, identityKey }) => {
      if (currentCloseoutIdentityKey.current !== identityKey) return;
      setCloseoutRecovery(readTableCloseoutRecovery(identityKey));
      openedCloseoutRecoveryRequest.current = null;
      setNotice(
        recovery.command.action === "CONFIRM_CLEANING"
          ? `${recovery.command.tableNumber} 号台已记录清洁完成，正在刷新；刷新后可单独释放球台。`
          : `${recovery.command.tableNumber} 号台的释放操作已确认，正在刷新当前经营状态。`,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
        queryClient.invalidateQueries({ queryKey: ["tables"] }),
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        queryClient.invalidateQueries({ queryKey: ["connectivity"] }),
      ]);
      if (recovery.command.action === "RELEASE_TABLE") setSelected(null);
    },
    onError: (error, input) => {
      if (currentCloseoutIdentityKey.current !== input.identityKey) return;
      const view = readTableCloseoutRecovery(input.identityKey);
      setCloseoutRecovery(view);
      const reason = businessErrorMessage(error, "系统没有返回明确结果");
      const baselineChanged =
        error instanceof ApiRequestError &&
        [
          "FRONTDESK_OPERATING_FACT_CONFLICT",
          "ORDER_AGGREGATE_VERSION_CONFLICT",
          "TABLE_CLEANING_ALREADY_COMPLETED",
          "TABLE_CLEANING_REQUIRED",
          "TABLE_NOT_AWAITING_CLEANING",
          "TABLE_SESSION_CHANGED",
          "TABLE_VERSION_CONFLICT",
        ].includes(error.code);
      const actionLabel =
        input.recovery.command.action === "CONFIRM_CLEANING" ? "确认清洁完成" : "释放球台";
      setCloseoutFeedback(
        baselineChanged
          ? `球台、原台局或订单已经变化，原${actionLabel}请求已确定无效并已清除。请刷新后重新核对。`
          : tableCloseoutOutcomeIsUnknown(error)
            ? view.status === "OWNED"
              ? `${input.recovery.command.tableNumber} 号台的${actionLabel}结果尚未确认：${reason}。系统保留了原请求，安全重试不会重复执行。`
              : `${actionLabel}结果尚未确认：${reason}。本机暂时无法读取保护记录，请勿再次操作这张球台。`
            : `这次${actionLabel}没有执行：${reason}。请刷新当前经营状态后重新操作。`,
      );
      if (baselineChanged) {
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
          queryClient.invalidateQueries({ queryKey: ["tables"] }),
        ]);
      }
    },
  });

  const dispatchTableCloseoutRecovery = useCallback(
    (recovery: TableCloseoutRecovery): void => {
      try {
        requireTrustedStoreAuthority(connectivity, "球台收尾");
      } catch (error) {
        setCloseoutFeedback(businessErrorMessage(error, "当前不能安全处理球台收尾"));
        return;
      }
      if (!beginTableCloseoutDispatch(recovery, closeoutRecoveryIdentityKey)) {
        const view = readTableCloseoutRecovery(closeoutRecoveryIdentityKey);
        setCloseoutRecovery(view);
        setCloseoutFeedback(
          view.status === "RECOVERY_STORAGE_UNAVAILABLE"
            ? "无法安全读取球台收尾保护记录，系统没有发出请求。"
            : "原球台收尾请求正在确认中，请勿重复点击。",
        );
        return;
      }
      tableCloseoutMutation.mutate({ recovery, identityKey: closeoutRecoveryIdentityKey });
    },
    [closeoutRecoveryIdentityKey, connectivity, tableCloseoutMutation],
  );

  const requestTableCloseout = useCallback(
    (table: TableSnapshot, action: TableCloseoutActionKind): void => {
      const fact = visibleFacts.find((candidate) => candidate.tableId === table.tableId);
      try {
        if (combinedCheckoutRecoveryBlocksTable(table)) {
          throw new Error(
            "这张球台属于结果尚未确认的合并收款，系统没有发出清台或释放请求；请先等待原收款核对完成",
          );
        }
        requireTrustedStoreAuthority(
          connectivity,
          action === "CONFIRM_CLEANING" ? "确认清洁完成" : "释放球台",
        );
        if (
          table.tenantId !== operator.tenantId ||
          table.storeId !== operator.storeId ||
          table.state !== "AWAITING_CLEANING" ||
          table.sessionId !== null ||
          table.sessionStartedAt !== null ||
          table.billingState !== "ENDED" ||
          !fact ||
          fact.blockers.length > 0 ||
          fact.table.state !== "AWAITING_CLEANING" ||
          fact.session === null ||
          fact.session.billingState !== "ENDED" ||
          (action === "CONFIRM_CLEANING" && fact.cleaningState !== "AWAITING_CLEANING") ||
          (action === "RELEASE_TABLE" && fact.cleaningState !== "CLEANING_COMPLETED") ||
          !frontdeskOperatingFactMatchesTable(fact, table)
        ) {
          throw new Error(
            "当前经营状态里的球台、原台局或清洁步骤无法安全确认，系统没有发出请求；请刷新后重新核对",
          );
        }
      } catch (error) {
        setCloseoutFeedback(businessErrorMessage(error, "当前不能安全处理球台收尾"));
        return;
      }
      const claimed = claimTableCloseoutRecovery(closeoutRecoveryIdentityKey, {
        kind: "TABLE_CLOSEOUT",
        action,
        tableId: table.tableId,
        tableNumber: table.tableNumber,
        expectedTableRevision: fact.table.revision,
        expectedSessionId: fact.session.sessionId,
        expectedAggregateVersion: fact.session.aggregateVersion,
        idempotencyKey: crypto.randomUUID(),
      });
      setCloseoutRecovery(claimed);
      if (claimed.status !== "OWNED") {
        setCloseoutFeedback(
          claimed.status === "OTHER_OPERATOR"
            ? "另一位员工有一笔球台收尾结果尚未确认，系统没有发出新请求。"
            : "本机无法安全保存球台收尾保护记录，系统没有发出请求。",
        );
        return;
      }
      if (claimed.recovery.command.tableId !== table.tableId) {
        setCloseoutFeedback(
          `${claimed.recovery.command.tableNumber} 号台仍有一笔球台收尾结果待确认，请先处理原请求。`,
        );
        return;
      }
      dispatchTableCloseoutRecovery(claimed.recovery);
    },
    [
      closeoutRecoveryIdentityKey,
      connectivity,
      dispatchTableCloseoutRecovery,
      operator,
      visibleFacts,
    ],
  );

  const openTable = useMutation({
    mutationFn: async ({
      recovery,
      identityKey,
    }: {
      recovery: TableSessionOpenRecovery;
      identityKey: string;
    }) => {
      try {
        const snapshot = await openSession(
          recovery.command.tableId,
          recovery.command.idempotencyKey,
          recovery.command.expectedTableRevision,
          { tenantId: operator.tenantId, storeId: operator.storeId },
          recovery.command.pricingPlanId !== null &&
            recovery.command.expectedPricingPlanVersion !== null &&
            recovery.command.pricingPlanName !== null
            ? {
                pricingPlanId: recovery.command.pricingPlanId,
                expectedPricingPlanVersion: recovery.command.expectedPricingPlanVersion,
                pricingPlanName: recovery.command.pricingPlanName,
              }
            : undefined,
          recovery.command.customerRequestEventId ?? undefined,
        );
        if (currentOpenIdentityKey.current !== identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的开台结果不会显示在当前账号，请切回原账号继续核对",
          );
        }
        if (!clearTableSessionOpenRecovery(recovery, identityKey)) {
          throw new ApiRequestError(
            "TABLE_SESSION_OPEN_RECOVERY_UNAVAILABLE",
            "开台结果已经返回，但本机未能安全清除保护记录；页面继续保持锁定",
          );
        }
        return { snapshot, recovery, identityKey };
      } catch (error) {
        if (tableSessionOpenOutcomeIsUnknown(error)) {
          markTableSessionOpenOutcomeUnknown(recovery, identityKey);
        } else {
          clearTableSessionOpenRecovery(recovery, identityKey);
        }
        throw error;
      } finally {
        finishTableSessionOpenDispatch(recovery, identityKey);
      }
    },
    onMutate: () => setOpenFeedback(null),
    onSuccess: async ({ snapshot, recovery, identityKey }) => {
      if (currentOpenIdentityKey.current !== identityKey) return;
      setOpenRecovery(readTableSessionOpenRecovery(identityKey));
      openedRecoveryRequest.current = null;
      const newerStateAlreadyVisible = frontdeskCacheHasNewerTableRevision(
        queryClient,
        operator,
        snapshot.tableId,
        snapshot.revision,
      );
      if (!newerStateAlreadyVisible) {
        queryClient.setQueryData<TableSnapshot[]>(["tables"], (current) =>
          replaceTableSnapshot(current, snapshot),
        );
      }
      setNotice(
        recovery.command.customerRequestEventId !== null
          ? `${snapshot.tableNumber} 号台已开台，价格与开始时间已经保存；原顾客开台申请也已完成。`
          : newerStateAlreadyVisible
            ? "原开台操作已确认执行，正在刷新当前经营状态。"
            : `${snapshot.tableNumber} 号台已开台，价格与开始时间已经保存。`,
      );
      setSelected(null);
      await queryClient.invalidateQueries({ queryKey: ["tables"] });
      await queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({ queryKey: ["connectivity"] });
      await queryClient.invalidateQueries({ queryKey: ["device-status"] });
      if (recovery.command.customerRequestEventId !== null) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: SERVICE_REQUEST_ACTIVE_QUERY_KEY }),
          queryClient.invalidateQueries({ queryKey: ["service-work-items"] }),
          queryClient.invalidateQueries({ queryKey: ["service-duty"] }),
        ]);
      }
    },
    onError: (error, input) => {
      if (currentOpenIdentityKey.current !== input.identityKey) return;
      const view = readTableSessionOpenRecovery(input.identityKey);
      setOpenRecovery(view);
      const reason = businessErrorMessage(error, "系统没有返回明确结果");
      const baselineChanged =
        error instanceof ApiRequestError &&
        [
          "FRONTDESK_OPERATING_FACT_CONFLICT",
          "TABLE_VERSION_CONFLICT",
          "OPEN_TABLE_REQUEST_TABLE_VERSION_CHANGED",
        ].includes(error.code);
      const pricingChanged =
        error instanceof ApiRequestError &&
        [
          "PRICING_PLAN_NOT_FOUND",
          "PRICING_PLAN_UNAVAILABLE",
          "PRICING_PLAN_VERSION_CONFLICT",
        ].includes(error.code);
      const customerRequestChanged =
        error instanceof ApiRequestError &&
        [
          "OPEN_TABLE_REQUEST_NOT_FOUND",
          "OPEN_TABLE_REQUEST_TYPE_MISMATCH",
          "OPEN_TABLE_REQUEST_TABLE_MISMATCH",
          "OPEN_TABLE_REQUEST_NOT_ACKNOWLEDGED",
          "OPEN_TABLE_REQUEST_ASSIGNEE_MISMATCH",
          "OPEN_TABLE_REQUEST_SESSION_INVALID",
          "OPEN_TABLE_REQUEST_REQUIRED",
          "OPEN_TABLE_PRICING_REQUIRED",
        ].includes(error.code);
      setOpenFeedback(
        baselineChanged
          ? "球台状态已经变化，原开台请求已确定失效并已清除。请刷新球台后重新核对。"
          : pricingChanged
            ? "价格方案已经变化，本次没有开台。系统正在重新读取当前可用方案，请按新价格重新确认。"
            : customerRequestChanged
              ? `顾客开台申请已经变化，本次没有开台：${reason}。请返回员工工作中心刷新后重新核对。`
              : view.status === "NONE"
                ? `这次开台没有执行：${reason}。确认球台和权限后可以重新开台。`
                : `这次开台结果尚未确认：${reason}。系统已保留原请求，安全重试不会重复开台。`,
      );
      if (baselineChanged) {
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
          queryClient.invalidateQueries({ queryKey: ["tables"] }),
        ]);
      }
      if (pricingChanged) {
        void queryClient.invalidateQueries({
          queryKey: [
            "frontdesk-pricing-options",
            operator.tenantId,
            operator.storeId,
            input.recovery.command.tableId,
          ],
        });
      }
      if (customerRequestChanged) {
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: SERVICE_REQUEST_ACTIVE_QUERY_KEY }),
          queryClient.invalidateQueries({ queryKey: ["service-work-items"] }),
        ]);
      }
    },
  });

  const dispatchOpenRecovery = useCallback(
    (recovery: TableSessionOpenRecovery): void => {
      try {
        if (
          recovery.command.customerRequestEventId !== null &&
          !canOperateStandardSessionForRoles(effectiveRoles)
        ) {
          throw new Error("当前账号已不具备开台权限，请切回原店长或收银员账号核对原顾客开台申请");
        }
        requireTrustedStoreAuthority(connectivity, "开台");
      } catch (error) {
        setOpenFeedback(businessErrorMessage(error, "当前不能安全开台"));
        return;
      }
      if (!beginTableSessionOpenDispatch(recovery, openIdentityKey)) {
        const view = readTableSessionOpenRecovery(openIdentityKey);
        setOpenRecovery(view);
        if (view.status === "RECOVERY_STORAGE_UNAVAILABLE") {
          setOpenFeedback("无法安全读取开台保护记录，系统没有发出开台请求。");
        }
        return;
      }
      openTable.mutate({ recovery, identityKey: openIdentityKey });
    },
    [connectivity, effectiveRoles, openIdentityKey, openTable],
  );

  const requestTableOpen = useCallback(
    (
      table: TableSnapshot,
      pricingSelection: FrontdeskPricingSelection,
      customerRequestEventId: string | null,
    ): void => {
      const fact = visibleFacts.find((candidate) => candidate.tableId === table.tableId);
      try {
        if (customerReservationRecoveryBlocksTable(table.tableId)) {
          throw new Error(
            "这张球台正在核对预约入座结果，系统没有发出普通开台请求；请先等待原预约操作确认",
          );
        }
        requireTrustedStoreAuthority(connectivity, "开台");
        if (openTableCustomerRequest.status === "INVALID") {
          throw new Error("顾客开台申请信息不完整或已被改动，请返回员工工作中心重新进入");
        }
        if (openTableCustomerRequest.status === "VALID") {
          if (
            !linkedOpenBindingReady ||
            customerRequestEventId !== openTableCustomerRequest.customerRequestEventId ||
            table.tableNodeId !== openTableCustomerRequest.tableNodeId ||
            table.tableId !== linkedOpenTable?.tableId
          ) {
            throw new Error(
              "原顾客申请、员工接单状态或对应球桌已经变化，系统没有发出开台请求；请返回员工工作中心重新核对",
            );
          }
        } else if (customerRequestEventId !== null) {
          throw new Error("当前不是顾客申请开台流程，系统没有发送关联请求");
        }
        if (
          !fact ||
          fact.blockers.length > 0 ||
          fact.table.state !== "IDLE" ||
          fact.session !== null ||
          fact.order !== null ||
          !frontdeskOperatingFactMatchesTable(fact, table)
        ) {
          throw new Error(
            "当前经营状态里的球台版本无法安全确认，系统没有发出开台请求；请刷新球台后重新核对",
          );
        }
        if (
          pricingSelection.pricingPlanId.trim().length === 0 ||
          pricingSelection.pricingPlanName.trim().length === 0 ||
          !Number.isSafeInteger(pricingSelection.expectedPricingPlanVersion) ||
          pricingSelection.expectedPricingPlanVersion < 1
        ) {
          throw new Error(
            "当前选择的价格方案无法安全确认，系统没有发出开台请求；请重新读取价格方案后再开台",
          );
        }
      } catch (error) {
        setOpenFeedback(businessErrorMessage(error, "当前不能安全开台"));
        return;
      }
      const claimed = claimTableSessionOpenRecovery(openIdentityKey, {
        kind: "OPEN_TABLE",
        tableId: table.tableId,
        tableNumber: table.tableNumber,
        expectedTableRevision: fact.table.revision,
        idempotencyKey: crypto.randomUUID(),
        pricingPlanId: pricingSelection.pricingPlanId,
        expectedPricingPlanVersion: pricingSelection.expectedPricingPlanVersion,
        pricingPlanName: pricingSelection.pricingPlanName,
        customerRequestEventId,
      });
      setOpenRecovery(claimed);
      if (claimed.status !== "OWNED") {
        setOpenFeedback(
          claimed.status === "OTHER_OPERATOR"
            ? "另一位员工有一笔开台结果尚未确认，系统没有发出新的开台请求。"
            : "本机无法安全保存开台保护记录，系统没有发出开台请求。",
        );
        return;
      }
      if (claimed.recovery.command.tableId !== table.tableId) {
        setOpenFeedback(
          `${claimed.recovery.command.tableNumber} 号台仍有一笔开台结果待确认，请先处理原请求。`,
        );
        return;
      }
      dispatchOpenRecovery(claimed.recovery);
    },
    [
      connectivity,
      dispatchOpenRecovery,
      linkedOpenBindingReady,
      linkedOpenTable?.tableId,
      openIdentityKey,
      openTableCustomerRequest,
      visibleFacts,
    ],
  );

  useEffect(() => {
    if (!canReadDeviceIncidents) return;
    return startDeviceIncidentTrustClock(setDeviceTrustNow);
  }, [canReadDeviceIncidents]);

  useEffect(() => {
    const view = readTableSessionOpenRecovery(openIdentityKey);
    setOpenRecovery(view);
    setOpenFeedback(null);
    openedRecoveryRequest.current = null;
  }, [openIdentityKey]);

  useEffect(() => {
    setItemAddRecovery(readOrderItemAddRecoveries(itemAddIdentityKey));
    openedOrderItemRecoverySession.current = null;
  }, [itemAddIdentityKey]);

  useEffect(() => {
    setCheckoutRecovery(readCheckoutRecovery(checkoutRecoveryIdentityKey));
    setCheckoutFeedback(null);
  }, [checkoutRecoveryIdentityKey]);

  useEffect(() => {
    setOfficialPaymentRecovery(readOfficialPaymentRecovery(officialPaymentRecoveryIdentityKey));
    setOfficialPaymentFeedback(null);
  }, [officialPaymentRecoveryIdentityKey]);

  useEffect(() => {
    setCloseoutRecovery(readTableCloseoutRecovery(closeoutRecoveryIdentityKey));
    setCloseoutFeedback(null);
    openedCloseoutRecoveryRequest.current = null;
  }, [closeoutRecoveryIdentityKey]);

  useEffect(() => {
    preservedSelectionDuringBillingRefresh.current = null;
    setSessionBillingRecovery(readSessionBillingRecovery(sessionBillingRecoveryIdentityKey));
    setSessionBillingFeedback(null);
    setSessionBillingFeedbackAction(null);
    openedSessionBillingRecoveryRequest.current = null;
  }, [sessionBillingRecoveryIdentityKey]);

  useEffect(() => {
    setSessionTransferRecovery(readSessionTransferRecovery(sessionTransferRecoveryIdentityKey));
    setSessionTransferFeedback(null);
    openedSessionTransferRecoveryRequest.current = null;
  }, [sessionTransferRecoveryIdentityKey]);

  useEffect(() => {
    if (openRecovery.status !== "OWNED" || tables.length === 0) return;
    const requestId = openRecovery.recovery.command.idempotencyKey;
    if (openedRecoveryRequest.current === requestId) return;
    const target = tables.find(
      (candidate) => candidate.tableId === openRecovery.recovery.command.tableId,
    );
    if (!target) {
      setOpenFeedback(
        `${openRecovery.recovery.command.tableNumber} 号台的原开台请求仍待确认，但当前球台列表没有返回它。请保持本页并稍后刷新。`,
      );
      return;
    }
    openedRecoveryRequest.current = requestId;
    setSelected(target);
  }, [openRecovery, tables]);

  useEffect(() => {
    if (itemAddRecovery.status !== "OWNED" || tables.length === 0) return;
    const sessionId = itemAddRecovery.recoveries[0]?.command.sessionId;
    if (!sessionId || openedOrderItemRecoverySession.current === sessionId) return;
    const target = tables.find(
      (candidate) =>
        candidate.tableId === itemAddRecovery.recoveries[0]?.command.tableId &&
        candidate.sessionId === sessionId,
    );
    if (!target) return;
    openedOrderItemRecoverySession.current = sessionId;
    setSelected(target);
  }, [itemAddRecovery, tables]);

  useEffect(() => {
    if (closeoutRecovery.status !== "OWNED" || tables.length === 0) return;
    const requestId = closeoutRecovery.recovery.command.idempotencyKey;
    if (openedCloseoutRecoveryRequest.current === requestId) return;
    const target = tables.find(
      (candidate) => candidate.tableId === closeoutRecovery.recovery.command.tableId,
    );
    if (!target) {
      setCloseoutFeedback(
        `${closeoutRecovery.recovery.command.tableNumber} 号台的原球台收尾请求仍待确认，但当前球台列表没有返回它。请保持本页并稍后刷新。`,
      );
      return;
    }
    openedCloseoutRecoveryRequest.current = requestId;
    setSelected(target);
  }, [closeoutRecovery, tables]);

  useEffect(() => {
    if (sessionBillingRecovery.status !== "OWNED" || tables.length === 0) return;
    const requestId = sessionBillingRecovery.recovery.command.idempotencyKey;
    if (openedSessionBillingRecoveryRequest.current === requestId) return;
    const isVoid = sessionBillingRecovery.recovery.command.action === "VOID";
    const target = tables.find(
      (candidate) =>
        candidate.tableId === sessionBillingRecovery.recovery.command.tableId &&
        (isVoid || candidate.sessionId === sessionBillingRecovery.recovery.command.sessionId),
    );
    if (!target) {
      setSessionBillingFeedback(
        `${sessionBillingRecovery.recovery.command.tableNumber} 号台的原${isVoid ? "作废" : "计费"}请求仍待确认，但当前球台列表没有返回原球台。请保持本页并稍后刷新。`,
      );
      return;
    }
    openedSessionBillingRecoveryRequest.current = requestId;
    if (!isVoid || target.sessionId === sessionBillingRecovery.recovery.command.sessionId) {
      setSelected(target);
    }
  }, [sessionBillingRecovery, tables]);

  useEffect(() => {
    if (sessionTransferRecovery.status !== "OWNED" || tables.length === 0) return;
    const requestId = sessionTransferRecovery.recovery.command.idempotencyKey;
    if (openedSessionTransferRecoveryRequest.current === requestId) return;
    const target = tables.find(
      (candidate) => candidate.sessionId === sessionTransferRecovery.recovery.command.sessionId,
    );
    if (!target) {
      setSessionTransferFeedback(
        `${sessionTransferRecovery.recovery.command.sourceTable.tableNumber} 号台的原转台请求仍待确认，但当前球台列表没有返回这局球。请保持本页并稍后刷新。`,
      );
      return;
    }
    openedSessionTransferRecoveryRequest.current = requestId;
    setSelected(target);
  }, [sessionTransferRecovery, tables]);

  useEffect(() => {
    if (openTableCustomerRequest.status === "NONE") return;
    if (
      openTableCustomerRequest.status !== "VALID" ||
      !linkedOpenBindingReady ||
      !linkedOpenTable
    ) {
      if (openRecovery.status !== "OWNED") setSelected(null);
      return;
    }
    const targetKey = `${openTableCustomerRequest.customerRequestEventId}:${linkedOpenTable.tableId}`;
    if (openedTaskTarget.current === targetKey) return;
    openedTaskTarget.current = targetKey;
    setSelected(linkedOpenTable);
    setNotice(
      `已核对 ${linkedOpenTable.tableNumber} 号台的顾客开台申请；请选择价格方案，确认后才会正式开台。`,
    );
  }, [linkedOpenBindingReady, linkedOpenTable, openRecovery.status, openTableCustomerRequest]);

  useEffect(() => {
    if (openTableCustomerRequest.status !== "NONE") return;
    const tableNodeId = new URLSearchParams(location.search).get("tableNodeId");
    if (!tableNodeId || openedTaskTarget.current === tableNodeId || tables.length === 0) return;
    openedTaskTarget.current = tableNodeId;
    const table = tables.find((candidate) => candidate.tableNodeId === tableNodeId);
    if (!table) {
      setNotice("未找到待办对应的球台，请刷新后再试");
      return;
    }
    setSelected(table);
    setNotice(`已打开 ${table.tableNumber} 号台，可核对当前状态并继续处理`);
  }, [location.search, openTableCustomerRequest.status, tables]);

  return (
    <>
      <PageHeader
        eyebrow="北岸桌球俱乐部 · A 区"
        title="前台营业台"
        action={
          <>
            <span className="demo-pill">演示环境</span>
            <button
              type="button"
              className="icon-button"
              title="刷新球台"
              onClick={() =>
                void Promise.all([operatingFactsQuery.refetch(), tablesQuery.refetch()])
              }
            >
              <RefreshCw size={18} aria-hidden="true" />
            </button>
          </>
        }
      />
      <section className="summary-strip" aria-label="营业概览">
        <div>
          <span>球台总数</span>
          <strong>{frontDeskTableCountLabel(operatingFactsView, visibleFacts.length)}</strong>
        </div>
        <div>
          <span>使用中</span>
          <strong>{frontDeskTableCountLabel(operatingFactsView, activeCount)}</strong>
        </div>
        <div>
          <span>待清台</span>
          <strong>{frontDeskTableCountLabel(operatingFactsView, cleaningCount)}</strong>
        </div>
        <div className="warning-summary">
          <AlertTriangle size={17} />
          <span>当前使用演示计费规则，不会作为正式门店账单</span>
        </div>
      </section>
      <DeviceIncidentPanel
        state={deviceTrust}
        resolution={{
          view: deviceIncidentResolutionRecovery,
          busy: deviceIncidentResolutionMutation.isPending,
          feedback: deviceIncidentResolutionFeedback,
          onResolve: submitDeviceIncidentResolution,
          onRetry: (recovery) => deviceIncidentResolutionMutation.mutate(recovery),
        }}
      />
      <CombinedCheckoutRecoveryProbe
        operator={operator}
        recoveryView={combinedCheckoutRecovery}
        onRecoveryViewChange={setCombinedCheckoutRecovery}
        onCompleted={(sessionIds) => {
          void Promise.all([
            ...sessionIds.map((sessionId) =>
              queryClient.invalidateQueries({ queryKey: ["session-order", sessionId] }),
            ),
            queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
            queryClient.invalidateQueries({ queryKey: ["tables"] }),
            queryClient.invalidateQueries({ queryKey: ["orders"] }),
          ]);
        }}
      />
      {canReadReservations ? (
        <CustomerReservationDesk
          operator={operator}
          effectiveRoles={effectiveRoles}
          tables={tables}
          reservationEnabled={reservationSelected}
          customerSelfServiceEnabled={customerSelfServiceReservationSelected}
          onRecoveryViewChange={setCustomerReservationRecovery}
          onSeated={() => {
            void Promise.all([
              queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
              queryClient.invalidateQueries({ queryKey: ["tables"] }),
              queryClient.invalidateQueries({ queryKey: ["orders"] }),
            ]);
          }}
        />
      ) : null}
      {openTableCustomerRequest.status !== "NONE" ? (
        linkedOpenBindingReady && linkedOpenTable ? (
          <output className="info-band">
            已确认这是 {linkedOpenTable.tableNumber} 号台已接单的顾客开台申请。
            请核对并选择本次价格方案；开台成功后，原顾客申请会同时完成。
          </output>
        ) : (
          <div className="error-band" role="alert">
            <AlertTriangle size={19} aria-hidden="true" />
            <span>{linkedOpenBindingMessage}</span>
          </div>
        )
      ) : null}
      {openRecovery.status === "OWNED" ? (
        <section className="error-band" role="alert" aria-live="assertive">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            {openRecovery.recovery.command.tableNumber} 号台有一笔开台结果待确认。
            {openRecovery.recovery.command.customerRequestEventId === null
              ? "请使用原请求安全重试；系统不会重复开台。"
              : "原顾客开台申请和价格方案均已锁定；只能用原请求重试，不能改选球桌或方案。"}
          </span>
          <button
            className="button secondary"
            type="button"
            disabled={
              openTable.isPending ||
              (openRecovery.recovery.command.customerRequestEventId !== null &&
                !canOperateStandardSessionForRoles(effectiveRoles))
            }
            onClick={() => dispatchOpenRecovery(openRecovery.recovery)}
          >
            使用原请求安全重试
          </button>
        </section>
      ) : openRecovery.status === "OTHER_OPERATOR" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            另一位员工有一笔开台结果尚未确认。为避免错接台单，本页经营操作已暂停；请切回原账号核对。
          </span>
        </section>
      ) : openRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            本机无法安全保存开台保护记录，因此不会发出新的开台请求。其他已存在台单仍可查看和办理。
          </span>
        </section>
      ) : null}
      {openFeedback ? (
        <div className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>{openFeedback}</span>
        </div>
      ) : null}
      {itemAddRecovery.status === "OWNED" ? (
        <section className="error-band" role="alert" aria-live="assertive">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            {itemAddRecovery.recoveries[0]?.command.tableNumber} 号台有
            {itemAddRecovery.recoveries.length}{" "}
            笔商品加单结果待确认。系统保留了原商品、原数量和原请求号，不会盲目重复扣库存。
          </span>
          <button
            className="button secondary"
            type="button"
            onClick={() => {
              const target = tables.find(
                (candidate) =>
                  candidate.tableId === itemAddRecovery.recoveries[0]?.command.tableId &&
                  candidate.sessionId === itemAddRecovery.recoveries[0]?.command.sessionId,
              );
              if (target) setSelected(target);
            }}
          >
            打开原台单核对
          </button>
        </section>
      ) : itemAddRecovery.status === "OTHER_OPERATOR" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>另一位员工有商品加单结果待确认。本页暂停新的商品加单，请切回原账号核对。</span>
        </section>
      ) : itemAddRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            {itemAddRecovery.pendingOutcomeMayExist
              ? "本机无法读取一笔可能已提交的商品加单保护记录。新的商品加单、收款和结账已暂停，请保留本页并恢复浏览器存储后核对。"
              : "本机无法安全保存商品加单保护记录，因此不会发出新的商品加单请求；其他已存在台单仍可查看和办理。"}
          </span>
        </section>
      ) : null}
      {checkoutRecovery.status === "OWNED" ? (
        <section className="error-band" role="alert" aria-live="assertive">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            {checkoutRecovery.recovery.command.tableNumber} 号台有一笔
            {formatFee(
              orderCheckoutOutstandingFen(checkoutRecovery.recovery.command.baselineOrder) ?? 0,
            )}{" "}
            的本次待收结果待确认。
            {checkoutRecovery.recovery.command.memberBalanceFen === undefined
              ? "系统保留了原金额、原方式和原请求号，不会盲目重复收款。"
              : `原请求中的会员余额 ${formatFee(checkoutRecovery.recovery.command.memberBalanceFen)}、${checkoutRecovery.recovery.command.method === "CASH" ? "现金" : checkoutRecovery.recovery.command.method === "STATIC_WECHAT" ? "微信收款码" : "支付宝收款码"} ${formatFee((orderCheckoutOutstandingFen(checkoutRecovery.recovery.command.baselineOrder) ?? 0) - checkoutRecovery.recovery.command.memberBalanceFen)} 已全部锁定；只能使用原请求恢复，不能改金额或换方式。`}
          </span>
          <button
            className="button secondary"
            type="button"
            disabled={
              checkoutPayment.isPending ||
              !trustedStoreAuthority ||
              !operatingFactAllowsWrite(checkoutRecovery.recovery.command.tableId)
            }
            onClick={() => dispatchCheckoutRecovery(checkoutRecovery.recovery)}
          >
            {checkoutPayment.isPending ? "正在确认原收款…" : "使用原请求安全重试"}
          </button>
        </section>
      ) : checkoutRecovery.status === "OTHER_OPERATOR" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            另一位员工有一笔收款结果尚未确认。为避免重复收款，本页经营写入已暂停；请切回原账号核对。
          </span>
        </section>
      ) : checkoutRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            {checkoutRecovery.pendingOutcomeMayExist
              ? "本机无法读取一笔可能已提交的收款保护记录。新的收款和台单修改已暂停，请保留本页并恢复浏览器存储后核对。"
              : "本机无法安全保存收款保护记录，因此不会发出新的收款请求；其他已存在台单仍可查看和办理非收款操作。"}
          </span>
        </section>
      ) : null}
      {checkoutFeedback ? (
        <div className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>{checkoutFeedback.message}</span>
        </div>
      ) : null}
      {officialPaymentRecovery.status === "OWNED" ? (
        <section className="error-band" role="alert" aria-live="assertive">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            {officialPaymentRecovery.recovery.command.tableNumber} 号台有一笔
            {formatFee(officialPaymentRecovery.recovery.command.expectedTotalFen)} 的
            {officialPaymentRecovery.recovery.command.rail === "WECHAT_PAY" ? "微信支付" : "支付宝"}
            待处理。
            {officialPaymentRecovery.recovery.result
              ? officialPaymentPresentation(officialPaymentRecovery.recovery.result).nextStep
              : "系统已锁定原订单、原渠道、原金额和原请求号，不能改收或让顾客再次付款。"}
          </span>
          {officialPaymentRecoveryCanRetry(officialPaymentRecovery.recovery) ? (
            <button
              className="button secondary"
              type="button"
              disabled={
                officialPayment.isPending ||
                !trustedStoreAuthority ||
                !operatingFactAllowsWrite(officialPaymentRecovery.recovery.command.tableId)
              }
              onClick={() => dispatchOfficialPaymentRecovery(officialPaymentRecovery.recovery)}
            >
              {officialPayment.isPending ? "正在查询原付款…" : "查询原付款，不重新创建"}
            </button>
          ) : null}
        </section>
      ) : officialPaymentRecovery.status === "OTHER_OPERATOR" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            另一位员工有一笔正式支付结果尚未确认。为避免重复付款，本页经营写入已暂停；请切回原账号核对。
          </span>
        </section>
      ) : officialPaymentRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            {officialPaymentRecovery.pendingOutcomeMayExist
              ? "本机无法读取一笔可能已提交的正式支付保护记录。新的付款和台单修改已暂停，请保留本页并恢复浏览器存储后核对。"
              : "本机无法安全保存正式支付保护记录，因此不会发出新的正式支付请求。"}
          </span>
        </section>
      ) : null}
      {officialPaymentFeedback && selected === null ? (
        <OfficialPaymentFeedbackNotice feedback={officialPaymentFeedback} />
      ) : null}
      {sessionBillingRecovery.status === "OWNED" ? (
        <section className="error-band" role="alert" aria-live="assertive">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            {sessionBillingRecovery.recovery.command.tableNumber} 号台有一笔“
            {sessionBillingRecovery.recovery.command.action === "PAUSE"
              ? "暂停计费"
              : sessionBillingRecovery.recovery.command.action === "RESUME"
                ? "恢复计费"
                : sessionBillingRecovery.recovery.command.action === "END_EARLY"
                  ? "提前停止计费"
                  : "误开台作废"}
            ”结果待确认。系统保留了原台单、原原因和原请求，不会重复产生业务效果。
          </span>
          <button
            className="button secondary"
            type="button"
            disabled={
              sessionBillingMutation.isPending ||
              !trustedStoreAuthority ||
              !operatingFactAllowsWrite(sessionBillingRecovery.recovery.command.tableId)
            }
            onClick={() => dispatchSessionBillingRecovery(sessionBillingRecovery.recovery)}
          >
            {sessionBillingMutation.isPending ? "正在确认原请求…" : "使用原请求安全重试"}
          </button>
        </section>
      ) : sessionBillingRecovery.status === "OTHER_OPERATOR" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            另一位员工有一笔计费或作废结果尚未确认。为避免台费、订单或球台状态错乱，本页台单修改已暂停；请切回原账号核对。
          </span>
        </section>
      ) : sessionBillingRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            {sessionBillingRecovery.pendingOutcomeMayExist
              ? "本机无法读取一笔可能已提交的计费或作废保护记录。新的台单修改已暂停，请保留本页并恢复浏览器存储后核对。"
              : "本机无法安全保存计费或作废保护记录，因此不会发出新的暂停、恢复、提前停表或误开台作废请求；其他已存在台单仍可查看和办理。"}
          </span>
        </section>
      ) : null}
      {sessionBillingFeedback ? (
        <div className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>{sessionBillingFeedback}</span>
        </div>
      ) : null}
      {sessionTransferRecovery.status === "OWNED" ? (
        <section className="error-band" role="alert" aria-live="assertive">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            {sessionTransferRecovery.recovery.command.sourceTable.tableNumber} 号台转到
            {sessionTransferRecovery.recovery.command.targetTable.tableNumber} 号台的结果待确认。
            系统保留了原球台、原台单、原原因和原请求，不会重复转台。
          </span>
          <button
            className="button secondary"
            type="button"
            disabled={
              sessionTransferMutation.isPending ||
              !trustedStoreAuthority ||
              !operatingFactAllowsWrite(
                sessionTransferRecovery.recovery.command.sourceTable.tableId,
              ) ||
              !operatingFactAllowsWrite(
                sessionTransferRecovery.recovery.command.targetTable.tableId,
              )
            }
            onClick={() => dispatchSessionTransferRecovery(sessionTransferRecovery.recovery)}
          >
            {sessionTransferMutation.isPending ? "正在确认原转台请求…" : "使用原请求安全重试"}
          </button>
        </section>
      ) : sessionTransferRecovery.status === "OTHER_OPERATOR" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            另一位员工有一笔转台结果尚未确认。为避免台单出现在两张球台，本页台单修改已暂停；请切回原账号核对。
          </span>
        </section>
      ) : sessionTransferRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            {sessionTransferRecovery.pendingOutcomeMayExist
              ? "本机无法读取一笔可能已提交的转台保护记录。新的台单修改已暂停，请保留本页并恢复浏览器存储后核对。"
              : "本机无法安全保存转台保护记录，因此不会发出新的转台请求；其他已存在台单仍可查看和办理。"}
          </span>
        </section>
      ) : null}
      {sessionTransferFeedback ? (
        <div className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>{sessionTransferFeedback}</span>
        </div>
      ) : null}
      {closeoutRecovery.status === "OWNED" ? (
        <section className="error-band" role="alert" aria-live="assertive">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            {closeoutRecovery.recovery.command.tableNumber} 号台有一笔
            {closeoutRecovery.recovery.command.action === "CONFIRM_CLEANING"
              ? "确认清洁完成"
              : "释放球台"}
            结果待确认。系统保留了原台局、操作时看到的状态和原请求，不会静默重复执行。
          </span>
          <button
            className="button secondary"
            type="button"
            disabled={tableCloseoutMutation.isPending}
            onClick={() => dispatchTableCloseoutRecovery(closeoutRecovery.recovery)}
          >
            {tableCloseoutMutation.isPending ? "正在确认原请求…" : "使用原请求安全重试"}
          </button>
        </section>
      ) : closeoutRecovery.status === "OTHER_OPERATOR" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            另一位员工有一笔球台收尾结果尚未确认。为避免误开新台，本页经营写入已暂停；请切回原账号核对。
          </span>
        </section>
      ) : closeoutRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
        <section className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>
            {closeoutRecovery.pendingOutcomeMayExist
              ? "本机无法读取一笔可能已提交的球台收尾保护记录。新的球台收尾和台单修改已暂停，请保留本页并恢复浏览器存储后核对。"
              : "本机无法安全保存球台收尾保护记录，因此不会发出新请求；其他已存在台单仍可查看和办理。"}
          </span>
        </section>
      ) : null}
      {closeoutFeedback ? (
        <div className="error-band" role="alert">
          <AlertTriangle size={19} aria-hidden="true" />
          <span>{closeoutFeedback}</span>
        </div>
      ) : null}
      <LongSessionAlertPanel
        tables={visibleTables}
        effectiveRoles={effectiveRoles}
        onSelect={setSelected}
      />
      <section className="table-section">
        <div className="section-heading">
          <div>
            <h2>球台状态</h2>
            <p>
              {operatingFactsView === "READY" || operatingFactsView === "EMPTY"
                ? `${visibleFacts.length} 张已绑定 · 以门店主机统一经营状态为准`
                : operatingFactsView === "LOADING"
                  ? "正在读取门店主机统一经营状态"
                  : "当前经营状态无法确认，旧摘要已隐藏"}
            </p>
          </div>
          <div className="legend">
            <span>
              <i className="legend-dot idle" />
              空闲
            </span>
            <span>
              <i className="legend-dot active" />
              使用中
            </span>
            <span>
              <i className="legend-dot issue" />
              待清台
            </span>
          </div>
        </div>
        {notice ? (
          <div className="success-band">
            <Check size={18} />
            <span>{notice}</span>
            <button type="button" title="关闭提示" onClick={() => setNotice(null)}>
              <X size={16} />
            </button>
          </div>
        ) : null}
        {operatingFactsView === "ERROR" ? (
          <div className="error-band" role="alert">
            <AlertTriangle size={19} />
            <span>无法读取门店主机统一经营状态，旧摘要已隐藏，当前经营操作已暂停。</span>
            <button
              className="button secondary compact"
              type="button"
              onClick={() => operatingFactsQuery.refetch()}
            >
              重新读取
            </button>
          </div>
        ) : operatingFactsView === "LOADING" ? (
          <div className="loading-state">正在读取当前经营状态...</div>
        ) : operatingFactsView === "EMPTY" ? (
          <div className="empty-state">当前门店没有已绑定球台</div>
        ) : operatingFactsView === "READY" ? (
          <div className="table-grid">
            {visibleFacts.map((fact) => {
              const table = tables.find((candidate) => candidate.tableId === fact.tableId);
              const compatibleTable = frontdeskOperatingFactMatchesTable(fact, table)
                ? table
                : null;
              return (
                <TableTile
                  key={fact.tableId}
                  table={compatibleTable}
                  operatingFact={fact}
                  onSelect={(candidate) => {
                    if (openTableCustomerRequest.status === "NONE") {
                      setSelected(candidate);
                    } else if (
                      linkedOpenBindingReady &&
                      candidate.tableId === linkedOpenTable?.tableId
                    ) {
                      setSelected(candidate);
                    } else {
                      setNotice("当前只能处理原顾客申请对应的球桌，不能改选其他球桌。");
                    }
                  }}
                />
              );
            })}
          </div>
        ) : null}
      </section>
      <SessionDialog
        key={selected?.sessionId ?? selected?.tableId ?? "closed"}
        table={operatingFactsView === "READY" && selectedFactMatches ? selected : null}
        operator={operator}
        operatingFact={selectedFactMatches ? selectedFact : null}
        tables={writableTables}
        effectiveRoles={effectiveRoles}
        requestedTab={
          selected?.sessionId && coachSessionDeepLinkTarget?.sessionId === selected.sessionId
            ? "COACH"
            : null
        }
        connectivity={connectivity}
        openRecovery={openRecovery}
        itemAddIdentityKey={itemAddIdentityKey}
        itemAddRecovery={itemAddRecovery}
        onItemAddRecoveryChanged={setItemAddRecovery}
        checkoutRecovery={checkoutRecovery}
        checkoutPending={checkoutPayment.isPending}
        checkoutFeedback={checkoutFeedback}
        onCheckoutFeedbackClear={() => setCheckoutFeedback(null)}
        onCheckoutRequested={requestCheckout}
        officialPaymentRecovery={officialPaymentRecovery}
        officialPaymentPending={officialPayment.isPending}
        officialPaymentFeedback={officialPaymentFeedback}
        onOfficialPaymentFeedbackClear={() => setOfficialPaymentFeedback(null)}
        onOfficialPaymentRequested={requestOfficialPayment}
        sessionBillingRecovery={sessionBillingRecovery}
        sessionBillingPending={sessionBillingMutation.isPending}
        sessionBillingFeedback={sessionBillingFeedback}
        sessionBillingFeedbackAction={sessionBillingFeedbackAction}
        onSessionBillingRequested={requestSessionBillingChange}
        sessionTransferRecovery={sessionTransferRecovery}
        sessionTransferPending={sessionTransferMutation.isPending}
        sessionTransferFeedback={sessionTransferFeedback}
        onSessionTransferRequested={requestSessionTransfer}
        combinedCheckoutRecovery={combinedCheckoutRecovery}
        onCombinedCheckoutRecoveryChange={setCombinedCheckoutRecovery}
        reservationTableWriteLocked={
          customerReservationRecovery.status !== "NONE" &&
          Boolean(selected && customerReservationRecoveryBlocksTable(selected.tableId))
        }
        closeoutRecovery={closeoutRecovery}
        closeoutPending={tableCloseoutMutation.isPending}
        closeoutFeedback={closeoutFeedback}
        onCloseoutRequested={requestTableCloseout}
        onCloseoutRetry={dispatchTableCloseoutRecovery}
        openPending={openTable.isPending}
        openFeedback={openFeedback}
        customerRequestEventId={
          linkedOpenBindingReady &&
          openTableCustomerRequest.status === "VALID" &&
          selected?.tableId === linkedOpenTable?.tableId
            ? openTableCustomerRequest.customerRequestEventId
            : null
        }
        customerRequestBindingRequired={openTableCustomerRequest.status !== "NONE"}
        linkedOpenRecoveryAllowed={
          openRecovery.status !== "OWNED" ||
          openRecovery.recovery.command.customerRequestEventId === null ||
          canOperateStandardSessionForRoles(effectiveRoles)
        }
        onOpenRequested={requestTableOpen}
        onOpenRetry={dispatchOpenRecovery}
        onClose={() => {
          preservedSelectionDuringBillingRefresh.current = null;
          setSelected(null);
        }}
      />
    </>
  );
}

export function LongSessionAlertPanel({
  tables,
  effectiveRoles,
  onSelect,
}: {
  tables: TableSnapshot[];
  effectiveRoles: readonly OperatorRole[];
  onSelect?: (table: TableSnapshot) => void;
}): React.JSX.Element | null {
  const riskyTables = tables
    .filter(
      (table) => table.sessionRiskLevel === "WARNING" || table.sessionRiskLevel === "CRITICAL",
    )
    .sort((left, right) => (right.sessionElapsedSeconds ?? 0) - (left.sessionElapsedSeconds ?? 0));
  if (riskyTables.length === 0) return null;
  const canOpenFrontdesk = canAccessRouteForRoles(effectiveRoles, "/frontdesk");
  return (
    <section className="long-session-alert-panel" role="alert" aria-live="polite">
      <div className="long-session-alert-heading">
        <AlertTriangle size={20} />
        <div>
          <strong>发现 {riskyTables.length} 张疑似漏关台 / 超长台局</strong>
          <span>
            系统会持续提醒，不会静默累计金额。请先核对现场，安全处置入口开放前保留原记录。
          </span>
        </div>
      </div>
      <div className="long-session-alert-list">
        {riskyTables.map((table) => (
          <article key={table.tableId} className={table.sessionRiskLevel?.toLowerCase()}>
            <div>
              <strong>
                {table.tableNumber} 号台 ·
                {table.sessionRiskLevel === "CRITICAL" ? "严重超长" : "预警"}
              </strong>
              <span>
                开台{" "}
                {table.sessionStartedAt
                  ? new Date(table.sessionStartedAt).toLocaleString("zh-CN")
                  : "未知"}
                · 已持续 {formatElapsedSeconds(table.sessionElapsedSeconds ?? 0)}
              </span>
              <span>
                当前总计 {formatFee(table.currentTotalFen ?? table.estimatedFeeFen)} · 台费
                {formatFee(table.estimatedFeeFen)}
              </span>
            </div>
            {onSelect ? (
              <button className="button secondary" type="button" onClick={() => onSelect(table)}>
                核对并查看
              </button>
            ) : canOpenFrontdesk ? (
              <NavLink
                className="button secondary"
                to={`/frontdesk?tableNodeId=${table.tableNodeId}`}
              >
                去前台核对
              </NavLink>
            ) : (
              <span className="authorization-note">请核对现场并通知店长或收银员。</span>
            )}
          </article>
        ))}
      </div>
      <p className="authorization-note">
        {canOpenFrontdesk
          ? "当前仅开放核对；完整处置需在统一审批中心固定金额、计费状态、球台和订单后果后执行。"
          : "当前岗位只能查看提醒；请核对现场并通知店长或收银员，安全处置入口开放前保留原记录。"}
      </p>
    </section>
  );
}

function formatElapsedSeconds(seconds: number): string {
  const totalMinutes = Math.max(0, Math.floor(seconds / 60));
  const days = Math.floor(totalMinutes / 1_440);
  const hours = Math.floor((totalMinutes % 1_440) / 60);
  const minutes = totalMinutes % 60;
  return days > 0 ? `${days} 天 ${hours} 小时 ${minutes} 分` : `${hours} 小时 ${minutes} 分`;
}

export function TableTile({
  table,
  operatingFact,
  onSelect,
}: {
  table: TableSnapshot | null;
  operatingFact?: FrontdeskOperatingFactReadModel;
  onSelect: (table: TableSnapshot) => void;
}): React.JSX.Element {
  const duration = useDuration(
    operatingFact?.session?.startedAt ?? table?.sessionStartedAt ?? null,
  );
  const tableState = operatingFact?.table.state ?? table?.state ?? "DISABLED";
  const stateTerm = tableStateTerm(tableState);
  const stateLabel = operatingFact?.statusLabel ?? stateTerm.label;
  return (
    <button
      className={`table-tile state-${tableState.toLowerCase()} risk-${(table?.sessionRiskLevel ?? "NORMAL").toLowerCase()}${operatingFact ? " has-operating-fact" : ""}`}
      type="button"
      disabled={table === null}
      onClick={() => table && onSelect(table)}
    >
      <span className="tile-rail" />
      <span className="tile-head">
        <span>
          <strong>{operatingFact?.tableNumber ?? table?.tableNumber}</strong>
          <small>{operatingFact?.area ?? table?.area}</small>
        </span>
        <span className="state-label">{stateLabel}</span>
      </span>
      <span className="tile-body">
        <span className="metric">
          <Clock3 size={15} />
          <span>{duration}</span>
        </span>
        {table?.sessionRiskLevel === "WARNING" || table?.sessionRiskLevel === "CRITICAL" ? (
          <span className={`long-session-badge ${table.sessionRiskLevel.toLowerCase()}`}>
            {table.sessionRiskLevel === "CRITICAL" ? "严重超长 · 疑似漏关台" : "超长台局预警"}
          </span>
        ) : null}
        {operatingFact ? (
          <FrontdeskOperatingFactSummary fact={operatingFact} compact />
        ) : table ? (
          <>
            <span className="table-fee-breakdown">
              <strong className="fee">
                {formatFee(table.currentTotalFen ?? table.estimatedFeeFen)}
              </strong>
              <small>当前总计 · 其中台费 {formatFee(table.estimatedFeeFen)}</small>
            </span>
            <small>
              {table.pricingPlanName ??
                (table.state === "AWAITING_CLEANING" ? "账单已结，等待清台" : "待选择计费方案")}
            </small>
            <small>
              {stateTerm.explanation} {stateTerm.nextStep}
            </small>
          </>
        ) : null}
        {operatingFact && table === null ? (
          <small className="fact-write-paused">经营明细暂时无法安全对应，操作已暂停</small>
        ) : null}
      </span>
      <span className="tile-foot">
        <span className="evidence-tag">
          {frontdeskOperatingFactCollectionLabel(operatingFact) ??
            (table?.billingEvidence === "SIMULATED" ? "模拟计费" : "门店统一事实")}
        </span>
        {table ? <ChevronRight size={17} /> : <LockKeyhole size={17} />}
      </span>
    </button>
  );
}

function FrontdeskOperatingFactSummary({
  fact,
  compact = false,
}: {
  fact: FrontdeskOperatingFactReadModel;
  compact?: boolean;
}): React.JSX.Element {
  const order = fact.order;
  const orderReference = order
    ? orderDisplayReference({
        id: order.orderId,
        orderNumber: order.orderNumber,
      })
    : null;
  const collectionLabel = frontdeskOperatingFactCollectionLabel(fact);
  return (
    <span className={`frontdesk-fact-summary${compact ? " is-compact" : ""}`}>
      {!compact ? (
        <span className="frontdesk-fact-state">
          <strong>{fact.statusLabel}</strong>
          <span>{fact.statusExplanation}</span>
        </span>
      ) : null}
      {orderReference ? (
        <span className="frontdesk-order-number">
          <strong>{orderReference.label}</strong>
          <span>{orderReference.value}</span>
        </span>
      ) : null}
      <span className="frontdesk-fact-money">
        <span>
          应收 <strong>{formatFee(order?.totalFen ?? 0)}</strong>
        </span>
        <span>
          已收 <strong>{formatFee(order?.paidFen ?? 0)}</strong>
        </span>
        <span>
          已退 <strong>{formatFee(order?.refundedFen ?? 0)}</strong>
        </span>
        <span>
          待收 <strong>{formatFee(order?.outstandingFen ?? 0)}</strong>
        </span>
      </span>
      {collectionLabel ? (
        <span className="frontdesk-channel-pending">{collectionLabel}</span>
      ) : null}
      <span className="frontdesk-light-off-state" data-light-off-status={fact.lightOff.status}>
        <strong>关灯状态</strong>
        <span>{frontdeskLightOffStatusLabel(fact)}</span>
      </span>
      <span className="frontdesk-fact-next">
        <strong>下一步</strong>
        <span>{fact.nextStepLabel}</span>
      </span>
      {fact.blockers.length > 0 ? (
        <span className="frontdesk-fact-blockers" role="alert">
          <strong>当前阻断</strong>
          {fact.blockers.map((blocker) => (
            <span key={blocker.code}>
              {blocker.label}：{blocker.explanation}
            </span>
          ))}
        </span>
      ) : null}
    </span>
  );
}

export function frontdeskLightOffStatusLabel(fact: FrontdeskOperatingFactReadModel): string {
  const device =
    fact.lightOff.evidence === "MOCK"
      ? "模拟设备"
      : fact.lightOff.evidence === "REAL_HARDWARE"
        ? "门店设备回执"
        : "设备回执";
  if (fact.lightOff.status === "NOT_REQUIRED") return "本次无需关灯";
  if (fact.lightOff.status === "PENDING") return `${device}：关灯指令处理中`;
  if (fact.lightOff.status === "CONFIRMED") {
    return fact.lightOff.evidence === "MOCK"
      ? "模拟设备：模拟关灯指令已完成（不代表真实灯具已关）"
      : `${device}：关灯回执已确认（请以现场为准）`;
  }
  if (fact.lightOff.status === "FAILED") return `${device}：关灯失败，请现场核对`;
  return `${device}：关灯结果待核对`;
}

export type TableCloseoutPhase = {
  status: "IDLE" | "SUBMITTING" | "UNKNOWN" | "ERROR";
  message?: string;
};

export function replaceTableSnapshot(
  current: TableSnapshot[] | undefined,
  snapshot: TableSnapshot,
): TableSnapshot[] {
  const existing = current ?? [];
  let replaced = false;
  const next = existing.map((candidate) => {
    if (candidate.tableId !== snapshot.tableId) return candidate;
    replaced = true;
    return snapshot;
  });
  return replaced ? next : [...next, snapshot];
}

export function TableCloseoutAction({
  action,
  phase,
  operatingFact,
  writeDisabled,
  onSubmit,
}: {
  action: TableCloseoutActionKind;
  phase: TableCloseoutPhase;
  operatingFact: FrontdeskOperatingFactReadModel | null;
  writeDisabled: boolean;
  onSubmit: () => void;
}): React.JSX.Element {
  const busy = phase.status === "SUBMITTING";
  const resultUnknown = phase.status === "UNKNOWN";
  const failed = phase.status === "ERROR";
  const unknownResultRef = useRef<HTMLOutputElement>(null);
  const failedResultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (resultUnknown) unknownResultRef.current?.focus();
    if (failed) failedResultRef.current?.focus();
  }, [failed, resultUnknown]);

  return (
    <div className="simple-action table-closeout-action">
      <p>
        {action === "CONFIRM_CLEANING"
          ? "这一步只记录现场清洁已经完成，不会释放球台，也不会修改已结账金额。"
          : "这一步会把已清洁的球台释放为空闲，不会修改已结账金额。"}
      </p>
      <p>
        关灯状态：
        {operatingFact
          ? frontdeskLightOffStatusLabel(operatingFact)
          : "当前状态暂时无法读取，请刷新后核对"}
        。关灯回执不会阻止完成清洁或释放球台，请按现场情况核对。
      </p>
      {phase.status === "SUBMITTING" ? (
        <output className="inline-operation-feedback is-info" aria-live="polite">
          <RefreshCw className="is-spinning" size={16} />
          <span>
            正在{action === "CONFIRM_CLEANING" ? "确认清洁完成" : "释放球台"}
            ：正在提交门店状态，请勿重复操作。
          </span>
        </output>
      ) : null}
      {resultUnknown ? (
        <output
          className="inline-operation-feedback is-info"
          aria-live="polite"
          ref={unknownResultRef}
          tabIndex={-1}
        >
          <AlertTriangle size={16} />
          <span>
            <strong>{action === "CONFIRM_CLEANING" ? "清洁确认" : "球台释放"}结果待确认</strong>
            <br />
            {phase.message}
          </span>
        </output>
      ) : null}
      {failed ? (
        <div
          className="inline-operation-feedback is-error"
          role="alert"
          ref={failedResultRef}
          tabIndex={-1}
        >
          <AlertTriangle size={16} />
          <span>
            <strong>{action === "CONFIRM_CLEANING" ? "清洁确认" : "球台释放"}未完成</strong>
            <br />
            {phase.message}
          </span>
        </div>
      ) : null}
      <div className="table-closeout-actions">
        <button
          className="button primary"
          type="button"
          disabled={busy || writeDisabled}
          onClick={onSubmit}
        >
          <Check size={16} />
          {phase.status === "SUBMITTING"
            ? action === "CONFIRM_CLEANING"
              ? "正在确认清洁…"
              : "正在释放球台…"
            : resultUnknown
              ? "使用同一请求安全确认"
              : failed
                ? action === "CONFIRM_CLEANING"
                  ? "重新尝试确认清洁完成"
                  : "重新尝试释放球台"
                : action === "CONFIRM_CLEANING"
                  ? "确认清洁完成"
                  : "释放球台"}
        </button>
      </div>
    </div>
  );
}

type SessionBusinessFlow = "OPEN" | "ORDER" | "BILLING" | "TRANSFER" | "CHECKOUT";

export function isSessionOperationErrorVisible(
  errorScope: SessionBusinessFlow | null,
  activeFlow: SessionBusinessFlow,
): boolean {
  return errorScope !== null && errorScope === activeFlow;
}

export function shouldResetSessionOperationErrorOnFlowChange(
  errorScope: SessionBusinessFlow | null,
  nextFlow: SessionBusinessFlow,
  hasSettledError: boolean,
): boolean {
  return hasSettledError && errorScope !== null && errorScope !== nextFlow;
}

export function sessionOperationWritePolicy({
  gatewayWriteDisabled,
  orderReadFailed,
  conflictIsolated,
}: {
  gatewayWriteDisabled: boolean;
  orderReadFailed: boolean;
  conflictIsolated: boolean;
}): {
  standardOrderWriteDisabled: boolean;
} {
  return {
    standardOrderWriteDisabled: gatewayWriteDisabled || orderReadFailed || conflictIsolated,
  };
}

function SessionDialog({
  table,
  operator,
  operatingFact,
  tables,
  effectiveRoles,
  requestedTab,
  connectivity,
  openRecovery,
  itemAddIdentityKey,
  itemAddRecovery,
  onItemAddRecoveryChanged,
  checkoutRecovery,
  checkoutPending,
  checkoutFeedback,
  onCheckoutFeedbackClear,
  onCheckoutRequested,
  officialPaymentRecovery,
  officialPaymentPending,
  officialPaymentFeedback,
  onOfficialPaymentFeedbackClear,
  onOfficialPaymentRequested,
  sessionBillingRecovery,
  sessionBillingPending,
  sessionBillingFeedback,
  sessionBillingFeedbackAction,
  onSessionBillingRequested,
  sessionTransferRecovery,
  sessionTransferPending,
  sessionTransferFeedback,
  onSessionTransferRequested,
  combinedCheckoutRecovery,
  onCombinedCheckoutRecoveryChange,
  reservationTableWriteLocked,
  closeoutRecovery,
  closeoutPending,
  closeoutFeedback,
  onCloseoutRequested,
  onCloseoutRetry,
  openPending,
  openFeedback,
  customerRequestEventId,
  customerRequestBindingRequired,
  linkedOpenRecoveryAllowed,
  onOpenRequested,
  onOpenRetry,
  onClose,
}: {
  table: TableSnapshot | null;
  operator: OperatorIdentity;
  operatingFact: FrontdeskOperatingFactReadModel | null;
  tables: TableSnapshot[];
  effectiveRoles: readonly OperatorRole[];
  requestedTab: SessionDialogTab | null;
  connectivity: ConnectivityTrustInput;
  openRecovery: TableSessionOpenRecoveryView;
  itemAddIdentityKey: string;
  itemAddRecovery: OrderItemAddRecoveryView;
  onItemAddRecoveryChanged: (view: OrderItemAddRecoveryView) => void;
  checkoutRecovery: CheckoutRecoveryView;
  checkoutPending: boolean;
  checkoutFeedback: CheckoutFeedback | null;
  onCheckoutFeedbackClear: () => void;
  onCheckoutRequested: (
    table: TableSnapshot,
    order: OrderDetail,
    method: PaymentMethod,
    member?: {
      memberId: string;
      memberBalanceFen?: number;
      memberSpendPlanHash?: string;
    },
  ) => void;
  officialPaymentRecovery: OfficialPaymentRecoveryView;
  officialPaymentPending: boolean;
  officialPaymentFeedback: OfficialPaymentFeedback | null;
  onOfficialPaymentFeedbackClear: () => void;
  onOfficialPaymentRequested: (
    table: TableSnapshot,
    order: OrderDetail,
    rail: OfficialPaymentRail,
  ) => void;
  sessionBillingRecovery: SessionBillingRecoveryView;
  sessionBillingPending: boolean;
  sessionBillingFeedback: string | null;
  sessionBillingFeedbackAction: SessionBillingAction | null;
  onSessionBillingRequested: (
    table: TableSnapshot,
    order: OrderDetail,
    action: SessionBillingAction,
    reason: string,
    coachStopPreview?: CoachSessionStopPreview | null,
    startAdjustmentPreview?: SessionStartAdjustmentPreview | null,
  ) => void;
  sessionTransferRecovery: SessionTransferRecoveryView;
  sessionTransferPending: boolean;
  sessionTransferFeedback: string | null;
  onSessionTransferRequested: (
    sourceTable: TableSnapshot,
    targetTable: TableSnapshot,
    order: OrderDetail,
    reason: string,
  ) => void;
  combinedCheckoutRecovery: CombinedCheckoutRecoveryView;
  onCombinedCheckoutRecoveryChange: (view: CombinedCheckoutRecoveryView) => void;
  reservationTableWriteLocked: boolean;
  closeoutRecovery: TableCloseoutRecoveryView;
  closeoutPending: boolean;
  closeoutFeedback: string | null;
  onCloseoutRequested: (table: TableSnapshot, action: TableCloseoutActionKind) => void;
  onCloseoutRetry: (recovery: TableCloseoutRecovery) => void;
  openPending: boolean;
  openFeedback: string | null;
  customerRequestEventId: string | null;
  customerRequestBindingRequired: boolean;
  linkedOpenRecoveryAllowed: boolean;
  onOpenRequested: (
    table: TableSnapshot,
    pricingSelection: FrontdeskPricingSelection,
    customerRequestEventId: string | null,
  ) => void;
  onOpenRetry: (recovery: TableSessionOpenRecovery) => void;
  onClose: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<SessionDialogTab>("ORDER");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("CASH");
  const [checkoutKind, setCheckoutKind] = useState<CheckoutKind>("WHOLE");
  const [splitPlanLocked, setSplitPlanLocked] = useState(true);
  const [combinedCheckoutLocked, setCombinedCheckoutLocked] = useState(
    combinedCheckoutRecovery.status !== "NONE",
  );
  const [storePackageRecoveryLocked, setStorePackageRecoveryLocked] = useState(() =>
    storePackageOperationRecoveryBlocksOrderWrites(operator),
  );
  const onStorePackageRecoveryLockedChange = useCallback((locked: boolean) => {
    setStorePackageRecoveryLocked(locked);
  }, []);
  const onSplitPlanLockedChange = useCallback((locked: boolean, focusSplit: boolean) => {
    setSplitPlanLocked(locked);
    setCheckoutKind((current) => checkoutKindAfterSplitPlanDiscovery(current, focusSplit));
  }, []);
  const onCombinedCheckoutLockedChange = useCallback((locked: boolean, focusCombined: boolean) => {
    setCombinedCheckoutLocked(locked);
    if (focusCombined) setCheckoutKind("COMBINED");
  }, []);
  const [targetTableId, setTargetTableId] = useState("");
  const [transferReason, setTransferReason] = useState("");
  const [selectedPricingPlanId, setSelectedPricingPlanId] = useState<string | null>(null);
  const [openPaymentTiming, setOpenPaymentTiming] = useState<"POSTPAID" | "PREPAID">("POSTPAID");
  const pendingSkuIdsRef = useRef<Set<string>>(new Set());
  const [pendingSkuIds, setPendingSkuIds] = useState<ReadonlySet<string>>(() => new Set());
  const [giftAuthorizationSkuId, setGiftAuthorizationSkuId] = useState<string | null>(null);
  const currentItemAddIdentityKey = useRef(itemAddIdentityKey);
  currentItemAddIdentityKey.current = itemAddIdentityKey;
  const currentSaleReturnContextKeyValue = JSON.stringify([
    operator.tenantId,
    operator.storeId,
    operator.id,
    table?.sessionId ?? null,
  ]);
  const currentSaleReturnContextKey = useRef(currentSaleReturnContextKeyValue);
  currentSaleReturnContextKey.current = currentSaleReturnContextKeyValue;
  const previousSaleReturnContextKey = useRef(currentSaleReturnContextKeyValue);
  const [, setSaleReturnRecoveryRevision] = useState(0);
  const [saleReturnPendingItemId, setSaleReturnPendingItemId] = useState<string | null>(null);
  const [saleReturnFeedback, setSaleReturnFeedback] = useState<string | null>(null);
  const [productScannerOpen, setProductScannerOpen] = useState(false);
  const [barcodeLookupPending, setBarcodeLookupPending] = useState(false);
  const [saleReturnFeedbackKind, setSaleReturnFeedbackKind] = useState<
    "INFO" | "SUCCESS" | "ERROR"
  >("INFO");
  useEffect(() => {
    if (previousSaleReturnContextKey.current === currentSaleReturnContextKeyValue) return;
    previousSaleReturnContextKey.current = currentSaleReturnContextKeyValue;
    setSaleReturnPendingItemId(null);
    setSaleReturnFeedback(null);
    setSaleReturnFeedbackKind("INFO");
    setProductScannerOpen(false);
    setBarcodeLookupPending(false);
  }, [currentSaleReturnContextKeyValue]);
  const [productFeedback, setProductFeedback] = useState<string | null>(null);
  const [productFeedbackKind, setProductFeedbackKind] = useState<"INFO" | "SUCCESS" | "ERROR">(
    "INFO",
  );
  const status = trustedConnectivity(connectivity);
  const productOrderSelected =
    status?.selectedConditionalModules?.includes("PRODUCT_ORDER") ?? false;
  const canOperateStandardSession = canOperateStandardSessionForRoles(effectiveRoles);
  const canOperateCoachSession = canOperateCoachSessionForRoles(effectiveRoles);
  const canAddOrderItems = canAddOrderItemsForRoles(effectiveRoles);
  const canCollectPayment = canCollectPaymentForRoles(effectiveRoles);
  const canViewCombinedCheckout = canViewCombinedCheckoutForRoles(effectiveRoles);
  const canOpenCheckoutTab = canCollectPayment || canViewCombinedCheckout;
  const pricingSelectionContext = JSON.stringify([
    operator.tenantId,
    operator.storeId,
    operator.id,
    table?.tableId ?? null,
    table?.revision ?? null,
  ]);
  const previousPricingSelectionContext = useRef(pricingSelectionContext);
  const targetTableSelectId = useId();
  const dialogTitleId = useId();
  const dialogDescriptionId = useId();
  const pricingSelectionTitleId = useId();
  const openFeedbackRef = useRef<HTMLDivElement>(null);
  const billingFeedbackRef = useRef<HTMLDivElement>(null);
  const transferFeedbackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (previousPricingSelectionContext.current === pricingSelectionContext) return;
    previousPricingSelectionContext.current = pricingSelectionContext;
    setSelectedPricingPlanId(null);
    setOpenPaymentTiming("POSTPAID");
  }, [pricingSelectionContext]);

  useEffect(() => {
    setStorePackageRecoveryLocked(storePackageOperationRecoveryBlocksOrderWrites(operator));
  }, [operator]);

  useEffect(() => {
    if (openFeedback) openFeedbackRef.current?.focus();
  }, [openFeedback]);

  useEffect(() => {
    if (tab === "BILLING" && sessionBillingFeedback && sessionBillingFeedbackAction !== "VOID") {
      billingFeedbackRef.current?.focus();
    }
  }, [sessionBillingFeedback, sessionBillingFeedbackAction, tab]);

  useEffect(() => {
    if (tab === "TRANSFER" && sessionTransferFeedback) {
      transferFeedbackRef.current?.focus();
    }
  }, [sessionTransferFeedback, tab]);

  useEffect(() => {
    if (requestedTab) setTab(requestedTab);
  }, [requestedTab]);

  useEffect(() => {
    const visibleTab = visibleSessionDialogTab(tab, {
      productOrderSelected,
      canOperateStandardSession,
      canCollectPayment: canOpenCheckoutTab,
      canOperateCoachSession,
    });
    if (visibleTab !== tab) setTab(visibleTab);
  }, [
    canOpenCheckoutTab,
    canOperateCoachSession,
    canOperateStandardSession,
    productOrderSelected,
    tab,
  ]);

  useEffect(() => {
    if (!canCollectPayment && canViewCombinedCheckout) setCheckoutKind("COMBINED");
  }, [canCollectPayment, canViewCombinedCheckout]);
  useEffect(() => {
    setCombinedCheckoutLocked(combinedCheckoutRecovery.status !== "NONE");
  }, [combinedCheckoutRecovery.status]);
  const orderQuery = useQuery({
    queryKey: ["session-order", table?.sessionId],
    queryFn: () => getOrder(table?.sessionId ?? ""),
    enabled: Boolean(table?.sessionId),
    refetchInterval: table?.state === "IN_USE" ? 1_000 : false,
  });
  const pricingOptionsQuery = useQuery({
    queryKey: [
      "frontdesk-pricing-options",
      operator.tenantId,
      operator.storeId,
      table?.tableId,
      table?.revision,
    ],
    queryFn: () =>
      getFrontdeskPricingOptions(table?.tableId ?? "", {
        tenantId: operator.tenantId,
        storeId: operator.storeId,
      }),
    enabled: table?.state === "IDLE",
    retry: false,
  });
  const pricingOptionsVersionContext = pricingOptionsQuery.data
    ? JSON.stringify([
        pricingOptionsQuery.data.configurationVersion,
        pricingOptionsQuery.data.defaultPricingPlanId,
        pricingOptionsQuery.data.options.map((option) => [option.id, option.version]),
      ])
    : null;
  const previousPricingOptionsVersionContext = useRef<string | null>(null);
  useEffect(() => {
    if (
      pricingOptionsVersionContext === null ||
      previousPricingOptionsVersionContext.current === pricingOptionsVersionContext
    ) {
      return;
    }
    const hadPreviousVersion = previousPricingOptionsVersionContext.current !== null;
    previousPricingOptionsVersionContext.current = pricingOptionsVersionContext;
    if (hadPreviousVersion) setSelectedPricingPlanId(null);
  }, [pricingOptionsVersionContext]);
  const pricingOptionsMatch = Boolean(
    table &&
      pricingOptionsQuery.data &&
      frontdeskPricingOptionsMatchContext(pricingOptionsQuery.data, operator, table),
  );
  const pricingOptionsReady =
    pricingOptionsMatch && !pricingOptionsQuery.isFetching && !pricingOptionsQuery.isError;
  const pricingOptions = pricingOptionsReady ? pricingOptionsQuery.data : undefined;
  const selectedPricingPlan = pricingOptions?.options.find(
    (option) =>
      option.id ===
      (selectedPricingPlanId === null
        ? pricingOptions.defaultPricingPlanId
        : selectedPricingPlanId),
  );
  const pricingSelection: FrontdeskPricingSelection | null = selectedPricingPlan
    ? {
        pricingPlanId: selectedPricingPlan.id,
        expectedPricingPlanVersion: selectedPricingPlan.version,
        pricingPlanName: selectedPricingPlan.name,
      }
    : null;
  const coachStopPreviewEnabled =
    tab === "BILLING" &&
    canOperateStandardSession &&
    table?.state === "IN_USE" &&
    orderQuery.data?.billingState === "RUNNING" &&
    Boolean(table.sessionId);
  const coachStopPreviewQuery = useQuery({
    queryKey: ["coach-session-stop-preview", table?.sessionId],
    queryFn: () => getCoachSessionStopPreview(table?.sessionId ?? ""),
    enabled: coachStopPreviewEnabled,
    refetchInterval: coachStopPreviewEnabled ? 1_000 : false,
  });
  const coachStopResultQuery = useQuery<CoachSessionStopResult | null>({
    queryKey: ["coach-session-stop-result", table?.sessionId],
    queryFn: async () => null,
    enabled: false,
    initialData: null,
  });
  const productsQuery = useQuery({
    queryKey: ["products"],
    queryFn: listProducts,
    enabled: table?.state === "IN_USE" && productOrderSelected,
  });
  const addProduct = useMutation({
    mutationFn: async ({
      recovery,
      identityKey,
      secondaryAuthorizationToken,
    }: {
      recovery: OrderItemAddRecovery;
      identityKey: string;
      secondaryAuthorizationToken?: string;
    }) => {
      if (!canAddOrderItems) {
        throw new Error("当前岗位只能查看商品记录，不能直接加单");
      }
      requireTrustedStoreAuthority(connectivity, "商品加单");
      try {
        const result =
          recovery.command.kind === "GIFT_ORDER_ITEM"
            ? await addGiftOrderItem(
                recovery.command.sessionId,
                recovery.command.skuId,
                recovery.command.quantity,
                recovery.command.reason,
                recovery.command.idempotencyKey,
                secondaryAuthorizationToken ?? "",
                {
                  tableId: recovery.command.tableId,
                  productName: recovery.command.productName,
                  listUnitPriceFen: recovery.command.listUnitPriceFen,
                  baselineItems: recovery.command.baselineItems,
                  originalAuthorizerId: recovery.command.originalAuthorizerId,
                },
              )
            : await addOrderItem(
                recovery.command.sessionId,
                recovery.command.skuId,
                recovery.command.quantity,
                recovery.command.idempotencyKey,
                {
                  tableId: recovery.command.tableId,
                  productName: recovery.command.productName,
                  unitPriceFen: recovery.command.unitPriceFen,
                  baselineItems: recovery.command.baselineItems,
                },
              );
        if (currentItemAddIdentityKey.current !== identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的商品加单结果不会显示在当前账号，请切回原账号继续核对",
          );
        }
        if (!clearOrderItemAddRecovery(recovery, identityKey)) {
          throw new ApiRequestError(
            "ORDER_ITEM_ADD_RECOVERY_UNAVAILABLE",
            "商品加单结果已经返回，但本机未能安全清除保护记录；页面继续保留待确认状态",
          );
        }
        return { recovery, result, identityKey };
      } catch (error) {
        if (orderItemAddRecoveryOutcomeIsUnknown(recovery, error)) {
          markOrderItemAddOutcomeUnknown(recovery, identityKey);
        } else {
          clearOrderItemAddRecovery(recovery, identityKey);
        }
        throw error;
      } finally {
        finishOrderItemAddDispatch(recovery, identityKey);
      }
    },
    onSuccess: ({ recovery, result, identityKey }) => {
      if (currentItemAddIdentityKey.current !== identityKey) return;
      onItemAddRecoveryChanged(readOrderItemAddRecoveries(identityKey));
      const { command } = recovery;
      queryClient.setQueryData<OrderDetail>(["session-order", command.sessionId], (current) => {
        return preferProductAddOrder(current, result);
      });
      setProductFeedback(
        command.kind === "GIFT_ORDER_ITEM"
          ? `“${command.productName}”已作为门店赠品加入台单；顾客应付增加0元，库存已扣减 ${command.quantity} 件并记录成本。`
          : command.inventoryManaged
            ? `“${command.productName}”已加入订单并扣减库存；正在刷新商品目录，请核对权威剩余量。`
            : `已把“${command.productName}”加入订单；此商品不跟踪库存，可以继续点其他商品。`,
      );
      setProductFeedbackKind("SUCCESS");
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
        queryClient.invalidateQueries({ queryKey: ["session-order", command.sessionId] }),
        queryClient.invalidateQueries({ queryKey: ["products"] }),
        queryClient.invalidateQueries({ queryKey: ["tables"] }),
      ]);
    },
    onError: (error, input) => {
      if (currentItemAddIdentityKey.current !== input.identityKey) return;
      const view = readOrderItemAddRecoveries(input.identityKey);
      onItemAddRecoveryChanged(view);
      const outcomeUnknown = orderItemAddRecoveryOutcomeIsUnknown(input.recovery, error);
      const giftUnknown = input.recovery.command.kind === "GIFT_ORDER_ITEM" && outcomeUnknown;
      setProductFeedback(
        giftUnknown
          ? view.status === "OWNED"
            ? `“${input.recovery.command.productName}”的赠品结果尚未确认：${businessErrorMessage(error, "系统没有返回明确原因")}。原商品、数量、原因和授权人已经锁定；只能由原授权人重新授权，并安全重试这一笔原请求。`
            : `“${input.recovery.command.productName}”的赠品结果尚未确认。本机暂时无法读取保护记录，新的商品加单、收款和结账已暂停；请勿重复操作。`
          : outcomeUnknown
            ? view.status === "OWNED"
              ? `“${input.recovery.command.productName}”的加单结果尚未确认：${businessErrorMessage(error, "系统没有返回明确原因")}。系统保留了原请求，只查询或安全重试这一笔，不会重复加单或扣库存。`
              : `“${input.recovery.command.productName}”的加单结果尚未确认：${businessErrorMessage(error, "系统没有返回明确原因")}。本机暂时无法读取保护记录，新的商品加单、收款和结账已暂停；请勿重复操作。`
            : productAddDefiniteFailureMessage(error, input.recovery.command.productName),
      );
      setProductFeedbackKind("ERROR");
      if (!outcomeUnknown) {
        void queryClient.invalidateQueries({ queryKey: ["products"] });
      }
    },
    onSettled: (_result, _error, input) => {
      pendingSkuIdsRef.current.delete(input.recovery.command.skuId);
      setPendingSkuIds(new Set(pendingSkuIdsRef.current));
    },
  });
  const dispatchOrderItemAddRecovery = (
    recovery: OrderItemAddRecovery,
    secondaryAuthorizationToken?: string,
  ): void => {
    try {
      if (!canAddOrderItems) {
        throw new Error("当前岗位只能查看商品记录，不能直接加单");
      }
      requireTrustedStoreAuthority(connectivity, "商品加单");
      if (recovery.command.kind === "GIFT_ORDER_ITEM" && !secondaryAuthorizationToken) {
        throw new Error("门店赠品必须由原授权人重新输入账号和凭证，完成本次一次性授权");
      }
      if (!beginOrderItemAddDispatch(recovery, itemAddIdentityKey)) {
        const view = readOrderItemAddRecoveries(itemAddIdentityKey);
        onItemAddRecoveryChanged(view);
        setProductFeedback(
          view.status === "RECOVERY_STORAGE_UNAVAILABLE"
            ? "无法安全读取商品加单保护记录，系统没有发出请求。"
            : `“${recovery.command.productName}”正在确认中，请勿重复点击。`,
        );
        setProductFeedbackKind(view.status === "RECOVERY_STORAGE_UNAVAILABLE" ? "ERROR" : "INFO");
        return;
      }
      claimPendingSku(pendingSkuIdsRef.current, recovery.command.skuId);
      setPendingSkuIds(new Set(pendingSkuIdsRef.current));
      setProductFeedback(`正在核对并提交“${recovery.command.productName}”的原加单请求…`);
      setProductFeedbackKind("INFO");
      addProduct.mutate({
        recovery,
        identityKey: itemAddIdentityKey,
        ...(secondaryAuthorizationToken ? { secondaryAuthorizationToken } : {}),
      });
    } catch (error) {
      setProductFeedback(businessErrorMessage(error, "当前不能安全提交商品加单"));
      setProductFeedbackKind("ERROR");
    }
  };
  const requestOrderItemAdd = (
    selectedProduct: ProductSaleCatalogItem,
    trustedProducts: readonly ProductSaleCatalogItem[] | undefined = productsQuery.data,
    catalogUntrusted = productsQuery.isError || productsQuery.isRefetchError,
  ): void => {
    try {
      if (!canAddOrderItems) {
        throw new Error("当前岗位只能查看商品记录，不能直接加单");
      }
      requireTrustedStoreAuthority(connectivity, "商品加单");
      const product = requireTrustedProductForAdd(
        trustedProducts,
        selectedProduct.skuId,
        catalogUntrusted,
      );
      if (product.inventoryManaged && product.stockOnHand === null) {
        throw new Error("商品库存数量无法安全确认，系统没有提交加单；请刷新目录后重试");
      }
      if (!table?.sessionId) throw new Error("进行中台单缺少 sessionId");
      const baseline = orderQuery.data;
      if (
        !baseline ||
        baseline.status !== "OPEN" ||
        baseline.sessionId !== table.sessionId ||
        baseline.tableId !== table.tableId
      ) {
        throw new Error("当前台单内容无法安全确认，系统没有提交加单；请刷新台单后重试");
      }
      const claimed = claimOrderItemAddRecovery(itemAddIdentityKey, {
        kind: "ADD_ORDER_ITEM",
        sessionId: table.sessionId,
        tableId: table.tableId,
        tableNumber: table.tableNumber,
        skuId: product.skuId,
        productName: product.name,
        unitPriceFen: product.unitPriceFen,
        inventoryManaged: product.inventoryManaged,
        stockOnHandBefore: product.inventoryManaged ? product.stockOnHand : null,
        quantity: 1,
        baselineItems: baseline.items,
        idempotencyKey: crypto.randomUUID(),
      });
      const view = readOrderItemAddRecoveries(itemAddIdentityKey);
      onItemAddRecoveryChanged(view);
      if (claimed.status !== "OWNED") {
        setProductFeedback(
          claimed.status === "OTHER_SESSION"
            ? "另一张台单仍有商品加单结果待确认，请先回到原台单处理。"
            : claimed.status === "OTHER_OPERATOR"
              ? "另一位员工有商品加单结果待确认，系统没有发出新请求。"
              : "本机无法安全保存商品加单保护记录，系统没有发出请求。",
        );
        setProductFeedbackKind("ERROR");
        return;
      }
      dispatchOrderItemAddRecovery(claimed.recovery);
    } catch (error) {
      setProductFeedback(businessErrorMessage(error, "当前不能安全提交商品加单"));
      setProductFeedbackKind("ERROR");
    }
  };
  const requestBarcodeOrderItemAdd = async (barcode: string): Promise<void> => {
    const identityKey = itemAddIdentityKey;
    try {
      if (!canAddOrderItems) throw new Error("当前岗位只能查看商品记录，不能扫码加单");
      requireTrustedStoreAuthority(connectivity, "扫码加商品");
      setBarcodeLookupPending(true);
      setProductFeedback(`正在按条码 ${barcode} 查找本店商品…`);
      setProductFeedbackKind("INFO");
      const lookup = await findProductByBarcode(barcode);
      if (currentItemAddIdentityKey.current !== identityKey) {
        throw new ApiRequestError(
          "OPERATOR_IDENTITY_CHANGED",
          "账号或台单已经切换；本次扫码结果不会用于当前台单",
        );
      }
      if (!lookup.product) {
        setProductFeedback(
          `没有找到条码 ${lookup.barcode} 对应的本店商品，系统没有加单或扣库存。请核对条码，或请店长先建立商品档案。`,
        );
        setProductFeedbackKind("ERROR");
        return;
      }
      queryClient.setQueryData<ProductSaleCatalogItem[]>(["products"], (current) => {
        const next = (current ?? []).filter((product) => product.skuId !== lookup.product?.skuId);
        return lookup.product ? [...next, lookup.product] : next;
      });
      setProductFeedback(`已识别“${lookup.product.name}”，正在按当前售价和库存提交一次加单…`);
      setProductFeedbackKind("INFO");
      requestOrderItemAdd(lookup.product, [lookup.product], false);
    } catch (error) {
      if (currentItemAddIdentityKey.current !== identityKey) return;
      setProductFeedback(
        `扫码查商品未完成：${businessErrorMessage(error, "系统没有返回明确原因")}。系统没有因此加单或扣库存，请手工输入条码或从商品列表选择。`,
      );
      setProductFeedbackKind("ERROR");
    } finally {
      if (currentItemAddIdentityKey.current === identityKey) setBarcodeLookupPending(false);
    }
  };
  const requestGiftOrderItem = async (submission: GiftOrderItemDraftSubmission): Promise<void> => {
    const { product: selectedProduct, quantity, reason, authorizerLogin, credential } = submission;
    const requestContextKey = currentSaleReturnContextKey.current;
    try {
      if (!canAddOrderItems) {
        throw new Error("当前岗位只能查看商品记录，不能直接赠送商品");
      }
      requireTrustedStoreAuthority(connectivity, "门店赠品");
      const product = requireTrustedProductForAdd(
        productsQuery.data,
        selectedProduct.skuId,
        productsQuery.isError || productsQuery.isRefetchError,
      );
      const normalizedReason = reason.trim();
      if (
        !product.inventoryManaged ||
        product.stockOnHand === null ||
        product.unitPriceFen <= 0 ||
        !Number.isSafeInteger(quantity) ||
        quantity < 1 ||
        quantity > Math.min(99, product.stockOnHand) ||
        [...normalizedReason].length < 2 ||
        [...normalizedReason].length > 200
      ) {
        throw new Error("请核对可赠数量，并填写 2 至 200 个字的具体赠送原因");
      }
      if (!table?.sessionId) throw new Error("进行中台单缺少 sessionId");
      const baseline = orderQuery.data;
      if (
        !baseline ||
        baseline.status !== "OPEN" ||
        baseline.sessionId !== table.sessionId ||
        baseline.tableId !== table.tableId
      ) {
        throw new Error("当前台单内容无法安全确认，系统没有提交赠品；请刷新台单后重试");
      }
      if (!authorizerLogin.trim() || !credential) {
        throw new Error("请填写店长或老板的授权账号和凭证");
      }
      const existingGiftRecovery =
        itemAddRecovery.status === "OWNED"
          ? itemAddRecovery.recoveries.find(
              (recovery) =>
                recovery.command.kind === "GIFT_ORDER_ITEM" &&
                recovery.command.skuId === product.skuId,
            )
          : undefined;
      if (existingGiftRecovery) {
        throw new Error(
          `“${product.name}”已有赠品结果待确认，请使用上方锁定的原请求和原授权人入口重试`,
        );
      }
      setGiftAuthorizationSkuId(product.skuId);
      setProductFeedback(`正在核对“${product.name}”的店长或老板一次性授权…`);
      setProductFeedbackKind("INFO");
      const authorizationContext = {
        sessionId: baseline.sessionId,
        skuId: product.skuId,
        quantity,
        expectedProductName: product.name,
        expectedListUnitPriceFen: product.unitPriceFen,
        reason: normalizedReason,
      };
      const grant = await requestSecondaryAuthorization(
        "GIFT_ORDER_ITEM",
        credential,
        authorizerLogin.trim(),
        authorizationContext,
      );
      if (currentSaleReturnContextKey.current !== requestContextKey) {
        throw new ApiRequestError(
          "OPERATOR_IDENTITY_CHANGED",
          "账号或台单已经切换；系统没有提交赠品，请回到当前台单重新操作",
        );
      }
      requireTrustedStoreAuthority(connectivity, "门店赠品");
      const idempotencyKey = crypto.randomUUID();
      const claimed = claimOrderItemAddRecovery(itemAddIdentityKey, {
        kind: "GIFT_ORDER_ITEM",
        sessionId: baseline.sessionId,
        tableId: baseline.tableId,
        tableNumber: baseline.tableNumber,
        skuId: product.skuId,
        productName: product.name,
        listUnitPriceFen: product.unitPriceFen,
        inventoryManaged: product.inventoryManaged,
        stockOnHandBefore: product.stockOnHand,
        quantity,
        reason: normalizedReason,
        baselineItems: baseline.items,
        originalAuthorizerId: grant.authorizerId,
        originalAuthorizerDisplayName: grant.authorizerDisplayName,
        idempotencyKey,
      });
      const view = readOrderItemAddRecoveries(itemAddIdentityKey);
      onItemAddRecoveryChanged(view);
      if (claimed.status !== "OWNED") {
        setProductFeedback(
          claimed.status === "OTHER_SESSION"
            ? "另一张台单仍有商品结果待确认，请先回到原台单处理。"
            : claimed.status === "OTHER_OPERATOR"
              ? "另一位员工有商品结果待确认，系统没有发出赠品请求。"
              : "本机无法安全保存赠品保护记录，系统没有发出请求。",
        );
        setProductFeedbackKind("ERROR");
        return;
      }
      if (claimed.recovery.command.idempotencyKey !== idempotencyKey) {
        setProductFeedback(
          `“${product.name}”已有赠品结果待确认；系统没有发送新请求，请使用上方锁定的原请求和原授权人入口重试。`,
        );
        setProductFeedbackKind("ERROR");
        return;
      }
      dispatchOrderItemAddRecovery(claimed.recovery, grant.token);
    } catch (error) {
      setProductFeedback(businessErrorMessage(error, "当前不能安全提交门店赠品"));
      setProductFeedbackKind("ERROR");
    } finally {
      setGiftAuthorizationSkuId((current) => (current === selectedProduct.skuId ? null : current));
    }
  };
  const retryGiftOrderItem = async (
    recovery: OrderItemAddRecovery,
    authorizerLogin: string,
    credential: string,
  ): Promise<void> => {
    if (recovery.command.kind !== "GIFT_ORDER_ITEM") return;
    const requestContextKey = currentSaleReturnContextKey.current;
    try {
      if (!authorizerLogin.trim() || !credential) {
        throw new Error("请由原授权人填写账号和凭证后再重试");
      }
      requireTrustedStoreAuthority(connectivity, "门店赠品结果确认");
      setGiftAuthorizationSkuId(recovery.command.skuId);
      setProductFeedback(`正在核对“${recovery.command.productName}”的原授权人…`);
      setProductFeedbackKind("INFO");
      const grant = await requestSecondaryAuthorization(
        "GIFT_ORDER_ITEM",
        credential,
        authorizerLogin.trim(),
        {
          sessionId: recovery.command.sessionId,
          skuId: recovery.command.skuId,
          quantity: recovery.command.quantity,
          expectedProductName: recovery.command.productName,
          expectedListUnitPriceFen: recovery.command.listUnitPriceFen,
          reason: recovery.command.reason,
        },
      );
      if (currentSaleReturnContextKey.current !== requestContextKey) {
        throw new ApiRequestError(
          "OPERATOR_IDENTITY_CHANGED",
          "账号或台单已经切换；系统没有重试原赠品请求",
        );
      }
      if (grant.authorizerId !== recovery.command.originalAuthorizerId) {
        throw new ApiRequestError(
          "GIFT_ORIGINAL_AUTHORIZER_REQUIRED",
          "这笔赠品只能由最初授权的同一位店长或老板重新授权；系统没有重试原请求",
          403,
        );
      }
      requireTrustedStoreAuthority(connectivity, "门店赠品结果确认");
      dispatchOrderItemAddRecovery(recovery, grant.token);
    } catch (error) {
      setProductFeedback(
        error instanceof ApiRequestError && error.code === "GIFT_ORIGINAL_AUTHORIZER_REQUIRED"
          ? error.message
          : businessErrorMessage(error, "原赠品请求没有重试"),
      );
      setProductFeedbackKind("ERROR");
      onItemAddRecoveryChanged(readOrderItemAddRecoveries(itemAddIdentityKey));
    } finally {
      setGiftAuthorizationSkuId((current) => (current === recovery.command.skuId ? null : current));
    }
  };
  const submitSaleableReturn = useMutation({
    mutationFn: async ({
      recovery,
      identityKey,
      contextKey,
    }: {
      recovery: OrderItemSaleReturnRecovery;
      identityKey: string;
      contextKey: string;
    }) => {
      if (!canAddOrderItems) {
        throw new Error("当前岗位只能查看商品记录，不能办理实物退回");
      }
      requireTrustedStoreAuthority(connectivity, "未结账商品实物退回");
      try {
        const result = await returnSaleableOrderItem(
          recovery.command.sessionId,
          recovery.command.request,
          { baselineOrder: recovery.command.baselineOrder },
        );
        if (currentSaleReturnContextKey.current !== contextKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号或台单已经切换；原请求结果不会显示在当前台单，请切回原账号和原台单核对",
          );
        }
        if (!clearOrderItemSaleReturnRecovery(recovery, identityKey)) {
          throw new ApiRequestError(
            "ORDER_ITEM_SALE_RETURN_RECOVERY_UNAVAILABLE",
            "商品退回结果已经返回，但本机未能安全清除保护记录；页面继续保留待确认状态",
          );
        }
        return { recovery, result, identityKey, contextKey };
      } catch (error) {
        if (orderItemSaleReturnOutcomeIsUnknown(error)) {
          markOrderItemSaleReturnOutcomeUnknown(recovery, identityKey);
        } else {
          clearOrderItemSaleReturnRecovery(recovery, identityKey);
        }
        throw error;
      } finally {
        finishOrderItemSaleReturnDispatch(recovery, identityKey);
      }
    },
    onSuccess: ({ recovery, result, contextKey }) => {
      if (currentSaleReturnContextKey.current !== contextKey) return;
      setSaleReturnRecoveryRevision((revision) => revision + 1);
      queryClient.setQueryData<OrderDetail>(
        ["session-order", recovery.command.sessionId],
        result.order,
      );
      const returnedGift = recovery.command.baselineOrder.items.some(
        (item) => item.id === recovery.command.itemId && item.lineType === "GIFT",
      );
      setSaleReturnFeedback(
        returnedGift
          ? result.inventoryEffect === "SALEABLE_STOCK_RESTORED"
            ? `“${recovery.command.productName}”门店赠品已退回 ${result.quantity} 件；订单减少0元、未退款。实物完好可再次销售，可售库存由 ${result.inventoryQuantityBefore} 增加到 ${result.inventoryQuantityAfter}；只有完好赠品才恢复库存。`
            : `“${recovery.command.productName}”门店赠品已退回 ${result.quantity} 件；订单减少0元、未退款。商品已记录为开封或破损，不恢复可售库存，也不会再扣一次库存；只有完好赠品才恢复库存。`
          : result.inventoryEffect === "SALEABLE_STOCK_RESTORED"
            ? `“${recovery.command.productName}”已退回 ${result.quantity} 件；订单减少 ${formatFee(result.orderAmountReductionFen)}，可售库存由 ${result.inventoryQuantityBefore} 增加到 ${result.inventoryQuantityAfter}。本台单尚未付款，因此没有退款。`
            : `“${recovery.command.productName}”已退回 ${result.quantity} 件；订单减少 ${formatFee(result.orderAmountReductionFen)}。商品已记录为开封或破损、不可再次销售，可售库存没有增加，也没有再扣一次库存。本台单尚未付款，因此没有退款。`,
      );
      setSaleReturnFeedbackKind("SUCCESS");
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
        queryClient.invalidateQueries({
          queryKey: ["session-order", recovery.command.sessionId],
        }),
        queryClient.invalidateQueries({ queryKey: ["products"] }),
        queryClient.invalidateQueries({ queryKey: ["tables"] }),
      ]);
    },
    onError: (error, input) => {
      if (currentSaleReturnContextKey.current !== input.contextKey) return;
      setSaleReturnRecoveryRevision((revision) => revision + 1);
      const view = readOrderItemSaleReturnRecovery(operator, table?.sessionId ?? null);
      const outcomeUnknown = orderItemSaleReturnOutcomeIsUnknown(error);
      setSaleReturnFeedback(
        outcomeUnknown
          ? view.status === "OWNED"
            ? `“${input.recovery.command.productName}”的退回结果尚未确认：${businessErrorMessage(error, "系统没有返回明确结果")}。页面已保留原商品、数量、实物状态、原因和请求号，只能使用原请求安全重试，不会重复减少订单或重复处理库存。`
            : "商品退回结果尚未确认，而且本机暂时无法读取原请求保护记录。新的加单、退回、转台和结账已暂停，请勿重复操作。"
          : businessErrorMessage(error, "商品没有退回；请按提示核对后再试"),
      );
      setSaleReturnFeedbackKind("ERROR");
      if (!outcomeUnknown) {
        void Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["session-order", input.recovery.command.sessionId],
          }),
          queryClient.invalidateQueries({ queryKey: ["products"] }),
        ]);
      }
    },
    onSettled: (_result, _error, input) => {
      if (currentSaleReturnContextKey.current === input.contextKey) {
        setSaleReturnPendingItemId(null);
      }
    },
  });
  const dispatchSaleableReturn = (recovery: OrderItemSaleReturnRecovery): void => {
    try {
      if (!canAddOrderItems) {
        throw new Error("当前岗位只能查看商品记录，不能办理实物退回");
      }
      requireTrustedStoreAuthority(connectivity, "未结账商品实物退回");
      if (!beginOrderItemSaleReturnDispatch(recovery, recovery.ownerIdentityKey)) {
        setSaleReturnRecoveryRevision((revision) => revision + 1);
        const view = readOrderItemSaleReturnRecovery(operator, table?.sessionId ?? null);
        setSaleReturnFeedback(
          view.status === "UNAVAILABLE"
            ? "无法安全读取商品退回保护记录，系统没有发出请求。"
            : `“${recovery.command.productName}”正在确认中，请勿重复点击。`,
        );
        setSaleReturnFeedbackKind(view.status === "UNAVAILABLE" ? "ERROR" : "INFO");
        return;
      }
      setSaleReturnPendingItemId(recovery.command.itemId);
      setSaleReturnFeedback(`正在核对并提交“${recovery.command.productName}”的原退回请求…`);
      setSaleReturnFeedbackKind("INFO");
      submitSaleableReturn.mutate({
        recovery,
        identityKey: recovery.ownerIdentityKey,
        contextKey: currentSaleReturnContextKey.current,
      });
    } catch (error) {
      setSaleReturnFeedback(businessErrorMessage(error, "当前不能安全提交商品退回"));
      setSaleReturnFeedbackKind("ERROR");
    }
  };
  const requestSaleableReturn = (
    selectedItem: OrderItemRecord,
    quantity: number,
    physicalDisposition: SaleReturnPhysicalDisposition,
    reason: string,
  ): void => {
    try {
      if (!canAddOrderItems) {
        throw new Error("当前岗位只能查看商品记录，不能办理实物退回");
      }
      requireTrustedStoreAuthority(connectivity, "未结账商品实物退回");
      if (!table?.sessionId) throw new Error("进行中台单缺少 sessionId");
      const baseline = orderQuery.data;
      const currentItem = baseline?.items.find((item) => item.id === selectedItem.id);
      const product = productsQuery.data?.find(
        (candidate) => candidate.skuId === selectedItem.skuId,
      );
      if (
        !baseline ||
        baseline.status !== "OPEN" ||
        baseline.paidFen !== 0 ||
        baseline.refundedFen !== 0 ||
        baseline.payments.length !== 0 ||
        baseline.refunds.length !== 0 ||
        baseline.sessionId !== table.sessionId ||
        baseline.tableId !== table.tableId ||
        !currentItem ||
        currentItem.skuId !== selectedItem.skuId
      ) {
        throw new Error("当前未付款台单无法安全确认，系统没有提交退回；请刷新台单后重试");
      }
      if (physicalDisposition === "SALEABLE_RETURNED" && (!product || !product.inventoryManaged)) {
        throw new Error("该商品未纳入库存管理，不能登记为完好可再次销售；请选择不可再次销售");
      }
      const normalizedReason = reason.trim();
      const availableQuantity = currentItem.quantity - currentItem.returnedQuantity;
      if (
        !Number.isSafeInteger(quantity) ||
        quantity < 1 ||
        quantity > availableQuantity ||
        [...normalizedReason].length < 2 ||
        [...normalizedReason].length > 200
      ) {
        throw new Error("请选择可退数量，并填写 2 至 200 个字的退回原因");
      }
      const identityKey = orderItemSaleReturnIdentityKey(
        operator,
        baseline.sessionId,
        currentItem.id,
      );
      const claimed = claimOrderItemSaleReturnRecovery(identityKey, {
        kind: "SALEABLE_ORDER_ITEM_RETURN",
        sessionId: baseline.sessionId,
        itemId: currentItem.id,
        skuId: currentItem.skuId,
        productName: currentItem.name,
        unitPriceFen: currentItem.unitPriceFen,
        request: {
          itemId: currentItem.id,
          quantity,
          physicalDisposition,
          paymentResolution: "UNPAID_ORDER_REDUCTION",
          reason: normalizedReason,
          idempotencyKey: crypto.randomUUID(),
        },
        baselineOrder: baseline,
      });
      setSaleReturnRecoveryRevision((revision) => revision + 1);
      if (claimed.status !== "OWNED" || claimed.recovery.ownerIdentityKey !== identityKey) {
        setSaleReturnFeedback(
          claimed.status === "UNAVAILABLE"
            ? "本机无法安全保存商品退回原请求，系统没有发出请求。"
            : "另一账号、台单或商品批次仍有退回结果待确认，系统没有发出新请求；请先回到原记录安全重试。",
        );
        setSaleReturnFeedbackKind("ERROR");
        return;
      }
      dispatchSaleableReturn(claimed.recovery);
    } catch (error) {
      setSaleReturnFeedback(businessErrorMessage(error, "当前不能安全提交商品退回"));
      setSaleReturnFeedbackKind("ERROR");
    }
  };
  const selectSessionTab = (nextTab: SessionDialogTab): void => {
    setTab(nextTab);
  };
  const closeSessionDialog = (): void => {
    setTab(
      visibleSessionDialogTab("ORDER", {
        productOrderSelected,
        canOperateStandardSession,
        canCollectPayment: canOpenCheckoutTab,
      }),
    );
    setSplitPlanLocked(false);
    setCombinedCheckoutLocked(false);
    onClose();
  };
  const operatingFactWriteSafe =
    operatingFact !== null &&
    operatingFact.blockers.length === 0 &&
    frontdeskOperatingFactMatchesTable(operatingFact, table ?? undefined);
  const writeDisabled = !status?.offlineAuthorityAvailable || !operatingFactWriteSafe;
  const unresolvedOpenExists =
    openRecovery.status === "OWNED" || openRecovery.status === "OTHER_OPERATOR";
  const openStorageUnavailable = openRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE";
  const sessionItemAddRecoveries =
    itemAddRecovery.status === "OWNED" &&
    itemAddRecovery.recoveries[0]?.command.sessionId === table?.sessionId
      ? itemAddRecovery.recoveries
      : [];
  const itemAddOwnedForOtherSession =
    itemAddRecovery.status === "OWNED" && sessionItemAddRecoveries.length === 0;
  const itemAddWriteUnavailable =
    itemAddRecovery.status === "OTHER_OPERATOR" ||
    itemAddRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" ||
    itemAddOwnedForOtherSession;
  const itemAddOutcomeBlocksOrderWrites =
    itemAddRecovery.status === "OTHER_OPERATOR" ||
    (itemAddRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" &&
      itemAddRecovery.pendingOutcomeMayExist);
  const checkoutOutcomeBlocksOrderWrites =
    checkoutRecovery.status === "OWNED" ||
    checkoutRecovery.status === "OTHER_OPERATOR" ||
    (checkoutRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" &&
      checkoutRecovery.pendingOutcomeMayExist);
  const checkoutWriteUnavailable = checkoutRecovery.status !== "NONE";
  const officialPaymentOutcomeBlocksOrderWrites =
    officialPaymentRecovery.status === "OWNED" ||
    officialPaymentRecovery.status === "OTHER_OPERATOR" ||
    (officialPaymentRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" &&
      officialPaymentRecovery.pendingOutcomeMayExist);
  const officialPaymentWriteUnavailable = officialPaymentRecovery.status !== "NONE";
  const sessionBillingOutcomeBlocksOrderWrites =
    sessionBillingRecovery.status === "OWNED" ||
    sessionBillingRecovery.status === "OTHER_OPERATOR" ||
    (sessionBillingRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" &&
      sessionBillingRecovery.pendingOutcomeMayExist);
  const sessionBillingWriteUnavailable = sessionBillingRecovery.status !== "NONE";
  const sessionTransferOutcomeBlocksOrderWrites =
    sessionTransferRecovery.status === "OWNED" ||
    sessionTransferRecovery.status === "OTHER_OPERATOR" ||
    (sessionTransferRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" &&
      sessionTransferRecovery.pendingOutcomeMayExist);
  const sessionTransferWriteUnavailable = sessionTransferRecovery.status !== "NONE";
  const closeoutOutcomeBlocksWrites =
    closeoutRecovery.status === "OWNED" ||
    closeoutRecovery.status === "OTHER_OPERATOR" ||
    (closeoutRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" &&
      closeoutRecovery.pendingOutcomeMayExist);
  const closeoutWriteUnavailable = closeoutRecovery.status !== "NONE";
  const combinedCheckoutCloseoutLocked = table ? combinedCheckoutRecoveryBlocksTable(table) : false;
  const authorityExpired = status?.mode === "AUTHORITY_EXPIRED";
  const order = orderQuery.data;
  const coachStopPreview =
    coachStopPreviewQuery.data &&
    coachStopPreviewQuery.data.sessionId === table?.sessionId &&
    coachStopPreviewQuery.data.tableId === table?.tableId &&
    coachStopPreviewQuery.data.tableNumber === table?.tableNumber
      ? coachStopPreviewQuery.data
      : null;
  const coachStopPreviewState: CoachStopPreviewState = !coachStopPreviewEnabled
    ? "IDLE"
    : coachStopPreviewQuery.isError || (coachStopPreviewQuery.data && !coachStopPreview)
      ? "ERROR"
      : coachStopPreviewQuery.isPending
        ? "LOADING"
        : coachStopPreview
          ? "READY"
          : "ERROR";
  const coachStopPreviewError =
    coachStopPreviewQuery.data && !coachStopPreview
      ? "预览返回的球桌或台单与当前页面不一致。请刷新后重新核对。"
      : coachStopPreviewQuery.isError
        ? `系统没有发出请求：${businessErrorMessage(
            coachStopPreviewQuery.error,
            "暂时无法读取本桌助教下钟预览",
          )}。请检查网络并重新读取助教预览。`
        : null;
  const saleReturnRecovery = readOrderItemSaleReturnRecovery(operator, table?.sessionId ?? null);
  const saleReturnOutcomeBlocksOrderWrites = saleReturnRecovery.status !== "NONE";
  const conflictIsolated = order?.status === "CONFLICT" || order?.syncState === "CONFLICT";
  const writePolicy = sessionOperationWritePolicy({
    gatewayWriteDisabled:
      writeDisabled ||
      unresolvedOpenExists ||
      sessionItemAddRecoveries.length > 0 ||
      itemAddOutcomeBlocksOrderWrites ||
      checkoutOutcomeBlocksOrderWrites ||
      officialPaymentOutcomeBlocksOrderWrites ||
      sessionBillingOutcomeBlocksOrderWrites ||
      sessionTransferOutcomeBlocksOrderWrites ||
      storePackageRecoveryLocked ||
      saleReturnOutcomeBlocksOrderWrites ||
      closeoutOutcomeBlocksWrites,
    orderReadFailed: orderQuery.isError,
    conflictIsolated,
  });
  const orderWriteDisabled = writePolicy.standardOrderWriteDisabled;
  const productAddWriteDisabled =
    writeDisabled ||
    unresolvedOpenExists ||
    orderQuery.isError ||
    conflictIsolated ||
    officialPaymentOutcomeBlocksOrderWrites ||
    sessionBillingOutcomeBlocksOrderWrites ||
    sessionTransferOutcomeBlocksOrderWrites ||
    storePackageRecoveryLocked ||
    saleReturnOutcomeBlocksOrderWrites ||
    itemAddWriteUnavailable;
  const recoverySkuIds = new Set(
    sessionItemAddRecoveries.map((recovery) => recovery.command.skuId),
  );
  const idleTargets = tables.filter(
    (candidate) =>
      candidate.state === "IDLE" &&
      candidate.sessionId === null &&
      candidate.sessionStartedAt === null &&
      candidate.billingState === "ENDED" &&
      candidate.billingPausedAt === null &&
      candidate.tableId !== table?.tableId &&
      candidate.tenantId === table?.tenantId &&
      candidate.storeId === table?.storeId,
  );
  const voidOperationErrorMessage =
    sessionBillingFeedbackAction === "VOID" ? sessionBillingFeedback : null;

  return (
    <CspModal
      open={table !== null}
      onOpenChange={(open) => !open && closeSessionDialog()}
      labelledBy={dialogTitleId}
      describedBy={dialogDescriptionId}
      className="session-dialog"
    >
      <h2 id={dialogTitleId}>{table?.tableNumber} 号台</h2>
      <p id={dialogDescriptionId}>
        {operatingFact
          ? `${operatingFact.statusLabel} · ${operatingFact.statusExplanation}`
          : "当前经营状态无法安全确认"}
      </p>
      {operatingFact ? <FrontdeskOperatingFactSummary fact={operatingFact} /> : null}
      {writeDisabled ? (
        <div className="disabled-reason">
          <AlertTriangle size={16} />
          {!operatingFactWriteSafe
            ? "当前经营状态或阻断原因尚未解除。为避免金额、台单或球台状态发生错误，当前只能查看。"
            : authorityExpired
              ? "门店主机的离线操作权限已过期。为防止两台设备同时改账，当前只能查看，不能新增或修改。"
              : "当前无法确认离线经营权威。系统没有使用上次的连接结果，当前只能查看，不能新增或修改。"}
        </div>
      ) : null}
      {openRecovery.status === "OWNED" &&
      openRecovery.recovery.command.tableId === table?.tableId ? (
        <div className="simple-action">
          <div className="disabled-reason" role="alert">
            <AlertTriangle size={16} />
            <span>
              {openRecovery.recovery.command.tableNumber} 号台的开台结果尚未确认。
              原请求已锁定价格方案“
              {openRecovery.recovery.command.pricingPlanName ?? "门店默认价格方案"}”以及原球台；
              {openRecovery.recovery.command.customerRequestEventId === null
                ? "无论当前显示空闲还是使用中，都只能用原请求安全重试确认结果。"
                : "原顾客开台申请也已锁定；无论当前显示空闲还是使用中，都只能用原请求安全重试确认结果。"}
            </span>
          </div>
          {!linkedOpenRecoveryAllowed ? (
            <div className="disabled-reason">
              当前账号已不具备办理原顾客开台申请的职责，请切回原账号或恢复原服务值班后重试。
            </div>
          ) : null}
          {openFeedback ? (
            <div className="dialog-error" role="alert" ref={openFeedbackRef} tabIndex={-1}>
              {openFeedback}
            </div>
          ) : null}
          <button
            className="button primary"
            type="button"
            disabled={openPending || writeDisabled || !linkedOpenRecoveryAllowed}
            onClick={() => onOpenRetry(openRecovery.recovery)}
          >
            {openPending ? "正在确认原请求..." : "使用同一请求安全重试"}
          </button>
        </div>
      ) : closeoutRecovery.status === "OWNED" &&
        closeoutRecovery.recovery.command.tableId === table?.tableId ? (
        canOperateStandardSession ? (
          <TableCloseoutAction
            action={closeoutRecovery.recovery.command.action}
            operatingFact={operatingFact}
            phase={
              closeoutPending
                ? { status: "SUBMITTING" }
                : {
                    status: "UNKNOWN",
                    message:
                      closeoutFeedback ??
                      "系统保留了原台局、操作时看到的状态和原请求。无论当前经营状态怎样变化，都可使用同一请求安全确认结果。",
                  }
            }
            writeDisabled={false}
            onSubmit={() => onCloseoutRetry(closeoutRecovery.recovery)}
          />
        ) : (
          <div className="disabled-reason">请切回原操作账号，由店长或收银员确认球台收尾结果。</div>
        )
      ) : table?.state === "IDLE" ? (
        <div className="simple-action">
          {customerRequestEventId !== null ? (
            <output className="info-band">
              这是员工工作中心中已经接单的顾客开台申请。这里只能为当前球桌选择价格方案；
              确认开台后会建立台单，并同时完成原顾客申请。
            </output>
          ) : customerRequestBindingRequired ? (
            <div className="disabled-reason" role="alert">
              原顾客申请与当前球桌尚未完成核对，不能从此窗口开台。请返回员工工作中心重新进入。
            </div>
          ) : null}
          {reservationTableWriteLocked ? (
            <div className="disabled-reason" role="alert">
              <AlertTriangle size={16} />
              这张球台正在核对预约入座结果。确认前不能从普通开台入口重复开台。
            </div>
          ) : null}
          <div className="dialog-facts">
            <span>
              <ShieldCheck size={16} />
              操作人：前台 01
            </span>
            <span>
              <Power size={16} />
              开台后会向测试灯控发送开灯指令；请现场确认灯是否点亮
            </span>
          </div>
          <section
            className="frontdesk-pricing-selection"
            aria-labelledby={pricingSelectionTitleId}
          >
            <header>
              <div>
                <h3 id={pricingSelectionTitleId}>本次开台价格方案</h3>
                <p>价格会在确认开台时保存到本单，之后门店调整方案也不会改动这次已选价格。</p>
              </div>
              <CircleDollarSign size={20} aria-hidden="true" />
            </header>
            {pricingOptionsQuery.isFetching ? (
              <output className="compact-empty" aria-live="polite">
                正在读取这张球台可用的价格方案，读取完成前不能开台...
              </output>
            ) : pricingOptionsQuery.isError ? (
              <div className="frontdesk-pricing-blocked" role="alert">
                <AlertTriangle size={16} />
                <span>
                  价格方案读取失败：
                  {businessErrorMessage(pricingOptionsQuery.error, "暂时无法读取门店价格")}。
                  请重新读取，确认方案后再开台。
                </span>
                <button
                  className="button secondary"
                  type="button"
                  onClick={() => void pricingOptionsQuery.refetch()}
                >
                  <RefreshCw size={15} />
                  重新读取价格方案
                </button>
              </div>
            ) : !pricingOptionsMatch ? (
              <div className="frontdesk-pricing-blocked" role="alert">
                <AlertTriangle size={16} />
                <span>
                  球台状态或门店范围已经变化，刚读取的价格方案不能用于当前球台。请关闭窗口刷新球台，再重新选择价格方案。
                </span>
              </div>
            ) : pricingOptions ? (
              <>
                <fieldset className="frontdesk-pricing-options">
                  <legend className="visually-hidden">价格方案</legend>
                  {pricingOptions.options.map((option) => {
                    const checked = option.id === selectedPricingPlan?.id;
                    const isDefault = option.id === pricingOptions.defaultPricingPlanId;
                    const optionBody = (
                      <>
                        <span className="frontdesk-pricing-option-heading">
                          <strong>{option.name}</strong>
                          {isDefault ? <b>球台默认</b> : null}
                        </span>
                        <span>{pricingPlanChargeSummary(option)}</span>
                        <small>{pricingPlanMinimumSummary(option)}</small>
                        <small>{pricingPlanRoundingSummary(option)}</small>
                        <small>{pricingPlanPauseSummary(option)}</small>
                        <PricingPlanTimeBandSummary plan={option} />
                      </>
                    );
                    return canOperateStandardSession ? (
                      <label
                        key={`${option.id}:${option.version}`}
                        className={
                          checked ? "frontdesk-pricing-option selected" : "frontdesk-pricing-option"
                        }
                      >
                        <input
                          type="radio"
                          name="frontdesk-pricing-plan"
                          checked={checked}
                          onChange={() => setSelectedPricingPlanId(option.id)}
                        />
                        <span>{optionBody}</span>
                      </label>
                    ) : (
                      <article
                        key={`${option.id}:${option.version}`}
                        className={
                          checked ? "frontdesk-pricing-option selected" : "frontdesk-pricing-option"
                        }
                      >
                        <span aria-hidden="true" className="frontdesk-pricing-readonly-marker">
                          {checked ? <Check size={16} /> : null}
                        </span>
                        <span>{optionBody}</span>
                      </article>
                    );
                  })}
                </fieldset>
                {selectedPricingPlan ? (
                  <output className="frontdesk-pricing-preview" aria-live="polite">
                    <strong>确认后会发生什么</strong>
                    <span>
                      本次使用“{selectedPricingPlan.name}”：
                      {pricingPlanChargeSummary(selectedPricingPlan)}；
                      {pricingPlanMinimumSummary(selectedPricingPlan)}；
                      {pricingPlanRoundingSummary(selectedPricingPlan)}；
                      {pricingPlanPauseSummary(selectedPricingPlan)}。
                    </span>
                    <PricingPlanTimeBandSummary plan={selectedPricingPlan} />
                  </output>
                ) : (
                  <div className="frontdesk-pricing-blocked" role="alert">
                    当前没有可确认的价格方案。请重新读取，确认方案后再开台。
                  </div>
                )}
              </>
            ) : null}
          </section>
          {canOperateStandardSession ? (
            <fieldset className="frontdesk-pricing-options">
              <legend>本次何时付款</legend>
              <label className={openPaymentTiming === "POSTPAID" ? "selected" : ""}>
                <input
                  type="radio"
                  name="frontdesk-open-payment-timing"
                  checked={openPaymentTiming === "POSTPAID"}
                  onChange={() => setOpenPaymentTiming("POSTPAID")}
                />
                <strong>结束时付款</strong>
                <span>先开台，结束计费后再按完整账单选择付款方式。</span>
              </label>
              <label className={openPaymentTiming === "PREPAID" ? "selected" : ""}>
                <input
                  type="radio"
                  name="frontdesk-open-payment-timing"
                  checked={openPaymentTiming === "PREPAID"}
                  disabled={selectedPricingPlan?.billingMode !== "PACKAGE"}
                  onChange={() => setOpenPaymentTiming("PREPAID")}
                />
                <strong>会员余额先付包时费</strong>
                <span>
                  只适用于包时方案。先核对并扣除会员余额，再建立台单；后续商品和助教费用结束时只补差额。
                </span>
              </label>
            </fieldset>
          ) : null}
          {openPaymentTiming === "PREPAID" && table && selectedPricingPlan ? (
            <MemberPrepaidOpenPanel
              operator={operator}
              table={table}
              pricingPlan={selectedPricingPlan}
              disabled={
                writeDisabled ||
                unresolvedOpenExists ||
                openStorageUnavailable ||
                closeoutOutcomeBlocksWrites ||
                reservationTableWriteLocked ||
                customerRequestEventId !== null ||
                !pricingOptionsReady
              }
              onCompleted={() => onClose()}
            />
          ) : null}
          {openFeedback ? (
            <div className="dialog-error" role="alert" ref={openFeedbackRef} tabIndex={-1}>
              {openFeedback}
            </div>
          ) : null}
          {canOperateStandardSession && openPaymentTiming === "POSTPAID" ? (
            <button
              className="button primary"
              type="button"
              disabled={
                openPending ||
                writeDisabled ||
                unresolvedOpenExists ||
                openStorageUnavailable ||
                closeoutOutcomeBlocksWrites ||
                reservationTableWriteLocked ||
                (customerRequestBindingRequired && customerRequestEventId === null) ||
                pricingSelection === null ||
                !pricingOptionsReady
              }
              onClick={() => {
                if (!table || reservationTableWriteLocked || !pricingSelection) return;
                onOpenRequested(table, pricingSelection, customerRequestEventId);
              }}
            >
              {openPending ? "正在开台..." : "确认开台"}
            </button>
          ) : !canOperateStandardSession ? (
            <div className="disabled-reason">
              当前岗位可查看球台状态；开台请由店长或收银员办理。
            </div>
          ) : null}
        </div>
      ) : table?.state === "AWAITING_CLEANING" ? (
        canOperateStandardSession && operatingFact ? (
          operatingFact.cleaningState === "AWAITING_CLEANING" ||
          operatingFact.cleaningState === "CLEANING_COMPLETED" ? (
            <>
              {combinedCheckoutCloseoutLocked ? (
                <div className="disabled-reason" role="alert">
                  <AlertTriangle size={16} />
                  原合并收款结果仍在核对。确认前不能清台或释放球台，避免原台单失去对应球台。
                </div>
              ) : null}
              <TableCloseoutAction
                action={
                  operatingFact.cleaningState === "AWAITING_CLEANING"
                    ? "CONFIRM_CLEANING"
                    : "RELEASE_TABLE"
                }
                operatingFact={operatingFact}
                phase={
                  closeoutPending
                    ? { status: "SUBMITTING" }
                    : closeoutFeedback
                      ? { status: "ERROR", message: closeoutFeedback }
                      : { status: "IDLE" }
                }
                writeDisabled={
                  writeDisabled ||
                  unresolvedOpenExists ||
                  closeoutWriteUnavailable ||
                  combinedCheckoutCloseoutLocked
                }
                onSubmit={() => {
                  if (!table || combinedCheckoutCloseoutLocked) return;
                  onCloseoutRequested(
                    table,
                    operatingFact.cleaningState === "AWAITING_CLEANING"
                      ? "CONFIRM_CLEANING"
                      : "RELEASE_TABLE",
                  );
                }}
              />
            </>
          ) : (
            <div className="disabled-reason">
              当前清洁步骤无法安全确认，请刷新当前经营状态后再操作。
            </div>
          )
        ) : (
          <div className="disabled-reason">
            当前岗位可查看已结账结果；确认清洁和释放球台请由店长或收银员办理。
          </div>
        )
      ) : table?.state === "IN_USE" ? (
        <>
          <div className="dialog-tabs" role="tablist">
            {productOrderSelected ? (
              <button
                type="button"
                className={tab === "ORDER" ? "is-active" : ""}
                onClick={() => selectSessionTab("ORDER")}
              >
                <PackagePlus size={16} />
                商品
              </button>
            ) : null}
            {canOperateCoachSession ? (
              <button
                type="button"
                className={tab === "COACH" ? "is-active" : ""}
                onClick={() => selectSessionTab("COACH")}
              >
                <UsersRound size={16} />
                助教服务
              </button>
            ) : null}
            <button
              type="button"
              className={tab === "BILLING" ? "is-active" : ""}
              onClick={() => selectSessionTab("BILLING")}
            >
              <Clock3 size={16} />
              计费操作
            </button>
            {canOperateStandardSession ? (
              <button
                type="button"
                className={tab === "TRANSFER" ? "is-active" : ""}
                onClick={() => selectSessionTab("TRANSFER")}
              >
                <ArrowRightLeft size={16} />
                转台
              </button>
            ) : null}
            {canOpenCheckoutTab ? (
              <button
                type="button"
                className={tab === "CHECKOUT" ? "is-active" : ""}
                onClick={() => selectSessionTab("CHECKOUT")}
              >
                <Banknote size={16} />
                结账
              </button>
            ) : null}
          </div>
          {!productOrderSelected ? (
            <div className="disabled-reason">
              <AlertTriangle size={16} />
              商品正式下单模块未选择；商品目录、加单和退单已禁用
            </div>
          ) : null}
          {orderQuery.isPending ? (
            <div className="loading-state">正在读取台单与金额...</div>
          ) : orderQuery.isError ? (
            <div className="error-band">
              台单读取失败；商品、转台和收款已暂停，以免金额或球台状态发生错误。
            </div>
          ) : null}
          {conflictIsolated ? (
            <div className="disabled-reason">
              <AlertTriangle size={16} />
              订单存在同步冲突，商品、转台和结账已隔离；请到“工作中心—异常核对”查看只读业务定位并保留原始单据。该区域当前仅只读，本页不能转交或处置
            </div>
          ) : null}
          {sessionItemAddRecoveries.length > 0 ? (
            <div className="simple-action" role="alert" aria-live="assertive">
              <div className="disabled-reason">
                <AlertTriangle size={16} />
                <span>
                  以下商品的加单结果尚未确认。结账等后续操作已暂停；可以逐笔使用原请求安全重试，也可以继续添加其他商品。
                </span>
              </div>
              <div className="clear-table-actions">
                {sessionItemAddRecoveries.map((recovery) =>
                  recovery.command.kind === "GIFT_ORDER_ITEM" ? (
                    <GiftOrderItemRecoveryAuthorization
                      key={recovery.command.idempotencyKey}
                      recovery={recovery}
                      pending={
                        pendingSkuIds.has(recovery.command.skuId) ||
                        giftAuthorizationSkuId === recovery.command.skuId
                      }
                      disabled={writeDisabled}
                      onRetry={(authorizerLogin, credential) => {
                        void retryGiftOrderItem(recovery, authorizerLogin, credential);
                      }}
                    />
                  ) : (
                    <button
                      className="button secondary"
                      type="button"
                      key={recovery.command.idempotencyKey}
                      disabled={pendingSkuIds.has(recovery.command.skuId) || writeDisabled}
                      onClick={() => dispatchOrderItemAddRecovery(recovery)}
                    >
                      {pendingSkuIds.has(recovery.command.skuId)
                        ? `正在确认“${recovery.command.productName}”…`
                        : `安全重试“${recovery.command.productName}”`}
                    </button>
                  ),
                )}
              </div>
            </div>
          ) : itemAddWriteUnavailable ? (
            <div className="disabled-reason" role="alert">
              <AlertTriangle size={16} />
              当前有其他账号或其他台单的商品加单待确认，新的商品加单已暂停；台单仍可查看。
            </div>
          ) : null}
          {tab === "ORDER" && productOrderSelected ? (
            <>
              <OrderEditor
                order={order}
                products={productsQuery.data ?? []}
                productsLoading={productsQuery.isPending}
                productsError={productsQuery.isError}
                pendingSkuIds={pendingSkuIds}
                recoverySkuIds={recoverySkuIds}
                productFeedback={productFeedback}
                productFeedbackKind={productFeedbackKind}
                barcodeLookupPending={barcodeLookupPending}
                saleReturnRecovery={saleReturnRecovery}
                saleReturnPendingItemId={saleReturnPendingItemId}
                saleReturnFeedback={saleReturnFeedback}
                saleReturnFeedbackKind={saleReturnFeedbackKind}
                saleReturnDisabled={
                  writeDisabled ||
                  unresolvedOpenExists ||
                  orderQuery.isError ||
                  conflictIsolated ||
                  itemAddOutcomeBlocksOrderWrites ||
                  checkoutOutcomeBlocksOrderWrites ||
                  officialPaymentOutcomeBlocksOrderWrites ||
                  sessionBillingOutcomeBlocksOrderWrites ||
                  sessionTransferOutcomeBlocksOrderWrites ||
                  closeoutOutcomeBlocksWrites
                }
                disabled={
                  productAddWriteDisabled ||
                  !productOrderSelected ||
                  giftAuthorizationSkuId !== null
                }
                effectiveRoles={effectiveRoles}
                onAdd={(product) => {
                  requestOrderItemAdd(product);
                }}
                onScan={() => setProductScannerOpen(true)}
                onGiftAdd={(submission) => {
                  void requestGiftOrderItem(submission);
                }}
                onSaleableReturn={requestSaleableReturn}
                onSaleableReturnRetry={dispatchSaleableReturn}
              />
              {productScannerOpen ? (
                <Suspense fallback={<div className="loading-state">正在打开扫码…</div>}>
                  <MobileBarcodeScanner
                    purpose="SALE"
                    onDetected={(barcode) => {
                      setProductScannerOpen(false);
                      void requestBarcodeOrderItemAdd(barcode);
                    }}
                    onClose={() => setProductScannerOpen(false)}
                  />
                </Suspense>
              ) : null}
              {order ? (
                <StorePackageOrderPanel
                  operator={operator}
                  effectiveRoles={effectiveRoles}
                  order={order}
                  expectedOrderVersion={order.aggregateVersion ?? null}
                  onRecoveryLockedChange={onStorePackageRecoveryLockedChange}
                  disabled={
                    productAddWriteDisabled ||
                    !productOrderSelected ||
                    giftAuthorizationSkuId !== null ||
                    conflictIsolated
                  }
                />
              ) : null}
            </>
          ) : null}
          {tab === "COACH" && canOperateCoachSession && table.sessionId ? (
            <CoachSessionOperationsPanel
              operator={operator}
              effectiveRoles={effectiveRoles}
              table={table}
            />
          ) : null}
          {tab === "BILLING" ? (
            <>
              {sessionBillingFeedback && sessionBillingFeedbackAction !== "VOID" ? (
                <div className="dialog-error" role="alert" ref={billingFeedbackRef} tabIndex={-1}>
                  {sessionBillingFeedback}
                </div>
              ) : null}
              <BillingOperationPanel
                order={order}
                table={table}
                effectiveRoles={effectiveRoles}
                pending={sessionBillingPending}
                disabled={orderWriteDisabled}
                billingStateChangeDisabled={sessionBillingWriteUnavailable || !order}
                coachStopPreview={coachStopPreview}
                coachStopPreviewState={coachStopPreviewState}
                coachStopPreviewError={coachStopPreviewError}
                completedCoachStopResult={coachStopResultQuery.data}
                onCoachStopPreviewRefresh={() => {
                  void coachStopPreviewQuery.refetch();
                }}
                onContinueToCheckout={() => selectSessionTab("CHECKOUT")}
                voidErrorMessage={voidOperationErrorMessage}
                onAction={(action) => {
                  if (
                    (action.type === "PAUSE" ||
                      action.type === "RESUME" ||
                      action.type === "END_EARLY" ||
                      action.type === "ADJUST_START" ||
                      action.type === "VOID") &&
                    action.reason &&
                    order
                  ) {
                    onSessionBillingRequested(
                      table,
                      order,
                      action.type,
                      action.reason,
                      action.type === "END_EARLY" ? action.coachStopPreview : null,
                      action.type === "ADJUST_START" ? action.preview : null,
                    );
                  }
                }}
              />
            </>
          ) : null}
          {tab === "TRANSFER" && canOperateStandardSession ? (
            <div className="transfer-panel">
              {sessionTransferFeedback ? (
                <div className="dialog-error" role="alert" ref={transferFeedbackRef} tabIndex={-1}>
                  {sessionTransferFeedback}
                </div>
              ) : null}
              <label htmlFor={targetTableSelectId}>目标球台</label>
              <select
                id={targetTableSelectId}
                value={targetTableId}
                onChange={(event) => setTargetTableId(event.target.value)}
              >
                <option value="">请选择空闲球台</option>
                {idleTargets.map((candidate) => (
                  <option key={candidate.tableId} value={candidate.tableId}>
                    {candidate.tableNumber} 号台 · {candidate.area}
                  </option>
                ))}
              </select>
              <label>
                转台原因
                <input
                  value={transferReason}
                  onChange={(event) => setTransferReason(event.target.value)}
                  placeholder="例如：顾客要求换到 8 号台"
                  maxLength={200}
                />
              </label>
              <button
                className="button primary"
                type="button"
                disabled={
                  !targetTableId ||
                  !order ||
                  transferReason.trim().length < 2 ||
                  sessionTransferPending ||
                  sessionTransferWriteUnavailable ||
                  orderWriteDisabled
                }
                onClick={() => {
                  const target = idleTargets.find(
                    (candidate) => candidate.tableId === targetTableId,
                  );
                  if (!table || !order || !target) return;
                  onSessionTransferRequested(table, target, order, transferReason.trim());
                }}
              >
                <ArrowRightLeft size={16} />
                {sessionTransferPending ? "正在确认转台…" : "确认转台"}
              </button>
            </div>
          ) : null}
          {tab === "CHECKOUT" && canOpenCheckoutTab ? (
            <>
              {canCollectPayment ? (
                <CheckoutKindSelector
                  value={checkoutKind}
                  wholeDisabled={splitPlanLocked || combinedCheckoutLocked}
                  splitDisabled={!order || order.totalFen <= 0 || combinedCheckoutLocked}
                  combinedDisabled={splitPlanLocked}
                  onChange={setCheckoutKind}
                />
              ) : null}
              {canCollectPayment && checkoutKind === "WHOLE" ? (
                <CheckoutPanel
                  key={order?.id ?? table?.sessionId ?? "checkout"}
                  method={paymentMethod}
                  onMethod={setPaymentMethod}
                  order={order}
                  pending={checkoutPending}
                  officialPaymentPending={officialPaymentPending}
                  disabled={
                    orderWriteDisabled ||
                    checkoutWriteUnavailable ||
                    officialPaymentWriteUnavailable ||
                    splitPlanLocked ||
                    combinedCheckoutLocked
                  }
                  officialPaymentOnline={status?.mode === "ONLINE"}
                  offlineMemberBalanceDeferred={status?.mode === "OFFLINE"}
                  memberFundsAvailable={status?.serviceHealth.memberFunds ?? false}
                  manualPaymentMethods={status?.manualPaymentMethods}
                  feedback={checkoutFeedback}
                  officialPaymentFeedback={officialPaymentFeedback}
                  onFeedbackClear={onCheckoutFeedbackClear}
                  onOfficialPaymentFeedbackClear={onOfficialPaymentFeedbackClear}
                  onRechargeMember={(memberId) => {
                    const query = new URLSearchParams({ memberId, from: "checkout" });
                    navigate(`/members?${query.toString()}`);
                  }}
                  onCheckout={(member) => {
                    if (!table || !order || checkoutPending) return;
                    onCheckoutRequested(
                      table,
                      order,
                      order.totalFen === 0 ? "CASH" : paymentMethod,
                      member,
                    );
                  }}
                  onOfficialPayment={(rail) => {
                    if (!table || !order || officialPaymentPending) return;
                    onOfficialPaymentRequested(table, order, rail);
                  }}
                />
              ) : null}
              {canCollectPayment && order && table ? (
                <div hidden={checkoutKind !== "SPLIT"}>
                  <CheckoutSplitPanel
                    key={`split:${order.id}`}
                    order={order}
                    table={table}
                    operator={operator}
                    effectiveRoles={effectiveRoles}
                    disabled={
                      orderWriteDisabled ||
                      checkoutWriteUnavailable ||
                      officialPaymentWriteUnavailable ||
                      checkoutPending ||
                      officialPaymentPending ||
                      combinedCheckoutLocked
                    }
                    onCompleted={() => {
                      void Promise.all([
                        queryClient.invalidateQueries({
                          queryKey: ["session-order", order.sessionId],
                        }),
                        queryClient.invalidateQueries({ queryKey: ["tables"] }),
                      ]);
                    }}
                    onPlanChanged={() => {
                      void Promise.all([
                        queryClient.invalidateQueries({
                          queryKey: ["session-order", order.sessionId],
                        }),
                        queryClient.invalidateQueries({ queryKey: ["tables"] }),
                      ]);
                    }}
                    onPlanLockedChange={onSplitPlanLockedChange}
                  />
                </div>
              ) : checkoutKind === "SPLIT" ? (
                <div className="loading-state">正在读取本单金额...</div>
              ) : null}
              {order && table ? (
                <div hidden={checkoutKind !== "COMBINED"}>
                  <CombinedCheckoutPanel
                    key={`combined:${operator.tenantId}:${operator.storeId}:${operator.id}`}
                    order={order}
                    table={table}
                    tables={tables}
                    operator={operator}
                    effectiveRoles={effectiveRoles}
                    active={checkoutKind === "COMBINED"}
                    disabled={
                      orderWriteDisabled ||
                      checkoutWriteUnavailable ||
                      officialPaymentWriteUnavailable ||
                      checkoutPending ||
                      officialPaymentPending ||
                      splitPlanLocked
                    }
                    onCompleted={(sessionIds) => {
                      void Promise.all([
                        ...sessionIds.map((sessionId) =>
                          queryClient.invalidateQueries({
                            queryKey: ["session-order", sessionId],
                          }),
                        ),
                        queryClient.invalidateQueries({ queryKey: ["tables"] }),
                      ]);
                    }}
                    onRecoveryLockedChange={onCombinedCheckoutLockedChange}
                    managedRecoveryView={combinedCheckoutRecovery}
                    onRecoveryViewChange={onCombinedCheckoutRecoveryChange}
                  />
                </div>
              ) : checkoutKind === "COMBINED" ? (
                <div className="loading-state">正在读取可合并台单...</div>
              ) : null}
            </>
          ) : null}
        </>
      ) : (
        <div className="disabled-reason">该球台当前不可操作</div>
      )}
      <button
        className="dialog-close icon-button"
        type="button"
        title="关闭"
        onClick={closeSessionDialog}
      >
        <X size={18} />
      </button>
    </CspModal>
  );
}

export type DisplayOrderItemGroup = {
  key: string;
  skuId: string;
  name: string;
  unitPriceFen: number;
  lineType: OrderItemRecord["lineType"];
  listUnitPriceFen: number;
  unitDiscountFen: number;
  evidence: OrderItemRecord["evidence"];
  quantity: number;
  returnedQuantity: number;
  availableQuantity: number;
  batches: OrderItemRecord[];
};

const orderItemSalesConditionFields = [
  "priceSnapshotId",
  "priceSnapshotVersion",
  "specification",
  "variantId",
  "discountId",
  "discountFen",
  "discountRateBasisPoints",
  "salesConditionId",
  "salesConditionVersion",
] as const;

function orderItemDisplayGroupKey(item: OrderItemRecord): string {
  const extensibleItem = item as unknown as Record<string, unknown>;
  return JSON.stringify([
    item.skuId,
    item.name,
    item.unitPriceFen,
    item.lineType ?? "PRODUCT",
    item.pricingSource ?? "CATALOG_PRICE",
    item.listUnitPriceFen ?? item.unitPriceFen,
    item.unitDiscountFen ?? 0,
    item.adjustmentReason ?? null,
    item.authorizedByRole ?? null,
    item.authorizedById ?? null,
    item.evidence,
    ...orderItemSalesConditionFields.map((field) => [field, extensibleItem[field] ?? null]),
  ]);
}

export function groupOrderItemsForDisplay(
  items: readonly OrderItemRecord[],
): DisplayOrderItemGroup[] {
  const groups = new Map<string, DisplayOrderItemGroup>();
  for (const item of items) {
    const key = orderItemDisplayGroupKey(item);
    const existing = groups.get(key);
    if (existing) {
      existing.quantity += item.quantity;
      existing.returnedQuantity += item.returnedQuantity;
      existing.availableQuantity += item.quantity - item.returnedQuantity;
      existing.batches.push(item);
      continue;
    }
    groups.set(key, {
      key,
      skuId: item.skuId,
      name: item.name,
      unitPriceFen: item.unitPriceFen,
      lineType: item.lineType,
      listUnitPriceFen: item.listUnitPriceFen ?? item.unitPriceFen,
      unitDiscountFen: item.unitDiscountFen ?? 0,
      evidence: item.evidence,
      quantity: item.quantity,
      returnedQuantity: item.returnedQuantity,
      availableQuantity: item.quantity - item.returnedQuantity,
      batches: [item],
    });
  }
  return [...groups.values()];
}

function saleReturnQuantityOptions(maximum: number): number[] {
  const options: number[] = [];
  for (let quantity = 1; quantity <= maximum; quantity += 1) options.push(quantity);
  return options;
}

export type GiftOrderItemDraftSubmission = {
  product: ProductSaleCatalogItem;
  quantity: number;
  reason: string;
  authorizerLogin: string;
  credential: string;
};

function GiftOrderItemRecoveryAuthorization({
  recovery,
  pending,
  disabled,
  onRetry,
}: {
  recovery: OrderItemAddRecovery;
  pending: boolean;
  disabled: boolean;
  onRetry: (authorizerLogin: string, credential: string) => void;
}): React.JSX.Element | null {
  const [authorizerLogin, setAuthorizerLogin] = useState("");
  const [credential, setCredential] = useState("");
  if (recovery.command.kind !== "GIFT_ORDER_ITEM") return null;
  const command = recovery.command;
  const canRetry =
    authorizerLogin.trim().length > 0 && credential.length > 0 && !pending && !disabled;
  return (
    <form
      className="gift-order-recovery"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canRetry) return;
        const submittedCredential = credential;
        setCredential("");
        onRetry(authorizerLogin.trim(), submittedCredential);
      }}
    >
      <div className="gift-order-recovery-summary">
        <Gift size={17} aria-hidden="true" />
        <span>
          <strong>门店赠品“{command.productName}”结果待确认</strong>
          <small>
            已锁定：原价 {formatFee(command.listUnitPriceFen)} × {command.quantity}
            、全额减免、顾客应付0元；赠送原因“{command.reason}
            ”。原授权人：{command.originalAuthorizerDisplayName}
            。商品、数量、原因和原授权人不能修改。
          </small>
        </span>
      </div>
      <p>
        请由最初授权的同一位店长或老板重新输入账号和凭证。系统核对为同一人后，才会使用原请求号安全重试。
      </p>
      <div className="gift-order-recovery-fields">
        <label>
          原授权人账号
          <input
            {...loginCredentialAttributes("username")}
            value={authorizerLogin}
            disabled={pending || disabled}
            onChange={(event) => setAuthorizerLogin(event.target.value)}
          />
        </label>
        <label>
          原授权人凭证
          <input
            type="password"
            {...loginCredentialAttributes("password")}
            value={credential}
            disabled={pending || disabled}
            onChange={(event) => setCredential(event.target.value)}
          />
        </label>
      </div>
      <button className="button primary" type="submit" disabled={!canRetry}>
        {pending ? "正在核对原授权并确认结果…" : "重新授权并安全重试原赠品"}
      </button>
    </form>
  );
}

export function OrderEditor({
  order,
  products,
  productsLoading,
  productsError,
  pendingSkuIds,
  recoverySkuIds,
  productFeedback,
  productFeedbackKind,
  barcodeLookupPending = false,
  saleReturnRecovery = { status: "NONE" },
  saleReturnPendingItemId = null,
  saleReturnFeedback = null,
  saleReturnFeedbackKind = "INFO",
  saleReturnDisabled = false,
  disabled,
  effectiveRoles,
  onAdd,
  onScan,
  onGiftAdd,
  onSaleableReturn,
  onSaleableReturnRetry,
}: {
  order: OrderDetail | undefined;
  products: ProductSaleCatalogItem[];
  productsLoading: boolean;
  productsError: boolean;
  pendingSkuIds?: ReadonlySet<string>;
  recoverySkuIds?: ReadonlySet<string>;
  productFeedback?: string | null;
  productFeedbackKind?: "INFO" | "SUCCESS" | "ERROR";
  barcodeLookupPending?: boolean;
  saleReturnRecovery?: OrderItemSaleReturnRecoveryView;
  saleReturnPendingItemId?: string | null;
  saleReturnFeedback?: string | null;
  saleReturnFeedbackKind?: "INFO" | "SUCCESS" | "ERROR";
  saleReturnDisabled?: boolean;
  disabled: boolean;
  effectiveRoles: readonly OperatorRole[];
  onAdd: (product: ProductSaleCatalogItem) => void;
  onScan?: () => void;
  onGiftAdd?: (submission: GiftOrderItemDraftSubmission) => void;
  onSaleableReturn?: (
    item: OrderItemRecord,
    quantity: number,
    physicalDisposition: SaleReturnPhysicalDisposition,
    reason: string,
  ) => void;
  onSaleableReturnRetry?: (recovery: OrderItemSaleReturnRecovery) => void;
}): React.JSX.Element {
  const [expandedItemGroups, setExpandedItemGroups] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [returnDraft, setReturnDraft] = useState<{
    itemId: string;
    quantity: number;
    reason: string;
    physicalDisposition: SaleReturnPhysicalDisposition;
    physicalReturnConfirmed: boolean;
  } | null>(null);
  const [giftDraft, setGiftDraft] = useState<{
    skuId: string;
    quantity: number;
    reason: string;
    authorizerLogin: string;
    credential: string;
  } | null>(null);
  const currentOrderSessionId = order?.sessionId;
  const previousOrderSessionId = useRef(currentOrderSessionId);
  useEffect(() => {
    if (previousOrderSessionId.current === currentOrderSessionId) return;
    previousOrderSessionId.current = currentOrderSessionId;
    setReturnDraft(null);
    setGiftDraft(null);
    setExpandedItemGroups(new Set());
  }, [currentOrderSessionId]);
  useEffect(() => {
    if (saleReturnFeedbackKind === "SUCCESS") setReturnDraft(null);
  }, [saleReturnFeedbackKind]);
  useEffect(() => {
    if (giftDraft && recoverySkuIds?.has(giftDraft.skuId)) setGiftDraft(null);
  }, [giftDraft, recoverySkuIds]);
  const itemGroups = groupOrderItemsForDisplay(order?.items ?? []);
  const canAddOrderItems = canAddOrderItemsForRoles(effectiveRoles);
  const canReturnSaleableItems = effectiveRoles.includes("CASHIER");
  const saleableReturnOrderEligible =
    order?.status === "OPEN" &&
    order.paidFen === 0 &&
    order.refundedFen === 0 &&
    order.payments.length === 0 &&
    order.refunds.length === 0;
  const canViewReturnReadOnlyNotice =
    !canReturnSaleableItems &&
    (effectiveRoles.includes("OWNER") || effectiveRoles.includes("MANAGER"));
  const ownedSaleReturn =
    saleReturnRecovery.status === "OWNED" &&
    saleReturnRecovery.recovery.command.sessionId === order?.sessionId
      ? saleReturnRecovery.recovery
      : null;
  return (
    <div className="order-editor">
      {productFeedback ? (
        <div
          className={`inline-operation-feedback is-${(productFeedbackKind ?? "INFO").toLowerCase()}`}
          role={productFeedbackKind === "ERROR" ? "alert" : "status"}
          aria-live="polite"
        >
          {productFeedbackKind === "ERROR" ? (
            <AlertTriangle size={16} aria-hidden="true" />
          ) : (
            <PackagePlus size={16} aria-hidden="true" />
          )}
          <span>{productFeedback}</span>
        </div>
      ) : null}
      {saleReturnFeedback ? (
        <div
          className={`inline-operation-feedback is-${saleReturnFeedbackKind.toLowerCase()}`}
          role={saleReturnFeedbackKind === "ERROR" ? "alert" : "status"}
          aria-live="polite"
        >
          {saleReturnFeedbackKind === "ERROR" ? (
            <AlertTriangle size={16} aria-hidden="true" />
          ) : (
            <PackagePlus size={16} aria-hidden="true" />
          )}
          <span>{saleReturnFeedback}</span>
        </div>
      ) : null}
      {canAddOrderItems ? (
        <div className="product-order-entry">
          <section className="barcode-action-strip" aria-label="扫码加商品">
            <div>
              <ScanBarcode size={22} aria-hidden="true" />
              <span>
                <strong>扫商品条码加入当前台单</strong>
                <small>
                  先核对本店商品、当前售价和库存，再安全加一次；相机不可用时可手工输入。
                </small>
              </span>
            </div>
            <div>
              <button
                className="button secondary"
                type="button"
                disabled={disabled || barcodeLookupPending}
                onClick={onScan}
              >
                {barcodeLookupPending ? "正在查找商品…" : "扫码加商品"}
              </button>
            </div>
          </section>
          <div className="gift-order-guidance">
            <Gift size={17} aria-hidden="true" />
            <span>
              <strong>门店赠品</strong>
              顾客应付增加0元，但会真实扣库存并记录成本。每次赠送都要填写具体原因，并由店长或老板账号完成一次性授权。
            </span>
          </div>
          <div className="product-picker">
            {productsLoading ? (
              <div className="compact-empty">正在读取商品...</div>
            ) : productsError ? (
              <div className="disabled-reason">商品目录读取失败；不影响转台和现金结账。</div>
            ) : products.length ? (
              products.map((product) => {
                const stockReason = product.canSell ? null : product.unavailableReason;
                const consequenceLabel = productSaleAddConsequenceLabel(product);
                const skuPending = pendingSkuIds?.has(product.skuId) ?? false;
                const skuNeedsRecovery = recoverySkuIds?.has(product.skuId) ?? false;
                const giftSelected = giftDraft?.skuId === product.skuId;
                return (
                  <div className="product-picker-item" key={product.skuId}>
                    <button
                      aria-label={`${product.name} ${formatFee(product.unitPriceFen)} ${consequenceLabel}${
                        product.evidence === "SIMULATED" ? " 演示商品" : ""
                      }`}
                      className={`product-primary-action${
                        !product.canSell
                          ? " is-sold-out"
                          : product.inventoryManaged && product.stockOnHand === 1
                            ? " is-low-stock"
                            : ""
                      }`}
                      type="button"
                      disabled={skuPending || skuNeedsRecovery || disabled || !product.canSell}
                      title={stockReason ?? undefined}
                      onClick={() => onAdd(product)}
                    >
                      <PackagePlus size={16} />
                      <span>{product.name}</span>
                      <strong>{formatFee(product.unitPriceFen)}</strong>
                      <small>
                        {skuPending
                          ? "正在加入订单…"
                          : skuNeedsRecovery
                            ? "结果待确认，请用上方按钮安全重试"
                            : `${consequenceLabel}${
                                product.evidence === "SIMULATED" ? " · 演示商品" : ""
                              }`}
                      </small>
                    </button>
                    <button
                      className="product-gift-action"
                      type="button"
                      aria-expanded={giftSelected}
                      aria-label={`${product.name} 作为门店赠品`}
                      disabled={
                        skuPending ||
                        skuNeedsRecovery ||
                        disabled ||
                        !product.canSell ||
                        !product.inventoryManaged ||
                        product.stockOnHand === null
                      }
                      onClick={() =>
                        setGiftDraft({
                          skuId: product.skuId,
                          quantity: 1,
                          reason: "",
                          authorizerLogin: "",
                          credential: "",
                        })
                      }
                    >
                      <Gift size={15} aria-hidden="true" />
                      <span>门店赠品</span>
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="compact-empty">当前没有可售商品</div>
            )}
          </div>
          {giftDraft
            ? (() => {
                const giftProduct = products.find((product) => product.skuId === giftDraft.skuId);
                if (!giftProduct) return null;
                const stockLimit = giftProduct.stockOnHand ?? 0;
                const maximumQuantity = Math.max(1, Math.min(99, stockLimit));
                const reasonLength = [...giftDraft.reason.trim()].length;
                const originalAmountFen = giftProduct.unitPriceFen * giftDraft.quantity;
                const valid =
                  reasonLength >= 2 &&
                  reasonLength <= 200 &&
                  giftDraft.authorizerLogin.trim().length > 0 &&
                  giftDraft.credential.length > 0 &&
                  giftDraft.quantity >= 1 &&
                  giftDraft.quantity <= maximumQuantity &&
                  !disabled;
                return (
                  <form
                    className="gift-order-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (!valid || !onGiftAdd) return;
                      const submission = {
                        product: giftProduct,
                        quantity: giftDraft.quantity,
                        reason: giftDraft.reason.trim(),
                        authorizerLogin: giftDraft.authorizerLogin.trim(),
                        credential: giftDraft.credential,
                      };
                      setGiftDraft((current) =>
                        current ? { ...current, credential: "" } : current,
                      );
                      onGiftAdd(submission);
                    }}
                  >
                    <div className="gift-order-form-heading">
                      <span>
                        <strong>赠送“{giftProduct.name}”</strong>
                        <small>授权只用于这一次赠送；提交其他赠品时要重新授权。</small>
                      </span>
                      <button
                        className="icon-button"
                        type="button"
                        title="取消赠品"
                        aria-label="取消赠品"
                        onClick={() => setGiftDraft(null)}
                      >
                        <X size={17} aria-hidden="true" />
                      </button>
                    </div>
                    <label>
                      赠送数量
                      <select
                        aria-label={`${giftProduct.name} 赠送数量`}
                        value={giftDraft.quantity}
                        onChange={(event) =>
                          setGiftDraft((current) =>
                            current
                              ? { ...current, quantity: Number(event.target.value) }
                              : current,
                          )
                        }
                      >
                        {saleReturnQuantityOptions(maximumQuantity).map((quantity) => (
                          <option key={`${giftProduct.skuId}:gift:${quantity}`} value={quantity}>
                            {quantity} 件
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      赠送原因（2–200 字）
                      <textarea
                        aria-label={`${giftProduct.name} 赠送原因`}
                        maxLength={200}
                        value={giftDraft.reason}
                        placeholder="例如：顾客生日赠送"
                        onChange={(event) =>
                          setGiftDraft((current) =>
                            current ? { ...current, reason: event.target.value } : current,
                          )
                        }
                      />
                      <small>{reasonLength}/200 字，请写明本次为什么赠送</small>
                    </label>
                    <fieldset className="gift-order-preview">
                      <legend>提交后会发生</legend>
                      <span>
                        原价：{formatFee(giftProduct.unitPriceFen)} × {giftDraft.quantity}
                      </span>
                      <span>全额减免：-{formatFee(originalAmountFen)}</span>
                      <strong>顾客应付增加：{formatFee(0)}</strong>
                      <span>
                        库存：现存 {stockLimit} {giftProduct.unit}，赠送后预计剩{" "}
                        {Math.max(0, stockLimit - giftDraft.quantity)} {giftProduct.unit}
                        ；提交成功会真实扣库存并记录成本。
                      </span>
                    </fieldset>
                    <div className="gift-order-authorization">
                      <strong>店长或老板一次性授权</strong>
                      <label>
                        授权账号
                        <input
                          {...loginCredentialAttributes("username")}
                          value={giftDraft.authorizerLogin}
                          onChange={(event) =>
                            setGiftDraft((current) =>
                              current
                                ? { ...current, authorizerLogin: event.target.value }
                                : current,
                            )
                          }
                        />
                      </label>
                      <label>
                        授权凭证
                        <input
                          type="password"
                          {...loginCredentialAttributes("password")}
                          value={giftDraft.credential}
                          onChange={(event) =>
                            setGiftDraft((current) =>
                              current ? { ...current, credential: event.target.value } : current,
                            )
                          }
                        />
                      </label>
                    </div>
                    <button
                      className="button primary"
                      type="submit"
                      disabled={!valid || !onGiftAdd}
                    >
                      <Gift size={16} aria-hidden="true" />
                      授权并提交门店赠品
                    </button>
                  </form>
                );
              })()
            : null}
        </div>
      ) : (
        <div className="disabled-reason">当前岗位可查看商品记录；直接加单请由收银员办理。</div>
      )}
      <div className="line-items">
        <h3>已加商品</h3>
        {itemGroups.length ? (
          itemGroups.map((group) => {
            const expanded = expandedItemGroups.has(group.key);
            const showBatches = group.batches.length === 1 || expanded;
            const giftGroup = group.lineType === "GIFT";
            return (
              <div className="line-item-group" key={group.key}>
                <div className="line-item">
                  <span>
                    <strong>
                      {giftGroup ? <span className="gift-order-badge">门店赠品</span> : null}
                      {group.name} × {group.availableQuantity}
                    </strong>
                    <small>
                      原加 {group.quantity} 件 · 已退 {group.returnedQuantity} ·{" "}
                      {giftGroup ? "顾客应付单价" : "单价"} {formatFee(group.unitPriceFen)}
                    </small>
                    {giftGroup ? (
                      <small>
                        原价 {formatFee(group.listUnitPriceFen)} · 全额减免{" "}
                        {formatFee(group.unitDiscountFen)}
                      </small>
                    ) : null}
                  </span>
                  <span>{formatFee(group.availableQuantity * group.unitPriceFen)}</span>
                </div>
                {group.batches.length > 1 ? (
                  <button
                    className="order-item-batch-toggle"
                    type="button"
                    aria-expanded={expanded}
                    onClick={() =>
                      setExpandedItemGroups((current) => {
                        const next = new Set(current);
                        if (next.has(group.key)) next.delete(group.key);
                        else next.add(group.key);
                        return next;
                      })
                    }
                  >
                    {expanded ? "收起" : "查看"} {group.batches.length} 笔加单记录
                  </button>
                ) : null}
                {showBatches ? (
                  <fieldset className="order-item-batches">
                    <legend className="visually-hidden">{group.name} 加单批次</legend>
                    {group.batches.map((item, index) => {
                      const availableQuantity = item.quantity - item.returnedQuantity;
                      const selected = returnDraft?.itemId === item.id;
                      const product = products.find((candidate) => candidate.skuId === item.skuId);
                      const saleableReturnSupported = product?.inventoryManaged === true;
                      const reasonLength = returnDraft ? [...returnDraft.reason.trim()].length : 0;
                      const returnValid =
                        selected &&
                        returnDraft.physicalReturnConfirmed &&
                        reasonLength >= 2 &&
                        reasonLength <= 200 &&
                        returnDraft.quantity >= 1 &&
                        returnDraft.quantity <= availableQuantity &&
                        (returnDraft.physicalDisposition === "UNSALEABLE_RETURNED" ||
                          saleableReturnSupported) &&
                        saleableReturnOrderEligible;
                      return (
                        <div className="order-item-batch" key={item.id}>
                          <div className="order-item-batch-summary">
                            <span>
                              第 {index + 1} 笔 · 原加 {item.quantity} 件 · 已退{" "}
                              {item.returnedQuantity} 件 · 可退 {availableQuantity} 件
                              {item.lineType === "GIFT" ? (
                                <small className="gift-order-batch-detail">
                                  门店赠品 · 原价 {formatFee(item.listUnitPriceFen ?? 0)} · 全额减免{" "}
                                  {formatFee(item.unitDiscountFen ?? 0)} · 赠送原因：
                                  {item.adjustmentReason} ·{" "}
                                  {item.authorizedByRole === "OWNER" ? "老板授权" : "店长授权"} ·
                                  已扣库存 {item.quantity} 件 · 已退回 {item.returnedQuantity} 件
                                </small>
                              ) : null}
                            </span>
                            {canReturnSaleableItems && availableQuantity > 0 ? (
                              <button
                                className="button secondary compact"
                                type="button"
                                disabled={
                                  saleReturnDisabled ||
                                  saleReturnRecovery.status !== "NONE" ||
                                  saleReturnPendingItemId !== null ||
                                  !saleableReturnOrderEligible
                                }
                                onClick={() =>
                                  setReturnDraft({
                                    itemId: item.id,
                                    quantity: 1,
                                    reason: "",
                                    physicalDisposition: saleableReturnSupported
                                      ? "SALEABLE_RETURNED"
                                      : "UNSALEABLE_RETURNED",
                                    physicalReturnConfirmed: false,
                                  })
                                }
                              >
                                {!saleableReturnOrderEligible
                                  ? "已有付款或退款不能在这里退"
                                  : "办理实物退回"}
                              </button>
                            ) : null}
                          </div>
                          {selected && canReturnSaleableItems ? (
                            <div className="saleable-return-form">
                              <strong>退回“{item.name}”</strong>
                              <p>
                                仅用于台单尚未付款且顾客已把实物交回门店的情况。请按现场状态选择是否还能再次销售。
                              </p>
                              <label>
                                退回数量
                                <select
                                  aria-label={`${item.name} 退回数量`}
                                  value={returnDraft.quantity}
                                  onChange={(event) =>
                                    setReturnDraft((current) =>
                                      current?.itemId === item.id
                                        ? { ...current, quantity: Number(event.target.value) }
                                        : current,
                                    )
                                  }
                                >
                                  {saleReturnQuantityOptions(availableQuantity).map(
                                    (quantityOption) => (
                                      <option
                                        key={`${item.id}:return-quantity:${quantityOption}`}
                                        value={quantityOption}
                                      >
                                        {quantityOption} 件
                                      </option>
                                    ),
                                  )}
                                </select>
                              </label>
                              <fieldset className="sale-return-disposition-options">
                                <legend>退回实物状态</legend>
                                <label>
                                  <input
                                    type="radio"
                                    name={`sale-return-disposition-${item.id}`}
                                    value="SALEABLE_RETURNED"
                                    disabled={!saleableReturnSupported}
                                    checked={
                                      returnDraft.physicalDisposition === "SALEABLE_RETURNED"
                                    }
                                    onChange={() =>
                                      setReturnDraft((current) =>
                                        current?.itemId === item.id
                                          ? {
                                              ...current,
                                              physicalDisposition: "SALEABLE_RETURNED",
                                              physicalReturnConfirmed: false,
                                            }
                                          : current,
                                      )
                                    }
                                  />
                                  <span>
                                    <strong>完好可再次销售</strong>
                                    <small>
                                      {saleableReturnSupported
                                        ? `商品未开封且状态完好；订单减少后，可售库存增加 ${returnDraft.quantity} 件。`
                                        : "此商品未纳入库存管理，不能选择可售回库；仍可选择不可再次销售办理退回。"}
                                    </small>
                                  </span>
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name={`sale-return-disposition-${item.id}`}
                                    value="UNSALEABLE_RETURNED"
                                    checked={
                                      returnDraft.physicalDisposition === "UNSALEABLE_RETURNED"
                                    }
                                    onChange={() =>
                                      setReturnDraft((current) =>
                                        current?.itemId === item.id
                                          ? {
                                              ...current,
                                              physicalDisposition: "UNSALEABLE_RETURNED",
                                              physicalReturnConfirmed: false,
                                            }
                                          : current,
                                      )
                                    }
                                  />
                                  <span>
                                    <strong>已开封或破损，不可再次销售</strong>
                                    <small>
                                      订单仍会减少；可售库存不会增加，也不会再扣一次库存。
                                    </small>
                                  </span>
                                </label>
                              </fieldset>
                              <label className="saleable-return-confirmation">
                                <input
                                  type="checkbox"
                                  checked={returnDraft.physicalReturnConfirmed}
                                  onChange={(event) =>
                                    setReturnDraft((current) =>
                                      current?.itemId === item.id
                                        ? {
                                            ...current,
                                            physicalReturnConfirmed: event.target.checked,
                                          }
                                        : current,
                                    )
                                  }
                                />
                                <span>我已确认实物已经交回门店，以上实物状态与现场一致</span>
                              </label>
                              <label>
                                退回原因（2–200 字）
                                <textarea
                                  aria-label={`${item.name} 退回原因`}
                                  value={returnDraft.reason}
                                  maxLength={200}
                                  placeholder="例如：顾客点错饮料，商品未开封"
                                  onChange={(event) =>
                                    setReturnDraft((current) =>
                                      current?.itemId === item.id
                                        ? { ...current, reason: event.target.value }
                                        : current,
                                    )
                                  }
                                />
                                <small>{reasonLength}/200 字</small>
                              </label>
                              <fieldset className="saleable-return-preview">
                                <legend>提交后会发生</legend>
                                <span>
                                  订单减少金额：
                                  {formatFee(item.unitPriceFen * returnDraft.quantity)}
                                </span>
                                {returnDraft.physicalDisposition === "SALEABLE_RETURNED" ? (
                                  <span>可售库存增加数量：+{returnDraft.quantity} 件</span>
                                ) : (
                                  <span>
                                    可售库存：不增加，也不会再扣一次；系统会记录为不可再次销售
                                  </span>
                                )}
                                <span>尚未退款：{formatFee(0)}（因为台单未付款）</span>
                                {item.lineType === "GIFT" ? (
                                  <strong>
                                    这是门店赠品：订单减少0元、未退款；只有实物完好可再次销售才会恢复库存。
                                  </strong>
                                ) : null}
                              </fieldset>
                              <div className="dialog-actions">
                                <button
                                  className="button secondary"
                                  type="button"
                                  onClick={() => setReturnDraft(null)}
                                >
                                  取消
                                </button>
                                <button
                                  className="button primary"
                                  type="button"
                                  disabled={
                                    !returnValid ||
                                    saleReturnDisabled ||
                                    saleReturnRecovery.status !== "NONE" ||
                                    saleReturnPendingItemId !== null ||
                                    !onSaleableReturn
                                  }
                                  onClick={() =>
                                    onSaleableReturn?.(
                                      item,
                                      returnDraft.quantity,
                                      returnDraft.physicalDisposition,
                                      returnDraft.reason.trim(),
                                    )
                                  }
                                >
                                  {saleReturnPendingItemId === item.id
                                    ? "正在确认退回…"
                                    : "确认退回并减少订单"}
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </fieldset>
                ) : null}
              </div>
            );
          })
        ) : (
          <p className="compact-empty">尚未添加商品</p>
        )}
        {ownedSaleReturn && canReturnSaleableItems ? (
          <div className="simple-action saleable-return-recovery" role="alert">
            <div className="disabled-reason">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>
                <strong>这笔商品退回结果尚未确认</strong>
                <br />
                原商品、数量、实物状态、原因和请求号已保留。只能重放同一请求，不能重新填写或改数量；这样不会重复减少订单或重复处理库存。
              </span>
            </div>
            <button
              className="button primary"
              type="button"
              disabled={saleReturnDisabled || saleReturnPendingItemId !== null}
              onClick={() => onSaleableReturnRetry?.(ownedSaleReturn)}
            >
              {saleReturnPendingItemId === ownedSaleReturn.command.itemId
                ? "正在确认原请求…"
                : "使用原请求安全重试"}
            </button>
          </div>
        ) : saleReturnRecovery.status !== "NONE" && canReturnSaleableItems ? (
          <div className="disabled-reason" role="alert">
            <AlertTriangle size={16} aria-hidden="true" />
            <span>
              <strong>另一账号或台单有商品退回待确认</strong>
              <br />
              当前只能查看，不能新建退回。请切回原账号和原台单，使用保留的原请求安全重试。
            </span>
          </div>
        ) : canViewReturnReadOnlyNotice ? (
          <div className="disabled-reason order-return-readonly">
            <AlertTriangle size={16} aria-hidden="true" />
            <span>
              <strong>当前岗位只能查看商品退回记录</strong>
              <br />
              商品实物退回请由收银员办理。收银员会逐批次确认实物已经退回，并选择“完好可再次销售”或“已开封/破损不可再次销售”；未付款台单会减少订单金额，不会发生退款，只有完好商品会增加可售库存。
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export type CoachStopPreviewState = "IDLE" | "LOADING" | "READY" | "ERROR";

type BillingOperationAction =
  | { type: "PAUSE" | "RESUME" | "VOID"; reason: string }
  | { type: "END_EARLY"; reason: string; coachStopPreview: CoachSessionStopPreview }
  | { type: "ADJUST_START"; reason: string; preview: SessionStartAdjustmentPreview };

export function BillingOperationPanel({
  order,
  table,
  effectiveRoles,
  pending,
  disabled,
  billingStateChangeDisabled = false,
  coachStopPreview = null,
  coachStopPreviewState = "IDLE",
  coachStopPreviewError = null,
  completedCoachStopResult = null,
  onCoachStopPreviewRefresh,
  onContinueToCheckout,
  voidErrorMessage,
  onAction,
}: {
  order: OrderDetail | undefined;
  table: TableSnapshot;
  effectiveRoles: readonly OperatorRole[];
  pending: boolean;
  disabled: boolean;
  billingStateChangeDisabled?: boolean;
  coachStopPreview?: CoachSessionStopPreview | null;
  coachStopPreviewState?: CoachStopPreviewState;
  coachStopPreviewError?: string | null;
  completedCoachStopResult?: CoachSessionStopResult | null;
  onCoachStopPreviewRefresh?: () => void;
  onContinueToCheckout?: () => void;
  voidErrorMessage: string | null;
  onAction: (action: BillingOperationAction) => void;
}): React.JSX.Element {
  const [billingReason, setBillingReason] = useState("");
  const [voidReason, setVoidReason] = useState("");
  const [adjustStartedAt, setAdjustStartedAt] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjustPreview, setAdjustPreview] = useState<SessionStartAdjustmentPreview | null>(null);
  const [adjustPreviewError, setAdjustPreviewError] = useState<string | null>(null);
  const [adjustPreviewPending, setAdjustPreviewPending] = useState(false);
  const [voidSubmitAttempt, setVoidSubmitAttempt] = useState(0);
  const adjustPreviewGeneration = useRef(0);
  const [confirmedCoachServiceSetToken, setConfirmedCoachServiceSetToken] = useState<string | null>(
    null,
  );
  const voidErrorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    void voidSubmitAttempt;
    if (voidErrorMessage) voidErrorRef.current?.focus();
  }, [voidErrorMessage, voidSubmitAttempt]);
  const billingState = order?.billingState ?? "RUNNING";
  const billingReasonValid = billingReason.trim().length >= 2;
  const voidValid = voidReason.trim().length >= 2;
  const canVoidAccidentalSession = effectiveRoles.includes("CASHIER");
  const canOperateSessionAdjustment = effectiveRoles.some(
    (role) => role === "MANAGER" || role === "CASHIER",
  );
  const canViewSessionAdjustmentReadOnly =
    effectiveRoles.includes("OWNER") && !canOperateSessionAdjustment;
  const canOperateStandardSession = canOperateStandardSessionForRoles(effectiveRoles);
  const coachStopPreviewMatchesTable =
    coachStopPreview !== null &&
    coachStopPreview.sessionId === order?.sessionId &&
    coachStopPreview.tableId === table.tableId &&
    coachStopPreview.tableNumber === table.tableNumber;
  const coachStopPreviewReady = coachStopPreviewState === "READY" && coachStopPreviewMatchesTable;
  const coachStopConfirmationValid =
    coachStopPreviewReady && confirmedCoachServiceSetToken === coachStopPreview.serviceSetToken;
  const completedCoachStopResultMatchesTable =
    completedCoachStopResult !== null &&
    completedCoachStopResult.order.id === order?.id &&
    completedCoachStopResult.order.sessionId === order?.sessionId &&
    completedCoachStopResult.order.sessionId === table.sessionId &&
    completedCoachStopResult.order.tableId === table.tableId &&
    completedCoachStopResult.order.tableNumber === table.tableNumber;
  const completedCoachStopTableMatchesCurrent =
    completedCoachStopResultMatchesTable &&
    completedCoachStopResult.table !== undefined &&
    completedCoachStopResult.table.tenantId === table.tenantId &&
    completedCoachStopResult.table.storeId === table.storeId &&
    completedCoachStopResult.table.tableId === table.tableId &&
    completedCoachStopResult.table.tableNumber === table.tableNumber &&
    completedCoachStopResult.table.sessionId === table.sessionId &&
    completedCoachStopResult.table.revision === table.revision &&
    table.state === "IN_USE" &&
    table.billingState === "ENDED";
  const canContinueToCheckout =
    completedCoachStopTableMatchesCurrent &&
    completedCoachStopResult.coachServiceClosure === "COMPLETED" &&
    completedCoachStopResult.order.billingState === "ENDED" &&
    completedCoachStopResult.order.endedAt === completedCoachStopResult.occurredAt &&
    canCollectPaymentForRoles(effectiveRoles) &&
    onContinueToCheckout !== undefined;
  const coachStopConfirmationIdentity = JSON.stringify([
    table.tableId,
    order?.sessionId ?? null,
    coachStopPreview?.serviceSetToken ?? null,
  ]);
  useEffect(() => {
    if (coachStopConfirmationIdentity) setConfirmedCoachServiceSetToken(null);
  }, [coachStopConfirmationIdentity]);
  const adjustmentContext = JSON.stringify([
    table.tenantId,
    table.storeId,
    table.tableId,
    table.sessionId,
    table.revision,
    order?.id ?? null,
    order?.startedAt ?? null,
    order?.tableFeeFen ?? null,
  ]);
  useEffect(() => {
    if (!adjustmentContext) return;
    adjustPreviewGeneration.current += 1;
    setAdjustPreview(null);
    setAdjustPreviewError(null);
    setAdjustPreviewPending(false);
  }, [adjustmentContext]);
  const adjustmentReasonLength = [...adjustReason.trim()].length;
  const adjustmentReasonValid = adjustmentReasonLength >= 2 && adjustmentReasonLength <= 200;
  const adjustmentPreviewMatchesInput =
    adjustPreview !== null &&
    order != null &&
    adjustStartedAt.length > 0 &&
    Number.isFinite(Date.parse(adjustStartedAt)) &&
    adjustPreview.proposedStartedAt === new Date(adjustStartedAt).toISOString() &&
    adjustPreview.orderId === order.id &&
    adjustPreview.previousStartedAt === order.startedAt &&
    adjustPreview.previousTableFeeFen >= order.tableFeeFen &&
    adjustPreview.previousBusinessDate === order.businessDate &&
    adjustPreview.proposedBusinessDate === order.businessDate &&
    adjustPreview.cashShiftBusinessDate === order.businessDate &&
    adjustPreview.notificationAudience[0] === "OWNER" &&
    adjustPreview.notificationAudience[1] === "MANAGER" &&
    adjustPreview.notificationChannel === "BUSINESS_OPERATION_LOG" &&
    adjustPreview.notificationOutcome === "RECORDED_IN_SAME_TRANSACTION" &&
    Date.parse(adjustPreview.expiresAt) > Date.now();
  const requestAdjustmentPreview = async (): Promise<void> => {
    const generation = adjustPreviewGeneration.current + 1;
    adjustPreviewGeneration.current = generation;
    setAdjustPreview(null);
    setAdjustPreviewError(null);
    if (!order || !adjustStartedAt || !Number.isFinite(Date.parse(adjustStartedAt))) {
      setAdjustPreviewError("请先选择有效的新开台时间，再查看金额和营业日变化。");
      return;
    }
    setAdjustPreviewPending(true);
    try {
      const proposedStartedAt = new Date(adjustStartedAt).toISOString();
      const preview = await getSessionStartAdjustmentPreview(order.sessionId, proposedStartedAt, {
        tenantId: table.tenantId,
        storeId: table.storeId,
        baselineOrder: order,
      });
      if (adjustPreviewGeneration.current !== generation) return;
      setAdjustPreview(preview);
    } catch (error) {
      if (adjustPreviewGeneration.current !== generation) return;
      setAdjustPreviewError(
        `${businessErrorMessage(error, "暂时无法生成调时预览")}。这只是本单调时没有继续，不代表整套 ERP 离线；请按提示核对本单后重试。`,
      );
    } finally {
      if (adjustPreviewGeneration.current === generation) setAdjustPreviewPending(false);
    }
  };
  return (
    <div className="billing-operation-panel">
      <div className="billing-state-card">
        <span>
          当前状态
          <strong>
            {billingState === "PAUSED"
              ? "计费已暂停"
              : billingState === "ENDED"
                ? "计费已停止"
                : "正在计费"}
          </strong>
        </span>
        <small>
          暂停期间{order?.priceSnapshot.pauseBillingMode === "FREE" ? "不计费" : "继续计费"}
          ；本单使用
          {order?.priceSnapshot.name ?? "已锁定价格"} v{order?.priceSnapshot.version ?? "-"}。
        </small>
      </div>
      <BillingBreakdownView order={order} />
      {completedCoachStopResultMatchesTable ? (
        <>
          <output className="coach-stop-result" aria-live="polite">
            {completedCoachStopResult.coachServiceClosure === "COMPLETED" ? (
              <Check size={18} aria-hidden="true" />
            ) : (
              <AlertTriangle size={18} aria-hidden="true" />
            )}
            <span>
              {completedCoachStopResult.coachServiceClosure === "COMPLETED" ? (
                <>
                  <strong>全部助教下钟处理已完成</strong>
                  <br />
                  完成 {completedCoachStopResult.completedCoachServices.length}{" "}
                  名助教下钟处理，服务费合计{" "}
                  {formatFee(
                    completedCoachStopResult.completedCoachServices.reduce(
                      (total, service) => total + service.serviceFeeFen,
                      0,
                    ),
                  )}
                  。台费已经冻结；本操作没有结账、清台或释放球台。
                </>
              ) : (
                <>
                  <strong>历史提前停表已确认，助教处理情况需核对</strong>
                  <br />
                  旧版本没有记录整桌助教是否随停表处理。请到助教工作台逐项核对；系统没有补写费用或提成。
                </>
              )}
            </span>
          </output>
          {canContinueToCheckout ? (
            <button className="button primary" type="button" onClick={onContinueToCheckout}>
              继续核对并结账
            </button>
          ) : null}
        </>
      ) : null}
      {table.sessionRiskLevel === "WARNING" || table.sessionRiskLevel === "CRITICAL" ? (
        <div className="billing-action-card long-session-resolution-card">
          <strong>
            {table.sessionRiskLevel === "CRITICAL" ? "严重超长台局处置" : "超长台局核对"}
          </strong>
          <p>
            原开台{" "}
            {table.sessionStartedAt
              ? new Date(table.sessionStartedAt).toLocaleString("zh-CN")
              : "未知"}
            ，已持续 {formatElapsedSeconds(table.sessionElapsedSeconds ?? 0)}，当前总计
            {formatFee(table.currentTotalFen ?? order?.totalFen ?? table.estimatedFeeFen)}。
          </p>
          <p>
            不知道真实结束时间时，系统不会编造时间，也不会在本页直接停止计费、释放球台或修改订单状态。
          </p>
          <div className="disabled-reason">
            <AlertTriangle size={16} aria-hidden="true" />
            <span>
              <strong>超长台局安全处置尚未开放</strong>
              <br />
              完整流程需要在统一审批中心固定当前金额、计费状态、球台和订单后果，再由同一请求安全执行。接入前不会在本页停止计费、释放球台或修改订单状态；请先核对现场并保留原记录。
            </span>
          </div>
        </div>
      ) : null}
      {canOperateStandardSession ? (
        <div className="billing-action-card">
          <strong>{billingState === "PAUSED" ? "恢复计费" : "暂停或提前停表"}</strong>
          <label>
            操作原因
            <input
              value={billingReason}
              onChange={(event) => setBillingReason(event.target.value)}
              placeholder="例如：顾客临时离开 / 提前结束打球"
              maxLength={200}
            />
          </label>
          {billingState === "RUNNING" ? (
            <section className="coach-stop-preview" aria-label="提前停表助教下钟预览">
              <div className="coach-stop-preview-heading">
                <div>
                  <strong>提前停表会怎样处理本桌助教</strong>
                  <p>
                    确认后，本桌全部助教会同时下钟并各自结算服务费和提成，最终分钟与费用以实际下钟时间为准。待上钟项目会取消且不收费。
                  </p>
                </div>
                {coachStopPreviewState === "LOADING" ? (
                  <RefreshCw className="is-spinning" size={17} aria-hidden="true" />
                ) : null}
              </div>
              <p className="coach-stop-boundary">
                这一步只停止台费并处理助教，不会结账、不会清台，也不会释放球台。
              </p>
              {coachStopPreviewState === "LOADING" ? (
                <output className="coach-stop-preview-state" aria-live="polite">
                  正在读取本桌最新助教情况，读取完成前不能提前停表。
                </output>
              ) : coachStopPreviewState === "ERROR" ? (
                <div className="coach-stop-preview-error" role="alert">
                  <AlertTriangle size={16} aria-hidden="true" />
                  <span>
                    <strong>暂时无法确认本桌助教情况</strong>
                    <br />
                    {coachStopPreviewError ?? "请检查网络并重新读取，预览恢复前不能提前停表。"}
                  </span>
                  {onCoachStopPreviewRefresh ? (
                    <button
                      className="button secondary"
                      type="button"
                      disabled={pending}
                      onClick={onCoachStopPreviewRefresh}
                    >
                      <RefreshCw size={15} aria-hidden="true" />
                      重新读取助教预览
                    </button>
                  ) : null}
                </div>
              ) : coachStopPreviewReady ? (
                <>
                  {coachStopPreview.services.length > 0 ? (
                    <div className="coach-stop-service-list">
                      {coachStopPreview.services.map((service) => (
                        <article key={service.id}>
                          <div>
                            <strong>{service.coachDisplayName}</strong>
                            <span
                              className={`coach-stop-status is-${service.status.toLowerCase()}`}
                            >
                              {service.status === "ACTIVE" ? "已上钟" : "待上钟"}
                            </span>
                          </div>
                          <p>{service.serviceTypeName}</p>
                          <dl>
                            <div>
                              <dt>当前预计分钟</dt>
                              <dd>{service.estimatedBilledMinutes} 分钟</dd>
                            </div>
                            <div>
                              <dt>当前预计服务费</dt>
                              <dd>{formatFee(service.estimatedServiceFeeFen)}</dd>
                            </div>
                          </dl>
                          <small>
                            {service.status === "ACTIVE"
                              ? `上钟时间：${new Date(service.startedAt ?? "").toLocaleString("zh-CN")}`
                              : "尚未上钟；确认停表后取消，不收服务费。"}
                          </small>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <p className="coach-stop-empty">
                      当前没有待下钟助教。提前停表只会冻结台费，不会新增助教费用。
                    </p>
                  )}
                  <div className="coach-stop-total">
                    <span>本桌当前预计助教服务费合计</span>
                    <strong>{formatFee(coachStopPreview.estimatedServiceFeeFen)}</strong>
                  </div>
                  <label className="coach-stop-confirmation">
                    <input
                      type="checkbox"
                      checked={coachStopConfirmationValid}
                      disabled={pending || disabled || billingStateChangeDisabled}
                      onChange={(event) =>
                        setConfirmedCoachServiceSetToken(
                          event.target.checked ? coachStopPreview.serviceSetToken : null,
                        )
                      }
                    />
                    <span>
                      我已核对以上助教、项目和预计费用，确认提前停表会让全部助教下钟；实际金额以执行时刻为准。
                    </span>
                  </label>
                </>
              ) : (
                <p className="coach-stop-preview-state">
                  正在等待本桌助教预览。预览完成并重新核对前不能提前停表。
                </p>
              )}
            </section>
          ) : null}
          <div className="dialog-actions">
            {billingState === "PAUSED" ? (
              <button
                className="button primary"
                type="button"
                disabled={pending || disabled || billingStateChangeDisabled || !billingReasonValid}
                onClick={() => onAction({ type: "RESUME", reason: billingReason.trim() })}
              >
                恢复计费
              </button>
            ) : billingState === "RUNNING" ? (
              <>
                <button
                  className="button secondary"
                  type="button"
                  disabled={
                    pending || disabled || billingStateChangeDisabled || !billingReasonValid
                  }
                  onClick={() => onAction({ type: "PAUSE", reason: billingReason.trim() })}
                >
                  暂停计费
                </button>
                <button
                  className="button primary"
                  type="button"
                  disabled={
                    pending ||
                    disabled ||
                    billingStateChangeDisabled ||
                    !billingReasonValid ||
                    !coachStopConfirmationValid
                  }
                  onClick={() => {
                    if (!coachStopPreview || !coachStopPreviewReady || !coachStopConfirmationValid)
                      return;
                    onAction({
                      type: "END_EARLY",
                      reason: billingReason.trim(),
                      coachStopPreview,
                    });
                  }}
                >
                  提前停表并冻结台费
                </button>
              </>
            ) : (
              <span className="authorization-note">台费已冻结，可继续核对商品后结账。</span>
            )}
          </div>
        </div>
      ) : (
        <div className="disabled-reason">
          当前岗位可查看计费结果；暂停、恢复和停表请由店长或收银员办理。
        </div>
      )}
      {canOperateSessionAdjustment ? (
        <div className="billing-action-card session-start-adjustment-card">
          <strong>修改开台时间</strong>
          <p>
            仅适用于仍在计费或暂停、尚未收款且只有台费的活动台单。新旧时间必须属于同一营业日和同一当前收银班次；必须先查看营业日、班次和台费变化，再确认修改。
          </p>
          <label>
            新开台时间
            <input
              type="datetime-local"
              value={adjustStartedAt}
              onChange={(event) => {
                adjustPreviewGeneration.current += 1;
                setAdjustStartedAt(event.target.value);
                setAdjustPreview(null);
                setAdjustPreviewError(null);
                setAdjustPreviewPending(false);
              }}
            />
            <small>
              请选择本营业日当前收银班次内的实际开台时间。系统按实际时间重新计算台费，不采用固定“最近
              7 天”范围。
            </small>
          </label>
          <label>
            修改原因
            <input
              value={adjustReason}
              onChange={(event) => setAdjustReason(event.target.value)}
              placeholder="例如：开台时误选了时间"
              minLength={2}
              maxLength={200}
            />
            <small>请填写 2-200 个字，经营操作记录会保存原因，方便交班和复核。</small>
          </label>
          <button
            className="button secondary"
            type="button"
            disabled={
              pending ||
              disabled ||
              billingStateChangeDisabled ||
              adjustPreviewPending ||
              !adjustStartedAt
            }
            onClick={() => void requestAdjustmentPreview()}
          >
            {adjustPreviewPending ? "正在计算预览…" : "先预览调整结果"}
          </button>
          {adjustPreviewError ? (
            <div className="inline-operation-feedback is-error" role="alert">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>{adjustPreviewError}</span>
            </div>
          ) : null}
          {adjustPreview && adjustmentPreviewMatchesInput ? (
            <section className="session-start-adjustment-preview" aria-label="开台时间修改预览">
              <strong>请核对后确认</strong>
              <dl>
                <div>
                  <dt>原开台时间</dt>
                  <dd>{new Date(adjustPreview.previousStartedAt).toLocaleString("zh-CN")}</dd>
                </div>
                <div>
                  <dt>新开台时间</dt>
                  <dd>{new Date(adjustPreview.proposedStartedAt).toLocaleString("zh-CN")}</dd>
                </div>
                <div>
                  <dt>原营业日</dt>
                  <dd>{adjustPreview.previousBusinessDate}</dd>
                </div>
                <div>
                  <dt>新营业日</dt>
                  <dd>{adjustPreview.proposedBusinessDate}</dd>
                </div>
                <div>
                  <dt>当前收银班次</dt>
                  <dd>
                    {adjustPreview.cashShiftBusinessDate}，开始于
                    {new Date(adjustPreview.cashShiftStartedAt).toLocaleString("zh-CN")}
                  </dd>
                </div>
                <div>
                  <dt>预览时点前台费</dt>
                  <dd>{formatFee(adjustPreview.previousTableFeeFen)}</dd>
                </div>
                <div>
                  <dt>预览时点后新台费</dt>
                  <dd>{formatFee(adjustPreview.proposedTableFeeFen)}</dd>
                </div>
                <div>
                  <dt>台费差额</dt>
                  <dd>{formatFee(adjustPreview.adjustmentFen)}</dd>
                </div>
                <div>
                  <dt>已预付款</dt>
                  <dd>{formatFee(adjustPreview.prepaidFen)}</dd>
                </div>
              </dl>
              <p>
                原时间和新时间均属于以上同一营业日、同一当前收银班次。本单没有预付款，不会发生预付款冲正。确认后会更新开台时间和台费，并在同一次操作中记录到老板和店长可查看的经营操作记录。
              </p>
              <button
                className="button primary"
                type="button"
                disabled={
                  pending || disabled || billingStateChangeDisabled || !adjustmentReasonValid
                }
                onClick={() =>
                  onAction({
                    type: "ADJUST_START",
                    reason: adjustReason.trim(),
                    preview: adjustPreview,
                  })
                }
              >
                确认修改开台时间
              </button>
            </section>
          ) : null}
        </div>
      ) : canViewSessionAdjustmentReadOnly ? (
        <div className="billing-action-card session-start-adjustment-readonly">
          <strong>修改开台时间</strong>
          <p>
            老板账号可查看当前开台时间和台费，但不能直接修改。请由店长或收银员在同一营业日、同一当前收银班次内先预览新旧时间和台费，再确认操作；完成后可在老板和店长的经营操作记录中核对。
          </p>
        </div>
      ) : null}
      {canVoidAccidentalSession ? (
        <div className="billing-action-card danger-zone">
          <strong>误开台作废</strong>
          <p>
            仅用于从未产生商品、助教服务、收款或团购核销记录的误开台；收银员确认原因后可直接作废。已有经营记录时，请按原记录办理退单、下钟、退款或冲正。
          </p>
          {voidErrorMessage ? (
            <div
              className="inline-operation-feedback is-error"
              role="alert"
              tabIndex={-1}
              ref={voidErrorRef}
            >
              <AlertTriangle size={16} aria-hidden="true" />
              <span>
                <strong>误开台没有作废</strong>
                <br />
                {voidErrorMessage}
                <br />
                已填写的作废原因会保留。请按提示修改；若已有经营记录，请办理退单、下钟、退款或冲正。
              </span>
            </div>
          ) : null}
          <label>
            作废原因
            <input
              value={voidReason}
              onChange={(event) => setVoidReason(event.target.value)}
              placeholder="例如：点错球台"
              maxLength={200}
            />
          </label>
          <button
            className="button danger"
            type="button"
            disabled={pending || disabled || billingStateChangeDisabled || !voidValid}
            onClick={() => {
              setVoidSubmitAttempt((current) => current + 1);
              onAction({
                type: "VOID",
                reason: voidReason.trim(),
              });
            }}
          >
            确认作废误开台
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function BillingBreakdownView({
  order,
}: {
  order: OrderDetail | undefined;
}): React.JSX.Element {
  const breakdown = order?.billingBreakdown;
  if (!order) return <div className="compact-empty">正在读取计费明细...</div>;
  const timeZone = order.priceSnapshot.timeZone ?? legacyPriceSnapshotTimeZone;
  const localDateTimeFormatter = storeLocalDateTimeFormatter(timeZone);
  return (
    <div className="billing-breakdown">
      <div className="section-heading compact">
        <div>
          <h3>台费怎么算</h3>
          <p>
            {order.priceSnapshot.name} · 当时采用的第 {order.priceSnapshot.version} 次价格设置 ·{" "}
            {order.priceSnapshot.evidence === "SIMULATED" ? "演示规则" : "门店已确认规则"}
          </p>
        </div>
        <strong>{formatFee(order.tableFeeFen)}</strong>
      </div>
      {breakdown?.segments.length ? (
        <div className="billing-segment-list">
          {breakdown.segments.map((segment) => (
            <article key={segment.id} className="billing-segment-row">
              <div>
                <strong>
                  {segment.kind === "PACKAGE"
                    ? "包时固定价"
                    : segment.kind === "PACKAGE_OVERTIME"
                      ? "包时结束后按时计费"
                      : segment.priceBandName}
                </strong>
                {segment.kind === "HOURLY" ? null : <small>时段：{segment.priceBandName}</small>}
                <small>营业日 {segment.businessDate}</small>
                <small>
                  门店当地时间：
                  {formatStoreLocalDateTime(segment.startedAt, localDateTimeFormatter)} 至{" "}
                  {formatStoreLocalDateTime(segment.endedAt, localDateTimeFormatter)}
                </small>
              </div>
              <div>
                <span>实际 {Math.ceil(segment.elapsedSeconds / 60)} 分钟</span>
                <span>计费 {segment.billedMinutes} 分钟</span>
                <span>单价 {formatFee(segment.hourlyRateFen)}/小时</span>
                <span>
                  {segment.roundingMode === "PACKAGE_FIXED"
                    ? "包时固定价"
                    : `${segment.roundingMinutes} 分钟步长 · ${
                        segment.roundingMode === "UP"
                          ? "向上取整"
                          : segment.roundingMode === "DOWN"
                            ? "向下取整"
                            : "就近取整"
                      }`}
                </span>
                <strong>本段 {formatFee(segment.amountFen)}</strong>
              </div>
            </article>
          ))}
          {breakdown.minimumAmountAdjustmentFen > 0 ? (
            <div className="billing-minimum-row">
              最低消费补足 <strong>{formatFee(breakdown.minimumAmountAdjustmentFen)}</strong>
            </div>
          ) : null}
          <small>
            总时长 {Math.ceil(breakdown.elapsedSeconds / 60)} 分钟 · 暂停{" "}
            {Math.ceil(breakdown.pausedSeconds / 60)} 分钟 · 计费 {breakdown.billedMinutes} 分钟
          </small>
        </div>
      ) : (
        <p className="compact-empty">
          当前暂无可展示的计费分段；系统仍按本单开台时采用的价格计算。
        </p>
      )}
      {(order.rentalItems ?? []).length > 0 ? (
        <section className="billing-segment-list" aria-label="租赁收费明细">
          <div className="section-heading compact">
            <div>
              <h3>租赁收费</h3>
              <p>借出时已按当时商品设置冻结；归还不会退款或重算原租金。</p>
            </div>
            <strong>{formatFee(order.rentalTotalFen ?? 0)}</strong>
          </div>
          {(order.rentalItems ?? []).map((item) => (
            <article key={item.id} className="billing-segment-row">
              <div>
                <strong>{item.productName}</strong>
                <small>租赁单号 {item.rentalNumber}</small>
              </div>
              <div>
                <span>
                  {item.quantity} {item.unit} × {formatFee(item.unitRentalPriceFen)}
                </span>
                <strong>租金 {formatFee(item.receivableFen)}</strong>
              </div>
            </article>
          ))}
        </section>
      ) : null}
    </div>
  );
}

export function isCheckoutPaymentMethodEnabled(
  candidate: PaymentMethod,
  memberFundsAvailable: boolean,
  manualPaymentMethods: ConnectivityReadModel["manualPaymentMethods"] | undefined,
  officialPaymentOnline = false,
  offlineMemberBalanceDeferred = false,
): boolean {
  const paymentMethods = manualPaymentMethods ?? {
    cash: true,
    staticWechat: true,
    staticAlipay: true,
  };
  return candidate === "WECHAT_PAY" || candidate === "ALIPAY"
    ? officialPaymentOnline
    : candidate === "MEMBER_BALANCE"
      ? memberFundsAvailable || offlineMemberBalanceDeferred
      : candidate === "CASH"
        ? paymentMethods.cash
        : candidate === "STATIC_WECHAT"
          ? paymentMethods.staticWechat
          : paymentMethods.staticAlipay;
}

export function nextCheckoutMemberIndex(
  current: number,
  key: "ArrowDown" | "ArrowUp" | "Escape" | string,
  resultCount: number,
): number {
  if (key === "Escape" || resultCount <= 0) return -1;
  if (key === "ArrowDown") return current < 0 ? 0 : (current + 1) % resultCount;
  if (key === "ArrowUp") return current <= 0 ? resultCount - 1 : current - 1;
  return current;
}

function checkoutMemberSearchLabel(member: MemberRecord): string {
  return `${member.name} · ${member.memberNumber}`;
}

export function CheckoutMemberPicker({
  search,
  results,
  selected,
  loading,
  error,
  activeIndex,
  scanNotice,
  orderTotalFen,
  disabled = false,
  onSearch,
  onSelect,
  onActiveIndex,
  onScanPlaceholder,
}: {
  search: string;
  results: MemberRecord[];
  selected: MemberRecord | null;
  loading: boolean;
  error: string | null;
  activeIndex: number;
  scanNotice: string | null;
  orderTotalFen?: number | undefined;
  disabled?: boolean;
  onSearch: (value: string) => void;
  onSelect: (member: MemberRecord) => void;
  onActiveIndex: (index: number) => void;
  onScanPlaceholder: () => void;
}): React.JSX.Element {
  const inputId = useId();
  const resultListId = useId();
  const visibleResults = results.filter((member) => member.status === "ACTIVE").slice(0, 30);
  const selectedLabel = selected ? checkoutMemberSearchLabel(selected) : null;
  const showResults = search.trim().length > 0 && search !== selectedLabel;

  return (
    <div className="checkout-member-search">
      <label htmlFor={inputId}>搜索会员</label>
      <div className="checkout-member-search-row">
        <span className="checkout-member-search-input">
          <Search size={16} aria-hidden="true" />
          <AutofillSafeInput
            id={inputId}
            fieldName="checkout-member-search"
            type="search"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showResults && visibleResults.length > 0}
            aria-controls={resultListId}
            aria-activedescendant={
              activeIndex >= 0 && activeIndex < visibleResults.length
                ? `${resultListId}-${visibleResults[activeIndex]?.id}`
                : undefined
            }
            value={search}
            placeholder="输入姓名、手机号或会员号"
            disabled={disabled}
            onUserChange={onSearch}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                onActiveIndex(
                  nextCheckoutMemberIndex(activeIndex, event.key, visibleResults.length),
                );
                return;
              }
              if (event.key === "Escape") {
                onActiveIndex(-1);
                return;
              }
              if (event.key === "Enter" && activeIndex >= 0) {
                const member = visibleResults[activeIndex];
                if (member) {
                  event.preventDefault();
                  onSelect(member);
                }
              }
            }}
          />
        </span>
        <button
          className="button secondary compact"
          type="button"
          disabled={disabled}
          onClick={onScanPlaceholder}
        >
          扫码 / 读卡
        </button>
      </div>
      {scanNotice ? <p className="checkout-member-scan-note">{scanNotice}</p> : null}
      {loading ? <output className="checkout-member-query-state">正在搜索会员…</output> : null}
      {error ? (
        <div className="checkout-member-query-state is-error" role="alert">
          会员搜索失败：{error}。请检查网络后继续输入原条件重试。
        </div>
      ) : null}
      {showResults && !loading && !error && visibleResults.length === 0 ? (
        <p className="checkout-member-query-state">没有找到会员，请核对姓名、手机号或会员号。</p>
      ) : null}
      {showResults && visibleResults.length > 0 ? (
        <div className="checkout-member-results" id={resultListId} role="listbox">
          {visibleResults.map((member, index) => (
            <button
              id={`${resultListId}-${member.id}`}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              className={index === activeIndex ? "is-active" : ""}
              key={member.id}
              disabled={disabled}
              onMouseEnter={() => onActiveIndex(index)}
              onClick={() => onSelect(member)}
            >
              <span>
                <strong>{member.name}</strong>
                <small>
                  {member.memberNumber} · {member.phoneMasked}
                </small>
              </span>
              <b>{formatFee(member.availableBalanceFen)}</b>
            </button>
          ))}
          {results.length > visibleResults.length ? (
            <small className="checkout-member-result-limit">
              当前只显示前 30 条，请继续输入手机号或会员号缩小范围。
            </small>
          ) : null}
        </div>
      ) : null}
      {selected ? (
        <section className="checkout-member-confirmation" aria-label="已选择会员，请再次核对">
          <div>
            <small>已选择会员，请再次核对</small>
            <strong>{selected.name}</strong>
            <span>
              {selected.memberNumber} · {selected.phoneMasked}
            </span>
          </div>
          <div>
            <small>可用余额</small>
            <strong>{formatFee(selected.availableBalanceFen)}</strong>
            <span>本次待收 {formatFee(orderTotalFen ?? 0)}</span>
          </div>
          <p>本次扣款前请核对会员与应收金额；确认结账后同一请求不会重复扣款。</p>
        </section>
      ) : null}
    </div>
  );
}

export function CheckoutFailureNotice({
  feedback,
  order,
  selectedMember,
  enabledManualMethods,
  onUseManualMethod,
  onUseCombined,
  onAuthorityResolved,
  onRecharge,
}: {
  feedback: CheckoutFeedback | null;
  order:
    | (Pick<OrderDetail, "orderNumber" | "totalFen"> & Partial<Pick<OrderDetail, "paidFen">>)
    | undefined;
  selectedMember: MemberRecord | null;
  enabledManualMethods: readonly MemberCombinedCheckoutMethod[];
  onUseManualMethod: (method: MemberCombinedCheckoutMethod) => void;
  onUseCombined: (method: MemberCombinedCheckoutMethod, member: MemberReadModel) => void;
  onAuthorityResolved: (member: MemberReadModel) => void;
  onRecharge: (memberId: string) => void;
}): React.JSX.Element | null {
  const alertRef = useRef<HTMLElement>(null);
  const appliedAuthorityRef = useRef<string | null>(null);
  const insufficient = feedback?.code === "MEMBER_BALANCE_INSUFFICIENT";
  const operatorIdentityEpoch = currentOperatorIdentityEpoch();
  const authority = useQuery({
    queryKey: [
      "checkout-member-authority",
      operatorIdentityEpoch,
      selectedMember?.id ?? null,
      selectedMember?.memberNumber ?? null,
    ],
    queryFn: async () => {
      if (!selectedMember) throw new Error("当前没有可核对的会员");
      return readCheckoutMemberAuthority(selectedMember);
    },
    enabled: insufficient && selectedMember !== null,
    retry: false,
    gcTime: 0,
  });
  useEffect(() => {
    if (feedback) alertRef.current?.focus();
  }, [feedback]);
  const authorityData = authority.data;
  const authoritativeMember =
    insufficient &&
    !authority.isFetching &&
    !authority.isError &&
    authorityData !== undefined &&
    selectedMember !== null &&
    authorityData.id === selectedMember.id &&
    authorityData.memberNumber === selectedMember.memberNumber
      ? authorityData
      : undefined;
  useEffect(() => {
    if (!authoritativeMember) return;
    const authorityIdentity = [
      operatorIdentityEpoch,
      authoritativeMember.id,
      authoritativeMember.memberNumber,
      authoritativeMember.version,
      authoritativeMember.walletVersion,
    ].join(":");
    if (appliedAuthorityRef.current === authorityIdentity) return;
    appliedAuthorityRef.current = authorityIdentity;
    onAuthorityResolved(authoritativeMember);
  }, [authoritativeMember, onAuthorityResolved, operatorIdentityEpoch]);
  if (!feedback) return null;

  const cnyWallets =
    authoritativeMember?.wallets.filter((wallet) => wallet.unit === "CNY_FEN") ?? [];
  const outstandingFen = orderCheckoutOutstandingFen(order);
  const cashShortfallFen =
    authoritativeMember &&
    outstandingFen &&
    outstandingFen > authoritativeMember.availableBalanceFen
      ? outstandingFen - authoritativeMember.availableBalanceFen
      : null;
  const firstManualMethod = enabledManualMethods[0] ?? null;
  return (
    <section
      className="error-band checkout-failure-notice"
      ref={alertRef}
      role="alert"
      tabIndex={-1}
      aria-live="assertive"
    >
      <AlertTriangle size={19} aria-hidden="true" />
      <div>
        <strong>{insufficient ? "会员余额扣款没有执行" : "本次收款没有完成"}</strong>
        <p>{feedback.message}</p>
        {insufficient ? (
          <>
            {selectedMember ? (
              <p>
                刚才选择：{selectedMember.name} · {selectedMember.memberNumber} ·{" "}
                {selectedMember.phoneMasked}
              </p>
            ) : null}
            {selectedMember && authority.isFetching ? (
              <output className="checkout-member-authority-state">
                正在重新核对该会员的钱包余额，本页旧余额不作为最终结果…
              </output>
            ) : selectedMember && authority.isError ? (
              <div className="checkout-member-authority-state is-error">
                会员余额核对失败：
                {authority.error instanceof Error
                  ? authority.error.message
                  : "系统没有返回明确原因"}
                。当前不展示旧余额，请检查门店连接后重试会员余额付款。
              </div>
            ) : authoritativeMember && order ? (
              <section className="checkout-member-authority" aria-label="会员钱包权威余额">
                <header>
                  <strong>{authoritativeMember.name}</strong>
                  <span>
                    {authoritativeMember.memberNumber} · {authoritativeMember.phoneMasked}
                  </span>
                </header>
                <dl>
                  {cnyWallets.map((wallet) => (
                    <div key={wallet.walletDefinitionId}>
                      <dt>
                        {wallet.walletName}
                        {wallet.enabled ? "" : "（已停用）"}
                      </dt>
                      <dd>{formatFee(wallet.balance)}</dd>
                    </div>
                  ))}
                  <div>
                    <dt>总可用余额</dt>
                    <dd>{formatFee(authoritativeMember.availableBalanceFen)}</dd>
                  </div>
                  <div>
                    <dt>本单应收</dt>
                    <dd>{formatFee(outstandingFen ?? 0)}</dd>
                  </div>
                  <div>
                    <dt>{cashShortfallFen === null ? "钱包规则" : "还差"}</dt>
                    <dd>
                      {cashShortfallFen === null
                        ? "总余额看似足够，但本单钱包规则或权益范围不满足"
                        : formatFee(cashShortfallFen)}
                    </dd>
                  </div>
                </dl>
              </section>
            ) : null}
            <p>
              原台单 {order?.orderNumber ?? "公开单号暂未返回"}{" "}
              保持未结账，系统没有写入付款，也不会自动改用其他收款方式。
            </p>
            <p>
              客户挂账尚未启用，未结台单不会自动变成挂账。可先充值、重新选择会员、改用门店已启用的现场方式全额收款，或启用现有两路组合付款后重新输入金额并确认。
            </p>
            <div className="checkout-failure-actions">
              {enabledManualMethods.map((manualMethod) => (
                <button
                  className="button secondary"
                  type="button"
                  key={manualMethod}
                  onClick={() => onUseManualMethod(manualMethod)}
                >
                  改用{memberCombinedCheckoutMethodLabel(manualMethod)}全额收款
                </button>
              ))}
              {firstManualMethod && authoritativeMember ? (
                <button
                  className="button secondary"
                  type="button"
                  onClick={() => onUseCombined(firstManualMethod, authoritativeMember)}
                >
                  启用会员余额 + {memberCombinedCheckoutMethodLabel(firstManualMethod)}组合付款
                </button>
              ) : null}
              {selectedMember ? (
                <button
                  className="button secondary"
                  type="button"
                  onClick={() => onRecharge(selectedMember.id)}
                >
                  去会员中心充值
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <p>请按上方原因处理后再试；结果尚未确认时，只能使用原请求安全重试。</p>
        )}
      </div>
    </section>
  );
}

export function CheckoutPanel({
  method,
  onMethod,
  order,
  pending,
  officialPaymentPending,
  disabled,
  officialPaymentOnline,
  offlineMemberBalanceDeferred = false,
  memberFundsAvailable,
  manualPaymentMethods,
  feedback,
  officialPaymentFeedback,
  onFeedbackClear,
  onOfficialPaymentFeedbackClear,
  onRechargeMember,
  onCheckout,
  onOfficialPayment,
}: {
  method: PaymentMethod;
  onMethod: (method: PaymentMethod) => void;
  order: OrderDetail | undefined;
  pending: boolean;
  officialPaymentPending: boolean;
  disabled: boolean;
  officialPaymentOnline: boolean;
  offlineMemberBalanceDeferred?: boolean;
  memberFundsAvailable: boolean;
  manualPaymentMethods: ConnectivityReadModel["manualPaymentMethods"] | undefined;
  feedback: CheckoutFeedback | null;
  officialPaymentFeedback: OfficialPaymentFeedback | null;
  onFeedbackClear: () => void;
  onOfficialPaymentFeedbackClear: () => void;
  onRechargeMember?: (memberId: string) => void;
  onCheckout: (member?: {
    memberId: string;
    memberBalanceFen?: number;
    memberSpendPlanHash?: string;
  }) => void;
  onOfficialPayment: (rail: OfficialPaymentRail) => void;
}): React.JSX.Element {
  const [memberSearch, setMemberSearch] = useState("");
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState("");
  const [selectedMember, setSelectedMember] = useState<MemberRecord | null>(null);
  const [activeMemberIndex, setActiveMemberIndex] = useState(-1);
  const [memberScanNotice, setMemberScanNotice] = useState<string | null>(null);
  const [combinedPayment, setCombinedPayment] = useState(false);
  const [memberAmountYuan, setMemberAmountYuan] = useState("");
  const [combinedPaymentConfirmationKey, setCombinedPaymentConfirmationKey] = useState<
    string | null
  >(null);
  const [memberAuthorityResolution, setMemberAuthorityResolution] = useState<{
    feedback: CheckoutFeedback;
    operatorIdentityEpoch: number;
    memberId: string;
    memberNumber: string;
    version: number;
    walletVersion: number;
  } | null>(null);
  const checkoutOutstandingFen = orderCheckoutOutstandingFen(order);
  const previousOrderTotalFenRef = useRef(order ? `${order.totalFen}:${order.paidFen}` : undefined);
  const zeroAmount = checkoutOutstandingFen === 0;
  const prepaidCovered = Boolean(
    order &&
      order.totalFen > 0 &&
      order.paidFen >= order.totalFen &&
      order.funding?.paymentTiming === "PREPAID",
  );
  const memberOnly = method === "MEMBER_BALANCE";
  const operatorIdentityEpoch = currentOperatorIdentityEpoch();
  const insufficientMemberFeedback = feedback?.code === "MEMBER_BALANCE_INSUFFICIENT";
  const memberAuthorityResolved = Boolean(
    insufficientMemberFeedback &&
      selectedMember &&
      memberAuthorityResolution?.feedback === feedback &&
      memberAuthorityResolution.operatorIdentityEpoch === operatorIdentityEpoch &&
      memberAuthorityResolution.memberId === selectedMember.id &&
      memberAuthorityResolution.memberNumber === selectedMember.memberNumber &&
      memberAuthorityResolution.version === selectedMember.version &&
      memberAuthorityResolution.walletVersion === selectedMember.walletVersion,
  );
  const memberAuthorityLocked = Boolean(
    insufficientMemberFeedback && selectedMember && !memberAuthorityResolved,
  );
  const paymentMember = memberAuthorityLocked ? null : selectedMember;
  const combinedExternalMethod: MemberCombinedCheckoutMethod =
    method === "STATIC_WECHAT" || method === "STATIC_ALIPAY" ? method : "CASH";
  const manualCombinedMethodSelected =
    method === "CASH" || method === "STATIC_WECHAT" || method === "STATIC_ALIPAY";
  const memberPayment = !zeroAmount && (memberOnly || combinedPayment);
  useEffect(() => {
    const collectionSnapshot = order ? `${order.totalFen}:${order.paidFen}` : undefined;
    if (previousOrderTotalFenRef.current === collectionSnapshot) return;
    previousOrderTotalFenRef.current = collectionSnapshot;
    setCombinedPaymentConfirmationKey(null);
  }, [order]);
  useEffect(() => {
    if (!insufficientMemberFeedback) return;
    setMemberAmountYuan("");
    setCombinedPaymentConfirmationKey(null);
  }, [insufficientMemberFeedback]);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedMemberSearch(memberSearch.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [memberSearch]);
  const members = useQuery({
    queryKey: ["checkout-members", debouncedMemberSearch],
    queryFn: () => listMembers(debouncedMemberSearch),
    enabled: memberPayment && debouncedMemberSearch.length > 0,
    retry: false,
  });
  const staticCode = method === "STATIC_WECHAT" || method === "STATIC_ALIPAY";
  const officialRail: OfficialPaymentRail | null =
    method === "WECHAT_PAY" || method === "ALIPAY" ? method : null;
  const isMethodEnabled = (candidate: PaymentMethod): boolean =>
    isCheckoutPaymentMethodEnabled(
      candidate,
      memberFundsAvailable,
      manualPaymentMethods,
      officialPaymentOnline,
      offlineMemberBalanceDeferred,
    );
  const enabledManualMethods = (["CASH", "STATIC_WECHAT", "STATIC_ALIPAY"] as const).filter(
    (candidate) => isMethodEnabled(candidate),
  );
  const selectedMethodEnabled = zeroAmount || isMethodEnabled(method);
  const memberOnlyAmountValid = isMemberPaymentAmountValid({
    selectedMember: paymentMember ?? undefined,
    orderTotalFen: checkoutOutstandingFen,
    memberOnly,
  });
  const combinedPlan = combinedPayment
    ? memberCombinedCheckoutPlan({
        orderTotalFen: checkoutOutstandingFen,
        memberAmountYuan: memberAuthorityLocked ? "" : memberAmountYuan,
        externalMethod: combinedExternalMethod,
      })
    : null;
  const combinedMemberBalanceSufficient = Boolean(
    combinedPlan &&
      paymentMember &&
      combinedPlan.walletAmountFen <= paymentMember.availableBalanceFen,
  );
  const paymentPlan = combinedPayment
    ? combinedPlan
    : memberCheckoutPlan({
        orderTotalFen: checkoutOutstandingFen,
        memberOnly,
      });
  const requiresOnlineMemberSpendPreview =
    memberPayment && !(memberOnly && offlineMemberBalanceDeferred && !memberFundsAvailable);
  const memberSpendPreviewAmountFen = paymentPlan?.walletAmountFen ?? null;
  const memberSpendPreviewOrderSnapshot = order ? JSON.stringify(order) : null;
  const memberSpendPreviewSessionId = order?.sessionId ?? null;
  const memberSpendPreviewOrderId = order?.id ?? null;
  const memberSpendPreviewExpectedTotalFen = order?.totalFen ?? null;
  const memberSpendPreviewMemberId = paymentMember?.id ?? null;
  const memberSpendPreviewMemberVersion = paymentMember?.version ?? null;
  const memberSpendPreviewWalletVersion = paymentMember?.walletVersion ?? null;
  const memberSpendPreviewCandidate = useMemo(() => {
    if (
      !requiresOnlineMemberSpendPreview ||
      !memberSpendPreviewMemberId ||
      memberSpendPreviewMemberVersion === null ||
      memberSpendPreviewWalletVersion === null ||
      memberSpendPreviewAmountFen === null ||
      memberSpendPreviewSessionId === null ||
      memberSpendPreviewOrderId === null ||
      memberSpendPreviewExpectedTotalFen === null ||
      memberSpendPreviewOrderSnapshot === null ||
      !memberFundsAvailable ||
      (combinedPayment && !combinedMemberBalanceSufficient)
    ) {
      return null;
    }
    const request = {
      memberId: memberSpendPreviewMemberId,
      memberBalanceFen: memberSpendPreviewAmountFen,
      expectedTotalFen: memberSpendPreviewExpectedTotalFen,
    };
    return {
      fingerprint: JSON.stringify([
        operatorIdentityEpoch,
        memberSpendPreviewOrderSnapshot,
        memberSpendPreviewMemberId,
        memberSpendPreviewMemberVersion,
        memberSpendPreviewWalletVersion,
        request.memberBalanceFen,
      ]),
      sessionId: memberSpendPreviewSessionId,
      orderId: memberSpendPreviewOrderId,
      request,
    };
  }, [
    combinedMemberBalanceSufficient,
    combinedPayment,
    memberFundsAvailable,
    memberSpendPreviewAmountFen,
    memberSpendPreviewExpectedTotalFen,
    memberSpendPreviewMemberId,
    memberSpendPreviewMemberVersion,
    memberSpendPreviewOrderId,
    memberSpendPreviewOrderSnapshot,
    memberSpendPreviewSessionId,
    memberSpendPreviewWalletVersion,
    operatorIdentityEpoch,
    requiresOnlineMemberSpendPreview,
  ]);
  const memberSpendPreviewFingerprint = memberSpendPreviewCandidate?.fingerprint ?? null;
  const [stableMemberSpendPreviewRequest, setStableMemberSpendPreviewRequest] =
    useState<typeof memberSpendPreviewCandidate>(null);
  useEffect(() => {
    setStableMemberSpendPreviewRequest(null);
    if (!memberSpendPreviewCandidate) return;
    const timer = window.setTimeout(() => {
      setStableMemberSpendPreviewRequest(memberSpendPreviewCandidate);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [memberSpendPreviewCandidate]);
  const activeMemberSpendPreviewRequest =
    stableMemberSpendPreviewRequest?.fingerprint === memberSpendPreviewFingerprint
      ? stableMemberSpendPreviewRequest
      : null;
  const memberSpendPreviewQuery = useQuery({
    queryKey: [
      "checkout-member-spend-preview",
      activeMemberSpendPreviewRequest?.fingerprint ?? "inactive",
    ],
    queryFn: () => {
      if (!activeMemberSpendPreviewRequest) {
        throw new Error("会员钱包扣款预览请求尚未稳定");
      }
      return getMemberSpendPreview(
        activeMemberSpendPreviewRequest.sessionId,
        activeMemberSpendPreviewRequest.orderId,
        activeMemberSpendPreviewRequest.request,
      );
    },
    enabled: activeMemberSpendPreviewRequest !== null,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const memberSpendPreview =
    activeMemberSpendPreviewRequest &&
    !memberSpendPreviewQuery.isFetching &&
    !memberSpendPreviewQuery.isError
      ? (memberSpendPreviewQuery.data ?? null)
      : null;
  const memberSpendPreviewReady = memberSpendPreview !== null;
  const memberSpendConfirmationKey =
    memberSpendPreview && memberSpendPreviewFingerprint
      ? `${memberSpendPreviewFingerprint}:${memberSpendPreview.planHash}`
      : null;
  const combinedPaymentConfirmed =
    memberSpendConfirmationKey !== null &&
    combinedPaymentConfirmationKey === memberSpendConfirmationKey;
  const combinedPaymentReady = Boolean(
    combinedPlan &&
      paymentMember &&
      selectedMethodEnabled &&
      combinedMemberBalanceSufficient &&
      combinedPaymentConfirmed &&
      memberSpendPreviewReady,
  );
  const paymentGroupName = useId();
  const paymentModeGroupName = useId();
  return (
    <div className="checkout-panel">
      <BillingBreakdownView order={order} />
      <fieldset
        className="payment-methods"
        disabled={zeroAmount || disabled || pending || officialPaymentPending}
      >
        <legend className={combinedPayment ? undefined : "visually-hidden"}>
          {combinedPayment ? "剩余金额收款方式" : "收款方式"}
        </legend>
        {(combinedPayment
          ? (["CASH", "STATIC_WECHAT", "STATIC_ALIPAY"] as PaymentMethod[])
          : ([
              "CASH",
              "WECHAT_PAY",
              "ALIPAY",
              "STATIC_WECHAT",
              "STATIC_ALIPAY",
              "MEMBER_BALANCE",
            ] as PaymentMethod[])
        ).map((candidate) => (
          <label
            className={method === candidate ? "is-active" : ""}
            title={isMethodEnabled(candidate) ? undefined : "该收款方式已在门店经营设置中停用"}
            key={candidate}
          >
            <input
              type="radio"
              name={paymentGroupName}
              value={candidate}
              checked={method === candidate}
              disabled={
                disabled || pending || officialPaymentPending || !isMethodEnabled(candidate)
              }
              onChange={() => {
                onFeedbackClear();
                onOfficialPaymentFeedbackClear();
                setCombinedPaymentConfirmationKey(null);
                onMethod(candidate);
              }}
            />
            {candidate === "CASH" ? <Banknote size={17} /> : <CircleDollarSign size={17} />}
            {combinedPayment
              ? memberCombinedCheckoutMethodLabel(candidate as MemberCombinedCheckoutMethod)
              : paymentMethodTerm(candidate).label}
            {!isMethodEnabled(candidate) ? (
              <small>
                {candidate === "MEMBER_BALANCE" ||
                candidate === "WECHAT_PAY" ||
                candidate === "ALIPAY"
                  ? "需要联网"
                  : "门店已停用"}
              </small>
            ) : null}
          </label>
        ))}
      </fieldset>
      {!zeroAmount ? (
        <fieldset
          className="checkout-payment-mode"
          disabled={disabled || pending || officialPaymentPending}
        >
          <legend>是否使用会员余额付一部分</legend>
          <label className={!combinedPayment ? "is-active" : ""}>
            <input
              type="radio"
              name={paymentModeGroupName}
              checked={!combinedPayment}
              disabled={disabled || pending || officialPaymentPending}
              onChange={() => {
                onFeedbackClear();
                onOfficialPaymentFeedbackClear();
                setCombinedPayment(false);
                setMemberAmountYuan("");
                setCombinedPaymentConfirmationKey(null);
              }}
            />
            <span>
              <strong>使用上方一种方式付清</strong>
              <small>按刚才选择的方式收取全部应收，不扣会员余额。</small>
            </span>
          </label>
          <label className={combinedPayment ? "is-active" : ""}>
            <input
              type="radio"
              name={paymentModeGroupName}
              checked={combinedPayment}
              disabled={
                disabled ||
                pending ||
                officialPaymentPending ||
                !memberFundsAvailable ||
                !manualCombinedMethodSelected ||
                !isMethodEnabled(method)
              }
              onChange={() => {
                onFeedbackClear();
                onOfficialPaymentFeedbackClear();
                setCombinedPayment(true);
                setMemberAmountYuan("");
                setCombinedPaymentConfirmationKey(null);
              }}
            />
            <span>
              <strong>会员余额 + 上方现场收款</strong>
              <small>
                {!memberFundsAvailable
                  ? offlineMemberBalanceDeferred
                    ? "当前离线，系统不能同时确认会员余额和现场收款；请联网后再使用。"
                    : "会员余额服务暂时不可用，当前不能同时使用会员余额和现场收款。"
                  : manualCombinedMethodSelected
                    ? `先扣一部分会员余额，剩余用${memberCombinedCheckoutMethodLabel(combinedExternalMethod)}收取。`
                    : "请先在上方选择现金、微信收款码或支付宝收款码，再启用组合支付。"}
              </small>
            </span>
          </label>
        </fieldset>
      ) : null}
      {!zeroAmount && !memberFundsAvailable ? (
        <output className="disabled-reason">
          {offlineMemberBalanceDeferred
            ? "当前离线，不能使用“会员余额 + 现场收款”。系统必须先联网确认最新余额，才能避免余额不足或重复扣款；请改用一种现场收款方式，或恢复联网后再使用组合支付。"
            : "会员余额服务暂时不可用，不能使用“会员余额 + 现场收款”；请改用一种现场收款方式，或稍后重试。"}
        </output>
      ) : null}
      {combinedPayment ? (
        <div className="checkout-combined-boundary">
          组合支付只支持“会员余额 +
          上方一种现场收款”。微信支付、支付宝正式支付和三路以上组合不能在这里发起。
        </div>
      ) : null}
      {memberPayment ? (
        <div className="checkout-member-panel">
          <CheckoutMemberPicker
            search={memberAuthorityLocked ? "" : memberSearch}
            results={memberAuthorityLocked ? [] : (members.data ?? [])}
            selected={paymentMember}
            loading={members.isFetching}
            error={
              members.isError
                ? members.error instanceof Error
                  ? members.error.message
                  : "系统没有返回明确原因"
                : null
            }
            activeIndex={activeMemberIndex}
            scanNotice={memberScanNotice}
            orderTotalFen={checkoutOutstandingFen}
            disabled={disabled || pending || officialPaymentPending}
            onSearch={(value) => {
              onFeedbackClear();
              setMemberSearch(value);
              setActiveMemberIndex(-1);
              setMemberScanNotice(null);
              setCombinedPaymentConfirmationKey(null);
              if (selectedMember && value !== checkoutMemberSearchLabel(selectedMember)) {
                setSelectedMember(null);
              }
            }}
            onSelect={(member) => {
              onFeedbackClear();
              setSelectedMember(member);
              setMemberSearch(checkoutMemberSearchLabel(member));
              setActiveMemberIndex(-1);
              setCombinedPaymentConfirmationKey(null);
            }}
            onActiveIndex={setActiveMemberIndex}
            onScanPlaceholder={() =>
              setMemberScanNotice(
                "扫码或读卡入口已经预留；本轮尚未连接真实设备，请继续用姓名、手机号或会员号搜索。",
              )
            }
          />
          {paymentMember ? (
            <p>
              {memberFundsAvailable ? (
                <>
                  当前可用会员余额 {formatFee(paymentMember.availableBalanceFen)}
                  ；积分、次数或时长权益会由会员系统按本单商品、台费和助教规则核算。
                </>
              ) : offlineMemberBalanceDeferred ? (
                "当前离线，本页不会显示余额已扣；台单结账后会保留待扣金额，联网后再按本单金额处理。"
              ) : (
                "会员余额服务暂时不可用，当前不能提交余额扣款。"
              )}
            </p>
          ) : null}
          {combinedPayment ? (
            <label className="checkout-member-amount">
              本次从会员余额扣多少
              <input
                inputMode="decimal"
                name="checkout-member-combined-amount"
                value={memberAuthorityLocked ? "" : memberAmountYuan}
                placeholder="例如 30.00"
                maxLength={11}
                disabled={disabled || pending}
                onChange={(event) => {
                  onFeedbackClear();
                  setMemberAmountYuan(event.target.value);
                  setCombinedPaymentConfirmationKey(null);
                }}
              />
              <small>必须大于 0 且小于本单应收，不能把全部金额都填为会员余额。</small>
            </label>
          ) : null}
          {!combinedPayment && !memberOnlyAmountValid && paymentMember ? (
            <div className="disabled-reason">请选择有效会员并核对本单应收金额后再确认扣款。</div>
          ) : null}
          {combinedPayment && memberAmountYuan.trim().length > 0 && !combinedPlan ? (
            <div className="disabled-reason">
              会员余额金额必须大于 0 且小于本单应收，请输入最多两位小数的金额。
            </div>
          ) : null}
          {combinedPayment && combinedPlan && paymentMember && !combinedMemberBalanceSufficient ? (
            <div className="disabled-reason">
              会员可用余额不足以扣除 {formatFee(combinedPlan.walletAmountFen)}
              ，请减少会员余额金额或先充值。
            </div>
          ) : null}
        </div>
      ) : null}
      {memberPayment && paymentMember && paymentPlan ? (
        <CheckoutPaymentPlan
          plan={paymentPlan}
          deferMemberBalance={memberOnly && offlineMemberBalanceDeferred && !memberFundsAvailable}
        />
      ) : null}
      {requiresOnlineMemberSpendPreview && paymentMember ? (
        <MemberSpendPreviewPanel
          preview={memberSpendPreview}
          loading={
            memberSpendPreviewCandidate !== null &&
            (!activeMemberSpendPreviewRequest || memberSpendPreviewQuery.isFetching)
          }
          error={
            activeMemberSpendPreviewRequest && memberSpendPreviewQuery.isError
              ? businessErrorMessage(memberSpendPreviewQuery.error, "系统没有返回明确原因")
              : null
          }
          inputReady={memberSpendPreviewCandidate !== null}
          memberFundsAvailable={memberFundsAvailable}
          remainingOnsiteFen={
            memberSpendPreviewCandidate && checkoutOutstandingFen !== undefined
              ? checkoutOutstandingFen - memberSpendPreviewCandidate.request.memberBalanceFen
              : null
          }
          onRetry={() => void memberSpendPreviewQuery.refetch()}
        />
      ) : null}
      {combinedPayment && combinedPlan && paymentMember && combinedMemberBalanceSufficient ? (
        <label className="checkout-combined-confirmation">
          <input
            type="checkbox"
            checked={combinedPaymentConfirmed}
            disabled={disabled || pending || !memberSpendPreviewReady}
            onChange={(event) =>
              setCombinedPaymentConfirmationKey(
                event.target.checked ? memberSpendConfirmationKey : null,
              )
            }
          />
          <span>
            <strong>我已核对这两路金额和收款后果</strong>
            <small>
              会员余额扣 {formatFee(combinedPlan.walletAmountFen)}；
              {combinedExternalMethod === "CASH"
                ? `当面收现金 ${formatFee(combinedPlan.externalAmountFen)}`
                : `确认顾客使用${memberCombinedCheckoutMethodLabel(combinedExternalMethod)}支付 ${formatFee(combinedPlan.externalAmountFen)}，该路结账后待渠道核对`}
              。
            </small>
          </span>
        </label>
      ) : null}
      <CheckoutFailureNotice
        feedback={feedback}
        order={order}
        selectedMember={selectedMember}
        enabledManualMethods={enabledManualMethods}
        onUseManualMethod={(manualMethod) => {
          onFeedbackClear();
          onOfficialPaymentFeedbackClear();
          setCombinedPayment(false);
          setMemberAmountYuan("");
          setCombinedPaymentConfirmationKey(null);
          onMethod(manualMethod);
        }}
        onUseCombined={(manualMethod, authoritativeMember) => {
          onFeedbackClear();
          onOfficialPaymentFeedbackClear();
          setSelectedMember(authoritativeMember);
          setMemberSearch(checkoutMemberSearchLabel(authoritativeMember));
          setActiveMemberIndex(-1);
          onMethod(manualMethod);
          setCombinedPayment(true);
          setMemberAmountYuan("");
          setCombinedPaymentConfirmationKey(null);
        }}
        onAuthorityResolved={(authoritativeMember) => {
          if (
            feedback?.code !== "MEMBER_BALANCE_INSUFFICIENT" ||
            selectedMember?.id !== authoritativeMember.id ||
            selectedMember.memberNumber !== authoritativeMember.memberNumber
          ) {
            return;
          }
          setSelectedMember(authoritativeMember);
          setMemberSearch(checkoutMemberSearchLabel(authoritativeMember));
          setMemberAuthorityResolution({
            feedback,
            operatorIdentityEpoch,
            memberId: authoritativeMember.id,
            memberNumber: authoritativeMember.memberNumber,
            version: authoritativeMember.version,
            walletVersion: authoritativeMember.walletVersion,
          });
        }}
        onRecharge={(memberId) => onRechargeMember?.(memberId)}
      />
      <OfficialPaymentFeedbackNotice feedback={officialPaymentFeedback} />
      <div className={`payment-notice ${staticCode || officialRail ? "is-pending" : ""}`}>
        {zeroAmount ? (
          <>
            <Check size={18} />
            <span>
              <strong>{prepaidCovered ? "会员预付已覆盖当前账单" : "团购券已覆盖本单应收"}</strong>
              <small>
                {prepaidCovered
                  ? `账单合计 ${formatFee(order?.totalFen ?? 0)} 已在开台时从会员余额支付；本次不再扣款，确认后直接结账并进入待清台。`
                  : "无需收现金、扫码或扣会员钱包，确认后直接结账并进入待清台。"}
              </small>
            </span>
          </>
        ) : combinedPayment && combinedPlan ? (
          <>
            <ShieldCheck size={18} />
            <span>
              <strong>两路按同一结账请求一次完成</strong>
              <small>
                会员余额扣 {formatFee(combinedPlan.walletAmountFen)}，
                {memberCombinedCheckoutMethodLabel(combinedExternalMethod)}收{" "}
                {formatFee(combinedPlan.externalAmountFen)}。
                任一路结果不明确时，不能改金额、换方式或新建第二笔。
              </small>
            </span>
          </>
        ) : memberOnly ? (
          <>
            <ShieldCheck size={18} />
            <span>
              <strong>
                {offlineMemberBalanceDeferred && !memberFundsAvailable
                  ? "离线先完成台单，余额待联网扣除"
                  : "在线核对余额后扣款"}
              </strong>
              <small>
                {offlineMemberBalanceDeferred && !memberFundsAvailable
                  ? "台单结账后进入待清洁；本次待扣金额会保留，联网后再按原金额处理。"
                  : "同一结账请求重试不会重复扣减会员余额。"}
              </small>
            </span>
          </>
        ) : officialRail ? (
          <>
            <ShieldCheck size={18} />
            <span>
              <strong>只有支付渠道确认到账后才会结账</strong>
              <small>
                测试渠道显示成功、等待中或结果未知都不会记作到账；结果未确认时不能改收或让顾客再次付款。
              </small>
            </span>
          </>
        ) : staticCode ? (
          <>
            <BadgeAlert size={18} />
            <span>
              <strong>员工人工确认，结账后保持“待渠道核对”</strong>
              <small>系统不会把顾客截图或口头确认伪报为渠道到账。</small>
            </span>
          </>
        ) : (
          <>
            <Check size={18} />
            <span>
              <strong>现金由门店本地确认</strong>
              <small>不依赖第三方渠道，可在受限离线模式完成。</small>
            </span>
          </>
        )}
      </div>
      <div className="checkout-total">
        <span>{(order?.paidFen ?? 0) > 0 ? "本次待收" : "应收"}</span>
        <strong>{formatFee(checkoutOutstandingFen ?? 0)}</strong>
        {(order?.paidFen ?? 0) > 0 ? (
          <small>
            账单合计 {formatFee(order?.totalFen ?? 0)}，已预付 {formatFee(order?.paidFen ?? 0)}；
            本次不会重复收取已预付金额。
          </small>
        ) : null}
      </div>
      <button
        className="button primary checkout-button"
        type="button"
        disabled={
          pending ||
          officialPaymentPending ||
          disabled ||
          (memberPayment && memberAuthorityLocked) ||
          (requiresOnlineMemberSpendPreview && !memberSpendPreviewReady) ||
          !order ||
          !selectedMethodEnabled ||
          (!zeroAmount && officialRail !== null && order.billingState !== "ENDED") ||
          (!zeroAmount &&
            (combinedPayment
              ? !combinedPaymentReady
              : !memberOnlyAmountValid || (memberPayment && !paymentMember)))
        }
        onClick={() => {
          if (zeroAmount) {
            onCheckout();
            return;
          }
          if (officialRail) {
            onOfficialPayment(officialRail);
            return;
          }
          if (requiresOnlineMemberSpendPreview && !memberSpendPreview) return;
          onCheckout(
            memberPayment && paymentMember
              ? {
                  memberId: paymentMember.id,
                  ...(memberSpendPreview
                    ? { memberSpendPlanHash: memberSpendPreview.planHash }
                    : {}),
                  ...(combinedPayment && combinedPlan
                    ? { memberBalanceFen: combinedPlan.walletAmountFen }
                    : {}),
                }
              : undefined,
          );
        }}
      >
        <ReceiptText size={17} />
        {zeroAmount
          ? "确认无需补收并结束本次使用"
          : officialPaymentPending
            ? "正在等待渠道结果…"
            : officialRail
              ? `发起${officialRail === "WECHAT_PAY" ? "微信支付" : "支付宝"}并等待渠道确认`
              : combinedPayment
                ? "确认两路收款并结束本次使用"
                : "确认收款并结束本次使用"}
      </button>
      {!zeroAmount && officialRail && order?.billingState !== "ENDED" ? (
        <div className="disabled-reason">
          正式支付前请先在“计费”页停止计费并核对最终应收金额；系统不会用仍在变化的金额发起付款。
        </div>
      ) : null}
    </div>
  );
}

export interface OrderSearchDraft {
  dateFrom: string;
  dateTo: string;
  orderQuery: string;
  tableNumber: string;
  memberSearch: string;
  productQuery: string;
  paymentMethod: PaymentMethod | "";
  openingMethod: NonNullable<OrderStrongReportFilter["openingMethod"]> | "";
}

export function orderSearchDraftError(draft: OrderSearchDraft): string | null {
  if (draft.dateFrom && draft.dateTo && draft.dateFrom > draft.dateTo) {
    return "开始日期不能晚于结束日期";
  }
  if (draft.orderQuery.trim().length > 128) return "业务单号或历史记录标识不能超过 128 个字符";
  if (draft.tableNumber.trim().length > 32) return "桌号搜索不能超过 32 个字符";
  if (draft.memberSearch.trim().length > 80) return "会员搜索不能超过 80 个字符";
  if (draft.productQuery.trim().length > 80) return "商品搜索不能超过 80 个字符";
  return null;
}

export type OrderListQueryStatus = "UNAVAILABLE" | "LOADING" | "ERROR" | "EMPTY" | "READY";

export function orderListQueryView(input: {
  businessDayLoading: boolean;
  businessDayError: boolean;
  ordersLoading: boolean;
  ordersError: boolean;
  orders: readonly unknown[] | undefined;
}): { status: OrderListQueryStatus; countLabel: string } {
  if (input.businessDayError) return { status: "UNAVAILABLE", countLabel: "无法确认" };
  if (input.ordersError) return { status: "ERROR", countLabel: "无法确认" };
  if (input.businessDayLoading || input.ordersLoading || !input.orders) {
    return { status: "LOADING", countLabel: "正在读取" };
  }
  return input.orders.length === 0
    ? { status: "EMPTY", countLabel: "找到 0 笔" }
    : { status: "READY", countLabel: `找到 ${input.orders.length} 笔` };
}

function orderStrongReportOpeningMethodExplanation(
  method: OrderSearchDraft["openingMethod"],
): string {
  switch (method) {
    case "DIRECT":
      return "只看工作人员直接开台的订单，不包含预约入座或在线团购核销。";
    case "RESERVATION":
      return "只看从预约入座开台的订单；明细会显示预约单号。";
    case "GROUP_BUY_ONLINE":
      return "只看在线团购核销后开台的订单；明细会显示团购核销单号。";
    case "UNKNOWN":
      return "只看开台来源尚未确认的订单；请按异常定位回到原单核对。";
    default:
      return "查看所有开台来源，不缩小订单范围。";
  }
}

export function orderStrongReportMatchesSelection(
  report: OrderStrongReportReadModel | undefined,
  operator: Pick<OperatorIdentity, "tenantId" | "storeId">,
  filters: OrderStrongReportFilter,
): report is OrderStrongReportReadModel {
  return Boolean(
    report &&
      report.tenantId === operator.tenantId &&
      report.storeId === operator.storeId &&
      report.filters.offlineOnly === filters.offlineOnly &&
      report.filters.dateFrom === filters.dateFrom &&
      report.filters.dateTo === filters.dateTo &&
      report.filters.orderQuery === filters.orderQuery &&
      report.filters.tableNumber === filters.tableNumber &&
      report.filters.memberSearch === filters.memberSearch &&
      report.filters.productQuery === filters.productQuery &&
      report.filters.paymentMethod === filters.paymentMethod &&
      report.filters.openingMethod === filters.openingMethod &&
      report.filters.shiftId === filters.shiftId,
  );
}

function orderStrongReportTableUsage(summary: OrderStrongReportReadModel["summary"]): {
  value: string;
  detail: string;
} {
  const detail = `已知 ${summary.tableUsageKnownOrders} 笔，暂不可用 ${summary.tableUsageUnknownOrders} 笔`;
  if (summary.totalBillableSeconds === null) return { value: "暂不可用", detail };
  const hours = Math.floor(summary.totalBillableSeconds / 3_600);
  const minutes = Math.floor((summary.totalBillableSeconds % 3_600) / 60);
  const availability =
    summary.tableUsageAvailability === "AVAILABLE"
      ? "范围完整"
      : summary.tableUsageAvailability === "PARTIAL"
        ? "仅展示已知范围"
        : "暂不可用";
  return { value: `${hours} 小时 ${minutes} 分钟`, detail: `${availability}；${detail}` };
}

function orderStrongReportCancellationRateLabel(
  summary: OrderStrongReportReadModel["summary"],
): string {
  if (summary.orderCount === 0 && summary.cancellationRateBasisPoints === null) return "暂无订单";
  if (summary.cancellationRateBasisPoints === 0) return "0%";
  if (summary.cancellationRateBasisPoints === null) return "暂时无法确认";
  return `${(summary.cancellationRateBasisPoints / 100).toFixed(2)}%`;
}

function orderStrongReportDownloadIdentity(
  report: OrderStrongReportReadModel,
  operator: Pick<OperatorIdentity, "id" | "tenantId" | "storeId">,
): string {
  return [
    operator.tenantId,
    operator.storeId,
    operator.id,
    JSON.stringify(report.filters),
    report.contentSha256,
  ].join("\u0000");
}

function orderStrongReportFilterDescription(filters: OrderStrongReportFilter): string {
  const dates =
    filters.dateFrom === filters.dateTo && filters.dateFrom
      ? `营业日 ${filters.dateFrom}`
      : filters.dateFrom || filters.dateTo
        ? `营业日 ${filters.dateFrom ?? "最早记录"} 至 ${filters.dateTo ?? "最新记录"}`
        : "全部营业日";
  const parts = [dates, filters.offlineOnly ? "仅离线期间产生" : "全部来源"];
  if (filters.orderQuery) parts.push(`订单号含“${filters.orderQuery}”`);
  if (filters.tableNumber) parts.push(`桌号含“${filters.tableNumber}”`);
  if (filters.productQuery) parts.push(`下单时商品含“${filters.productQuery}”`);
  if (filters.memberSearch) parts.push(`会员含“${filters.memberSearch}”`);
  parts.push(
    filters.paymentMethod
      ? `收款方式：${paymentMethodTerm(filters.paymentMethod).label}`
      : "全部收款方式",
  );
  parts.push(
    filters.openingMethod
      ? `开台方式：${orderOpeningMethodTerm(filters.openingMethod).label}`
      : "全部开台方式",
  );
  parts.push(filters.shiftId ? "订单范围：当前进行中班次" : "订单范围：全部订单");
  return parts.join("；");
}

function saveOrderStrongReport(report: OrderStrongReportReadModel): void {
  downloadTextFile(report.fileName, report.csv, "text/csv;charset=utf-8");
}

function printOrderStrongReport(_report: OrderStrongReportReadModel): void {
  window.print();
}

type ReceiptReprintInlineNotice = {
  orderId: string;
  message: string;
  needsAttention: boolean;
};

function ReceiptReprintInlineFeedback({
  orderId,
  orderReference,
  errorMessage,
  notice,
  recovery,
  pending,
  onRetry,
}: {
  orderId: string;
  orderReference?: OrderDisplayReference;
  errorMessage: string | null;
  notice: ReceiptReprintInlineNotice | null;
  recovery: ReceiptReprintRecovery | null;
  pending: boolean;
  onRetry: (recovery: ReceiptReprintRecovery) => void;
}): React.JSX.Element | null {
  const visibleOrderReference = orderReference ?? orderDisplayReference({ id: orderId });
  const feedbackRef = useRef<HTMLElement>(null);
  const feedbackKey = errorMessage
    ? `error:${errorMessage}`
    : recovery
      ? `recovery:${recovery.state}:${recovery.command.idempotencyKey}`
      : notice
        ? `notice:${notice.needsAttention}:${notice.message}`
        : "";
  useEffect(() => {
    if (feedbackKey) feedbackRef.current?.focus();
  }, [feedbackKey]);

  if (errorMessage) {
    return (
      <section
        className="error-band receipt-reprint-inline-feedback"
        ref={feedbackRef}
        role="alert"
        tabIndex={-1}
        aria-live="assertive"
      >
        <strong>小票补打没有确认完成</strong>
        <p>
          {visibleOrderReference.label} {visibleOrderReference.value}
        </p>
        <p>原因：{errorMessage}</p>
        <p>订单和收款记录没有改变。当前结果尚未确认，请使用原请求重试，系统不会新建第二笔补打。</p>
        {recovery ? (
          <button
            className="button secondary compact"
            type="button"
            disabled={pending || recovery.state === "CLAIMED"}
            onClick={() => onRetry(recovery)}
          >
            <RefreshCw size={16} /> 重试本单补打
          </button>
        ) : null}
      </section>
    );
  }

  if (recovery) {
    const submitting = recovery.state === "CLAIMED";
    return (
      <section
        className="error-band receipt-reprint-inline-feedback"
        ref={feedbackRef}
        role={submitting ? "status" : "alert"}
        tabIndex={-1}
        aria-live={submitting ? "polite" : "assertive"}
      >
        <strong>{submitting ? "正在提交本单小票补打" : "本单上次补打结果尚未确认"}</strong>
        <p>
          {visibleOrderReference.label} {visibleOrderReference.value}
        </p>
        <p>
          {submitting
            ? "请勿重复点击。"
            : "请沿用原请求重试，系统不会另建第二笔补打，也不会改变订单和收款记录。"}
        </p>
        {!submitting ? (
          <button
            className="button secondary compact"
            type="button"
            disabled={pending}
            onClick={() => onRetry(recovery)}
          >
            <RefreshCw size={16} /> 重试本单补打
          </button>
        ) : null}
      </section>
    );
  }

  if (!notice) return null;
  return (
    <section
      className={`${notice.needsAttention ? "error-band" : "success-band"} receipt-reprint-inline-feedback`}
      ref={feedbackRef}
      role={notice.needsAttention ? "alert" : "status"}
      tabIndex={-1}
      aria-live={notice.needsAttention ? "assertive" : "polite"}
    >
      <strong>{notice.needsAttention ? "本单小票需要人工检查" : "本单补打请求已登记"}</strong>
      <p>
        {visibleOrderReference.label} {visibleOrderReference.value}
      </p>
      <p>{notice.message}</p>
    </section>
  );
}

type OrdersDeskProps = {
  operator: OperatorIdentity;
  effectiveRoles?: readonly OperatorRole[];
  authenticationEvidence: "SIMULATED" | "STORE_ACCEPTED";
  sessionContext?: OperatorSessionContext;
  saveReport?: (report: OrderStrongReportReadModel) => void;
  printReport?: (report: OrderStrongReportReadModel) => void;
};

export function OrdersDesk(props: OrdersDeskProps): React.JSX.Element {
  const location = useLocation();
  const detailViews = new URLSearchParams(location.search).getAll("view");
  if (detailViews.includes("ORDER_RECEIVABLE_DETAILS")) {
    const orderReceivableDetailRoute = orderReceivableBusinessMetricDetailRouteFromSearch(
      location.search,
    );
    if (orderReceivableDetailRoute.kind !== "NONE") {
      return (
        <OrderReceivableBusinessMetricDetailPanel
          operator={props.operator}
          effectiveRoles={props.effectiveRoles ?? [props.operator.role]}
          route={orderReceivableDetailRoute}
        />
      );
    }
  }
  if (detailViews.includes("COMMISSION_PAYABLE_DETAILS")) {
    const commissionPayableDetailRoute = commissionPayableBusinessMetricDetailRouteFromSearch(
      location.search,
    );
    if (commissionPayableDetailRoute.kind !== "NONE") {
      return (
        <CommissionPayableBusinessMetricDetailPanel
          operator={props.operator}
          effectiveRoles={props.effectiveRoles ?? [props.operator.role]}
          route={commissionPayableDetailRoute}
        />
      );
    }
  }
  if (detailViews.includes("PRODUCT_COST_DETAILS")) {
    const productCostDetailRoute = productCostBusinessMetricDetailRouteFromSearch(location.search);
    if (productCostDetailRoute.kind !== "NONE") {
      return (
        <ProductCostBusinessMetricDetailPanel
          operator={props.operator}
          effectiveRoles={props.effectiveRoles ?? [props.operator.role]}
          route={productCostDetailRoute}
        />
      );
    }
  }
  if (detailViews.includes("CONFIRMED_RECEIPT_DETAILS")) {
    const confirmedReceiptDetailRoute = confirmedReceiptBusinessMetricDetailRouteFromSearch(
      location.search,
    );
    if (confirmedReceiptDetailRoute.kind !== "NONE") {
      return (
        <ConfirmedReceiptBusinessMetricDetailPanel
          operator={props.operator}
          effectiveRoles={props.effectiveRoles ?? [props.operator.role]}
          route={confirmedReceiptDetailRoute}
        />
      );
    }
  }
  const refundDetailRoute = refundBusinessMetricDetailRouteFromSearch(location.search);
  if (refundDetailRoute.kind !== "NONE") {
    return (
      <RefundBusinessMetricDetailPanel
        operator={props.operator}
        effectiveRoles={props.effectiveRoles ?? [props.operator.role]}
        route={refundDetailRoute}
      />
    );
  }
  return <OrdersDeskContent {...props} />;
}

function OrdersDeskContent({
  operator,
  effectiveRoles = [operator.role],
  authenticationEvidence,
  sessionContext,
  saveReport = saveOrderStrongReport,
  printReport = printOrderStrongReport,
}: OrdersDeskProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const location = useLocation();
  const initialOrderQuery = useMemo(
    () => (new URLSearchParams(location.search).get("orderQuery") ?? "").slice(0, 128),
    [location.search],
  );
  const initialBusinessDate = useMemo(() => {
    const value = new URLSearchParams(location.search).get("businessDate") ?? "";
    if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return "";
    const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
    return Number.isFinite(milliseconds) &&
      new Date(milliseconds).toISOString().slice(0, 10) === value
      ? value
      : "";
  }, [location.search]);
  const printIdentityKey = receiptReprintIdentityKey(operator);
  const printIdentityKeyRef = useRef(printIdentityKey);
  printIdentityKeyRef.current = printIdentityKey;
  const canChooseReportShift =
    effectiveRoles.includes("OWNER") || effectiveRoles.includes("MANAGER");
  const cashierShiftLocked =
    !canChooseReportShift &&
    effectiveRoles.includes("CASHIER") &&
    sessionContext?.mode === "SHARED_SHIFT";
  const cashierPersonalExactLookup =
    !canChooseReportShift && effectiveRoles.includes("CASHIER") && !cashierShiftLocked;
  const cashierShiftId =
    cashierShiftLocked &&
    sessionContext?.mode === "SHARED_SHIFT" &&
    sessionContext.shiftStatus === "OPEN"
      ? sessionContext.shiftId
      : null;
  const shifts = useQuery({
    queryKey: [
      "cash-shifts",
      "order-report-scope",
      operator.tenantId,
      operator.storeId,
      operator.id,
    ],
    queryFn: listCashShifts,
    enabled: canChooseReportShift,
    refetchInterval: 2_000,
  });
  const currentOpenShiftId = shifts.data?.find((shift) => shift.status === "OPEN")?.id ?? null;
  const [shiftScopeDraft, setShiftScopeDraft] = useState<"ALL" | "CURRENT">("ALL");
  const [appliedShiftScope, setAppliedShiftScope] = useState<"ALL" | "CURRENT">("ALL");
  const [dateRangeUserEdited, setDateRangeUserEdited] = useState(false);
  const [offlineOnly, setOfflineOnly] = useState(false);
  const [searchDraft, setSearchDraft] = useState<OrderSearchDraft>({
    dateFrom: "",
    dateTo: "",
    orderQuery: initialOrderQuery,
    tableNumber: "",
    memberSearch: "",
    productQuery: "",
    paymentMethod: "",
    openingMethod: "",
  });
  const [appliedSearch, setAppliedSearch] = useState<OrderSearchDraft>(searchDraft);
  const [focusedAnomalyOrderId, setFocusedAnomalyOrderId] = useState<string | null>(null);
  const [reportDownloadFeedback, setReportDownloadFeedback] = useState<{
    identity: string;
    message: string;
  } | null>(null);
  const orderSearchInitialized = useRef(false);
  const [printNotice, setPrintNotice] = useState<ReceiptReprintInlineNotice | null>(null);
  const [printNoticeIdentityKey, setPrintNoticeIdentityKey] = useState<string | null>(null);
  const [lastPrintAttemptIdentityKey, setLastPrintAttemptIdentityKey] = useState<string | null>(
    null,
  );
  const [printRecoveryState, setPrintRecoveryState] = useState<{
    identityKey: string;
    view: ReceiptReprintRecoveryView;
  }>(() => ({
    identityKey: printIdentityKey,
    view: readReceiptReprintRecovery(printIdentityKey),
  }));
  const printRecoveryView: ReceiptReprintRecoveryView =
    printRecoveryState.identityKey === printIdentityKey
      ? printRecoveryState.view
      : { status: "OTHER_OPERATOR" };
  const summary = useQuery({
    queryKey: ["reconciliation", "current-business-day", operator.tenantId, operator.storeId],
    queryFn: () => getReconciliationSummary(),
    refetchInterval: 2_000,
  });
  useEffect(() => {
    if (!summary.data?.businessDate || orderSearchInitialized.current) return;
    orderSearchInitialized.current = true;
    const clearDefaultDateRange =
      cashierShiftLocked || cashierPersonalExactLookup || shiftScopeDraft === "CURRENT";
    const initialSearch: OrderSearchDraft = {
      dateFrom:
        initialOrderQuery || clearDefaultDateRange
          ? ""
          : initialBusinessDate || summary.data.businessDate,
      dateTo:
        initialOrderQuery || clearDefaultDateRange
          ? ""
          : initialBusinessDate || summary.data.businessDate,
      orderQuery: initialOrderQuery,
      tableNumber: "",
      memberSearch: "",
      productQuery: "",
      paymentMethod: "",
      openingMethod: "",
    };
    setDateRangeUserEdited(Boolean(initialBusinessDate) && !clearDefaultDateRange);
    setSearchDraft(initialSearch);
    setAppliedSearch(initialSearch);
  }, [
    cashierShiftLocked,
    cashierPersonalExactLookup,
    initialBusinessDate,
    initialOrderQuery,
    shiftScopeDraft,
    summary.data?.businessDate,
  ]);
  const reportFilters: OrderStrongReportFilter = useMemo(() => {
    const paymentMethod: OrderStrongReportFilter["paymentMethod"] = [
      "CASH",
      "STATIC_WECHAT",
      "STATIC_ALIPAY",
      "MEMBER_BALANCE",
    ].includes(appliedSearch.paymentMethod)
      ? (appliedSearch.paymentMethod as OrderStrongReportFilter["paymentMethod"])
      : null;
    return {
      offlineOnly,
      dateFrom: appliedSearch.dateFrom || null,
      dateTo: appliedSearch.dateTo || null,
      orderQuery: appliedSearch.orderQuery.trim(),
      tableNumber: appliedSearch.tableNumber.trim(),
      memberSearch: appliedSearch.memberSearch.trim(),
      productQuery: appliedSearch.productQuery.trim(),
      paymentMethod,
      openingMethod: appliedSearch.openingMethod || null,
      shiftId: cashierShiftLocked
        ? cashierShiftId
        : appliedShiftScope === "CURRENT"
          ? currentOpenShiftId
          : null,
    };
  }, [
    appliedSearch,
    appliedShiftScope,
    cashierShiftId,
    cashierShiftLocked,
    currentOpenShiftId,
    offlineOnly,
  ]);
  const reportShiftScopeReady = cashierShiftLocked
    ? cashierShiftId !== null
    : cashierPersonalExactLookup
      ? isOrderNumber(appliedSearch.orderQuery.trim())
      : appliedShiftScope !== "CURRENT" || currentOpenShiftId !== null;
  const report = useQuery({
    queryKey: [
      "order-strong-report",
      operator.tenantId,
      operator.storeId,
      operator.id,
      reportFilters,
    ],
    queryFn: () =>
      getOrderStrongReport(reportFilters, {
        tenantId: operator.tenantId,
        storeId: operator.storeId,
      }),
    enabled: orderSearchInitialized.current && reportShiftScopeReady,
    refetchInterval: 2_000,
  });
  const visibleReport =
    reportShiftScopeReady &&
    !report.isError &&
    orderStrongReportMatchesSelection(report.data, operator, reportFilters)
      ? report.data
      : undefined;
  const reportScopeChanged = Boolean(
    report.data && !orderStrongReportMatchesSelection(report.data, operator, reportFilters),
  );
  const visibleReportIdentity = visibleReport
    ? orderStrongReportDownloadIdentity(visibleReport, operator)
    : null;
  const connectivity = useQuery({
    queryKey: ["connectivity"],
    queryFn: getConnectivity,
    refetchInterval: 1_000,
  });
  const reprint = useMutation({
    mutationFn: async (input: {
      recovery: ReceiptReprintRecovery;
      identityKey: string;
      identityEpoch: number;
    }) => {
      try {
        if (printIdentityKeyRef.current !== input.identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的小票补打仍待确认，请切回原账号处理",
          );
        }
        assertOperatorIdentityUnchanged(input.identityEpoch);
        const effect = await reprintReceipt(
          input.recovery.command.orderId,
          input.recovery.command.idempotencyKey,
        );
        assertOperatorIdentityUnchanged(input.identityEpoch);
        if (printIdentityKeyRef.current !== input.identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的小票补打仍待确认，请切回原账号处理",
          );
        }
        if (!clearReceiptReprintRecovery(input.recovery, input.identityKey)) {
          throw new ApiRequestError(
            "RECEIPT_REPRINT_RECOVERY_CLEAR_FAILED",
            "补打请求已返回结果，但本机安全记录无法清除；已暂停新操作，请刷新后核对",
          );
        }
        return effect;
      } catch (error) {
        markReceiptReprintOutcomeUnknown(input.recovery, input.identityKey);
        throw error;
      } finally {
        finishReceiptReprintDispatch(input.recovery, input.identityKey);
      }
    },
    onSuccess: async (effect, input) => {
      setPrintNotice({ orderId: effect.orderId, ...receiptReprintNotice(effect) });
      setPrintNoticeIdentityKey(input.identityKey);
      try {
        await queryClient.invalidateQueries({ queryKey: ["device-status"] });
      } catch {
        // The strict print result remains valid; the device status query reports refresh failures.
      }
    },
    onSettled: (_data, _error, input) => {
      if (printIdentityKeyRef.current !== input.identityKey) return;
      setPrintRecoveryState({
        identityKey: input.identityKey,
        view: readReceiptReprintRecovery(input.identityKey),
      });
    },
  });
  useEffect(() => {
    setPrintRecoveryState({
      identityKey: printIdentityKey,
      view: readReceiptReprintRecovery(printIdentityKey),
    });
    setPrintNotice(null);
    setPrintNoticeIdentityKey(null);
    setLastPrintAttemptIdentityKey(null);
  }, [printIdentityKey]);
  const dispatchFrozenReprint = (recovery: ReceiptReprintRecovery): void => {
    setPrintNotice(null);
    setPrintNoticeIdentityKey(null);
    reprint.reset();
    try {
      dispatchReceiptReprint(connectivity, recovery.command.orderId, () => {
        if (!beginReceiptReprintDispatch(recovery, printIdentityKey)) {
          setPrintRecoveryState({
            identityKey: printIdentityKey,
            view: readReceiptReprintRecovery(printIdentityKey),
          });
          return;
        }
        setLastPrintAttemptIdentityKey(printIdentityKey);
        reprint.mutate({
          recovery,
          identityKey: printIdentityKey,
          identityEpoch: currentOperatorIdentityEpoch(),
        });
      });
    } catch (error) {
      setLastPrintAttemptIdentityKey(printIdentityKey);
      reprint.reset();
      setPrintNotice({
        orderId: recovery.command.orderId,
        message: error instanceof Error ? error.message : "当前无法安全提交小票补打",
        needsAttention: true,
      });
      setPrintNoticeIdentityKey(printIdentityKey);
    }
  };
  const startReceiptReprint = (orderId: string): void => {
    try {
      dispatchReceiptReprint(connectivity, orderId, () => {
        const command = { orderId, idempotencyKey: crypto.randomUUID() };
        const claimed = claimReceiptReprintRecovery(printIdentityKey, command);
        setPrintRecoveryState({ identityKey: printIdentityKey, view: claimed });
        if (
          claimed.status !== "OWNED" ||
          claimed.recovery.command.idempotencyKey !== command.idempotencyKey
        ) {
          return;
        }
        dispatchFrozenReprint(claimed.recovery);
      });
    } catch (error) {
      setPrintNotice({
        orderId,
        message: error instanceof Error ? error.message : "当前无法安全提交小票补打",
        needsAttention: true,
      });
      setPrintNoticeIdentityKey(printIdentityKey);
    }
  };
  const searchError =
    orderSearchDraftError(searchDraft) ??
    (cashierPersonalExactLookup && !isOrderNumber(searchDraft.orderQuery.trim())
      ? "个人账号申请退款时，请完整输入小票上的业务单号，例如 DD-20260827-012345ABCDEF。"
      : cashierPersonalExactLookup &&
          (searchDraft.dateFrom !== "" ||
            searchDraft.dateTo !== "" ||
            searchDraft.tableNumber.trim() !== "" ||
            searchDraft.memberSearch.trim() !== "" ||
            searchDraft.productQuery.trim() !== "" ||
            searchDraft.paymentMethod !== "" ||
            searchDraft.openingMethod !== "" ||
            offlineOnly)
        ? "个人账号申请退款只按完整业务单号读取一张原单，请清空其他筛选条件。"
        : null);
  const orderListView = orderListQueryView({
    businessDayLoading: !orderSearchInitialized.current && summary.isPending,
    businessDayError: !orderSearchInitialized.current && summary.isError,
    ordersLoading:
      reportShiftScopeReady &&
      (report.isPending || (!visibleReport && !report.isError && !reportScopeChanged)),
    ordersError: !reportShiftScopeReady || report.isError || reportScopeChanged,
    orders: visibleReport?.orders,
  });
  const reportReadFailed =
    orderListView.status === "ERROR" || orderListView.status === "UNAVAILABLE";
  const reportRowByOrderId = useMemo(
    () => new Map(visibleReport?.rows.map((row) => [row.orderId, row]) ?? []),
    [visibleReport],
  );
  const accidentalVoidRows = useMemo(
    () => visibleReport?.rows.filter((row) => row.accidentalVoid !== null) ?? [],
    [visibleReport],
  );
  const tableUsage = visibleReport ? orderStrongReportTableUsage(visibleReport.summary) : null;
  const reportFacts =
    visibleReport && tableUsage
      ? [
          { label: "系统应收", value: formatFee(visibleReport.summary.receivableFen) },
          {
            label: "已付款退货应收调减",
            value: formatFee(visibleReport.summary.paidReturnAdjustmentFen),
            detail: "仅统计已完成审批并已执行的商品退回",
          },
          {
            label: "已确认实收",
            value: formatFee(visibleReport.summary.confirmedReceiptFen),
          },
          {
            label: "未结应收",
            value: formatFee(visibleReport.summary.unsettledFen),
            detail: `${visibleReport.summary.unsettledOrderCount} 笔未结订单`,
          },
          {
            label: "退款",
            value: formatFee(visibleReport.summary.refundedFen),
            detail: `${visibleReport.summary.refundedOrderCount} 笔`,
          },
          {
            label: "异常订单",
            value: `${visibleReport.summary.anomalyOrderCount} 笔`,
            detail: `核对异常 ${formatFee(visibleReport.summary.exceptionReconciliationFen)}`,
          },
          {
            label: "平均每单应收",
            value:
              visibleReport.summary.averageOrderReceivableFen === null
                ? "暂不可用"
                : formatFee(visibleReport.summary.averageOrderReceivableFen),
          },
          {
            label: "有退款订单占比",
            value:
              visibleReport.summary.refundOrderRateBasisPoints === null
                ? "暂不可用"
                : `${(visibleReport.summary.refundOrderRateBasisPoints / 100).toLocaleString(
                    "zh-CN",
                    {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    },
                  )}%`,
            detail: `${visibleReport.summary.refundedOrderCount} 笔有退款记录，共 ${visibleReport.summary.orderCount} 笔订单`,
          },
          { label: "计费台时", value: tableUsage.value, detail: tableUsage.detail },
          {
            label: "待渠道核对",
            value: formatFee(visibleReport.summary.pendingReconciliationFen),
          },
        ]
      : [];
  const printMutationError =
    reprint.error instanceof Error && lastPrintAttemptIdentityKey === printIdentityKey
      ? reprint.error.message
      : null;
  const printMutationOrderId = printMutationError
    ? (reprint.variables?.recovery.command.orderId ?? null)
    : null;
  const ownedPrintRecovery =
    printRecoveryView.status === "OWNED" ? printRecoveryView.recovery : null;
  const activePrintOrderId =
    printMutationOrderId ??
    (printNoticeIdentityKey === printIdentityKey ? (printNotice?.orderId ?? null) : null) ??
    ownedPrintRecovery?.command.orderId ??
    null;
  const activePrintNotice = printNoticeIdentityKey === printIdentityKey ? printNotice : null;
  const activePrintOrderVisible = Boolean(
    activePrintOrderId && visibleReport?.orders.some((order) => order.id === activePrintOrderId),
  );
  useEffect(() => {
    if (!focusedAnomalyOrderId) return;
    const target = document.getElementById(`order-report-${focusedAnomalyOrderId}`);
    target?.focus();
    target?.scrollIntoView?.({ block: "center" });
  }, [focusedAnomalyOrderId]);
  const activeReportDownloadFeedback =
    reportDownloadFeedback?.identity === visibleReportIdentity ? reportDownloadFeedback : null;
  const canPrintReport = effectiveRoles.includes("OWNER") || effectiveRoles.includes("MANAGER");
  const downloadVisibleReport = (): void => {
    if (!visibleReport || !visibleReportIdentity) return;
    try {
      saveReport(visibleReport);
      setReportDownloadFeedback({
        identity: visibleReportIdentity,
        message: `已下载 ${visibleReport.fileName}，内容与当前页面完全一致。`,
      });
    } catch {
      setReportDownloadFeedback({
        identity: visibleReportIdentity,
        message: "下载未完成，当前页面数据不受影响，请重试。",
      });
    }
  };
  const printVisibleReport = (): void => {
    if (!visibleReport || !visibleReportIdentity || !canPrintReport) return;
    try {
      printReport(visibleReport);
      setReportDownloadFeedback({
        identity: visibleReportIdentity,
        message: "已打开打印窗口，请核对打印预览",
      });
    } catch {
      setReportDownloadFeedback({
        identity: visibleReportIdentity,
        message: "打印预览未打开，请检查浏览器打印设置后重试。",
      });
    }
  };
  return (
    <div className="orders-desk">
      <PageHeader
        eyebrow={`账单与日结 · ${visibleReport ? `当前筛选 ${visibleReport.summary.orderCount} 笔` : "正在读取筛选结果"}`}
        title="订单核账"
        action={
          <div className="segmented">
            <button
              type="button"
              className={!offlineOnly ? "is-active" : ""}
              onClick={() => {
                setFocusedAnomalyOrderId(null);
                setOfflineOnly(false);
              }}
            >
              全部
            </button>
            <button
              type="button"
              className={offlineOnly ? "is-active" : ""}
              onClick={() => {
                setFocusedAnomalyOrderId(null);
                setOfflineOnly(true);
              }}
            >
              离线订单
            </button>
          </div>
        }
      />
      {visibleReport ? (
        <section className="order-strong-report" aria-label="当前筛选订单核账结果">
          <div className="order-strong-report-heading">
            <div>
              <strong>当前筛选核心指标</strong>
              <p>
                {canPrintReport
                  ? "下方汇总、订单明细、下载和打印来自同一份快照；未知不等于 0。"
                  : "下方汇总、订单明细和下载文件来自同一份快照；未知不等于 0。"}
              </p>
              <p className="order-strong-report-filter-description">
                <strong>当前条件：</strong>
                {orderStrongReportFilterDescription(visibleReport.filters)}
              </p>
              <time dateTime={visibleReport.generatedAt}>
                生成于{" "}
                {new Date(visibleReport.generatedAt).toLocaleString("zh-CN", { hour12: false })}
              </time>
            </div>
            <div className="order-strong-report-commands">
              <button className="button secondary" type="button" onClick={downloadVisibleReport}>
                <Download size={17} aria-hidden="true" /> 下载当前快照中文 CSV
              </button>
              {canPrintReport ? (
                <button className="button secondary" type="button" onClick={printVisibleReport}>
                  <ReceiptText size={17} aria-hidden="true" /> 打印当前核账快照
                </button>
              ) : null}
            </div>
          </div>
          <dl className="order-strong-report-facts">
            {reportFacts.map((fact) => (
              <div key={fact.label}>
                <dt>{fact.label}</dt>
                <dd>{fact.value}</dd>
                {fact.detail ? <small>{fact.detail}</small> : null}
              </div>
            ))}
          </dl>
          {visibleReport.shift ? (
            <section className="order-strong-report-shift" aria-label="当前班次核账范围">
              <strong>当前进行中班次</strong>
              <span>营业日 {visibleReport.shift.businessDate}</span>
              <span>开班人 {visibleReport.shift.openedByDisplayName}</span>
              <time dateTime={visibleReport.shift.startedAt}>
                开始时间{" "}
                {new Date(visibleReport.shift.startedAt).toLocaleString("zh-CN", { hour12: false })}
              </time>
              <time dateTime={visibleReport.shift.endedAt}>
                核账截止{" "}
                {new Date(visibleReport.shift.endedAt).toLocaleString("zh-CN", { hour12: false })}
              </time>
            </section>
          ) : (
            <p className="order-strong-report-shift-all">
              本页包含符合当前条件的全部订单，不限定班次。
            </p>
          )}
          <div className="order-strong-report-unavailable">
            <div>
              <strong>挂账：暂不可用</strong>
              <p>{visibleReport.summary.onAccountReason}</p>
            </div>
            <div>
              <strong>
                误开台作废率：{orderStrongReportCancellationRateLabel(visibleReport.summary)}
              </strong>
              <p>
                {visibleReport.summary.cancellationOrderCount} 笔误开台作废，共{" "}
                {visibleReport.summary.orderCount} 笔订单。
                {visibleReport.summary.cancellationRateReason}
              </p>
              {accidentalVoidRows[0] ? (
                <button
                  className="button secondary compact"
                  type="button"
                  onClick={() => setFocusedAnomalyOrderId(accidentalVoidRows[0]?.orderId ?? null)}
                >
                  定位第一笔误开台作废原单
                </button>
              ) : null}
            </div>
          </div>
          {accidentalVoidRows.length > 0 ? (
            <section className="order-strong-report-voids" aria-label="误开台作废订单">
              <strong>误开台作废订单</strong>
              <p>作废时间和原因来自当前核账报告；点击可定位同一份报告里的原订单。</p>
              <div>
                {accidentalVoidRows.map((row) => {
                  const accidentalVoid = row.accidentalVoid;
                  const order = visibleReport.orders.find((item) => item.id === row.orderId);
                  if (!accidentalVoid || !order) return null;
                  const reference = orderDisplayReference({
                    id: order.id,
                    orderNumber: order.orderNumber,
                  });
                  return (
                    <button
                      className="button secondary compact"
                      type="button"
                      key={row.orderId}
                      onClick={() => setFocusedAnomalyOrderId(row.orderId)}
                    >
                      {reference.value} · 作废于{" "}
                      {new Date(accidentalVoid.voidedAt).toLocaleString("zh-CN", { hour12: false })}
                      · {accidentalVoid.reason}
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}
          {visibleReport.summary.anomalyOrderCount > 0 ? (
            <section className="order-strong-report-anomalies" aria-label="异常订单定位">
              <strong>异常订单定位</strong>
              <div>
                {visibleReport.rows
                  .filter((row) => row.anomalyCodes.length > 0)
                  .map((row) => {
                    const order = visibleReport.orders.find((item) => item.id === row.orderId);
                    const reference = order
                      ? orderDisplayReference({ id: order.id, orderNumber: order.orderNumber })
                      : orderDisplayReference({ id: row.orderId });
                    return (
                      <button
                        className="button secondary compact"
                        type="button"
                        key={row.orderId}
                        onClick={() => setFocusedAnomalyOrderId(row.orderId)}
                      >
                        {reference.value} ·{" "}
                        {row.anomalyCodes.map((code) => orderAnomalyTerm(code).label).join("、")}
                      </button>
                    );
                  })}
              </div>
            </section>
          ) : null}
          <div className="order-strong-report-footnote">
            <span>{authenticationEvidence === "SIMULATED" ? "演示账目" : "门店账目"}</span>
            {activeReportDownloadFeedback ? (
              <output>{activeReportDownloadFeedback.message}</output>
            ) : null}
          </div>
        </section>
      ) : (
        <section
          className={`order-strong-report-status ${reportReadFailed ? "is-error" : ""}`}
          role={reportReadFailed ? "alert" : "status"}
          aria-live="polite"
        >
          {!reportShiftScopeReady ? (
            <AlertTriangle size={20} aria-hidden="true" />
          ) : reportReadFailed ? (
            <AlertTriangle size={20} aria-hidden="true" />
          ) : (
            <RefreshCw className="spin" size={20} aria-hidden="true" />
          )}
          <div>
            <strong>
              {!reportShiftScopeReady
                ? cashierShiftLocked
                  ? "当前收银班次无法确认"
                  : cashierPersonalExactLookup
                    ? "请输入完整业务单号查找退款原单"
                    : "当前没有可核账的进行中班次"
                : reportReadFailed
                  ? "当前筛选核账结果无法确认"
                  : "正在读取当前筛选核账结果"}
            </strong>
            <span>
              {!reportShiftScopeReady
                ? cashierShiftLocked
                  ? "请先在公共收银设备开始班次，或完成待处理的交接班；确认班次后系统才会读取本班订单。"
                  : cashierPersonalExactLookup
                    ? "个人账号不会读取整班或全店账目；请按顾客小票完整输入业务单号，只核对这一张原单。"
                    : shifts.isError
                      ? "班次资料暂时无法确认，请检查门店连接后重试；系统不会使用旧班次继续核账。"
                      : "请先到“交接班”开始新班次，或改选“全部订单”后重新查找。"
                : reportReadFailed
                  ? canPrintReport
                    ? "汇总、明细、下载和打印已一并隐藏；未知不等于 0，请检查门店连接后重试。"
                    : "汇总、明细和下载已一并隐藏；未知不等于 0，请检查门店连接后重试。"
                  : canPrintReport
                    ? "正在按当前组合条件生成同一份汇总、明细、下载和打印内容。"
                    : "正在按当前组合条件生成同一份汇总、明细和下载内容。"}
            </span>
          </div>
          {reportReadFailed && reportShiftScopeReady ? (
            <button
              className="button secondary compact"
              type="button"
              onClick={() => report.refetch()}
            >
              <RefreshCw size={16} aria-hidden="true" /> 重新读取当前筛选
            </button>
          ) : null}
        </section>
      )}
      <form
        className="order-search-panel"
        name="order-search"
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          if (searchError) return;
          setFocusedAnomalyOrderId(null);
          setAppliedSearch(searchDraft);
          setAppliedShiftScope(shiftScopeDraft);
        }}
      >
        <div className="order-search-heading">
          <div>
            <strong>查找订单</strong>
            <small>
              可组合日期、业务单号或历史记录标识、桌号、会员、商品、收款方式和开台方式；会员姓名或手机号搜索需要联网。
            </small>
          </div>
          <span>{orderListView.countLabel}</span>
        </div>
        <label>
          开始日期
          <input
            type="date"
            name="order-search-date-from"
            value={searchDraft.dateFrom}
            onChange={(event) => {
              setDateRangeUserEdited(true);
              setSearchDraft((current) => ({ ...current, dateFrom: event.target.value }));
            }}
          />
        </label>
        <label>
          结束日期
          <input
            type="date"
            name="order-search-date-to"
            value={searchDraft.dateTo}
            onChange={(event) => {
              setDateRangeUserEdited(true);
              setSearchDraft((current) => ({ ...current, dateTo: event.target.value }));
            }}
          />
        </label>
        <label>
          业务单号或历史记录标识
          <input
            name="order-search-order-number"
            autoComplete="off"
            value={searchDraft.orderQuery}
            onChange={(event) =>
              setSearchDraft((current) => ({ ...current, orderQuery: event.target.value }))
            }
            maxLength={128}
            placeholder="例如 DD-20260827-012345ABCDEF"
          />
        </label>
        <label>
          桌号
          <input
            name="order-search-table-number"
            autoComplete="off"
            value={searchDraft.tableNumber}
            onChange={(event) =>
              setSearchDraft((current) => ({ ...current, tableNumber: event.target.value }))
            }
            maxLength={32}
            placeholder="例如：01"
          />
        </label>
        <label>
          会员
          <input
            name="order-search-member"
            autoComplete="off"
            value={searchDraft.memberSearch}
            onChange={(event) =>
              setSearchDraft((current) => ({ ...current, memberSearch: event.target.value }))
            }
            maxLength={80}
            placeholder="姓名、手机号或会员号"
          />
        </label>
        <label>
          商品
          <input
            name="order-search-product"
            autoComplete="off"
            value={searchDraft.productQuery}
            onChange={(event) =>
              setSearchDraft((current) => ({ ...current, productQuery: event.target.value }))
            }
            maxLength={80}
            placeholder="商品名称或商品编号"
          />
          <small>按下单时记录的商品名称或商品编号查找；后来改名不影响历史订单。</small>
        </label>
        <label>
          收款方式
          <select
            name="order-search-payment-method"
            value={searchDraft.paymentMethod}
            onChange={(event) =>
              setSearchDraft((current) => ({
                ...current,
                paymentMethod: event.target.value as PaymentMethod | "",
              }))
            }
          >
            <option value="">全部方式</option>
            <option value="CASH">现金</option>
            <option value="STATIC_WECHAT">微信静态码</option>
            <option value="STATIC_ALIPAY">支付宝静态码</option>
            <option value="MEMBER_BALANCE">会员余额</option>
          </select>
        </label>
        <label>
          开台方式
          <select
            name="order-search-opening-method"
            value={searchDraft.openingMethod}
            onChange={(event) =>
              setSearchDraft((current) => ({
                ...current,
                openingMethod: event.target.value as OrderSearchDraft["openingMethod"],
              }))
            }
          >
            <option value="">全部开台方式</option>
            <option value="DIRECT">{orderOpeningMethodTerm("DIRECT").label}</option>
            <option value="RESERVATION">{orderOpeningMethodTerm("RESERVATION").label}</option>
            <option value="GROUP_BUY_ONLINE">
              {orderOpeningMethodTerm("GROUP_BUY_ONLINE").label}
            </option>
            <option value="UNKNOWN">{orderOpeningMethodTerm("UNKNOWN").label}</option>
          </select>
          <small>{orderStrongReportOpeningMethodExplanation(searchDraft.openingMethod)}</small>
        </label>
        {canChooseReportShift ? (
          <label>
            订单范围
            <select
              name="order-search-shift-scope"
              value={shiftScopeDraft}
              onChange={(event) => {
                const nextScope = event.target.value === "CURRENT" ? "CURRENT" : "ALL";
                if (nextScope === "CURRENT" && !dateRangeUserEdited) {
                  setSearchDraft((current) => ({ ...current, dateFrom: "", dateTo: "" }));
                }
                setShiftScopeDraft(nextScope);
              }}
            >
              <option value="ALL">全部订单</option>
              <option value="CURRENT">当前进行中班次</option>
            </select>
            <small>
              {shiftScopeDraft === "CURRENT"
                ? "只核对当前仍在进行的收银班次。为包含跨营业日仍在本班发生的订单，系统已清空默认日期；如手动填写日期会继续缩小范围。交班后旧结果会立即隐藏。"
                : "核对符合其他条件的全部订单，不按收银班次缩小范围。"}
            </small>
          </label>
        ) : cashierShiftLocked ? (
          <div className="order-search-locked-shift">
            <strong>订单范围：当前收银班次</strong>
            <span>公共收银设备只能核对本班订单，不能切换到其他班次。</span>
          </div>
        ) : cashierPersonalExactLookup ? (
          <div className="order-search-locked-shift">
            <strong>订单范围：一张退款原单</strong>
            <span>请输入顾客小票上的完整业务单号；个人账号不会展开整班或全店账目。</span>
          </div>
        ) : null}
        {searchError ? <div className="form-hint">还需修改：{searchError}</div> : null}
        <div className="order-search-actions">
          <button
            className="button secondary"
            type="button"
            onClick={() => {
              const cleared: OrderSearchDraft = {
                dateFrom:
                  cashierShiftLocked || cashierPersonalExactLookup || shiftScopeDraft === "CURRENT"
                    ? ""
                    : (summary.data?.businessDate ?? ""),
                dateTo:
                  cashierShiftLocked || cashierPersonalExactLookup || shiftScopeDraft === "CURRENT"
                    ? ""
                    : (summary.data?.businessDate ?? ""),
                orderQuery: "",
                tableNumber: "",
                memberSearch: "",
                productQuery: "",
                paymentMethod: "",
                openingMethod: "",
              };
              setFocusedAnomalyOrderId(null);
              setDateRangeUserEdited(false);
              setSearchDraft(cleared);
              setAppliedSearch(cleared);
              setAppliedShiftScope(shiftScopeDraft);
            }}
          >
            清空条件
          </button>
          <button className="button primary" type="submit" disabled={Boolean(searchError)}>
            <Search size={16} aria-hidden="true" /> 查找订单
          </button>
        </div>
      </form>
      <PaidOrderItemReturnApprovalPanel operator={operator} effectiveRoles={effectiveRoles} />
      <section className="order-list">
        <output className="notice-band">
          现金订单可由收银员先查看原单和影响，再提交退款审批。批准后仍要等待安全执行成功；微信、支付宝和会员余额不能当成现金退款。
        </output>
        {activePrintOrderId && !activePrintOrderVisible ? (
          <ReceiptReprintInlineFeedback
            orderId={activePrintOrderId}
            errorMessage={printMutationOrderId === activePrintOrderId ? printMutationError : null}
            notice={activePrintNotice?.orderId === activePrintOrderId ? activePrintNotice : null}
            recovery={
              ownedPrintRecovery?.command.orderId === activePrintOrderId ? ownedPrintRecovery : null
            }
            pending={reprint.isPending}
            onRetry={dispatchFrozenReprint}
          />
        ) : null}
        {printRecoveryView.status === "OTHER_OPERATOR" ? (
          <div className="error-band" role="alert">
            另一个账号有尚未确认的小票补打。请切回原账号处理；当前账号已暂停新的补打操作。
          </div>
        ) : printRecoveryView.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
          <div className="error-band" role="alert">
            无法安全保存小票补打进度。为避免重复任务，已暂停补打，请关闭此标签页后重新打开。
          </div>
        ) : null}
        {orderListView.status === "LOADING" ? (
          <div className="loading-state">正在读取当前筛选订单...</div>
        ) : orderListView.status === "READY" ? (
          (visibleReport?.orders ?? []).map((order) => {
            const orderReference = orderDisplayReference({
              id: order.id,
              orderNumber: order.orderNumber,
            });
            const reportRow = reportRowByOrderId.get(order.id);
            const orderStatus = orderStatusTerm(order.status);
            const syncState = syncStateTerm(order.syncState);
            const openingMethod = reportRow
              ? orderOpeningMethodTerm(reportRow.openingSource.method)
              : null;
            return (
              <article
                className={`order-row ${focusedAnomalyOrderId === order.id ? "is-report-focused" : ""}`}
                id={`order-report-${order.id}`}
                key={order.id}
                tabIndex={-1}
              >
                <div className="order-main">
                  <span className="order-table">{order.tableNumber}</span>
                  <div>
                    <strong>
                      {formatFee(order.totalFen)} · {orderStatus.label}
                    </strong>
                    <small>
                      营业日 {order.businessDate} ·{" "}
                      {new Date(order.startedAt).toLocaleString("zh-CN", { hour12: false })} ·{" "}
                      {order.offlineOrigin ? "离线期间产生" : "在线产生"} · {syncState.label}
                    </small>
                    <small>
                      {orderStatus.explanation} {orderStatus.nextStep} {syncState.explanation}{" "}
                      {syncState.nextStep}
                    </small>
                    <small className="order-number">
                      {orderReference.label} {orderReference.value}
                    </small>
                  </div>
                </div>
                <OrderBreakdown order={order} />
                {reportRow ? (
                  <div className="order-strong-report-row-facts">
                    <span>已确认实收 {formatFee(reportRow.confirmedReceiptFen)}</span>
                    <span>已付款退货应收调减 {formatFee(reportRow.paidReturnAdjustmentFen)}</span>
                    <span>未结应收 {formatFee(reportRow.unsettledFen)}</span>
                    <span>退款 {formatFee(reportRow.refundedFen)}</span>
                    <span>
                      计费台时{" "}
                      {reportRow.billableSeconds === null
                        ? "暂不可用"
                        : `${Math.floor(reportRow.billableSeconds / 60)} 分钟`}
                    </span>
                    <div
                      className={`order-strong-report-opening-source ${
                        reportRow.openingSource.method === "UNKNOWN" ? "is-unknown" : ""
                      }`}
                    >
                      <strong>开台方式 {openingMethod?.label}</strong>
                      {reportRow.openingSource.method === "RESERVATION" ? (
                        <span>预约单号 {reportRow.openingSource.referenceNumber}</span>
                      ) : reportRow.openingSource.method === "GROUP_BUY_ONLINE" ? (
                        <span>团购核销单号 {reportRow.openingSource.referenceNumber}</span>
                      ) : reportRow.openingSource.method === "UNKNOWN" ? (
                        <span>{reportRow.openingSource.reason}</span>
                      ) : (
                        <span>工作人员直接为顾客开台</span>
                      )}
                      <small>
                        {openingMethod?.explanation} {openingMethod?.nextStep}
                      </small>
                    </div>
                    {reportRow.accidentalVoid ? (
                      <div className="order-strong-report-row-void">
                        <strong>误开台作废</strong>
                        <span>
                          作废时间{" "}
                          {new Date(reportRow.accidentalVoid.voidedAt).toLocaleString("zh-CN", {
                            hour12: false,
                          })}
                        </span>
                        <span>作废原因 {reportRow.accidentalVoid.reason}</span>
                      </div>
                    ) : null}
                    {reportRow.anomalyCodes.length ? (
                      <div className="order-strong-report-row-anomalies">
                        {reportRow.anomalyCodes.map((code) => {
                          const anomaly = orderAnomalyTerm(code);
                          return (
                            <span key={code}>
                              <strong>{anomaly.label}</strong>
                              <small>
                                {anomaly.explanation} {anomaly.nextStep}
                              </small>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <small>当前快照未标记异常</small>
                    )}
                  </div>
                ) : null}
                <div className="payment-records">
                  {order.payments.length ? (
                    order.payments.map((payment) => {
                      const method = paymentMethodTerm(payment.method);
                      const status = paymentStatusTerm(payment.status);
                      return (
                        <span
                          className={`payment-tag status-${payment.status.toLowerCase()}`}
                          key={payment.id}
                        >
                          <strong>
                            {method.label} · {status.label}
                          </strong>
                          <small>
                            {status.explanation} {status.nextStep}
                          </small>
                        </span>
                      );
                    })
                  ) : (
                    <span className="payment-tag">尚未收款</span>
                  )}
                </div>
                {order.refunds.length ? (
                  <div className="refund-records">
                    {order.refunds.map((record) => (
                      <small key={record.id}>
                        退款 {formatFee(record.amountFen)} · {record.reason} · 经办{" "}
                        {record.operatorDisplayName} · 授权 {record.authorizerDisplayName} ·{" "}
                        {new Date(record.createdAt).toLocaleString("zh-CN", { hour12: false })}
                      </small>
                    ))}
                  </div>
                ) : null}
                <OrderCashRefundApprovalForm
                  order={order}
                  operator={operator}
                  effectiveRoles={effectiveRoles}
                  personalSession={
                    authenticationEvidence === "SIMULATED" || sessionContext?.mode === "PERSONAL"
                  }
                />
                {order.status !== "OPEN" && order.status !== "CONFLICT" ? (
                  <button
                    className="button secondary refund-button"
                    type="button"
                    disabled={reprint.isPending || printRecoveryView.status !== "NONE"}
                    onClick={() => startReceiptReprint(order.id)}
                  >
                    <ReceiptText size={15} />
                    重新打印小票
                  </button>
                ) : null}
                {activePrintOrderId === order.id ? (
                  <ReceiptReprintInlineFeedback
                    orderId={order.id}
                    orderReference={orderReference}
                    errorMessage={printMutationOrderId === order.id ? printMutationError : null}
                    notice={activePrintNotice?.orderId === order.id ? activePrintNotice : null}
                    recovery={
                      ownedPrintRecovery?.command.orderId === order.id ? ownedPrintRecovery : null
                    }
                    pending={reprint.isPending}
                    onRetry={dispatchFrozenReprint}
                  />
                ) : null}
              </article>
            );
          })
        ) : orderListView.status === "EMPTY" ? (
          <div className="empty-state">当前筛选没有订单</div>
        ) : null}
      </section>
    </div>
  );
}

function manualReviewCategoryLabel(category: ManualReviewReadItem["category"]): string {
  switch (category) {
    case "PAYMENT_RECONCILIATION":
      return "付款核对";
    case "ORDER_SYNC_REVIEW":
      return "订单记录核对";
    case "COACH_SERVICE_REVIEW":
      return "助教服务核对";
    case "GROUP_BUY_REDEMPTION_REVIEW":
      return "团购券核销核对";
    case "MEMBER_RECHARGE_RECONCILIATION":
      return "会员充值核对";
    case "MEMBER_RECEIVABLE":
      return "会员余额待扣";
    case "CUSTOMER_STORAGE_EXCEPTION":
      return "顾客寄存异常";
    case "CUSTOMER_RENTAL_EXCEPTION":
      return "顾客租赁异常";
  }
}

export function manualReviewSummaryLabel(input: {
  isLoading: boolean;
  isError: boolean;
  data: ManualReviewSummaryReadModel | undefined;
}): string {
  if (input.isLoading) return "正在读取";
  if (input.isError || !input.data) return "暂时无法确认";
  return input.data.capacityExceeded
    ? `${input.data.pendingCount} 笔，超过安全显示上限`
    : `${input.data.pendingCount} 笔`;
}

export function ReviewDesk(): React.JSX.Element {
  const query = useQuery({
    queryKey: ["manual-review"],
    queryFn: listManualReview,
    refetchInterval: 2_000,
  });
  const items = query.isError ? [] : (query.data?.items ?? []);
  const countLabel = query.isLoading
    ? "正在读取"
    : query.isError
      ? "暂时无法确认"
      : `${query.data?.pendingCount ?? 0} 项待处理`;

  return (
    <>
      <PageHeader
        eyebrow="异常与人工处置"
        title="待人工确认"
        action={<span className="demo-pill">{countLabel}</span>}
      />
      <section className="review-list" aria-label="待人工确认事项安全只读清单">
        <div className="disabled-reason review-form-notice">
          <AlertTriangle size={15} aria-hidden="true" />
          这里是工作中心中的异常只读区，不是审批记录。处理动作必须回到原业务流程；
          本页不会收集经理密码，也不会提交付款、订单或助教记录处置。
        </div>
        {query.isError ? (
          <div className="error-band">
            待人工确认事项暂时无法安全读取，旧事项已经隐藏，请检查门店主机连接后重试
          </div>
        ) : query.isLoading ? (
          <div className="empty-state">正在读取待人工确认事项</div>
        ) : items.length ? (
          items.map((item) => (
            <article className="review-row" key={item.itemKey}>
              <div className="review-icon">
                {item.category === "PAYMENT_RECONCILIATION" ? (
                  <CircleDollarSign aria-hidden="true" />
                ) : (
                  <AlertTriangle aria-hidden="true" />
                )}
              </div>
              <div className="review-content">
                <strong>{item.title}</strong>
                <p>{item.summary}</p>
                <small>
                  {manualReviewCategoryLabel(item.category)}
                  {item.category === "COACH_SERVICE_REVIEW" ||
                  item.category === "CUSTOMER_STORAGE_EXCEPTION" ||
                  item.category === "CUSTOMER_RENTAL_EXCEPTION" ? null : (
                    <>
                      {" "}
                      · {item.offlineOrigin ? "离线期间产生" : "在线产生"} ·{" "}
                      {item.amountFen === null ? "金额待核对" : formatFee(item.amountFen)}
                    </>
                  )}{" "}
                  · <time dateTime={item.createdAt}>{formatTime(item.createdAt)}</time>
                </small>
                {item.tableNumber || item.businessDate || item.occurredAt ? (
                  <dl className="review-business-context" aria-label="业务定位信息">
                    {item.tableNumber ? (
                      <div>
                        <dt>关联桌号</dt>
                        <dd>{item.tableNumber}</dd>
                      </div>
                    ) : null}
                    {item.businessDate ? (
                      <div>
                        <dt>营业日</dt>
                        <dd>{item.businessDate}</dd>
                      </div>
                    ) : null}
                    {item.occurredAt ? (
                      <div>
                        <dt>发生时间</dt>
                        <dd>
                          <time dateTime={item.occurredAt}>{formatTime(item.occurredAt)}</time>
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                ) : null}
                <div className="review-context-explanation is-recommendation">
                  <strong>下一步</strong>
                  <p>{item.nextStep}</p>
                </div>
                {item.category === "CUSTOMER_RENTAL_EXCEPTION" ? (
                  <NavLink
                    className="button secondary compact"
                    to={`/rentals?workItemId=${encodeURIComponent(item.workItemId)}`}
                  >
                    打开对应租赁记录
                  </NavLink>
                ) : null}
              </div>
            </article>
          ))
        ) : (
          <div className="empty-state">当前没有待人工确认事项</div>
        )}
      </section>
    </>
  );
}

function serviceRequestActorLabel(
  request: Pick<ServiceRequestReadModel, "status" | "assignedActorDisplayName">,
): string {
  if (request.status === "PENDING") return "尚未接单";
  return request.assignedActorDisplayName ?? "负责人未记录";
}

export function ServiceRequestTimeline({
  request,
}: {
  request: ServiceRequestReadModel;
}): React.JSX.Element {
  const actor = serviceRequestActorLabel(request);
  return (
    <ol className="task-timeline" aria-label="处理时间线">
      <li>
        <span>桌边发起</span>
        <time dateTime={request.requestedAt}>
          {new Date(request.requestedAt).toLocaleString("zh-CN", { hour12: false })}
        </time>
      </li>
      {request.acknowledgedAt ? (
        <li>
          <span>{actor} 接单</span>
          <time dateTime={request.acknowledgedAt}>
            {new Date(request.acknowledgedAt).toLocaleString("zh-CN", { hour12: false })}
          </time>
        </li>
      ) : null}
      {request.completedAt ? (
        <li>
          <span>{actor} 完成</span>
          <time dateTime={request.completedAt}>
            {new Date(request.completedAt).toLocaleString("zh-CN", { hour12: false })}
          </time>
        </li>
      ) : null}
      {request.cancelledAt ? (
        <li>
          <span>顾客取消</span>
          <time dateTime={request.cancelledAt}>
            {new Date(request.cancelledAt).toLocaleString("zh-CN", { hour12: false })}
          </time>
        </li>
      ) : null}
      {request.status === "FAILED" ? (
        <li>
          <span>处理失败，等待重新安排</span>
          <time dateTime={request.updatedAt}>
            {new Date(request.updatedAt).toLocaleString("zh-CN", { hour12: false })}
          </time>
        </li>
      ) : null}
      <li className="task-identity" title={request.eventId}>
        <span>任务编号</span>
        <code>{request.eventId}</code>
      </li>
    </ol>
  );
}

export function taskDestination(
  request: Pick<ServiceRequestReadModel, "eventId" | "tableNodeId">,
  role: OperatorRole,
  currentSearch = "",
): string {
  const search = new URLSearchParams(currentSearch);
  if (role === "WAITER") {
    search.set("eventId", request.eventId);
    return `/staff?${search.toString()}`;
  }
  search.set("tableNodeId", request.tableNodeId);
  return `/frontdesk?${search.toString()}`;
}

export function canCompleteServiceRequest(
  request: Pick<ServiceRequestReadModel, "assignedToCurrentActor">,
  effectiveRoles: readonly OperatorRole[],
): boolean {
  return request.assignedToCurrentActor || effectiveRoles.includes("MANAGER");
}

export function canAcknowledgeServiceRequest(
  effectiveRoles: readonly OperatorRole[],
  configuration: Pick<ServiceRoutingConfigurationReadModel, "primaryRole" | "copiedRoles"> | null,
): boolean {
  if (!configuration) return false;
  if (effectiveRoles.includes("MANAGER")) return true;
  return (
    effectiveRoles.includes("WAITER") &&
    (configuration.primaryRole === "WAITER" || configuration.copiedRoles.includes("WAITER"))
  );
}

export type ServiceRequestReadState = "LOADING" | "ERROR" | "TRUSTED";

export function serviceRequestReadState(
  hasData: boolean,
  isError: boolean,
): ServiceRequestReadState {
  if (hasData) return "TRUSTED";
  return isError ? "ERROR" : "LOADING";
}

export function serviceRequestEmptyStateVisible(
  state: ServiceRequestReadState,
  itemCount: number,
): boolean {
  return state === "TRUSTED" && itemCount === 0;
}

export function serviceRequestCountPresentation(
  state: ServiceRequestReadState,
  itemCount: number,
): { header: string; value: string } {
  if (state === "ERROR") return { header: "无法确认", value: "—" };
  if (state === "LOADING") return { header: "读取中", value: "—" };
  return { header: `${itemCount} 项`, value: String(itemCount) };
}

export const SERVICE_REQUEST_ACTIVE_QUERY_KEY = ["service-requests"] as const;
export const SERVICE_REQUEST_HISTORY_QUERY_KEY = ["service-request-history"] as const;

function activeServiceRequestItems(
  items: readonly ServiceRequestReadModel[],
): ServiceRequestReadModel[] {
  return items.filter(
    (item) =>
      (item.status === "PENDING" || item.status === "ACKNOWLEDGED") &&
      (item.type === "OPEN_TABLE" ? item.sessionId === null : item.sessionId !== null),
  );
}

export function serviceRequestSessionLabel(
  request: Pick<ServiceRequestReadModel, "status" | "sessionId" | "tableNodeId" | "type">,
  tables: readonly Pick<TableSnapshot, "state" | "sessionId" | "tableNodeId">[] | undefined,
): string {
  if (request.type === "OPEN_TABLE") {
    return request.status === "PENDING"
      ? "本桌开台申请，尚未建立台次"
      : request.status === "ACKNOWLEDGED"
        ? "已接单，等待核对价格方案并开台"
        : "";
  }
  if (request.status === "PENDING") return "本桌当前台次请求";
  if (request.status !== "ACKNOWLEDGED") return "";
  if (!tables) return "已接单，暂时无法确认本桌当前台次是否仍在进行";
  return tables.some(
    (table) =>
      table.tableNodeId === request.tableNodeId &&
      table.state === "IN_USE" &&
      table.sessionId === request.sessionId,
  )
    ? "本桌当前台次请求"
    : "原台次已结束，仍由已接单员工收尾";
}

function serviceRequestTypeLabel(type: ServiceRequestReadModel["type"]): string {
  if (type === "SERVICE") return "呼叫服务";
  if (type === "COACH") return "呼叫助教";
  if (type === "OPEN_TABLE") return "申请开台";
  return "请求结账";
}

function ServiceRequestTypeIcon({
  type,
}: {
  type: ServiceRequestReadModel["type"];
}): React.JSX.Element {
  if (type === "SERVICE") return <BellRing />;
  if (type === "COACH") return <UsersRound />;
  if (type === "OPEN_TABLE") return <Power />;
  return <Banknote />;
}

export function openTableCustomerRequestHref(
  request: Pick<ServiceRequestReadModel, "eventId" | "tableNodeId">,
): string {
  const search = new URLSearchParams({
    customerRequestEventId: request.eventId,
    tableNodeId: request.tableNodeId,
  });
  return `/frontdesk?${search.toString()}`;
}

export function canOpenTableCustomerRequestForRoles(
  effectiveRoles: readonly OperatorRole[],
  duty:
    | {
        status: ServiceDutyReadModel["status"];
        currentAssignee: Pick<
          NonNullable<ServiceDutyReadModel["currentAssignee"]>,
          "isCurrentActor"
        > | null;
      }
    | undefined,
): boolean {
  if (effectiveRoles.includes("MANAGER")) return true;
  return (
    effectiveRoles.includes("CASHIER") &&
    duty?.status === "ACTIVE" &&
    duty.currentAssignee?.isCurrentActor === true
  );
}

export type OpenTableCustomerRequestQuery =
  | { status: "NONE" }
  | { status: "INVALID" }
  | { status: "VALID"; customerRequestEventId: string; tableNodeId: string };

export function openTableCustomerRequestQuery(searchValue: string): OpenTableCustomerRequestQuery {
  const search = new URLSearchParams(searchValue);
  if (!search.has("customerRequestEventId")) return { status: "NONE" };
  const keys = [...search.keys()];
  if (
    keys.some((key) => key !== "customerRequestEventId" && key !== "tableNodeId") ||
    search.getAll("customerRequestEventId").length !== 1 ||
    search.getAll("tableNodeId").length !== 1
  ) {
    return { status: "INVALID" };
  }
  const customerRequestEventId = search.get("customerRequestEventId") ?? "";
  const tableNodeId = search.get("tableNodeId") ?? "";
  if (
    customerRequestEventId !== customerRequestEventId.trim() ||
    [...customerRequestEventId].length < 8 ||
    [...customerRequestEventId].length > 128 ||
    tableNodeId !== tableNodeId.trim() ||
    [...tableNodeId].length < 1 ||
    [...tableNodeId].length > 128
  ) {
    return { status: "INVALID" };
  }
  return { status: "VALID", customerRequestEventId, tableNodeId };
}

export function coachCustomerRequestHref(
  request: Pick<ServiceRequestReadModel, "eventId" | "sessionId"> & { sessionId: string },
): string {
  const search = new URLSearchParams({
    customerRequestEventId: request.eventId,
    sessionId: request.sessionId,
  });
  return `/coach?${search.toString()}`;
}

export async function refreshServiceRequestsAfterTransition(
  queryClient: Pick<QueryClient, "invalidateQueries">,
  action: "ACKNOWLEDGE" | "COMPLETE",
): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: SERVICE_REQUEST_ACTIVE_QUERY_KEY });
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["service-duty"] }),
    queryClient.invalidateQueries({ queryKey: ["service-work-items"] }),
  ]);
  if (action !== "COMPLETE") return;
  await queryClient.invalidateQueries({
    queryKey: SERVICE_REQUEST_HISTORY_QUERY_KEY,
    exact: true,
    predicate: (query) => query.state.data !== undefined,
  });
}

export function ServiceRequestHistoryPanel({
  expanded,
  items,
  isLoading,
  isError,
  isFetching,
  isFetchingNextPage,
  hasNextPage,
  errorMessage,
  onToggle,
  onRefresh,
  onLoadMore,
}: {
  expanded: boolean;
  items: readonly ServiceRequestReadModel[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  errorMessage?: string | null;
  onToggle: () => void;
  onRefresh: () => void;
  onLoadMore: () => void;
}): React.JSX.Element {
  return (
    <section className="service-request-history" aria-label="服务待办处理历史">
      <button
        className="button secondary"
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <ChevronRight size={16} aria-hidden="true" />
        {expanded ? "收起处理历史" : "展开处理历史"}
      </button>
      {expanded ? (
        <div className="service-request-history-body">
          <div className="service-request-history-heading">
            <div>
              <h3>处理历史</h3>
              <p>打开后才读取，每次 50 条；活动待办更新不会反复读取历史。</p>
            </div>
            <button
              className="button secondary"
              type="button"
              disabled={isFetching}
              onClick={onRefresh}
            >
              <RefreshCw size={15} aria-hidden="true" />
              {isFetching && !isFetchingNextPage ? "刷新中" : "刷新已加载历史"}
            </button>
          </div>
          {isError ? (
            <div className="error-band">
              {items.length > 0
                ? `历史更新失败，保留上次确认的记录${errorMessage ? `：${errorMessage}` : ""}`
                : `处理历史读取失败，请稍后重试${errorMessage ? `：${errorMessage}` : ""}`}
            </div>
          ) : null}
          {isLoading && items.length === 0 ? (
            <div className="empty-state">正在读取处理历史</div>
          ) : items.length === 0 && !isError ? (
            <div className="empty-state">还没有处理历史</div>
          ) : (
            <div className="service-request-history-list">
              {items.map((item) => (
                <article className="task-row" key={item.eventId}>
                  <span className={`task-icon ${item.type.toLowerCase()}`}>
                    <ServiceRequestTypeIcon type={item.type} />
                  </span>
                  <div className="task-content">
                    <strong>
                      {item.tableNodeId.replace("table_node_", "")} 号台 ·{" "}
                      {serviceRequestTypeLabel(item.type)}
                    </strong>
                    {item.status === "FAILED" ? (
                      <span className="service-request-failure-message">
                        失败原因：{item.failureMessage ?? "门店没有提供更多原因，请重新核对现场"}
                      </span>
                    ) : null}
                    {item.status === "CANCELLED" && item.type === "CHECKOUT" ? (
                      <span className="service-request-cancelled-note">
                        取消结账请求不表示已经收款、结账或关台
                      </span>
                    ) : null}
                    {item.sessionId === null ? (
                      <span className="service-request-session-note">历史记录未保存所属台次</span>
                    ) : null}
                    {item.status !== "CANCELLED" ? (
                      <span className="task-assignee">
                        负责人：{serviceRequestActorLabel(item)}
                      </span>
                    ) : null}
                    <ServiceRequestTimeline request={item} />
                  </div>
                  <div className="task-actions">
                    <span className={`task-status ${item.status.toLowerCase()}`}>
                      {serviceStatusLabels[item.status]}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}
          {hasNextPage ? (
            <button
              className="button secondary service-request-history-more"
              type="button"
              disabled={isFetchingNextPage}
              onClick={onLoadMore}
            >
              {isFetchingNextPage ? "正在加载更多" : "加载更多"}
            </button>
          ) : items.length > 0 ? (
            <p className="service-request-history-end">已显示全部加载记录</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export function TaskDesk({
  operator,
  effectiveRoles,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
}): React.JSX.Element {
  const location = useLocation();
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const taskQuery = useQuery({
    queryKey: SERVICE_REQUEST_ACTIVE_QUERY_KEY,
    queryFn: () => listServiceRequests(),
    refetchInterval: 1_000,
  });
  const historyQuery = useInfiniteQuery({
    queryKey: SERVICE_REQUEST_HISTORY_QUERY_KEY,
    queryFn: ({ pageParam }) =>
      listServiceRequestHistory(pageParam?.cursor, pageParam?.previous ?? null),
    initialPageParam: null as {
      cursor: string;
      previous: ServiceRequestHistoryPageBoundary;
    } | null,
    getNextPageParam: (lastPage) =>
      lastPage.nextCursor === null
        ? null
        : { cursor: lastPage.nextCursor, previous: lastPage.boundary },
    enabled: historyExpanded,
    refetchInterval: false,
  });
  const tablesQuery = useQuery({
    queryKey: ["tables"],
    queryFn: () => listTables({ tenantId: operator.tenantId, storeId: operator.storeId }),
    refetchInterval: 1_000,
  });
  const items = activeServiceRequestItems(taskQuery.data ?? []);
  const readState = serviceRequestReadState(taskQuery.data !== undefined, taskQuery.isError);
  const itemCount = serviceRequestCountPresentation(readState, items.length);
  const taskRole = effectiveRoles.some((role) => ["CASHIER", "MANAGER", "OWNER"].includes(role))
    ? "CASHIER"
    : "WAITER";
  return (
    <>
      <PageHeader
        eyebrow="现场协作"
        title="服务待办"
        action={<span className="demo-pill">{itemCount.header}</span>}
      />
      <LongSessionAlertPanel tables={tablesQuery.data ?? []} effectiveRoles={effectiveRoles} />
      <section className="task-list">
        {taskQuery.isError ? (
          <div className="error-band">
            {readState === "TRUSTED" ? "待办更新失败，正在显示上次成功结果" : "待办读取失败"}
          </div>
        ) : null}
        {serviceRequestEmptyStateVisible(readState, items.length) ? (
          <div className="empty-state">当前没有桌边请求</div>
        ) : readState === "LOADING" ? (
          <div className="empty-state">正在读取服务待办</div>
        ) : readState === "ERROR" ? null : (
          items.map((item) => (
            <article className="task-row" key={item.eventId}>
              <span className={`task-icon ${item.type.toLowerCase()}`}>
                <ServiceRequestTypeIcon type={item.type} />
              </span>
              <div className="task-content">
                <strong>
                  {item.tableNodeId.replace("table_node_", "")} 号台 ·{" "}
                  {serviceRequestTypeLabel(item.type)}
                </strong>
                <span className="service-request-session-note">
                  {serviceRequestSessionLabel(
                    item,
                    tablesQuery.isError ? undefined : tablesQuery.data,
                  )}
                </span>
                {item.type === "CHECKOUT" ? (
                  <p className="service-request-checkout-note">
                    完成待办只表示已响应顾客，收款和关台仍须在结账页面确认
                  </p>
                ) : null}
                <span className="task-assignee">负责人：{serviceRequestActorLabel(item)}</span>
                <ServiceRequestTimeline request={item} />
              </div>
              <div className="task-actions">
                <span className={`task-status ${item.status.toLowerCase()}`}>
                  {serviceStatusLabels[item.status]}
                </span>
                <NavLink
                  className="task-open-link"
                  to={taskDestination(item, taskRole, location.search)}
                >
                  {taskRole === "WAITER" ? "去处理" : "打开这张球台"}
                  <ChevronRight size={15} aria-hidden="true" />
                </NavLink>
              </div>
            </article>
          ))
        )}
      </section>
      <ServiceRequestHistoryPanel
        expanded={historyExpanded}
        items={historyQuery.data?.pages.flatMap((page) => page.items) ?? []}
        isLoading={historyQuery.isLoading}
        isError={historyQuery.isError}
        isFetching={historyQuery.isFetching}
        isFetchingNextPage={historyQuery.isFetchingNextPage}
        hasNextPage={historyQuery.hasNextPage}
        errorMessage={historyQuery.error instanceof Error ? historyQuery.error.message : null}
        onToggle={() => setHistoryExpanded((current) => !current)}
        onRefresh={() => void historyQuery.refetch()}
        onLoadMore={() => void historyQuery.fetchNextPage()}
      />
    </>
  );
}

const serviceStatusLabels = {
  PENDING: "待接单",
  ACKNOWLEDGED: "处理中",
  COMPLETED: "已完成",
  CANCELLED: "顾客已取消",
  FAILED: "处理失败",
} as const;

function ServiceRequestTransitionInlineFeedback({
  eventId,
  action,
  errorMessage,
  recovery,
  pending,
  onRetry,
}: {
  eventId: string;
  action: ServiceRequestTransitionAction;
  errorMessage: string | null;
  recovery: ServiceRequestTransitionRecovery | null;
  pending: boolean;
  onRetry: (recovery: ServiceRequestTransitionRecovery) => void;
}): React.JSX.Element | null {
  const feedbackRef = useRef<HTMLElement>(null);
  const actionLabel = action === "ACKNOWLEDGE" ? "接单" : "完成待办";
  const feedbackKey = errorMessage
    ? `error:${action}:${errorMessage}`
    : recovery
      ? `recovery:${recovery.state}:${recovery.command.idempotencyKey}`
      : "";
  useEffect(() => {
    if (feedbackKey) feedbackRef.current?.focus();
  }, [feedbackKey]);

  if (errorMessage) {
    return (
      <section
        className="error-band service-request-inline-feedback"
        ref={feedbackRef}
        role="alert"
        tabIndex={-1}
        aria-live="assertive"
      >
        <strong>这项{actionLabel}没有确认完成</strong>
        <p>任务编号 {eventId}</p>
        <p>原因：{errorMessage}</p>
        <p>
          {recovery
            ? "结果尚未确认，请使用原请求重试，系统不会重复接单或重复完成。"
            : "本次请求已明确未执行。请按上方原因处理后，再从这张待办重新操作。"}
        </p>
        {recovery ? (
          <button
            className="button secondary compact"
            type="button"
            disabled={pending || recovery.state === "CLAIMED"}
            onClick={() => onRetry(recovery)}
          >
            <RefreshCw size={16} /> 重试上次操作
          </button>
        ) : null}
      </section>
    );
  }

  if (!recovery) return null;
  const submitting = recovery.state === "CLAIMED";
  return (
    <section
      className="error-band service-request-inline-feedback"
      ref={feedbackRef}
      role={submitting ? "status" : "alert"}
      tabIndex={-1}
      aria-live={submitting ? "polite" : "assertive"}
    >
      <strong>{submitting ? `正在提交${actionLabel}` : `上次${actionLabel}结果尚未确认`}</strong>
      <p>任务编号 {eventId}</p>
      <p>
        {submitting ? "请勿重复点击。" : "请沿用原操作重试，系统不会重复接单或重复完成这项待办。"}
      </p>
      {!submitting ? (
        <button
          className="button secondary compact"
          type="button"
          disabled={pending}
          onClick={() => onRetry(recovery)}
        >
          <RefreshCw size={16} /> 重试上次操作
        </button>
      ) : null}
    </section>
  );
}

function ServiceRequestWorkItemContext({
  workItem,
}: {
  workItem: ServiceRequestWorkItemReadModel | null;
}): React.JSX.Element {
  if (!workItem) {
    return (
      <div className="error-band service-request-assignment-unavailable" role="alert">
        <strong>分派状态暂时无法确认</strong>
        <span>请刷新工作中心；确认前不能接单、完成或转交这项请求。</span>
      </div>
    );
  }

  const escalation = serviceRequestEscalationPresentation(workItem);
  return (
    <section
      className={`service-request-work-item-context${workItem.slaStatus === "OVERDUE" ? " is-overdue" : ""}`}
      aria-label="分派和处理时限"
    >
      <p className="service-request-deadline">
        <Clock3 size={15} aria-hidden="true" />
        <strong>{serviceRequestDeadlineLabel(workItem)}</strong>
      </p>
      {escalation ? (
        <div className="service-request-escalation">
          <p>
            <BadgeAlert size={15} aria-hidden="true" />
            <strong>{escalation.label}</strong>
          </p>
          {escalation.reason ? <span>{escalation.reason}</span> : null}
          <span>提醒只用于请店长或老板协助安排，不表示顾客请求已处理完成。</span>
        </div>
      ) : null}
      <p>
        <strong>现场安排：</strong>
        {workItem.coverageReason}
      </p>
      <p>
        <strong>下一步：</strong>
        {workItem.nextStepLabel}
      </p>
    </section>
  );
}

export function CoachCustomerRequestPage({
  operator,
  effectiveRoles,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
}): React.JSX.Element {
  const location = useLocation();
  const customerRequest = coachCustomerRequestAssignmentQuery(location.search);
  const canArrange = canOperateCoachSessionForRoles(effectiveRoles);
  const requestsQuery = useQuery({
    queryKey: [
      ...SERVICE_REQUEST_ACTIVE_QUERY_KEY,
      "coach-customer-request",
      operator.tenantId,
      operator.storeId,
      operator.id,
    ],
    queryFn: () => listServiceRequests(),
    enabled: customerRequest.status === "VALID" && canArrange,
    retry: false,
  });
  const workItemsQuery = useQuery({
    queryKey: serviceWorkItemsQueryKey(operator),
    queryFn: listServiceWorkItems,
    enabled: customerRequest.status === "VALID" && canArrange,
    retry: false,
  });
  const matchingRequests =
    customerRequest.status === "VALID"
      ? (requestsQuery.data ?? []).filter(
          (request) =>
            request.eventId === customerRequest.customerRequestEventId &&
            request.type === "COACH" &&
            request.status === "ACKNOWLEDGED" &&
            request.sessionId === customerRequest.sessionId,
        )
      : [];
  const request = matchingRequests.length === 1 ? (matchingRequests[0] ?? null) : null;
  const workItem =
    request && workItemsQuery.data
      ? (serviceRequestStaffRows(
          [request],
          workItemsQuery.data.items,
          !workItemsQuery.isError,
          request.eventId,
        )[0]?.workItem ?? null)
      : null;
  const bindingTrusted = request !== null && workItem !== null;
  const tablesQuery = useQuery({
    queryKey: ["tables", "coach-customer-request", operator.tenantId, operator.storeId],
    queryFn: () => listTables({ tenantId: operator.tenantId, storeId: operator.storeId }),
    enabled: customerRequest.status === "VALID" && canArrange && bindingTrusted,
    retry: false,
  });

  if (customerRequest.status === "NONE" || !canArrange) {
    return <CoachDesk operator={operator} effectiveRoles={effectiveRoles} />;
  }

  if (customerRequest.status === "INVALID") {
    return (
      <>
        <PageHeader eyebrow="顾客呼叫助教" title="无法确认原顾客请求" />
        <div className="error-band" role="alert">
          顾客呼叫信息不完整或已被改动，系统没有打开分配操作。请返回员工工作中心重新进入。
        </div>
      </>
    );
  }

  const table = tablesQuery.data?.find(
    (candidate) =>
      candidate.tenantId === operator.tenantId &&
      candidate.storeId === operator.storeId &&
      candidate.state === "IN_USE" &&
      candidate.sessionId === customerRequest.sessionId,
  );
  const returnSearch = new URLSearchParams({ eventId: customerRequest.customerRequestEventId });

  return (
    <>
      <PageHeader eyebrow="顾客呼叫助教" title="安排助教并立即上钟" />
      <p className="info-band">
        这里只处理员工工作中心中已接单的呼叫助教请求。分配成功后，原顾客请求会同时完成。
      </p>
      <NavLink className="button secondary compact" to={`/staff?${returnSearch.toString()}`}>
        返回员工工作中心
      </NavLink>
      {requestsQuery.isPending || workItemsQuery.isPending ? (
        <div className="empty-state">正在核对原顾客请求和员工接单状态</div>
      ) : requestsQuery.isError ? (
        <div className="error-band" role="alert">
          原顾客请求暂时无法确认，系统已停止分配。请返回员工工作中心刷新后重试。
        </div>
      ) : !request ? (
        <div className="error-band" role="alert">
          原顾客请求已结束、不是呼叫助教或已更换台次，不能从这个链接安排助教。
        </div>
      ) : workItemsQuery.isError || !workItem ? (
        <div className="error-band" role="alert">
          分派状态暂时无法确认，系统已停止分配。请返回员工工作中心重新核对。
        </div>
      ) : tablesQuery.isPending ? (
        <div className="empty-state">正在核对原顾客请求对应的当前台单</div>
      ) : tablesQuery.isError ? (
        <div className="error-band" role="alert">
          当前台单暂时无法安全确认，系统已停止分配。请检查门店网络后重试。
        </div>
      ) : !table ? (
        <div className="error-band" role="alert">
          原顾客请求对应的台单已结束或更换，不能改选其他球桌。请返回员工工作中心核对。
        </div>
      ) : (
        <CoachSessionOperationsPanel
          operator={operator}
          effectiveRoles={effectiveRoles}
          table={table}
        />
      )}
    </>
  );
}

export function StaffDesk({
  operator,
  effectiveRoles,
  personalSession = true,
  simulated = false,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
  personalSession?: boolean;
  simulated?: boolean;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const location = useLocation();
  const focusedEventId = new URLSearchParams(location.search).get("eventId");
  const personalCashier = isPersonalCashier(effectiveRoles, personalSession);
  const dutyQuery = useQuery({
    queryKey: serviceDutyQueryKey(operator),
    queryFn: getServiceDuty,
    enabled: true,
    refetchInterval: 1_000,
  });
  const identityKey = serviceRequestTransitionIdentityKey(operator);
  const identityKeyRef = useRef(identityKey);
  identityKeyRef.current = identityKey;
  const [recoveryState, setRecoveryState] = useState<{
    identityKey: string;
    view: ServiceRequestTransitionRecoveryView;
  }>(() => ({
    identityKey,
    view: readServiceRequestTransitionRecovery(identityKey),
  }));
  const [lastAttemptIdentityKey, setLastAttemptIdentityKey] = useState<string | null>(null);
  const recoveryView: ServiceRequestTransitionRecoveryView =
    recoveryState.identityKey === identityKey ? recoveryState.view : { status: "OTHER_OPERATOR" };
  const transferIdentityKey = serviceRequestTransferIdentityKey(operator);
  const transferIdentityKeyRef = useRef(transferIdentityKey);
  transferIdentityKeyRef.current = transferIdentityKey;
  const [transferRecoveryState, setTransferRecoveryState] = useState<{
    identityKey: string;
    view: ServiceRequestTransferRecoveryView;
  }>(() => ({
    identityKey: transferIdentityKey,
    view: readServiceRequestTransferRecovery(transferIdentityKey),
  }));
  const [lastTransferAttemptIdentityKey, setLastTransferAttemptIdentityKey] = useState<
    string | null
  >(null);
  const [transferSuccess, setTransferSuccess] = useState<{
    identityKey: string;
    eventId: string;
    message: string;
  } | null>(null);
  const transferRecoveryView: ServiceRequestTransferRecoveryView =
    transferRecoveryState.identityKey === transferIdentityKey
      ? transferRecoveryState.view
      : { status: "OTHER_OPERATOR" };
  const taskQuery = useQuery({
    queryKey: [...SERVICE_REQUEST_ACTIVE_QUERY_KEY, "waiter"],
    queryFn: () => listServiceRequests(),
    refetchInterval: 1_000,
  });
  const tablesQuery = useQuery({
    queryKey: ["tables"],
    queryFn: () => listTables({ tenantId: operator.tenantId, storeId: operator.storeId }),
    refetchInterval: 1_000,
  });
  const workItemsQuery = useQuery({
    queryKey: serviceWorkItemsQueryKey(operator),
    queryFn: listServiceWorkItems,
    refetchInterval: 1_000,
  });
  const cleaningCompletion = useTableCleaningWorkItemCompletion(operator);
  const transition = useMutation({
    mutationFn: async (input: {
      recovery: ServiceRequestTransitionRecovery;
      identityKey: string;
      identityEpoch: number;
    }) => {
      const { recovery } = input;
      try {
        if (identityKeyRef.current !== input.identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的待办操作仍待确认，请切回原账号处理",
          );
        }
        assertOperatorIdentityUnchanged(input.identityEpoch);
        const result =
          recovery.command.action === "ACKNOWLEDGE"
            ? await acknowledgeServiceRequest(
                recovery.command.eventId,
                recovery.command.idempotencyKey,
                {
                  assignedActorDisplayName: recovery.command.expectedAssignedActorDisplayName,
                  assignedToCurrentActor: recovery.command.expectedAssignedToCurrentActor,
                },
              )
            : await completeServiceRequest(
                recovery.command.eventId,
                recovery.command.idempotencyKey,
                {
                  assignedActorDisplayName: recovery.command.expectedAssignedActorDisplayName,
                  assignedToCurrentActor: recovery.command.expectedAssignedToCurrentActor,
                },
              );
        assertOperatorIdentityUnchanged(input.identityEpoch);
        if (identityKeyRef.current !== input.identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的待办操作仍待确认，请切回原账号处理",
          );
        }
        if (!clearServiceRequestTransitionRecovery(recovery, input.identityKey)) {
          throw new ApiRequestError(
            "SERVICE_REQUEST_TRANSITION_RECOVERY_CLEAR_FAILED",
            "服务端已返回结果，但本机安全记录无法清除；已暂停新操作，请刷新后核对",
          );
        }
        return result;
      } catch (error) {
        if (serviceRequestTransitionOutcomeIsUnknown(error, recovery.state)) {
          markServiceRequestTransitionOutcomeUnknown(recovery, input.identityKey);
        } else {
          clearServiceRequestTransitionRecovery(recovery, input.identityKey);
        }
        throw error;
      } finally {
        finishServiceRequestTransitionDispatch(recovery, input.identityKey);
      }
    },
    onSuccess: async (_result, input) => {
      try {
        await refreshServiceRequestsAfterTransition(queryClient, input.recovery.command.action);
      } catch {
        // The mutation result is already strictly confirmed. The active query reports refresh failures.
      }
    },
    onSettled: (_data, _error, input) => {
      if (identityKeyRef.current !== input.identityKey) return;
      setRecoveryState({
        identityKey: input.identityKey,
        view: readServiceRequestTransitionRecovery(input.identityKey),
      });
    },
  });
  const transfer = useMutation({
    mutationFn: async (input: {
      recovery: ServiceRequestTransferRecovery;
      identityKey: string;
      identityEpoch: number;
    }) => {
      const { recovery } = input;
      try {
        if (transferIdentityKeyRef.current !== input.identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的转交操作仍待确认，请切回原账号处理",
          );
        }
        assertOperatorIdentityUnchanged(input.identityEpoch);
        const command = recovery.command;
        const result = await transferServiceRequest(command.eventId, {
          expectedWorkItemVersion: command.expectedWorkItemVersion,
          targetRole: command.targetRole,
          targetCandidateId: command.targetCandidateId,
          reason: command.reason,
          idempotencyKey: command.idempotencyKey,
        });
        assertOperatorIdentityUnchanged(input.identityEpoch);
        if (transferIdentityKeyRef.current !== input.identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的转交操作仍待确认，请切回原账号处理",
          );
        }
        if (!clearServiceRequestTransferRecovery(recovery, input.identityKey)) {
          throw new ApiRequestError(
            "SERVICE_REQUEST_TRANSFER_RECOVERY_CLEAR_FAILED",
            "服务端已返回转交结果，但本机安全记录无法清除；已暂停新操作，请刷新核对",
          );
        }
        return result;
      } catch (error) {
        if (serviceRequestTransferOutcomeIsUnknown(error, recovery.state)) {
          markServiceRequestTransferOutcomeUnknown(recovery, input.identityKey);
        } else {
          clearServiceRequestTransferRecovery(recovery, input.identityKey);
        }
        throw error;
      } finally {
        finishServiceRequestTransferDispatch(recovery, input.identityKey);
      }
    },
    onSuccess: async (_result, input) => {
      if (transferIdentityKeyRef.current !== input.identityKey) return;
      setTransferSuccess({
        identityKey: input.identityKey,
        eventId: input.recovery.command.eventId,
        message: `已安全转交给${input.recovery.command.targetDisplayName}；待办仍未完成，请接手人继续处理。`,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: SERVICE_REQUEST_ACTIVE_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: ["service-work-items"] }),
        queryClient.invalidateQueries({ queryKey: ["service-duty"] }),
        queryClient.invalidateQueries({ queryKey: ["service-request-transfer-options"] }),
      ]);
    },
    onSettled: (_data, _error, input) => {
      if (transferIdentityKeyRef.current !== input.identityKey) return;
      setTransferRecoveryState({
        identityKey: input.identityKey,
        view: readServiceRequestTransferRecovery(input.identityKey),
      });
    },
  });
  useEffect(() => {
    setRecoveryState({
      identityKey,
      view: readServiceRequestTransitionRecovery(identityKey),
    });
    setLastAttemptIdentityKey(null);
  }, [identityKey]);
  useEffect(() => {
    setTransferRecoveryState({
      identityKey: transferIdentityKey,
      view: readServiceRequestTransferRecovery(transferIdentityKey),
    });
    setLastTransferAttemptIdentityKey(null);
    setTransferSuccess(null);
  }, [transferIdentityKey]);
  const dispatchRecovery = (recovery: ServiceRequestTransitionRecovery): void => {
    if (!beginServiceRequestTransitionDispatch(recovery, identityKey)) {
      setRecoveryState({
        identityKey,
        view: readServiceRequestTransitionRecovery(identityKey),
      });
      return;
    }
    setLastAttemptIdentityKey(identityKey);
    transition.mutate({
      recovery,
      identityKey,
      identityEpoch: currentOperatorIdentityEpoch(),
    });
  };
  const startTransition = (
    eventId: string,
    action: ServiceRequestTransitionAction,
    expectedAssignedActorDisplayName: string | null,
    expectedAssignedToCurrentActor: boolean,
  ): void => {
    if (readServiceRequestTransferRecovery(transferIdentityKey).status !== "NONE") return;
    const command = {
      eventId,
      action,
      expectedAssignedActorDisplayName,
      expectedAssignedToCurrentActor,
      idempotencyKey: crypto.randomUUID(),
    };
    const claimed = claimServiceRequestTransitionRecovery(identityKey, command);
    setRecoveryState({ identityKey, view: claimed });
    if (
      claimed.status !== "OWNED" ||
      claimed.recovery.command.idempotencyKey !== command.idempotencyKey
    ) {
      return;
    }
    dispatchRecovery(claimed.recovery);
  };
  const dispatchTransferRecovery = (recovery: ServiceRequestTransferRecovery): void => {
    if (!beginServiceRequestTransferDispatch(recovery, transferIdentityKey)) {
      setTransferRecoveryState({
        identityKey: transferIdentityKey,
        view: readServiceRequestTransferRecovery(transferIdentityKey),
      });
      return;
    }
    setLastTransferAttemptIdentityKey(transferIdentityKey);
    setTransferSuccess(null);
    transfer.mutate({
      recovery,
      identityKey: transferIdentityKey,
      identityEpoch: currentOperatorIdentityEpoch(),
    });
  };
  const startTransfer = (command: ServiceRequestTransferFrozenCommand): void => {
    if (
      transition.isPending ||
      readServiceRequestTransitionRecovery(identityKey).status !== "NONE"
    ) {
      return;
    }
    const claimed = claimServiceRequestTransferRecovery(transferIdentityKey, command);
    setTransferRecoveryState({ identityKey: transferIdentityKey, view: claimed });
    if (
      claimed.status !== "OWNED" ||
      claimed.recovery.command.idempotencyKey !== command.idempotencyKey
    ) {
      return;
    }
    dispatchTransferRecovery(claimed.recovery);
  };
  const rows = serviceRequestStaffRows(
    activeServiceRequestItems(taskQuery.data ?? []),
    workItemsQuery.data?.items ?? [],
    workItemsQuery.data !== undefined && !workItemsQuery.isError,
    focusedEventId,
  );
  const items = rows.map((row) => row.request);
  const cleaningItems = (workItemsQuery.data?.items ?? []).filter(
    (item): item is TableCleaningWorkItem => item.source.kind === "TABLE_CLEANING",
  );
  const readState = serviceRequestReadState(taskQuery.data !== undefined, taskQuery.isError);
  const pendingCount = serviceRequestCountPresentation(
    readState,
    items.filter((item) => item.status === "PENDING").length,
  );
  const acknowledgedCount = serviceRequestCountPresentation(
    readState,
    items.filter((item) => item.status === "ACKNOWLEDGED").length,
  );
  const transitionError =
    transition.error instanceof Error && lastAttemptIdentityKey === identityKey
      ? transition.error.message
      : null;
  const transitionCommand = transitionError
    ? (transition.variables?.recovery.command ?? null)
    : null;
  const ownedTransitionRecovery = recoveryView.status === "OWNED" ? recoveryView.recovery : null;
  const activeTransitionEventId =
    transitionCommand?.eventId ?? ownedTransitionRecovery?.command.eventId ?? null;
  const activeTransitionAction =
    transitionCommand?.action ?? ownedTransitionRecovery?.command.action ?? null;
  const activeTransitionVisible = Boolean(
    activeTransitionEventId && items.some((item) => item.eventId === activeTransitionEventId),
  );
  const ownedTransferRecovery =
    transferRecoveryView.status === "OWNED" ? transferRecoveryView.recovery : null;
  const activeTransferEventId = ownedTransferRecovery?.command.eventId ?? null;
  const activeTransferVisible = Boolean(
    activeTransferEventId && items.some((item) => item.eventId === activeTransferEventId),
  );
  const transferError =
    transfer.error instanceof Error && lastTransferAttemptIdentityKey === transferIdentityKey
      ? transfer.error.message
      : null;

  return (
    <>
      <PageHeader
        eyebrow="移动现场工作台"
        title="服务员待办"
        action={
          <span className="demo-pill">
            {operator.displayName} · {roleLabels[operator.role]}
          </span>
        }
      />
      <section className="staff-summary" aria-label="服务待办概况">
        <div>
          <span>待接单</span>
          <strong>{pendingCount.value}</strong>
        </div>
        <div>
          <span>处理中</span>
          <strong>{acknowledgedCount.value}</strong>
        </div>
        <div>
          <span>本端身份</span>
          <strong>{operator.displayName}</strong>
        </div>
      </section>
      {personalCashier ? (
        <ServiceDutyStaffPanel
          operator={operator}
          duty={dutyQuery.data}
          isError={dutyQuery.isError}
          simulated={simulated}
        />
      ) : null}
      <KioskProductOrderWorkItemPanel
        operator={operator}
        effectiveRoles={effectiveRoles}
        items={workItemsQuery.data?.items ?? []}
        disabled={workItemsQuery.isError}
      />
      {taskQuery.isError ? (
        <div className="error-band">
          {readState === "TRUSTED"
            ? "服务待办更新失败，正在显示上次成功结果"
            : "服务待办读取失败，请检查门店网络"}
        </div>
      ) : null}
      {workItemsQuery.isError ? (
        <div className="error-band" role="alert">
          分派状态更新失败。请求仍会保留显示，但每条请求在重新确认前都不能开始新的处理操作。
        </div>
      ) : null}
      {activeTransitionEventId && activeTransitionAction && !activeTransitionVisible ? (
        <ServiceRequestTransitionInlineFeedback
          eventId={activeTransitionEventId}
          action={activeTransitionAction}
          errorMessage={
            transitionCommand?.eventId === activeTransitionEventId ? transitionError : null
          }
          recovery={
            ownedTransitionRecovery?.command.eventId === activeTransitionEventId
              ? ownedTransitionRecovery
              : null
          }
          pending={transition.isPending}
          onRetry={dispatchRecovery}
        />
      ) : null}
      {recoveryView.status === "OTHER_OPERATOR" ? (
        <div className="error-band" role="alert">
          另一个账号有尚未确认的待办操作。请切回原账号处理；当前账号已暂停新的待办操作。
        </div>
      ) : recoveryView.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
        <div className="error-band" role="alert">
          无法安全保存待办操作进度。为避免重复接单，已暂停操作，请关闭此标签页后重新打开。
        </div>
      ) : null}
      {transferRecoveryView.status === "OTHER_OPERATOR" ? (
        <div className="error-band" role="alert">
          另一个账号有尚未确认的转交操作。请切回原账号处理；目标人员和原因不会向当前账号显示。
        </div>
      ) : null}
      {transferSuccess?.identityKey === transferIdentityKey ? (
        <output className="success-band">{transferSuccess.message}</output>
      ) : null}
      <TableCleaningCompletionFeedback completion={cleaningCompletion} />
      <section className="staff-task-list" aria-label="结账后清洁待办">
        <div className="section-heading">
          <div>
            <span className="eyebrow">结账后清洁</span>
            <h2>清洁完成后，再由店长或收银员释放球台</h2>
            <p>服务员这里只确认现场清洁，不会收款、退款或释放球台。</p>
          </div>
        </div>
        {workItemsQuery.isError ? (
          <div className="error-band" role="alert">
            {workItemsQuery.data
              ? "清洁待办更新失败，正在显示上次读取结果；已暂停新的清洁确认。"
              : "清洁待办暂时无法安全读取，请检查门店网络。"}
          </div>
        ) : null}
        {workItemsQuery.isLoading && !workItemsQuery.data ? (
          <div className="empty-state">正在读取结账后清洁待办</div>
        ) : workItemsQuery.data && cleaningItems.length === 0 ? (
          <div className="empty-state">当前没有结账后清洁事项</div>
        ) : (
          cleaningItems.map((item) => (
            <article className="staff-task table-cleaning" key={item.workItemId}>
              <div className="staff-task-heading">
                <span className="task-icon service">
                  <Check aria-hidden="true" />
                </span>
                <div>
                  <strong>{item.tableNumber} 号台</strong>
                  <small>结账后清洁</small>
                </div>
                <span className={`task-status ${item.source.state.toLowerCase()}`}>
                  {item.statusLabel}
                </span>
              </div>
              <div className="staff-task-meta">
                <span>{item.statusExplanation}</span>
                <span>灯光关闭：{tableCleaningLightOffLabel(item)}</span>
                <span>下一步：{item.nextStepLabel}</span>
              </div>
              <TableCleaningWorkItemAction
                item={item}
                effectiveRoles={workItemsQuery.isError ? [] : effectiveRoles}
                completion={cleaningCompletion}
              />
            </article>
          ))
        )}
      </section>
      {activeTransferEventId && !activeTransferVisible ? (
        <ServiceRequestTransferComposer
          operator={operator}
          eventId={activeTransferEventId}
          recoveryView={transferRecoveryView}
          pending={transfer.isPending}
          blocked={transition.isPending || recoveryView.status !== "NONE"}
          errorMessage={transferError}
          successMessage={null}
          onSubmit={startTransfer}
          onRetry={dispatchTransferRecovery}
        />
      ) : null}
      <section className="staff-task-list" aria-live="polite">
        {serviceRequestEmptyStateVisible(readState, items.length) ? (
          <div className="empty-state">当前没有未完成的现场待办</div>
        ) : readState === "LOADING" ? (
          <div className="empty-state">正在读取服务待办</div>
        ) : readState === "ERROR" ? null : (
          rows.map(({ request: item, workItem }) => {
            const canAcknowledge = workItem?.viewerActions.canAcknowledge === true;
            const canComplete = workItem?.viewerActions.canComplete === true;
            const canTransfer = workItem?.viewerActions.canTransfer === true;
            const actionUnavailableReason =
              workItem?.viewerActions.unavailableReason ?? "分派状态暂时无法确认";
            const operationLocked =
              transition.isPending ||
              recoveryView.status !== "NONE" ||
              transfer.isPending ||
              transferRecoveryView.status !== "NONE";
            const canArrangeCoach =
              item.type === "COACH" &&
              item.status === "ACKNOWLEDGED" &&
              item.sessionId !== null &&
              workItem !== null &&
              canOperateCoachSessionForRoles(effectiveRoles);
            const openTableWorkItemTrusted =
              workItem !== null && workItem.workItemId === `service:${item.eventId}`;
            const canOpenRequestedTable =
              item.type === "OPEN_TABLE" &&
              item.status === "ACKNOWLEDGED" &&
              item.sessionId === null &&
              (item.assignedToCurrentActor || effectiveRoles.includes("MANAGER")) &&
              openTableWorkItemTrusted &&
              (workItem?.assignedToCurrentActor === true || effectiveRoles.includes("MANAGER")) &&
              canOpenTableCustomerRequestForRoles(effectiveRoles, dutyQuery.data);
            return (
              <article
                className={`staff-task ${item.type.toLowerCase()}${item.eventId === focusedEventId ? " is-focused" : ""}`}
                key={item.eventId}
                aria-current={item.eventId === focusedEventId ? "true" : undefined}
              >
                <div className="staff-task-heading">
                  <span className={`task-icon ${item.type.toLowerCase()}`}>
                    <ServiceRequestTypeIcon type={item.type} />
                  </span>
                  <div>
                    <strong>
                      {workItem?.tableNumber ?? item.tableNodeId.replace("table_node_", "")} 号台
                    </strong>
                    <small>{serviceRequestTypeLabel(item.type)}</small>
                  </div>
                  <span className={`task-status ${item.status.toLowerCase()}`}>
                    {workItem?.statusLabel ?? serviceStatusLabels[item.status]}
                  </span>
                </div>
                <ServiceRequestWorkItemContext workItem={workItem} />
                <div className="staff-task-meta">
                  <span className="service-request-session-note">
                    {serviceRequestSessionLabel(
                      item,
                      tablesQuery.isError ? undefined : tablesQuery.data,
                    )}
                  </span>
                  <span>
                    发起 {new Date(item.requestedAt).toLocaleString("zh-CN", { hour12: false })}
                  </span>
                  {item.acknowledgedAt ? (
                    <span>
                      接单{" "}
                      {new Date(item.acknowledgedAt).toLocaleString("zh-CN", { hour12: false })}
                    </span>
                  ) : null}
                  <span className="task-identity" title={item.eventId}>
                    任务编号 {item.eventId}
                  </span>
                </div>
                {item.type === "CHECKOUT" ? (
                  <p className="service-request-checkout-note">
                    完成待办只表示已响应顾客，收款和关台仍须在结账页面确认
                  </p>
                ) : null}
                {item.status === "PENDING" ? (
                  <button
                    className="button primary"
                    type="button"
                    disabled={!canAcknowledge || operationLocked}
                    title={canAcknowledge ? "由当前员工接下这项现场待办" : actionUnavailableReason}
                    onClick={() =>
                      startTransition(item.eventId, "ACKNOWLEDGE", operator.displayName, true)
                    }
                  >
                    <UserRoundCheck size={17} /> {canAcknowledge ? "接单" : "当前不可接单"}
                  </button>
                ) : item.type === "COACH" ? (
                  canArrangeCoach && item.sessionId !== null && !operationLocked ? (
                    <NavLink
                      className="button primary"
                      to={coachCustomerRequestHref({
                        eventId: item.eventId,
                        sessionId: item.sessionId,
                      })}
                    >
                      <UsersRound size={17} aria-hidden="true" /> 安排助教并立即上钟
                    </NavLink>
                  ) : workItem ? (
                    <p className="service-request-action-unavailable">
                      {canArrangeCoach
                        ? "请先处理页面中尚未确认的原操作，再安排助教。"
                        : "呼叫助教不能在这里直接完成；请使用下方转交功能交给店长、老板或收银员安排。"}
                    </p>
                  ) : null
                ) : item.type === "OPEN_TABLE" ? (
                  canOpenRequestedTable && !operationLocked ? (
                    <NavLink className="button primary" to={openTableCustomerRequestHref(item)}>
                      <Power size={17} aria-hidden="true" /> 核对价格方案并开台
                    </NavLink>
                  ) : workItem ? (
                    <p className="service-request-action-unavailable">
                      {!openTableWorkItemTrusted
                        ? "分派状态暂时无法确认，不能进入开台。"
                        : operationLocked
                          ? "请先处理页面中尚未确认的原操作，再核对这笔开台申请。"
                          : effectiveRoles.includes("WAITER")
                            ? "服务员不能直接开台，请使用下方转交功能交给店长或当前服务值班收银员。"
                            : effectiveRoles.includes("OWNER") &&
                                !effectiveRoles.includes("MANAGER")
                              ? "老板账号在员工工作中心只读；请由店长或当前服务值班收银员办理开台。"
                              : "当前收银员未承担服务值班，请转交给店长或当前服务值班收银员办理。"}
                    </p>
                  ) : null
                ) : (
                  <button
                    className="button primary"
                    type="button"
                    disabled={!canComplete || operationLocked}
                    title={canComplete ? "确认现场事项已经完成" : actionUnavailableReason}
                    onClick={() =>
                      startTransition(
                        item.eventId,
                        "COMPLETE",
                        item.assignedActorDisplayName,
                        item.assignedToCurrentActor,
                      )
                    }
                  >
                    <Check size={17} /> {canComplete ? "完成待办" : "当前不可完成"}
                  </button>
                )}
                {!workItem ? null : item.status === "PENDING" && !canAcknowledge ? (
                  <p className="service-request-action-unavailable">
                    当前不可接单：{actionUnavailableReason}
                  </p>
                ) : item.status === "ACKNOWLEDGED" &&
                  item.type !== "COACH" &&
                  item.type !== "OPEN_TABLE" &&
                  !canComplete ? (
                  <p className="service-request-action-unavailable">
                    当前不可完成：{actionUnavailableReason}
                  </p>
                ) : null}
                {!canTransfer && workItem?.viewerActions.transferUnavailableReason ? (
                  <p className="service-request-action-unavailable">
                    转交：{workItem.viewerActions.transferUnavailableReason}
                  </p>
                ) : null}
                {canTransfer || activeTransferEventId === item.eventId ? (
                  <ServiceRequestTransferComposer
                    operator={operator}
                    eventId={item.eventId}
                    recoveryView={transferRecoveryView}
                    pending={transfer.isPending}
                    blocked={!canTransfer || transition.isPending || recoveryView.status !== "NONE"}
                    errorMessage={activeTransferEventId === item.eventId ? transferError : null}
                    successMessage={null}
                    onSubmit={startTransfer}
                    onRetry={dispatchTransferRecovery}
                  />
                ) : null}
                {activeTransitionEventId === item.eventId && activeTransitionAction ? (
                  <ServiceRequestTransitionInlineFeedback
                    eventId={item.eventId}
                    action={activeTransitionAction}
                    errorMessage={
                      transitionCommand?.eventId === item.eventId ? transitionError : null
                    }
                    recovery={
                      ownedTransitionRecovery?.command.eventId === item.eventId
                        ? ownedTransitionRecovery
                        : null
                    }
                    pending={transition.isPending}
                    onRetry={dispatchRecovery}
                  />
                ) : null}
              </article>
            );
          })
        )}
      </section>
    </>
  );
}

export type OwnerReconciliationQueryView =
  | "LOADING"
  | "EMPTY"
  | "FORBIDDEN"
  | "OFFLINE"
  | "ERROR"
  | "READY";

export function ownerReconciliationQueryView(input: {
  isPending: boolean;
  error: unknown;
  summary: Pick<ReconciliationSummary, "orders"> | undefined;
  connectivityMode: ConnectivityReadModel["mode"] | null;
}): OwnerReconciliationQueryView {
  const error = input.error;
  const forbidden = error instanceof ApiRequestError && error.statusCode === 403;
  const gatewayUnreachable =
    error instanceof ApiRequestError &&
    ["GATEWAY_UNREACHABLE", "OFFLINE_CONFIGURATION_MISSING"].includes(error.code);
  if (!input.summary) {
    if (forbidden) return "FORBIDDEN";
    if (
      gatewayUnreachable ||
      (input.connectivityMode !== null && input.connectivityMode !== "ONLINE")
    ) {
      return "OFFLINE";
    }
    if (error) return "ERROR";
    return "LOADING";
  }
  if (input.connectivityMode !== null && input.connectivityMode !== "ONLINE") return "OFFLINE";
  if (forbidden) return "FORBIDDEN";
  if (error) return "ERROR";
  if (input.summary.orders === 0) return "EMPTY";
  return "READY";
}

export function ownerReconciliationMoneyLabel(
  view: OwnerReconciliationQueryView,
  value: number | undefined,
): string {
  if (view === "FORBIDDEN") return "无权查看";
  if ((view === "OFFLINE" || view === "ERROR") && value !== undefined) {
    return `${formatFee(value)}（上次可信）`;
  }
  if (view === "ERROR") return "无法确认";
  if (view === "OFFLINE") return "离线，暂不可确认";
  if (view === "LOADING" || value === undefined) return "读取中";
  return formatFee(value);
}

export function ownerReconciliationCountLabel(
  view: OwnerReconciliationQueryView,
  value: number | undefined,
): string {
  if (view === "FORBIDDEN") return "无权查看";
  if ((view === "OFFLINE" || view === "ERROR") && value !== undefined) {
    return `${value} 笔（上次可信）`;
  }
  if (view === "ERROR") return "无法确认";
  if (view === "OFFLINE") return "离线，暂不可确认";
  if (view === "LOADING" || value === undefined) return "读取中";
  return `${value} 笔`;
}

export function OwnerReconciliationStatePanel({
  view,
  summary,
}: {
  view: OwnerReconciliationQueryView;
  summary: ReconciliationSummary | undefined;
}): React.JSX.Element {
  const dataTime = summary ? formatTime(summary.generatedAt) : null;
  const content = {
    LOADING: {
      title: "正在读取本店核对数据",
      detail: "金额尚未确认，读取完成前不会把空白或未知显示成 0 元。",
    },
    EMPTY: {
      title: "该营业日暂无本机订单",
      detail: `这是系统确认过的空结果；不是“0 元已经核对完成”。数据时间：${dataTime}`,
    },
    FORBIDDEN: {
      title: "当前账号无权查看核对金额",
      detail: "请由本店老板、店长或收银员登录；系统不会显示其他门店或更高权限的资金信息。",
    },
    OFFLINE: {
      title: summary ? "当前离线，保留上次可信核对数据" : "当前离线，暂时无法读取核对数据",
      detail: summary
        ? `下面数据截至 ${dataTime}；新到账和云端核对可能尚未同步，请联网后刷新。`
        : "请恢复门店网关连接后重试；未知金额不会显示为 0 元。",
    },
    ERROR: {
      title: summary ? "本次刷新失败，保留上次可信核对数据" : "核对数据读取失败",
      detail: summary
        ? `下面数据截至 ${dataTime}；请稍后重试，不能把它当作最新到账结果。`
        : "返回内容未通过校验或读取发生异常；页面没有把未知金额当作 0 元。",
    },
    READY: {
      title: "核对数据已更新",
      detail: `数据时间：${dataTime}。待核对和异常金额仍需回到原业务记录处理。`,
    },
  }[view];
  return (
    <section
      className={view === "READY" || view === "EMPTY" ? "evidence-band" : "error-band"}
      aria-live="polite"
      aria-busy={view === "LOADING" ? true : undefined}
    >
      <strong>{content.title}</strong>
      <span>{content.detail}</span>
    </section>
  );
}

export function OwnerPaymentMethodAvailability({
  summary,
}: {
  summary: ReconciliationSummary;
}): React.JSX.Element {
  const methods = [
    {
      key: "cash",
      label: "现金",
      enabled: summary.configuredManualPaymentMethods.cash,
      receivedFen: summary.cashFen,
      refundedFen: summary.cashRefundedFen,
    },
    {
      key: "static-wechat",
      label: "微信静态码",
      enabled: summary.configuredManualPaymentMethods.staticWechat,
      receivedFen: summary.staticWechatFen,
      refundedFen: summary.staticWechatRefundedFen,
    },
    {
      key: "static-alipay",
      label: "支付宝静态码",
      enabled: summary.configuredManualPaymentMethods.staticAlipay,
      receivedFen: summary.staticAlipayFen,
      refundedFen: summary.staticAlipayRefundedFen,
    },
  ];
  return (
    <section className="owner-breakdown" aria-label="本营业日收款渠道适用范围">
      <div>
        <h2>收款渠道适用范围</h2>
        <p>“不适用”只表示门店当前没有启用该收款方式；历史订单和待核对记录仍会保留。</p>
        <dl>
          {methods.map((method) => (
            <div key={method.key}>
              <dt>{method.label}</dt>
              <dd>
                {method.enabled
                  ? `已启用 · 已确认收款 ${formatFee(method.receivedFen)} · 已退款 ${formatFee(method.refundedFen)}`
                  : "当前未启用，本日新收款不适用"}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

export function OwnerBusinessMetrics({
  summary,
  status = summary ? "READY" : "LOADING",
}: {
  summary: OwnerBusinessSummary | undefined;
  status?: OwnerReconciliationQueryView;
}): React.JSX.Element {
  if (status !== "READY" || !summary) {
    const stateLabel = status === "ERROR" ? "无法确认" : "读取中";
    const stateDetail =
      status === "ERROR" ? "本次读取失败，旧金额已隐藏" : "正在读取本店权威经营数据";
    return (
      <section
        className="owner-metrics"
        aria-label="经营收益摘要"
        aria-busy={status === "LOADING" ? true : undefined}
      >
        <div>
          <span>商品成本</span>
          <strong>{stateLabel}</strong>
          <small>{stateDetail}</small>
        </div>
        <div>
          <span>商品毛利</span>
          <strong>{stateLabel}</strong>
          <small>{stateDetail}</small>
        </div>
        <div>
          <span>提成应付</span>
          <strong>{stateLabel}</strong>
          <small>{stateDetail}</small>
        </div>
        <div>
          <span>退款损失</span>
          <strong>{stateLabel}</strong>
          <small>{stateDetail}</small>
        </div>
      </section>
    );
  }
  const incompleteCost = summary.productCostKnownQuantity < summary.productQuantity;
  return (
    <section className="owner-metrics" aria-label="经营收益摘要">
      <div className={incompleteCost ? "needs-attention" : ""}>
        <span>商品成本</span>
        <strong>{formatFee(summary.productCostFen)}</strong>
        <small>
          {summary.productCostKnownQuantity} / {summary.productQuantity} 件已记录成交时成本
        </small>
      </div>
      <div className={incompleteCost ? "needs-attention" : ""}>
        <span>商品毛利（按已记录成本）</span>
        <strong>{formatFee(summary.productGrossProfitFen)}</strong>
        <small>商品销售 {formatFee(summary.productRevenueFen)}</small>
      </div>
      <div>
        <span>提成应付</span>
        <strong>{formatFee(summary.commissionPayableFen)}</strong>
        <small>来自当日助教服务记录</small>
      </div>
      <div className={summary.refundLossFen > 0 ? "needs-attention" : ""}>
        <span>退款损失</span>
        <strong>{formatFee(summary.refundLossFen)}</strong>
        <small>按退款流水汇总</small>
      </div>
      <div
        className={
          summary.lowStockProducts > 0 || summary.outOfStockProducts > 0 ? "needs-attention" : ""
        }
      >
        <span>库存预警</span>
        <strong>{summary.lowStockProducts + summary.outOfStockProducts} 种</strong>
        <small>
          低库存 {summary.lowStockProducts} 种 · 缺货 {summary.outOfStockProducts} 种
        </small>
      </div>
      <div className={summary.cashDifferenceFen ? "needs-attention" : ""}>
        <span>现金差异</span>
        <strong>
          {summary.cashDifferenceFen === null ? "待交班清点" : formatFee(summary.cashDifferenceFen)}
        </strong>
        <small>账面应有 {formatFee(summary.expectedCashFen)}</small>
      </div>
      {incompleteCost ? (
        <details className="owner-cost-detail">
          <summary>查看缺少成交时成本记录的订单明细</summary>
          {summary.missingCostSnapshots.length ? (
            <div className="owner-cost-detail-list">
              {summary.missingCostSnapshots.map((item) => (
                <article key={`${item.orderId}:${item.skuId}`}>
                  <strong>
                    {item.tableNumber} 号台 · {item.productName} × {item.quantity}
                  </strong>
                  <span>订单 {item.orderId}</span>
                  <span>
                    当时售价 {formatFee(item.unitPriceFen)} · {formatTime(item.startedAt)}
                  </span>
                </article>
              ))}
            </div>
          ) : (
            <p>
              历史汇总显示部分商品没有成交时成本记录，但当前明细已不在本机，请按营业日到订单中心核对。
            </p>
          )}
        </details>
      ) : null}
    </section>
  );
}

export function OwnerOrderCohortFollowUp({
  summary,
  status,
  manualReviewLabel,
}: {
  summary: ReconciliationSummary | undefined;
  status: OwnerReconciliationQueryView;
  manualReviewLabel: string;
}): React.JSX.Element {
  return (
    <section className="owner-breakdown owner-order-cohort-follow-up" aria-label="现存订单批次待办">
      <div>
        <h2>现存订单批次待办</h2>
        <p>
          这里只按订单所属营业日追踪本机现存订单，不代表该日实际收到或退回的钱。实收、退款、成本和库存请以上方“指标口径与来源”为准。
        </p>
        <dl>
          <div>
            <dt>待渠道核对</dt>
            <dd>{ownerReconciliationMoneyLabel(status, summary?.pendingReconciliationFen)}</dd>
          </div>
          <div>
            <dt>核对异常</dt>
            <dd>{ownerReconciliationMoneyLabel(status, summary?.exceptionReconciliationFen)}</dd>
          </div>
          <div>
            <dt>离线来源订单</dt>
            <dd>{ownerReconciliationCountLabel(status, summary?.offlineOrders)}</dd>
          </div>
          <div>
            <dt>同步冲突</dt>
            <dd>{ownerReconciliationCountLabel(status, summary?.conflicts)}</dd>
          </div>
        </dl>
      </div>
      <div>
        <h2>整店待人工确认</h2>
        <p>这里只显示需要人工回到原业务流程核对的记录，不代表整套系统离线。</p>
        <strong>{manualReviewLabel}</strong>
      </div>
    </section>
  );
}

function OwnerDesk({
  operator,
  effectiveRoles,
  authenticationEvidence,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
  authenticationEvidence: "SIMULATED" | "STORE_ACCEPTED";
}): React.JSX.Element {
  const [businessDate, setBusinessDate] = useState("");
  const summaryQuery = useQuery({
    queryKey: ["owner-reconciliation", businessDate],
    queryFn: () => getOwnerReconciliationSummary(businessDate || undefined),
    refetchInterval: 5_000,
  });
  const connectivityQuery = useQuery({
    queryKey: ["connectivity"],
    queryFn: getConnectivity,
    refetchInterval: 1_000,
  });
  const reviewSummaryQuery = useQuery({
    queryKey: ["manual-review-summary"],
    queryFn: getManualReviewSummary,
  });
  const tablesQuery = useQuery({
    queryKey: ["tables"],
    queryFn: () => listTables({ tenantId: operator.tenantId, storeId: operator.storeId }),
    refetchInterval: 1_000,
  });
  const ownerConnectivity = connectivityTrustState(connectivityQuery);
  const summaryView = ownerReconciliationQueryView({
    isPending: summaryQuery.isPending,
    error: summaryQuery.error,
    summary: summaryQuery.data,
    connectivityMode: ownerConnectivity.kind === "TRUSTED" ? ownerConnectivity.data.mode : null,
  });
  const summary = summaryQuery.data;

  return (
    <>
      <PageHeader
        eyebrow="经营配置与核对后台"
        title="老板 / 店长核账"
        action={
          <>
            <span className="demo-pill">
              {roleLabels[operator.role]} · {operatorDataEvidenceLabel(authenticationEvidence)}
            </span>
            <NavLink className="icon-button" to="/system" title="门店状态与工具">
              <Settings size={18} aria-hidden="true" />
            </NavLink>
          </>
        }
      />
      <LongSessionAlertPanel tables={tablesQuery.data ?? []} effectiveRoles={effectiveRoles} />
      <section className="owner-filter">
        <label>
          营业日
          <input
            type="date"
            value={businessDate || summary?.businessDate || ""}
            onChange={(event) => setBusinessDate(event.target.value)}
          />
        </label>
        <span>
          {authenticationEvidence === "SIMULATED"
            ? "这是本地演示账本，不代表真实营业结果。"
            : "数据来自本店账本；静态码等人工确认渠道仍按待核对状态显示，不会冒充到账。"}
        </span>
      </section>
      <OwnerReconciliationStatePanel view={summaryView} summary={summary} />
      <OwnerOperatingDashboardPanel
        operator={operator}
        businessDate={businessDate || summary?.businessDate || ""}
      />
      <BusinessMetricDictionaryPanel
        operator={operator}
        businessDate={businessDate || summary?.businessDate || ""}
      />
      {summary ? <OwnerPaymentMethodAvailability summary={summary} /> : null}
      <OwnerOrderCohortFollowUp
        summary={summary}
        status={summaryView}
        manualReviewLabel={manualReviewSummaryLabel(reviewSummaryQuery)}
      />
    </>
  );
}

function downloadTextFile(filename: string, content: string, type: string): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export type SystemStatusDisplayState = "LOADING" | "ERROR" | "READY";

export function systemStatusDisplayState(
  initialLoading: boolean,
  requestError: boolean,
  hasData: boolean,
): SystemStatusDisplayState {
  if (initialLoading && !hasData) return "LOADING";
  if (requestError || !hasData) return "ERROR";
  return "READY";
}

export function connectivityModeLabel(mode: ConnectivityReadModel["mode"]): string {
  if (mode === "ONLINE") return "在线";
  if (mode === "RECOVERING") return "正在同步";
  if (mode === "AUTHORITY_EXPIRED") return "离线，写入授权已失效";
  return "离线营业";
}

function productCatalogChangedFieldLabel(
  field: ProductCatalogPreviewResult["rows"][number]["changedFields"][number],
): string {
  if (field === "NAME") return "商品名称";
  if (field === "UNIT_PRICE") return "售价";
  return "销售状态";
}

export function ProductCatalogPreviewSummary({
  preview,
}: {
  preview: ProductCatalogPreviewResult;
}): React.JSX.Element {
  return (
    <div className="csv-preview" aria-live="polite">
      <strong>
        校验通过：将新增 {preview.summary.create} 项、更新 {preview.summary.update} 项、不变{" "}
        {preview.summary.unchanged} 项
      </strong>
      <div className="preview-list">
        {preview.rows.slice(0, 6).map((row) => (
          <span key={row.skuId}>
            <strong>
              {row.outcome === "CREATE"
                ? "新增商品"
                : row.outcome === "UPDATE"
                  ? "更新商品"
                  : "保持不变"}
            </strong>
            ：{row.skuId} · {row.name} · 售价 {formatFee(row.unitPriceFen)} ·{" "}
            {row.enabled ? "在售" : "停售"}。
            {row.outcome === "CREATE"
              ? " 库存从 0 开始；分类为“未分类”、单位为“件”、成本为 0 元；启用库存管理，5 件时提醒低库存，不允许负库存；条码和店内编码留空。"
              : row.outcome === "UPDATE"
                ? ` 本次只修改${row.changedFields.map(productCatalogChangedFieldLabel).join("、")}；分类、单位、成本、条码、店内编码、库存设置和当前库存保持不变。`
                : " 现有商品不会新增版本，库存和其他资料均保持不变。"}
          </span>
        ))}
      </div>
    </div>
  );
}

export function SystemDesk({ operator }: { operator: OperatorIdentity }): React.JSX.Element {
  const queryClient = useQueryClient();
  const componentHealthTitleId = useId();
  const localStorageTitleId = useId();
  const dataDeliveryTitleId = useId();
  const [catalogCsv, setCatalogCsv] = useState("");
  const [catalogFilename, setCatalogFilename] = useState("");
  const [catalogImportKey, setCatalogImportKey] = useState<string>(() => crypto.randomUUID());
  const [fileError, setFileError] = useState<string | null>(null);
  const [catalogRecovery, setCatalogRecovery] = useState<ProductCatalogImportRecoveryView>(() =>
    readProductCatalogImportRecovery(productCatalogImportIdentityKey(operator)),
  );
  const [catalogRecoveryNotice, setCatalogRecoveryNotice] = useState<string | null>(null);
  const catalogIdentityKey = productCatalogImportIdentityKey(operator);
  const productIdentityKey = productSaveIdentityKey(operator);
  const productSaveRecovery = readProductSaveRecovery(productIdentityKey);
  const ownedCatalogRecovery = catalogRecovery.status === "OWNED" ? catalogRecovery.recovery : null;
  const statusQuery = useQuery({
    queryKey: ["system-status"],
    queryFn: getSystemStatus,
    refetchInterval: 3_000,
  });
  const auditQuery = useQuery({
    queryKey: ["business-audit-timeline", operator.tenantId, operator.storeId, operator.id],
    queryFn: () => getBusinessAuditTimeline(operator),
  });
  const auditExport = useMutation({
    mutationFn: async (timeline: BusinessAuditTimelineReadModel) => {
      downloadTextFile(
        businessAuditTimelineFileName(timeline.generatedAt),
        buildBusinessAuditTimelineCsv(timeline),
        "text/csv;charset=utf-8",
      );
    },
  });
  const versionQuery = useQuery({
    queryKey: ["public-version"],
    queryFn: getPublicVersion,
    retry: 1,
    staleTime: 60_000,
  });
  const preview = useMutation({ mutationFn: previewProductCatalog });
  const applyImport = useMutation({
    mutationFn: async (recovery: ProductCatalogImportRecovery) => {
      try {
        const result = await importProductCatalogWithProductSaveGuard(
          productIdentityKey,
          recovery.command,
        );
        return { recovery, result };
      } catch (error) {
        if (error instanceof ApiRequestError && error.code === "OPERATOR_IDENTITY_CHANGED") {
          markProductCatalogImportOutcomeUnknown(recovery, recovery.ownerIdentityKey);
        }
        throw error;
      } finally {
        finishProductCatalogImportDispatch(recovery, recovery.ownerIdentityKey);
      }
    },
    onSuccess: async ({ recovery }) => {
      if (!clearProductCatalogImportRecovery(recovery, catalogIdentityKey)) {
        setCatalogRecovery(readProductCatalogImportRecovery(catalogIdentityKey));
        setCatalogRecoveryNotice(
          "商品导入结果已返回，但本机无法安全清除原请求。页面继续锁定，请不要再次导入。",
        );
        return;
      }
      setCatalogRecovery({ status: "NONE" });
      setCatalogRecoveryNotice(null);
      await queryClient.invalidateQueries({ queryKey: ["system-status"] });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({ queryKey: ["connectivity"] });
    },
    onError: (error, recovery) => {
      if (productCatalogImportOutcomeIsUnknown(error)) {
        const retained = markProductCatalogImportOutcomeUnknown(recovery, catalogIdentityKey);
        setCatalogRecovery(readProductCatalogImportRecovery(catalogIdentityKey));
        setCatalogRecoveryNotice(
          retained
            ? `${error instanceof Error ? error.message : "商品导入结果尚未确认"} 页面已保留原文件和原请求号，只能安全重试这一笔。`
            : "商品导入结果尚未确认，而且本机无法安全保存恢复记录。请不要重复导入，并联系技术支持。",
        );
        return;
      }
      if (
        recovery.state === "OUTCOME_UNKNOWN" &&
        error instanceof ApiRequestError &&
        (error.code === "CATALOG_IMPORT_CONFIGURATION_VERSION_CONFLICT" ||
          error.code === "IDEMPOTENCY_COLLISION")
      ) {
        const cleared = clearProductCatalogImportRecovery(recovery, catalogIdentityKey);
        setCatalogRecovery(readProductCatalogImportRecovery(catalogIdentityKey));
        setCatalogRecoveryNotice(
          cleared
            ? `${error.message} 已确认原文件没有按这次请求写入，请重新选择文件并校验。`
            : "已确认原文件没有按这次请求写入，但本机无法安全清除恢复记录。页面继续锁定，请联系技术支持。",
        );
        return;
      }
      if (recovery.state === "OUTCOME_UNKNOWN") {
        setCatalogRecovery(readProductCatalogImportRecovery(catalogIdentityKey));
        setCatalogRecoveryNotice(
          `${error instanceof Error ? error.message : "本次重试未执行"} 原导入结果仍未确认，请继续保留原请求。`,
        );
        return;
      }
      const cleared = clearProductCatalogImportRecovery(recovery, catalogIdentityKey);
      setCatalogRecovery(readProductCatalogImportRecovery(catalogIdentityKey));
      setCatalogRecoveryNotice(
        cleared
          ? `${error instanceof Error ? error.message : "商品导入未执行"} 可以修正文件后重新校验。`
          : "商品导入未执行，但本机无法安全清除恢复记录。页面继续锁定，请联系技术支持。",
      );
    },
  });
  const download = useMutation({
    mutationFn: async (kind: "ORDERS" | "DIAGNOSTIC" | "PRODUCT_TEMPLATE") => {
      if (kind === "ORDERS") {
        downloadTextFile(orderReportFilename(), await getOrdersCsv(), "text/csv;charset=utf-8");
      } else if (kind === "DIAGNOSTIC") {
        downloadTextFile(
          "billiard-erp-diagnostic.json",
          await getDiagnosticBundle(),
          "application/json;charset=utf-8",
        );
      } else {
        downloadTextFile(
          "商品批量导入模板.csv",
          await getProductCatalogTemplate(),
          "text/csv;charset=utf-8",
        );
      }
    },
  });
  const statusDisplayState = systemStatusDisplayState(
    statusQuery.isPending,
    statusQuery.isError || statusQuery.isRefetchError,
    Boolean(statusQuery.data),
  );
  const status = statusDisplayState === "READY" ? statusQuery.data : undefined;
  const importDisabledReason =
    catalogRecovery.status !== "NONE"
      ? catalogRecovery.status === "OWNED"
        ? "上次商品导入结果尚未确认，请使用原请求安全重试"
        : catalogRecovery.status === "OTHER_OPERATOR"
          ? "另一位员工有一笔商品导入结果尚未确认，请等待原操作人处理"
          : "本机无法安全读取商品导入恢复记录，批量导入已暂停"
      : !catalogCsv
        ? "请先选择商品 CSV"
        : applyImport.data
          ? "当前 CSV 已应用；选择新文件后才能再次导入"
          : preview.isPending
            ? "正在校验 CSV"
            : !preview.data
              ? "请先完成校验预览"
              : preview.data.errors.length > 0
                ? "CSV 仍有校验错误"
                : status?.gateway.mode !== "ONLINE"
                  ? "批量导入需要中心账本在线且同步正常"
                  : productSaveRecovery.status !== "NONE"
                    ? productSaveRecovery.status === "OWNED"
                      ? "你有一笔商品资料保存结果尚未确认，请先回到商品库存页面处理"
                      : productSaveRecovery.status === "OTHER_OPERATOR"
                        ? "另一位员工有一笔商品资料保存结果尚未确认，请等待原操作人处理"
                        : "本机无法安全读取商品资料保存恢复记录，批量导入已暂停"
                    : null;

  useEffect(() => {
    const current = readProductCatalogImportRecovery(catalogIdentityKey);
    setCatalogRecovery(current);
    applyImport.reset();
    preview.reset();
    setFileError(null);
    if (current.status === "OWNED") {
      setCatalogCsv(current.recovery.command.csv);
      setCatalogFilename(current.recovery.command.filename);
      setCatalogImportKey(current.recovery.command.idempotencyKey);
      setCatalogRecoveryNotice(
        "上次商品导入结果尚未确认。页面已恢复原文件并锁定新选择；请使用同一请求安全重试。",
      );
    } else {
      setCatalogCsv("");
      setCatalogFilename("");
      setCatalogImportKey(crypto.randomUUID());
      setCatalogRecoveryNotice(
        current.status === "OTHER_OPERATOR"
          ? "另一位员工有一笔商品导入结果尚未确认。你看不到对方文件内容，也不能代为重试。"
          : current.status === "RECOVERY_STORAGE_UNAVAILABLE"
            ? "本机无法安全读取商品导入恢复记录。为避免重复导入，批量工具已暂停；请联系技术支持。"
            : null,
      );
    }
  }, [applyImport.reset, catalogIdentityKey, preview.reset]);

  const dispatchCatalogImport = (recovery: ProductCatalogImportRecovery): void => {
    setCatalogRecoveryNotice(null);
    applyImport.reset();
    if (!beginProductCatalogImportDispatch(recovery, catalogIdentityKey)) {
      setCatalogRecovery(readProductCatalogImportRecovery(catalogIdentityKey));
      setCatalogRecoveryNotice(
        "本机无法在发送前核对原商品导入请求，本次没有提交。请不要重复操作。",
      );
      return;
    }
    applyImport.mutate(recovery);
  };

  const startCatalogImport = (): void => {
    if (!preview.data || preview.data.errors.length > 0 || !catalogCsv || !catalogFilename) return;
    const command = {
      filename: catalogFilename.trim(),
      csv: catalogCsv,
      idempotencyKey: catalogImportKey,
      previewFingerprint: preview.data.fingerprint,
      expectedConfigurationVersion: preview.data.expectedConfigurationVersion,
      rowCount: preview.data.rows.length,
    };
    const claimed = claimProductCatalogImportRecovery(catalogIdentityKey, command);
    setCatalogRecovery(claimed);
    if (
      claimed.status === "OWNED" &&
      claimed.recovery.command.idempotencyKey === command.idempotencyKey
    ) {
      dispatchCatalogImport(claimed.recovery);
      return;
    }
    setCatalogRecoveryNotice(
      claimed.status === "RECOVERY_STORAGE_UNAVAILABLE"
        ? "本机无法在发送前安全保存恢复记录，本次没有提交。请不要重复操作。"
        : "当前已有一笔商品导入结果尚未确认，不能创建新请求。",
    );
  };

  return (
    <>
      <PageHeader
        eyebrow={`${roleLabels[operator.role]} · 门店管理`}
        title="门店状态与批量工具"
        action={
          <button
            type="button"
            className="icon-button"
            title="刷新系统状态"
            onClick={() => void statusQuery.refetch()}
          >
            <RefreshCw size={18} aria-hidden="true" />
          </button>
        }
      />
      <SupportAccessPanel operator={operator} />
      {statusDisplayState === "ERROR" ? (
        <div className="error-band" role="alert">
          系统状态暂时无法确认：连接中断、当前角色无权访问，或返回内容未通过校验
        </div>
      ) : null}
      <section
        className={`system-health ${
          statusDisplayState !== "READY"
            ? "is-unknown"
            : status?.overall === "HEALTHY"
              ? "is-healthy"
              : "is-degraded"
        }`}
        aria-live="polite"
      >
        <ServerCog size={22} aria-hidden="true" />
        <div>
          <strong>
            {statusDisplayState === "LOADING"
              ? "正在核对组件状态"
              : statusDisplayState === "ERROR"
                ? "系统状态暂时无法确认"
                : status?.gateway.mode === "ONLINE" && status.storage.pendingManualReview > 0
                  ? `系统在线，${status.storage.pendingManualReview} 条记录待人工处理`
                  : status?.overall === "HEALTHY"
                    ? "门店系统运行正常"
                    : "部分组件需要关注"}
          </strong>
          <span>
            联网状态：
            {status ? connectivityModeLabel(status.gateway.mode) : "—"} · 本地数据：
            {status ? (status.storage.integrity === "OK" ? "正常" : "异常") : "—"}
          </span>
        </div>
        <span className="evidence-label">
          {status ? (status.evidence === "STORE_ACCEPTED" ? "门店状态" : "演示数据") : "状态待确认"}
        </span>
      </section>

      <BusinessAuditTimelinePanel
        timeline={auditQuery.data}
        isLoading={auditQuery.isPending}
        isError={auditQuery.isError || auditQuery.isRefetchError}
        isExporting={auditExport.isPending}
        exportError={auditExport.isError}
        onRefresh={() => {
          auditExport.reset();
          void auditQuery.refetch();
        }}
        onExport={(timeline) => {
          auditExport.reset();
          auditExport.mutate(timeline);
        }}
      />

      <details className="system-maintenance-details">
        <summary>
          <ServerCog size={18} aria-hidden="true" /> 项目维护诊断（非日常经营）
        </summary>
        <p className="system-maintenance-note">
          这里供项目维护人员排查同步和版本问题。普通经营操作不需要理解下面的数据。
        </p>
        <BuildProvenanceStamp
          gateway={versionQuery.data}
          loading={versionQuery.isPending}
          unavailable={versionQuery.isError}
        />
        <section className="system-section" aria-labelledby={componentHealthTitleId}>
          <div className="section-heading">
            <div>
              <h2 id={componentHealthTitleId}>版本与健康</h2>
              <p>当前收银端可见组件</p>
            </div>
          </div>
          <table className="component-table">
            <caption className="visually-hidden">组件版本与健康状态</caption>
            <thead>
              <tr className="component-row component-head">
                <th scope="col">组件</th>
                <th scope="col">版本</th>
                <th scope="col">数据结构版本</th>
                <th scope="col">状态</th>
              </tr>
            </thead>
            <tbody>
              {(status?.components ?? []).map((component) => (
                <tr className="component-row" key={component.component}>
                  <th scope="row">{component.component}</th>
                  <td>{component.productVersion}</td>
                  <td>{component.schemaVersion || "-"}</td>
                  <td>
                    <span className={`health-badge is-${component.health.toLowerCase()}`}>
                      {component.health}
                    </span>
                  </td>
                </tr>
              ))}
              {statusDisplayState === "LOADING" ? (
                <tr>
                  <td className="loading-state" colSpan={4}>
                    正在读取版本信息…
                  </td>
                </tr>
              ) : null}
              {statusDisplayState === "ERROR" ? (
                <tr>
                  <td className="loading-state" colSpan={4}>
                    系统状态暂时无法确认
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </section>

        <section className="system-section" aria-labelledby={localStorageTitleId}>
          <div className="section-heading">
            <div>
              <h2 id={localStorageTitleId}>本地可靠性</h2>
              <p>本地订单、待同步记录与人工事项</p>
            </div>
          </div>
          <div className="system-metrics">
            <div>
              <Database size={18} aria-hidden="true" />
              <span>订单</span>
              <strong>{status?.storage.orders ?? "—"}</strong>
            </div>
            <div>
              <HardDrive size={18} aria-hidden="true" />
              <span>待同步记录</span>
              <strong>{status?.storage.pendingOutbox ?? "—"}</strong>
            </div>
            <div>
              <AlertTriangle size={18} aria-hidden="true" />
              <span>失败/冲突</span>
              <strong>{status?.storage.failedOutbox ?? "—"}</strong>
            </div>
            <div>
              <FileCheck2 size={18} aria-hidden="true" />
              <span>待人工确认</span>
              <strong>{status?.storage.pendingManualReview ?? "—"}</strong>
            </div>
          </div>
        </section>
      </details>

      <details className="system-maintenance-details system-batch-tools">
        <summary>
          <FileUp size={18} aria-hidden="true" /> 导出与批量工具（非日常经营）
        </summary>
        <p className="system-maintenance-note">
          日常商品新增、修改以及库存和流水查看请使用“商品库存”页面；库存登记等待审批中心开放。这里仅供项目维护或确需批量处理时使用。
        </p>
        <section className="system-section delivery-section" aria-labelledby={dataDeliveryTitleId}>
          <div className="section-heading">
            <div>
              <h2 id={dataDeliveryTitleId}>资料导出与批量商品工具</h2>
              <p>订单资料、脱敏诊断和可选批量导入</p>
            </div>
          </div>
          <div className="delivery-actions">
            <button
              className="button secondary"
              type="button"
              onClick={() => download.mutate("ORDERS")}
              disabled={download.isPending}
            >
              <Download size={17} aria-hidden="true" />
              导出订单 CSV
            </button>
            <button
              className="button secondary"
              type="button"
              onClick={() => download.mutate("DIAGNOSTIC")}
              disabled={download.isPending}
            >
              <Download size={17} aria-hidden="true" />
              下载脱敏诊断
            </button>
            <button
              className="button secondary"
              type="button"
              onClick={() => download.mutate("PRODUCT_TEMPLATE")}
              disabled={download.isPending}
            >
              <Download size={17} aria-hidden="true" />
              商品 CSV 模板
            </button>
          </div>
          {download.error instanceof Error ? (
            <div className="error-band" role="alert">
              文件生成失败：{download.error.message}
            </div>
          ) : null}

          <div className="catalog-import">
            <p className="system-maintenance-note">
              模板共四列：商品编码用于唯一识别商品；商品名称填写顾客看到的名称；售价按元填写，最多两位小数，例如
              3.50；销售状态只填写“在售”或“停售”。导入前先校验预览，不会直接改库存数量。
            </p>
            <div className="catalog-file-row">
              <label className="button secondary file-button">
                <FileUp size={17} aria-hidden="true" />
                选择商品 CSV
                <input
                  type="file"
                  accept=".csv,text/csv"
                  disabled={catalogRecovery.status !== "NONE" || applyImport.isPending}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    preview.reset();
                    applyImport.reset();
                    setFileError(null);
                    if (!file) return;
                    setCatalogImportKey(crypto.randomUUID());
                    event.currentTarget.value = "";
                    if (file.size > 256 * 1024) {
                      setCatalogCsv("");
                      setCatalogFilename(file.name);
                      setFileError("CSV 文件不能超过 256 KiB");
                      return;
                    }
                    void file
                      .text()
                      .then((content) => {
                        setCatalogCsv(content);
                        setCatalogFilename(file.name);
                      })
                      .catch(() => {
                        setCatalogCsv("");
                        setCatalogFilename(file.name);
                        setFileError("无法读取所选 CSV 文件");
                      });
                  }}
                />
              </label>
              <span className="selected-file">{catalogFilename || "尚未选择文件"}</span>
              <button
                className="button secondary"
                type="button"
                onClick={() => preview.mutate(catalogCsv)}
                disabled={!catalogCsv || preview.isPending || catalogRecovery.status !== "NONE"}
              >
                <FileCheck2 size={17} aria-hidden="true" />
                校验预览
              </button>
              <button
                className="button primary"
                type="button"
                title={importDisabledReason ?? "应用商品目录"}
                onClick={startCatalogImport}
                disabled={Boolean(importDisabledReason) || applyImport.isPending}
              >
                <FileUp size={17} aria-hidden="true" />
                应用导入
              </button>
            </div>
            {fileError ? (
              <div className="error-band" role="alert">
                {fileError}
              </div>
            ) : null}
            {catalogRecoveryNotice ? (
              <div className="error-band" role="alert">
                {catalogRecoveryNotice}
              </div>
            ) : null}
            {ownedCatalogRecovery ? (
              <output className="notice-band">
                <strong>待确认的商品导入</strong>
                <span>
                  {ownedCatalogRecovery.command.filename} · {ownedCatalogRecovery.command.rowCount}
                  项商品
                </span>
                <button
                  className="button secondary"
                  type="button"
                  disabled={applyImport.isPending || ownedCatalogRecovery.state === "CLAIMED"}
                  onClick={() => dispatchCatalogImport(ownedCatalogRecovery)}
                >
                  <RefreshCw size={16} aria-hidden="true" /> 重试上次导入
                </button>
              </output>
            ) : null}
            {preview.data ? (
              preview.data.errors.length > 0 ? (
                <div className="csv-errors" role="alert">
                  <strong>CSV 校验未通过</strong>
                  <ul>
                    {preview.data.errors.slice(0, 8).map((error) => (
                      <li key={[error.line, error.field, error.message].join("-")}>
                        第 {error.line || "-"} 行 · {error.field}：{error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <ProductCatalogPreviewSummary preview={preview.data} />
              )
            ) : null}
            {applyImport.data && catalogRecovery.status === "NONE" ? (
              <output className="success-band">
                <Check size={18} aria-hidden="true" />
                已应用 {applyImport.data.result.imported} 项，未变化{" "}
                {applyImport.data.result.unchanged}{" "}
                项；结果已经保存。维护人员可在诊断信息中查看配置版本和数据来源。
              </output>
            ) : null}
            {applyImport.error instanceof Error ? (
              <div className="error-band" role="alert">
                商品导入失败：{applyImport.error.message}
              </div>
            ) : null}
            {importDisabledReason && catalogCsv && preview.data?.errors.length === 0 ? (
              <p className="disabled-reason">当前不可应用：{importDisabledReason}</p>
            ) : null}
          </div>
        </section>
      </details>
    </>
  );
}

const OPERATOR_SESSION_CHANNEL = "billiard-operator-session-v1";
const OPERATOR_SESSION_STORAGE_KEY = "billiard.operator-session.changed";
const STORE_ACCEPTED_REVALIDATE_MS = 30_000;

function originalCanonicalCoachRecovery(
  pending: WorkforceMutationPending,
  operator: OperatorIdentity,
): CanonicalCoachOnboardingRecovery | null {
  if (
    pending.kind !== "ACCOUNT_INVITE" ||
    operator.role !== "OWNER" ||
    !pending.request.idempotencyKey.startsWith("coach-onboard-invitation:coach-onboard-")
  ) {
    return null;
  }
  const view = readCanonicalCoachOnboardingRecovery(canonicalCoachOnboardingIdentityKey(operator));
  if (
    view.status !== "OWNED" ||
    view.recovery.state !== "OUTCOME_UNKNOWN" ||
    canonicalCoachOnboardingInvitationIdempotencyKey(view.recovery.command.idempotencyKey) !==
      pending.request.idempotencyKey
  ) {
    return null;
  }
  return view.recovery;
}

function workforceMutationRecoveryTitle(kind: WorkforceMutationPending["kind"]): string {
  switch (kind) {
    case "EMPLOYMENT_CREATE":
      return "继续确认新增员工";
    case "EMPLOYMENT_PROFILE_UPDATE":
      return "继续确认人员资料修改";
    case "EMPLOYMENT_ROLE_CHANGE":
      return "继续确认工作角色调整";
    case "EMPLOYMENT_END":
      return "继续确认员工离职";
    case "EMPLOYMENT_REHIRE":
      return "继续确认员工重新入职";
    case "ACCOUNT_DISABLE":
      return "继续确认账号停用";
    case "ACCOUNT_INVITE":
      return "继续确认员工账号邀请";
    case "ACCOUNT_PASSWORD_RESET":
      return "继续确认员工密码恢复";
    case "ACCOUNT_INVITATION_CANCEL":
      return "继续确认取消员工邀请";
    case "ACCOUNT_INVITATION_REISSUE":
      return "继续确认重发员工邀请";
  }
}

function WorkforceMutationRecoveryScreen({
  pending,
  canonicalCoachRecoveryReady,
  retrying,
  loggingOut,
  error,
  logoutError,
  onRetry,
  onLogout,
}: {
  pending: WorkforceMutationPending;
  canonicalCoachRecoveryReady: boolean;
  retrying: boolean;
  loggingOut: boolean;
  error: Error | null;
  logoutError: Error | null;
  onRetry: () => void;
  onLogout: () => void;
}): React.JSX.Element {
  const canonicalCoachPending =
    pending.kind === "ACCOUNT_INVITE" &&
    pending.request.idempotencyKey.startsWith("coach-onboard-invitation:coach-onboard-");
  const actionTitle = canonicalCoachPending
    ? "继续确认助教入职与本人邀请"
    : workforceMutationRecoveryTitle(pending.kind);
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, []);
  return (
    <main className="auth-screen">
      <section className="auth-panel workforce-recovery-panel" aria-labelledby={titleId}>
        <ShieldCheck size={24} aria-hidden="true" />
        <h1 id={titleId} ref={titleRef} tabIndex={-1}>
          上次人员操作仍在安全确认
        </h1>
        <span>
          系统已保留完全相同的原操作。为避免重复建档或改错权限，当前只允许原操作账号继续确认这一笔，其他经营页面暂时锁定。
        </span>
        <dl className="workforce-recovery-summary">
          <div>
            <dt>操作</dt>
            <dd>{actionTitle}</dd>
          </div>
          {pending.kind === "ACCOUNT_DISABLE" ? (
            <>
              <div>
                <dt>员工</dt>
                <dd>{pending.personDisplayName}</dd>
              </div>
              <div>
                <dt>登录账号</dt>
                <dd>{pending.accountLoginHint}</dd>
              </div>
              <div>
                <dt>停用后</dt>
                <dd>这个账号不能再登录；员工档案和历史经营记录不会删除</dd>
              </div>
            </>
          ) : pending.kind === "ACCOUNT_PASSWORD_RESET" ? (
            <>
              <div>
                <dt>员工</dt>
                <dd>原本店在职员工（姓名将在恢复后重新显示）</dd>
              </div>
              <div>
                <dt>本次处理</dt>
                <dd>重新发送本人密码恢复入口；不会新建账号或改变岗位</dd>
              </div>
              <div>
                <dt>完成后</dt>
                <dd>旧设备将退出，员工档案、资金、提成和历史经营记录保持不变</dd>
              </div>
            </>
          ) : pending.kind === "ACCOUNT_INVITE" ? (
            pending.request.personMode === "NEW_PERSON" ? (
              <>
                <div>
                  <dt>员工</dt>
                  <dd>{pending.request.person.displayName}</dd>
                </div>
                <div>
                  <dt>本店工号与岗位</dt>
                  <dd>
                    {pending.request.employment.employmentNumber} ·{" "}
                    {pending.request.employment.positionTitle}
                  </dd>
                </div>
                <div>
                  <dt>工作角色</dt>
                  <dd>
                    {pending.request.employment.roles.map((role) => roleLabels[role]).join("、")}
                  </dd>
                </div>
              </>
            ) : (
              <>
                <div>
                  <dt>员工</dt>
                  <dd>原本店在职员工（姓名将在恢复后重新显示）</dd>
                </div>
                <div>
                  <dt>本店任职</dt>
                  <dd>沿用原本店在职任职和工作角色</dd>
                </div>
              </>
            )
          ) : pending.kind === "ACCOUNT_INVITATION_CANCEL" ||
            pending.kind === "ACCOUNT_INVITATION_REISSUE" ? (
            <>
              <div>
                <dt>员工</dt>
                <dd>原邀请对应员工（姓名将在恢复后重新显示）</dd>
              </div>
              <div>
                <dt>本次处理</dt>
                <dd>
                  {pending.kind === "ACCOUNT_INVITATION_CANCEL"
                    ? "取消仍未完成的本人账号邀请"
                    : "为原员工重新发送一份新的本人账号邀请"}
                </dd>
              </div>
            </>
          ) : (
            <>
              <div>
                <dt>员工</dt>
                <dd>{pending.request.person.displayName}</dd>
              </div>
              <div>
                <dt>本店工号与岗位</dt>
                <dd>
                  {pending.request.employment.employmentNumber} ·{" "}
                  {pending.request.employment.positionTitle}
                </dd>
              </div>
              <div>
                <dt>工作角色</dt>
                <dd>
                  {pending.request.employment.roles.map((role) => roleLabels[role]).join("、")}
                </dd>
              </div>
            </>
          )}
          <div>
            <dt>原操作原因</dt>
            <dd>{pending.request.reason}</dd>
          </div>
        </dl>
        <div className="workforce-recovery-actions">
          <button
            className="button primary"
            type="button"
            disabled={
              retrying || loggingOut || (canonicalCoachPending && !canonicalCoachRecoveryReady)
            }
            onClick={onRetry}
          >
            <RefreshCw className={retrying ? "spin" : undefined} size={17} aria-hidden="true" />
            {retrying ? "正在核对原操作…" : actionTitle}
          </button>
          <button
            className="button secondary"
            type="button"
            disabled={retrying || loggingOut}
            onClick={onLogout}
          >
            <LogOut size={17} aria-hidden="true" />
            {loggingOut ? "正在退出…" : "退出并换账号"}
          </button>
        </div>
        <span>
          请不要新建另一名员工、换一个操作内容或反复点击。页面会在云端和本店资料都确认后自动恢复。
        </span>
        {canonicalCoachPending && !canonicalCoachRecoveryReady ? (
          <div className="error-band" role="alert">
            本机没有可核对的原助教入职内容，不能把这笔操作当作普通员工邀请重试。请回到发起时的浏览器继续；若原浏览器已丢失，请联系项目团队核对原操作号。
          </div>
        ) : null}
        {error ? (
          <div className="error-band" role="alert">
            本次确认尚未完成：{error.message}。原操作仍被保留，可以稍后继续确认。
          </div>
        ) : null}
        {logoutError ? (
          <div className="error-band" role="alert">
            暂时无法退出：{logoutError.message}
          </div>
        ) : null}
      </section>
    </main>
  );
}

function AuthenticatedApp(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [forcedAuthenticationRequired, setForcedAuthenticationRequired] = useState(false);
  const [identityRevalidationRequired, setIdentityRevalidationRequired] = useState(false);
  const session = useQuery({
    queryKey: ["operator-session"],
    queryFn: ({ signal }) => getOperatorSession(signal),
    retry: false,
    refetchInterval: (query) => {
      const evidence = (
        query.state.data as { evidence?: "SIMULATED" | "STORE_ACCEPTED" } | undefined
      )?.evidence;
      if (isNativeRuntime()) return 2_000;
      return evidence === "STORE_ACCEPTED" ? STORE_ACCEPTED_REVALIDATE_MS : false;
    },
  });
  const sessionAuthenticationRequired =
    session.error instanceof ApiRequestError && session.error.code === "AUTH_REQUIRED";
  const authenticationRequired = forcedAuthenticationRequired || sessionAuthenticationRequired;
  const baseIdentityScope =
    session.data && !authenticationRequired
      ? operatorPrivateQueryScope(session.data.operator)
      : null;
  const baseIdentityScopeRef = useRef<string | null>(baseIdentityScope);
  baseIdentityScopeRef.current = baseIdentityScope;
  const [isolatedBaseIdentityScope, setIsolatedBaseIdentityScope] = useState<string | null>(null);
  useEffect(() => {
    if (baseIdentityScope === isolatedBaseIdentityScope) return;
    let active = true;
    void clearPrivateOperatorQueries(queryClient).then(() => {
      if (active) setIsolatedBaseIdentityScope(baseIdentityScope);
    });
    return () => {
      active = false;
    };
  }, [baseIdentityScope, isolatedBaseIdentityScope, queryClient]);
  const workforceContext = useQuery({
    queryKey: ["workforce-principal-context", baseIdentityScope],
    queryFn: ({ signal }) => {
      if (!session.data) {
        throw new ApiRequestError("AUTH_REQUIRED", "请先登录");
      }
      return getWorkforcePrincipalContext(session.data.operator, signal);
    },
    enabled:
      session.data?.evidence === "STORE_ACCEPTED" &&
      baseIdentityScope !== null &&
      baseIdentityScope === isolatedBaseIdentityScope,
    retry: false,
    refetchInterval:
      session.data?.evidence === "STORE_ACCEPTED" ? STORE_ACCEPTED_REVALIDATE_MS : false,
  });
  const [isolatedWorkforceErrorScope, setIsolatedWorkforceErrorScope] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (
      session.data?.evidence !== "STORE_ACCEPTED" ||
      !workforceContext.error ||
      baseIdentityScope === null
    ) {
      setIsolatedWorkforceErrorScope(null);
      return;
    }
    let active = true;
    setIdentityRevalidationRequired(true);
    setIsolatedWorkforceErrorScope(null);
    void clearPrivateOperatorQueries(queryClient, true).then(() => {
      if (active) setIsolatedWorkforceErrorScope(baseIdentityScope);
    });
    return () => {
      active = false;
    };
  }, [baseIdentityScope, queryClient, session.data?.evidence, workforceContext.error]);
  const workforceMutationRecoveryRequired =
    workforceContext.error !== null &&
    typeof workforceContext.error === "object" &&
    "code" in workforceContext.error &&
    workforceContext.error.code === "WORKFORCE_MUTATION_OUTCOME_UNKNOWN";
  const workforceMutationRecovery = useQuery({
    queryKey: ["workforce-mutation-recovery", baseIdentityScope],
    queryFn: ({ signal }) => getPendingWorkforceMutation(signal),
    enabled:
      workforceMutationRecoveryRequired &&
      session.data?.evidence === "STORE_ACCEPTED" &&
      baseIdentityScope !== null &&
      baseIdentityScope === isolatedBaseIdentityScope &&
      baseIdentityScope === isolatedWorkforceErrorScope,
    retry: false,
    staleTime: 0,
    gcTime: Number.POSITIVE_INFINITY,
  });
  const workforceMutationRetryInFlight = useRef(false);
  const workforceMutationRetry = useMutation({
    mutationFn: async (pending: WorkforceMutationPending) => {
      if (pending.kind === "ACCOUNT_DISABLE") {
        await disableWorkforceAccount(pending.personId, pending.accountId, pending.request);
      } else if (pending.kind === "ACCOUNT_INVITE") {
        if (pending.request.idempotencyKey.startsWith("coach-onboard-invitation:coach-onboard-")) {
          const operator = session.data?.operator;
          const recovery = operator ? originalCanonicalCoachRecovery(pending, operator) : null;
          if (!operator || !recovery) {
            throw new ApiRequestError(
              "CANONICAL_COACH_ONBOARDING_RECOVERY_MISSING",
              "本机缺少原助教入职内容，不能改用普通员工邀请重试",
            );
          }
          const identityKey = canonicalCoachOnboardingIdentityKey(operator);
          if (!beginCanonicalCoachOnboardingDispatch(recovery, identityKey)) {
            throw new ApiRequestError(
              "CANONICAL_COACH_ONBOARDING_RECOVERY_BUSY",
              "原助教入职请求正在核对，请不要重复点击",
            );
          }
          try {
            const configuration = await getCoachConfiguration();
            await createCanonicalCoachOnboarding(recovery.command, {
              operator,
              configuration,
              replayExactCommand: true,
            });
            if (!clearCanonicalCoachOnboardingRecovery(recovery, identityKey)) {
              throw new ApiRequestError(
                "CANONICAL_COACH_ONBOARDING_RESULT_UNKNOWN",
                "助教邀请已返回成功，但本机原请求保护记录未安全清除；请继续核对",
              );
            }
          } finally {
            finishCanonicalCoachOnboardingDispatch(recovery, identityKey);
          }
        } else {
          await createWorkforceInvitation(pending.request);
        }
      } else if (pending.kind === "ACCOUNT_PASSWORD_RESET") {
        await createWorkforcePasswordResetInvitation(pending.request);
      } else if (pending.kind === "ACCOUNT_INVITATION_CANCEL") {
        if (!session.data) throw new ApiRequestError("AUTH_REQUIRED", "请先登录");
        const expected = await getWorkforceInvitationAdmin(
          session.data.operator.tenantId,
          session.data.operator.storeId,
          pending.personId,
        );
        await cancelWorkforceInvitation(pending.request, expected);
      } else if (pending.kind === "ACCOUNT_INVITATION_REISSUE") {
        if (!session.data) throw new ApiRequestError("AUTH_REQUIRED", "请先登录");
        const expected = await getWorkforceInvitationAdmin(
          session.data.operator.tenantId,
          session.data.operator.storeId,
          pending.personId,
        );
        await reissueWorkforceInvitation(pending.request, expected);
      } else {
        await upsertCanonicalWorkforceEmployment(pending.request);
      }
      const refreshed = await workforceMutationRecovery.refetch();
      if (refreshed.error) throw refreshed.error;
      if (refreshed.data !== null) {
        throw new ApiRequestError(
          "WORKFORCE_WRITE_CONFIRMATION_PENDING",
          "原操作已经重试，但本店人员资料仍在确认",
        );
      }
      await workforceContext.refetch();
    },
    onSettled: () => {
      workforceMutationRetryInFlight.current = false;
    },
  });
  const workforceRecoveryLogout = useMutation({
    mutationFn: logoutOperator,
    onSuccess: async () => {
      await clearPrivateOperatorQueries(queryClient);
      await queryClient.resetQueries({ queryKey: ["operator-session"] });
    },
  });
  const authorizationIdentityScope =
    session.data && !authenticationRequired
      ? session.data.evidence === "SIMULATED"
        ? baseIdentityScope
        : workforceContext.data
          ? operatorPrivateQueryScope(session.data.operator, workforceContext.data)
          : null
      : null;
  const [isolatedAuthorizationIdentityScope, setIsolatedAuthorizationIdentityScope] = useState<
    string | null
  >(null);
  useEffect(() => {
    if (
      authorizationIdentityScope === null ||
      authorizationIdentityScope === isolatedAuthorizationIdentityScope
    ) {
      return;
    }
    let active = true;
    void clearPrivateOperatorQueries(queryClient, true).then(() => {
      if (active) setIsolatedAuthorizationIdentityScope(authorizationIdentityScope);
    });
    return () => {
      active = false;
    };
  }, [authorizationIdentityScope, isolatedAuthorizationIdentityScope, queryClient]);

  useEffect(
    () =>
      subscribeOperatorAuthenticationInvalidation(() => {
        setForcedAuthenticationRequired(true);
        setIdentityRevalidationRequired(true);
        setIsolatedBaseIdentityScope(null);
        setIsolatedAuthorizationIdentityScope(null);
        void clearPrivateOperatorQueries(queryClient);
      }),
    [queryClient],
  );

  useEffect(() => {
    if (session.error && !sessionAuthenticationRequired) {
      setIdentityRevalidationRequired(true);
      void clearPrivateOperatorQueries(queryClient);
    }
  }, [queryClient, session.error, sessionAuthenticationRequired]);
  useEffect(() => {
    if (session.data?.operator) setOperatorRequestIdentity(session.data.operator);
  }, [session.data?.operator]);

  const revalidationInFlight = useRef(false);
  const revalidateCurrentIdentity = useCallback(async () => {
    if (revalidationInFlight.current) return;
    revalidationInFlight.current = true;
    setIdentityRevalidationRequired(true);
    try {
      const sessionResult = await session.refetch();
      if (
        !sessionResult.error &&
        sessionResult.data?.evidence === "STORE_ACCEPTED" &&
        operatorPrivateQueryScope(sessionResult.data.operator) === baseIdentityScopeRef.current
      ) {
        await workforceContext.refetch();
      }
    } finally {
      revalidationInFlight.current = false;
    }
  }, [session.refetch, workforceContext.refetch]);

  useEffect(() => {
    let handle: { remove(): Promise<void> } | null = null;
    void listenForNativeResume(() => void revalidateCurrentIdentity()).then((next) => {
      handle = next;
    });
    return () => void handle?.remove();
  }, [revalidateCurrentIdentity]);
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const revalidateWhenVisible = () => {
      if (document.visibilityState === "visible") void revalidateCurrentIdentity();
    };
    window.addEventListener("focus", revalidateWhenVisible);
    document.addEventListener("visibilitychange", revalidateWhenVisible);
    return () => {
      window.removeEventListener("focus", revalidateWhenVisible);
      document.removeEventListener("visibilitychange", revalidateWhenVisible);
    };
  }, [revalidateCurrentIdentity]);

  const sessionChannelRef = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const receiveScope = (incomingScope: string | null) => {
      if (!operatorSessionChangeRequiresRefresh(baseIdentityScopeRef.current, incomingScope))
        return;
      setIdentityRevalidationRequired(true);
      setIsolatedBaseIdentityScope(null);
      setIsolatedAuthorizationIdentityScope(null);
      if (incomingScope === null) setForcedAuthenticationRequired(true);
      void clearPrivateOperatorQueries(queryClient).then(async () => {
        if (incomingScope !== null) {
          setForcedAuthenticationRequired(false);
          await session.refetch();
        }
      });
    };
    const channel =
      typeof BroadcastChannel === "function"
        ? new BroadcastChannel(OPERATOR_SESSION_CHANNEL)
        : null;
    sessionChannelRef.current = channel;
    if (channel) {
      channel.onmessage = (event: MessageEvent<unknown>) => {
        const scope = (event.data as { scope?: unknown } | null)?.scope;
        if (scope === null || typeof scope === "string") receiveScope(scope);
      };
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key !== OPERATOR_SESSION_STORAGE_KEY || !event.newValue) return;
      try {
        const scope = (JSON.parse(event.newValue) as { scope?: unknown }).scope;
        if (scope === null || typeof scope === "string") receiveScope(scope);
      } catch {
        // Ignore malformed cross-tab notifications and keep the current identity closed over.
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      sessionChannelRef.current = null;
      channel?.close();
      window.removeEventListener("storage", onStorage);
    };
  }, [queryClient, session.refetch]);
  const lastPublishedScope = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const scopeToPublish = authenticationRequired
      ? null
      : session.data && baseIdentityScope
        ? baseIdentityScope
        : undefined;
    if (scopeToPublish === undefined || lastPublishedScope.current === scopeToPublish) return;
    lastPublishedScope.current = scopeToPublish;
    sessionChannelRef.current?.postMessage({ scope: scopeToPublish });
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(
          OPERATOR_SESSION_STORAGE_KEY,
          JSON.stringify({ scope: scopeToPublish, changedAt: Date.now() }),
        );
      } catch {
        // BroadcastChannel remains the primary path when browser storage is unavailable.
      }
    }
  }, [authenticationRequired, baseIdentityScope, session.data]);

  useEffect(() => {
    if (!identityRevalidationRequired || authenticationRequired) return;
    if (!session.data || session.error || baseIdentityScope !== isolatedBaseIdentityScope) return;
    if (session.data.evidence === "STORE_ACCEPTED") {
      if (
        workforceContext.isPending ||
        workforceContext.error ||
        !workforceContext.data ||
        authorizationIdentityScope !== isolatedAuthorizationIdentityScope
      ) {
        return;
      }
    }
    setIdentityRevalidationRequired(false);
  }, [
    authenticationRequired,
    authorizationIdentityScope,
    baseIdentityScope,
    identityRevalidationRequired,
    isolatedAuthorizationIdentityScope,
    isolatedBaseIdentityScope,
    session.data,
    session.error,
    workforceContext.data,
    workforceContext.error,
    workforceContext.isPending,
  ]);

  if (session.isPending && !session.data) {
    return (
      <>
        <NativeRuntimeNotice />
        <main className="auth-screen">
          <div className="auth-loading" aria-live="polite">
            <RefreshCw className="spin" size={20} />
            正在验证员工会话
          </div>
        </main>
      </>
    );
  }
  if (authenticationRequired) {
    return (
      <>
        <NativeRuntimeNotice />
        <LoginScreen
          onAuthenticated={async () => {
            await clearPrivateOperatorQueries(queryClient);
            setIdentityRevalidationRequired(true);
            await queryClient.resetQueries({ queryKey: ["operator-session"], exact: true });
            setForcedAuthenticationRequired(false);
          }}
        />
      </>
    );
  }

  const accessGate = operatorAccessGate({
    deploymentEnvironment: webBuildProvenance.deploymentEnvironment,
    evidence: session.data?.evidence ?? null,
    sessionHasData: Boolean(session.data),
    sessionError: session.error,
    workforceHasData: Boolean(workforceContext.data),
    workforceError: workforceContext.error,
  });
  if (accessGate === "SESSION_UNAVAILABLE") {
    return (
      <>
        <NativeRuntimeNotice />
        <main className="auth-screen">
          <div className="auth-panel auth-unreachable" role="alert">
            <WifiOff size={22} />
            <strong>员工会话暂时无法核验</strong>
            <span>
              {session.error instanceof Error
                ? session.error.message
                : "系统不会使用上一次登录缓存继续开放经营页面"}
            </span>
            <button
              className="button secondary"
              type="button"
              onClick={() => void revalidateCurrentIdentity()}
            >
              <RefreshCw size={17} />
              重新核验
            </button>
          </div>
        </main>
      </>
    );
  }
  if (accessGate === "SIMULATED_NOT_ALLOWED") {
    return (
      <>
        <NativeRuntimeNotice />
        <main className="auth-screen">
          <div className="auth-panel auth-unreachable" role="alert">
            <LockKeyhole size={22} />
            <strong>当前部署拒绝演示身份</strong>
            <span>正式或预发布环境只能使用门店已核验账号，系统不会使用演示角色继续。</span>
          </div>
        </main>
      </>
    );
  }
  if (baseIdentityScope !== isolatedBaseIdentityScope) {
    return (
      <>
        <NativeRuntimeNotice />
        <main className="auth-screen">
          <div className="auth-loading" aria-live="polite">
            <RefreshCw className="spin" size={20} />
            正在隔离上一账号的本机数据
          </div>
        </main>
      </>
    );
  }
  if (session.data?.evidence === "STORE_ACCEPTED" && workforceContext.isPending) {
    return (
      <>
        <NativeRuntimeNotice />
        <main className="auth-screen">
          <div className="auth-loading" aria-live="polite">
            <RefreshCw className="spin" size={20} />
            正在核验本店任职和权限
          </div>
        </main>
      </>
    );
  }
  if (accessGate === "WORKFORCE_UNAVAILABLE") {
    if (workforceMutationRecoveryRequired) {
      if (workforceMutationRecovery.isPending) {
        return (
          <>
            <NativeRuntimeNotice />
            <main className="auth-screen">
              <div className="auth-loading" aria-live="polite">
                <RefreshCw className="spin" size={20} />
                正在读取上次人员操作
              </div>
            </main>
          </>
        );
      }
      if (workforceMutationRecovery.data) {
        return (
          <>
            <NativeRuntimeNotice />
            <WorkforceMutationRecoveryScreen
              pending={workforceMutationRecovery.data}
              canonicalCoachRecoveryReady={Boolean(
                session.data?.operator &&
                  originalCanonicalCoachRecovery(
                    workforceMutationRecovery.data,
                    session.data.operator,
                  ),
              )}
              retrying={workforceMutationRetry.isPending}
              loggingOut={workforceRecoveryLogout.isPending}
              error={
                workforceMutationRetry.error instanceof Error ? workforceMutationRetry.error : null
              }
              logoutError={
                workforceRecoveryLogout.error instanceof Error
                  ? workforceRecoveryLogout.error
                  : null
              }
              onRetry={() => {
                if (!workforceMutationRetryInFlight.current && workforceMutationRecovery.data) {
                  workforceMutationRetryInFlight.current = true;
                  workforceMutationRetry.mutate(workforceMutationRecovery.data);
                }
              }}
              onLogout={() => workforceRecoveryLogout.mutate()}
            />
          </>
        );
      }
    }
    return (
      <>
        <NativeRuntimeNotice />
        <main className="auth-screen">
          <div className="auth-panel auth-unreachable" role="alert">
            <LockKeyhole size={22} />
            <strong>本店权限资料暂时无法核验</strong>
            <span>
              {workforceContext.error instanceof Error
                ? workforceContext.error.message
                : "系统不会使用旧角色继续开放经营页面"}
            </span>
            <button
              className="button secondary"
              type="button"
              onClick={() => void revalidateCurrentIdentity()}
            >
              <RefreshCw size={17} aria-hidden="true" />
              重新核验
            </button>
            {workforceMutationRecoveryRequired ? (
              <button
                className="button secondary"
                type="button"
                disabled={workforceRecoveryLogout.isPending}
                onClick={() => workforceRecoveryLogout.mutate()}
              >
                <LogOut size={17} aria-hidden="true" />
                {workforceRecoveryLogout.isPending ? "正在退出…" : "退出并换原操作账号"}
              </button>
            ) : null}
            {workforceMutationRecoveryRequired && workforceRecoveryLogout.error instanceof Error ? (
              <div className="error-band" role="alert">
                暂时无法退出：{workforceRecoveryLogout.error.message}
              </div>
            ) : null}
          </div>
        </main>
      </>
    );
  }
  if (
    identityRevalidationRequired ||
    authorizationIdentityScope !== isolatedAuthorizationIdentityScope
  ) {
    return (
      <>
        <NativeRuntimeNotice />
        <main className="auth-screen">
          <div className="auth-loading" aria-live="polite">
            <RefreshCw className="spin" size={20} />
            正在应用最新本店权限
          </div>
        </main>
      </>
    );
  }
  const effectiveRoles =
    session.data?.evidence === "SIMULATED"
      ? [session.data.operator.role]
      : effectiveRolesFromWorkforceContext(workforceContext.data as WorkforcePrincipalContext);
  return (
    <>
      <NativeRuntimeNotice />
      <Shell
        operator={session.data?.operator as OperatorIdentity}
        effectiveRoles={effectiveRoles}
        authenticationEvidence={session.data?.evidence as "SIMULATED" | "STORE_ACCEPTED"}
        authorizationIdentityScope={authorizationIdentityScope}
        sessionContext={
          session.data?.evidence === "STORE_ACCEPTED" ? session.data.session : undefined
        }
        workforceContext={workforceContext.data ?? null}
      />
    </>
  );
}

export function App(): React.JSX.Element {
  const location = useLocation();
  return location.pathname === "/marketing/unsubscribe" ? (
    <MarketingUnsubscribePage />
  ) : location.pathname === "/activate" ? (
    <WorkforceInvitationActivationPage />
  ) : (
    <AuthenticatedApp />
  );
}
